/**
 * PSP/CRE Tool v3.0 - Tab 3: Medical Configuration
 * PHASE 2.5 - Architecture Components + v3.0 PHASE 5: Sub-Unit MTF Assignment
 * 
 * Author: Dr. Tatar Radu MD
 * Date: March 5, 2026
 * Version: 4.33.0 (March 25, 2026) - BUG-209: Removed internal tracking tags (ENH/ARCH/Phase) from rendered UI strings. No logic change.
 *
 * Version: 4.32.0 (March 25, 2026) - BUG-200 + BUG-201:
 *   BUG-200/201 ROOT CAUSE: extractPrimaryNationalStandard() line ~3701 short-circuits
 *   returning 'MULTINATIONAL' for any multinational force before reaching brigade-level
 *   disaggregation. getDefaultGroundFleet('MULTINATIONAL') returned a hardcoded minimum
 *   placeholder (SPRINTER=8, BOXER=4) regardless of actual brigade composition. This is
 *   why the fleet showed only German vehicles (Mercedes Sprinter + Boxer) in every MND
 *   scenario — 'MULTINATIONAL' keys to the NATO placeholder, not to any per-nation fleet.
 *   French VAB Sanitaire / Griffon SAN and Romanian TABC79 / MLVM / Piranha V were never
 *   populated. Total count (12) was one German brigade fleet, not RO+FR+DE aggregated (~58).
 *   Fix: module-level helper buildAggregatedMNDFleet(brigades) sums getDefaultGroundFleet()
 *   across each brigade's own nationalStandard. Vehicle codes present in multiple nations
 *   are additive (e.g. both NATO and GERMAN use SPRINTER → counts sum correctly).
 *   Applied to all three call sites: useState initializer, useEffect fleet-reset,
 *   and RESET TO TOE DEFAULTS button handler.
 *
 * Version: 4.31.0 (March 20, 2026) - ENH-027 / BUG-170: Ground ambulance fleet no longer
 *   stuck at NATO defaults when Romanian, Polish, or other non-NATO force is loaded.
 *   Root cause: useEffect fleet-reset guard (lines ~3061) checked pool validity using
 *   getMEDEVACPoolOptions(), which always appends NATO_GENERIC to every nation's list.
 *   Therefore currentPoolValid was always true → setTacticalEvacConfig returned prev →
 *   fleet never updated from whatever the useState initializer resolved to (often NATO
 *   if scenario.force not yet populated at mount time).
 *   Fix: replaced pool-validity guard with prevFleetNatStdRef comparison. Effect now
 *   skips only when the resolved natStd hasn't actually changed. Fleet and air pool
 *   always reset to correct national defaults on genuine natStd transitions.
 *   Added corpsLeadNation + brigades?.length to dep array — multinational division
 *   brigade count changes now also trigger re-evaluation.
 *   Load handler path (loadMedicalConfig → setTacticalEvacConfig) is unaffected —
 *   saved user config continues to win over this effect.
 *
 * Version: 4.30.3 (March 20, 2026) - BUG-168: F.3 STRATEVAC Bottleneck Monitor showed
 *   "Run Tab 5 Calculate" even when scenario.casualty_projections already had results.
 *   Root cause: F.3 read exclusively from getBaseline()?.results (localStorage, populated
 *   only when Tab5 mounts via storeBaseline). scenario.casualty_projections is the identical
 *   results object written by Tab3 Save & Continue — but was never checked.
 *   Fix: baselineResults = getBaseline()?.results ?? scenario?.casualty_projections.
 *   F.3 now shows data immediately after Tab3 Save & Continue without needing Tab5 mount.
 *   Text updates: chip "Run Tab 5 to refresh" → "Save & Continue to calculate";
 *   alert body updated; footer "Results from last Tab 5 calculation" → "Results from last calculation".
 *
 * Version: 4.30.2 (March 20, 2026) - BUG-167: Attach dialog Detach Day accepted values
 *   before Attach Day with no feedback. Fix:
 *   (1) inputProps.min on Detach Day = parseInt(attach_day) + 1 (dynamic, tracks Attach Day).
 *   (2) TextField error + helperText: "Must be after Attach Day (D+N)" when detach <= attach.
 *   (3) Attach Asset button disabled extended: blocks save when detach_day <= attach_day.
 *   handleSaveAttachment guard at line 4035 was already correct — UI now matches it.
 *   Also: MOU helperText updated with AJP-4.10C §5.4 reference (ENH-029 one-liner).
 *
 * Version: 4.30.1 (March 18, 2026) - ESLint: remove unused strEvac variable.
 *   strEvac was declared but BUG-153 fix changed airEnabled to read from
 *   strategicEvac local state, leaving strEvac unreferenced. Removed.
 * Version: 4.30.0 - BUG-152 + BUG-153 + BUG-154 (March 18, 2026):
 *   BUG-152: Section 3G bare text markers `*3G SECTION` and
 *     `-> Section 3G: Advanced Configuration` rendering as visible DOM text nodes.
 *     BUG-134 (Session 39) converted markers in 3B-3F but missed 3G.
 *     Fix: `*3G SECTION` removed; `-> Section 3G:` heading converted to
 *     JSX comment + proper Typography (no bare arrow prefix).
 *   BUG-153: F.3 STRATEVAC Bottleneck Monitor shows "not enabled" despite air
 *     evacuation toggle ON with fully configured airfield.
 *     Root cause: F.3 read airEnabled from scenario prop (scenario?.medical_configuration
 *     ?.strategic_evac_config) which only updates AFTER Save & Continue. The local
 *     React state (strategicEvac) is live the moment the toggle fires.
 *     Fix: airEnabled now derived from strategicEvac local state directly.
 *   BUG-154: Attach Evacuation Asset dialog — Ground Ambulance model dropdown empty.
 *     Root cause: filter `spec.code && spec.label` — AMBULANCE_TYPES entries use
 *     `name` not `label`. spec.label always undefined → filter returned [] → empty.
 *     Fix: filter changed to `spec.code && spec.name`; display uses `spec.name`.
 * Version: 4.29.0 - BUG-133 + BUG-134 (March 17, 2026):
 *   BUG-133 FIX: F.3 STRATEVAC Bottleneck Monitor now detects air evacuation correctly.
 *     Root cause: airEnabled check required BOTH air_enabled key AND airfields[].some(is_active).
 *     Pre-Phase 6 saves (or scenarios saved before buildUpdates() included airfields key)
 *     have air_enabled: true but no airfields array — second condition always false →
 *     "not enabled" shown even with toggle ON and aircraft configured.
 *     Fix: when airfields key is absent, trust air_enabled === true alone. When airfields
 *     array is present, still verify at least one has is_active: true (existing behaviour).
 *     Also fixes Tab 6 Section 4 Evacuation Analysis which reads the same prop path.
 *   BUG-134 FIX: Dev section comment markers ( 3B SECTION etc.) converted from bare
 *     JSX text nodes (rendered visibly in DOM) to JSX comments (invisible).
 *     Six locations: 3B SECTION, 3C SECTION, 3D SECTION, 3E SECTION, 3F Section, A2 subsection.
 *
 * Version: 4.28.0 - BUG-120: MASCAL recommendation gated on actual casualty_projections — no longer fires at config time before simulation runs (March 16, 2026)
 *
 * Version: 4.27.0 - BUG-114: Helicopter summary chip now shows MEDEVAC sorties/day from tacticalEvacConfig.air_pool instead of always-zero organic_assets.helicopters.allocated (March 16, 2026)
 *
 * Version: 4.26.0 - BUG-112/116: MULTINATIONAL division national standard resolution — extractPrimaryNationalStandard + fleet initializer now detect via fbResult.brigades[] (March 16, 2026)
 *
 * Version: 4.25.0 - Phase 6: Configurable MASCAL Thresholds (March 15, 2026)
 *   Expert Mode MASCAL Triggers box replaced with editable TextFields:
 *   mtf_warning (%), mtf_critical (%), surgical_queue_warning (h), surgical_queue_critical (h).
 *   Bound to mascalConfig.thresholds (already saved/loaded via mascal_config.thresholds).
 *   Reset to NATO Defaults button restores MASCAL_TRIGGERS constant values.
 *   Standard Mode display updated to read from mascalConfig.thresholds (not MASCAL_TRIGGERS).
 *   Zero calculator changes in Tab 3 — thresholds reach calculator via saved scenario.
 *
 * Version: 4.25.0 - Phase 6: Configurable MASCAL Thresholds (March 15, 2026)
 *   Expert Mode: Read-only MASCAL Triggers box replaced with four editable TextField inputs
 *   bound to mascalConfig.thresholds (mtf_warning, mtf_critical, surgical_queue_warning,
 *   surgical_queue_critical) + "Reset to NATO Defaults" button.
 *   Standard Mode: Warning/Critical display now reads mascalConfig.thresholds (state) instead
 *   of MASCAL_TRIGGERS constants — reflects user-configured values after scenario load.
 *   Save/load unchanged — thresholds already saved to mascal_config.thresholds since v4.x.
 *   No calculator change in this file — calculator v5.44.0 reads the saved thresholds.
 *
 * Version: 4.24.0 - ENH-005: STRATEVAC Bottleneck Monitor in Section F.3 (March 15, 2026)
 *   Import: getBaseline added to calculationService.js import.
 *   Section F.3 Accordion in Section 3F (after F.2, before 3G):
 *   Reads getBaseline()?.results?.mascal_analysis?.stratevac_schedule_analysis.
 *   - No STRATEVAC configured: info note → configure in 3E.
 *   - STRATEVAC configured, no results: "Run Tab 5 Calculate" prompt.
 *   - Results available:
 *     · Corps MASCAL auto-alert when any_mascal OR total_backflow_to_r3 > 0.
 *       Includes corrective action guidance.
 *     · Per-airfield panels: PHA fill LinearProgress gauge (peak day highlight),
 *       stats row (arrived/departed/backflow/max wait), departure schedule chips
 *       (amber when queue > 75% of PHA beds on that day).
 *     · Doctrinal footer: AJMedP-1 PHA thresholds + 72h DOW penalty reference.
 *   Header chips: PHA OVERFLOW (red) / BACKFLOW (amber) / "Run Tab 5" (outlined).
 *   Zero calculator changes — display-only, reads last saved baseline.
 *
 * Version: 4.23.1 - ENH-006 doctrinal setup hours (March 15, 2026)
 *   Import MTF_SETUP_TIMES_HOURS from casualtyCalculatorConstants.
 *   getDoctrinalSetupHours(facilityType): resolves Role 1/2B/2E/3 setup hours from constant.
 *   handleFacilityActivationUpdate: auto-fills setup_hours from doctrine when activate_day
 *     is first set and setup_hours is still null.
 *   Setup Hours helperText shows "AJMedP-1 default: Xh" for known facility types.
 *   Always-visible doctrinal reference line below the three fields:
 *     "AJMedP-1 setup times: Role 1=1h · Role 2B=12h · Role 2E=24h · Role 3=48h"
 *   Tooltip updated to include AJMedP-1 citation and auto-fill behaviour.
 *   Alert text updated: "Setup ramp: Xh — capacity reaches 100% on Day Y (AJMedP-1)."
 *
 * Version: 4.23.0 - ENH-006: Dynamic MTF activation UI (March 15, 2026)
 *   Facility card (each Accordion): new "Activation Window" row below Detached Assets.
 *   Three inline fields: Activate Day (number, null=Day1), Deactivate Day (number, null=end),
 *   Setup Hours (number, null=instant). All optional — leave blank for always-active behaviour.
 *   handleFacilityActivationUpdate(facilityId, field, value): updates forceBuilderFacilities.
 *   Data flows through existing buildUpdates() per_facility_evacuation_assets path unchanged.
 *   casualtyCalculator.js v5.44.0 reads activate_day/deactivate_day/setup_hours per-day.
 *
 * Version: 4.22.0 - ENH-002: Attach/Detach Evacuation Assets (March 15, 2026)
 *   Phase 1 (MedicalService.js v2.1.0): attached_assets/detached_assets initialised to []
 *   (not null) in all 19 facility mapping blocks across extractMedicalFacilities().
 *   Phase 2+3 (Tab3 v4.22.0):
 *   - State: attachDialog, detachDialog, attachForm, detachForm.
 *   - Load path: saved attached_assets/detached_assets merged back from
 *     per_facility_evacuation_assets into fresh forceBuilderFacilities on scenario load.
 *   - Handlers: handleOpenAttachDialog, handleCloseAttachDialog, handleSaveAttachment,
 *     handleRemoveAttachment, handleOpenDetachDialog, handleCloseDetachDialog,
 *     handleSaveDetachment, handleRemoveDetachment.
 *   - UI: Attach Assets / Detached Assets rows in each facility Accordion card,
 *     below Organic Assets. Chips show model, qty, day range, source_unit tooltip.
 *   - Dialogs: AttachAssetDialog (asset_type, model, qty, days, source, MOU),
 *     DetachAssetDialog (asset_type, qty, days, destination).
 *   buildUpdates() already passed attached_assets/detached_assets through — no change needed.
 *
 * Version: 4.21.0 - BUG-100: Rail STRATEVAC schedule preview uses actual scenario duration (March 14, 2026)
 *   Root cause: computeTrainDepartureDays call at rail preview hardcoded `14` as num_days.
 *   Air schedule preview correctly used scenario?.operation_parameters?.duration_days ?? 10.
 *   Fix: extracted opDays const (same pattern as air section); used in call, header label,
 *   and total-capacity label. Display now reads "20-day operation" for a 20-day scenario.
 *
 * Version: 4.20.0 - BUG-098/102: Helicopter litter and Division Support Matrix (March 13, 2026)
 *   BUG-098: Helicopter type dropdown showed "? litter" for all aircraft.
 *     Root cause: spec.litter_capacity is not a top-level field; correct path is
 *     spec.capacity?.stretchers (documented in v4.9.1 FIX-3 but missed in this Select).
 *     Fixed in MenuItem Chip label and FormHelperText.
 *   BUG-102: Division Support Matrix all 12 cross-division pairs showed "?".
 *     Root cause: emoji/unicode checkmark and X characters corrupted to "?" (APP_RULES §5.1).
 *     Fix: diagonal cells render "-"; pair cells use CheckCircleIcon (enabled) / CancelIcon
 *     (disabled) — both already imported from @mui/icons-material. Legend updated to match.
 *     Added CancelIcon to existing MUI icon import.
 *
 * Version: 4.19.0 - BUG-099/104: STRATEVAC distance fields no longer render blank (March 13, 2026)
 *   Root cause: primary airfield r2b/r2e distances initialised from DEFAULT_STRATEGIC_DISTANCES
 *   key paths that do not exist → undefined. Alternate airfield distances initialised to null.
 *   Both render blank because (undefined ?? '') = '' and (null ?? '') = ''.
 *   Fix: ?? 0 fallbacks on all 4 distance fields in (a) useState lazy init, (b) load-from-saved
 *   flat-key path, (c) load-from-saved modern airfields[] path — covers pre-fix saved scenarios.
 *   0 is the correct sentinel: field shows 0 km, planner edits to actual value.
 *   onChange handlers already use parseInt(...) || 0 — no change needed there.
 *
 * Version: 4.18.0 - BUG-097/101: National Standard shows correct lead nation for MULTINATIONAL corps (March 13, 2026)
 *   extractPrimaryNationalStandard() and initNatStd now check force.corpsLeadNation first (ENH-018F field).
 *   Fixes header "National Standard" chip + ground ambulance fleet defaults for Romanian lead-nation corps.
 *
 * Version: 4.17.0 - BUG-079: Secondary aircraft schedule (schedule_b) per airfield (March 13, 2026)
 *   schedule_b added to airfields[0] and airfields[1] state (null = disabled, backward compatible).
 *   UI: "Secondary Aircraft (Mixed-Fleet)" toggle + Aircraft/Mode/Sorties/DayChips inside primary
 *     airfield Paper. Day chips use 'secondary' color; overlap with primary days shown with warning border.
 *   Load path: schedule_b preserved from saved airfields[]; null-defaulted on pre-v4.17 scenarios.
 *   Calculator: casualtyCalculator.js v5.37.0 reads schedule_b, unions departure days, adds capacity
 *     to same PHA queue on departure days. schedule_b parse failures silently downgrade to primary only.
 *
 * Version: 4.16.0 - BUG-078: Helicopter type selector in Tactical MEDEVAC (March 13, 2026)
 *   air_pool.aircraft_type existed in state and was saved since v4.8.0, but had no UI control —
 *   silently auto-set from pool's primary_aircraft and never user-overridable.
 *   Fix: FormControl Select added below the Air Pool selector, populated from HELICOPTER_SPECS.
 *   Pool change pre-populates the selector (primary_aircraft); user can override independently.
 *   Shows litter capacity + speed chip per option for doctrinal context.
 *   Zero data model change, zero calculator change — aircraft_type was already read by medevacModule.
 *
 * Version: 4.15.0 - ENV-003b + UI-3B-001: Tab2 re-sync alert + fleet total row (March 7, 2026)
 *   ENV-003b: TAB2_TO_AD_THREAT / TAB2_TO_WX hoisted to module level.
 *     Added lastKnownTab2Ref to track Tab2 threat+weather state at last sync.
 *     loadExistingConfiguration() + initializeDefaults() update the ref after seeding.
 *     New useEffect watches scenario.operation_parameters.threat_level and weather.
 *     If Tab2 changes after Tab3 is configured → inline Alert in Section 3B.0
 *     with "Apply Tab 2 defaults" (updates tacticalEvacConfig + ref) and
 *     "Keep current settings" (updates ref only — marks Tab2 change acknowledged).
 *     Alert disappears when AD + weather already match Tab2 suggestion.
 *   UI-3B-001: Fleet total row appended inside TableBody (no new import). AccordionSummary
 *     "Ground: X/Y" chip added. fleetTotals computed before return() via reduce().
 *
 * Version: 4.13.0 - ENH-STRAT-001: flights_per_day field in STRATEVAC schedule UI (March 7, 2026)
 *   Added flights_per_day Select (1–6 sorties) to primary and alternate airfield schedule rows.
 *   Capacity-preview captions updated to reflect N sorties × M litter = total slots.
 *   PHA summary Alert updated to show effective daily capacity.
 *   FLIGHTS_PER_DAY_OPTIONS imported from stratevacScheduling.js v1.1.0.
 *   Backward compatible: missing field defaults to 1 in calculator.
 * Version: 4.12.0 - UI-3D-001 + UI-3D-002: HLZ reference selector + per-type CSU/PHA numbering (March 7, 2026)
 *   UI-3D-001: HLZ location_type CO_LOCATED_CSU/PHA now stores colocated_facility_id referencing the
 *     specific facility. Conditional second dropdown appears when CO_LOCATED_* is selected, listing
 *     available CSUs (for CO_LOCATED_CSU) or PHAs (for CO_LOCATED_PHA) with per-type labels.
 *     colocated_facility_id auto-clears when location_type changes away from CO_LOCATED_*.
 *     createHLZFromStandards updated: colocated_facility_id: null added to object shape.
 *   UI-3D-002: stagingFacilities render loop now computes per-type index. CSU #1, CSU #2, PHA #1
 *     instead of CSU #1, CSU #2, PHA #3. Same fix applied to CSU reference labels in HLZ selector.
 * Version: 4.11.0 - LOC-001: Seed evacuationRoutes from Tab2 loc_config on load + mismatch warning (March 7, 2026)
 *   On load: when no saved evacuation_routes present, seed primary route surface/status from loc_config.msr_status
 *   and alternate from loc_config.asr_status. LOC security level further constrains route status.
 *   Mismatch warning Alert in Section B.3 when Tab3 route status is more optimistic than Tab2 LOC implies.
 *   Both seed and warning use the same LOC_SECURITY_TO_STATUS / MSR_TO_SURFACE mapping tables.
 * Version: 4.10.1 - ENV-001/002 Tab3 fixes (March 7, 2026)
 *   ENV-001a: On load, derive ad_threat / weather defaults from Tab2 scenario.environment when
 *             no saved tactical_evac_config value. Mapping: FULL→HIGH, MODERATE→MEDIUM, LOW→LOW.
 *             Weather mapping: CLEAR→VMC_CLEAR, LIGHT_RAIN→IMC_LIGHT_RAIN, etc.
 *   ENV-001b: Warning Alert in Section 3B.0 when Tab3 AD threat is less restrictive than Tab2
 *             threat_level implies (e.g. Tab2 FULL threat, Tab3 still LOW/MEDIUM AD).
 *   ENV-002:  Deprecation note on legacy Section 3A MEDEVAC Helicopters field.
 *             3B.0 (tactical_evac_config.air_pool) is authoritative; Section 3A counts are legacy.
 *             Calculator fix (getTacticalEvacSummary reads medevac_config) tracked in
 *             evacuationCapacityCalculator.js — not a Tab3 issue.
 * Version: 4.9.3 - AMB-002 UX: Reset to TOE defaults button in Section 3B.0 fleet table (March 6, 2026)
 *   Button restores national-standard brigade medical company fleet from GROUND_FLEET_BY_NATION.
 *   Tooltip shows which standard is being applied (e.g. "Reset to ROMANIAN brigade medical company TOE").
 *   No logic changes — Tab3 was already correct. Fix is in casualtyCalculator.js v5.15.0.
 *   FIX-5-REVERT: Restored BLOOD_PRODUCTS import; info text now correctly shows
 *                 Object.keys(BLOOD_PRODUCTS).length (7 products).
 *   ENH-BLOOD-1:  Added FDP (Freeze-Dried Plasma) as 5th depot stock input field.
 *                 fdp_units added to currentStock state, load fallback, and save path.
 *                 Shelf life / cold-chain info in helperText for all 5 depot products.
 *   ENH-BLOOD-2:  FWB and LTOWB are WBB donor-generated — not depot stock.
 *                 WBB section now shows projected yield per activation:
 *                 FWB = donors × 0.70 × 0.95; LTOWB = ltowb_donors × 0.70 × 0.95
 *                 (constants from WALKING_BLOOD_BANK.planning in bloodProducts.js).
 *   ENH-BLOOD-3:  Standard-mode summary updated to show all 5 depot products (xs=2 grid).
 *   ENH-BLOOD-4:  Shelf life helper text updated to cover all 7 products incl. FWB/LTOWB.
 *
 * Version: 4.9.1 - UI fixes (March 6, 2026)
 *   FIX-1: Golden Hour chip label changed from "N/A (Surgical)" to "Has Surgical Capability"
 *          with corrected tooltip. Students were misreading as "no surgical capability."
 *   FIX-2: Removed redundant "Calculation Breakdown" box below Facility Validation alert.
 *          Medical Brigade exclusion note folded into the Alert as a footnote.
 *   FIX-3: Litter capacity column now reads spec.capacity.stretchers (correct field) instead
 *          of spec.litter_capacity (undefined top-level field). Was showing "?" for all vehicles.
 *   FIX-4: Weather dropdown now expands VMC/IMC/IFR acronyms for student comprehension.
 *          expandWeatherLabel() helper added; applied to wxOptions render.
 *   FIX-5: Blood management info text corrected from "{BLOOD_PRODUCTS.length} product types"
 *          to "4 primary products tracked (PRBC, FFP, Platelets, Cryoprecipitate)."
 *
 * Version: 4.9.0 - Phase 6.0: Tab 3 Additional Features UI (March 5, 2026)
 *
 * ==============================================================================
 * v4.17.1 (March 8, 2026) - BUG-066: flushState markDirty:
 * - BUG-066 FIXED: flushState() now calls markDirty() — toolbar nav bypassed
 *   Save & Continue, silently saving without flagging Tab 5 results as stale.
 *
 * v4.17.0 (March 8, 2026) - calculationService integration:
 * - IMPORT: markDirty from calculationService.js.
 * - handleSaveAndComplete: markDirty() called after onUpdate() — signals Tab 5
 *   that medical configuration and preliminary casualties have changed.
 *
 * ==============================================================================
 * v4.9.0 PHASE 6.0: TAB 3 ADDITIONAL FEATURES (March 5, 2026)
 * ==============================================================================
 *
 * SECTION 3C — BLOOD MANAGEMENT: New fields
 *   - MTP (Massive Transfusion Protocol) toggle + 1:1:1 / 2:1:1 selector (Expert mode).
 *     State: bloodConfig.mtpEnabled, bloodConfig.mtpProtocol.
 *     Saved to blood_config.mtp_enabled / mtp_protocol.
 *   - Shelf life tracking toggle (Expert mode). Shows PLT expiry warning.
 *     State: bloodConfig.shelfLifeTracking.
 *     Saved to blood_config.shelf_life_tracking.
 *
 * SECTION 3E — STRATEGIC EVACUATION: Full architectural redesign
 *   DATA MODEL: strategicEvac.airfields[] array introduced (ENH-003 readiness).
 *     Each entry: { id, label, class, type, distances{}, pha_beds, farp_enabled,
 *                   is_primary, coordinates: null, distance_source: 'MANUAL' }.
 *     Phase 6: UI renders airfields[0] only. ENH-003 (Phase 8) adds "+ Add Airfield" button.
 *     coordinates/distance_source are null/'MANUAL' until Tab 6 map is built.
 *
 *   E.1 REDESIGN (owns all infrastructure data):
 *     - Air toggle + Airfield Type + Airfield Class selector (new CLASS_A/B/C/D).
 *     - All 4 distances: R2B→Airfield, R2E→Airfield, R3→Airfield, Airfield→R4.
 *     - PHA beds input (new — ENH-003 pre-wire).
 *     - FARP toggle (new).
 *     - Airfield label/name input.
 *     FIXED: E.1 is now sole owner of all distance data. (APP_RULES 6.2 — single owner).
 *
 *   E.2 REDESIGN (operations display only — reads from E.1):
 *     REMOVED: duplicate R3→Airfield and Airfield→R4 distance inputs. (APP_RULES 6.2 violation).
 *     REMOVED: duplicate timeline card.
 *     KEPT: national STRATEVAC liaison note.
 *     NEW: read-only STRATEVAC timeline (reads airfields[0].distances — no re-entry).
 *     NEW: ENH-003 placeholder section (disabled, documents Phase 8 dependency).
 *
 *   BACKWARD COMPATIBILITY: buildUpdates() still emits flat keys (role3_to_airfield etc.)
 *     alongside airfields[] array. Calculator reads flat keys — no calculator change needed.
 *   LOAD: Handles both pre-Phase 6 (flat keys) and Phase 6+ (airfields[]) saved scenarios.
 *
 *   NOTE: AIRFIELD_CLASS_SPECS defined as module-level constant in this file.
 *     TODO: Migrate to trainEvacuation.js in next constants session (APP_RULES 2.1).
 *
 * ==============================================================================
 * Version: 4.8.1 - Phase 5.1: National accuracy — fleet + air pool defaults by nation (March 5, 2026)
 *   - IMPORT: getDefaultGroundFleet from ambulanceTypes (new export).
 *   - tacticalEvacConfig useState: lazy initializer reads force nationalStandard, calls
 *     getDefaultGroundFleet() and getMEDEVACPoolOptions() for correct national defaults.
 *     Romanian force → TABC79+MLVM fleet + FAR pool. German → Sprinter+Boxer + NH90 pool.
 *     Polish → Star266+Rosomak + W3_SOKOL pool. British → Wolf+Foxhound + AAC pool. Etc.
 *   - NEW useEffect: resets fleet+pool when force nationalStandard changes. Heuristic guard:
 *     if current pool_code is still valid for the new nation, preserves user config.
 * v4.8.0 - Phase 5.0: Tactical MEDEVAC Configuration (Section 3B.0) (March 5, 2026)
 *   - NEW STATE: tacticalEvacConfig — ground ambulance fleet, MEDEVAC air pool, AD threat, weather.
 *   - NEW IMPORTS: AD_THREAT_MODIFIERS, WEATHER_MODIFIERS from helicopterSpecs;
 *     getMEDEVACPoolOptions, getDefaultMEDEVACPool from aviationAssets;
 *     AMBULANCE_TYPES, AMBULANCE_CATEGORIES from ambulanceTypes.
 *   - NEW UI: Section 3B.0 Accordion before B.1. Air pool filtered by nationalStandard
 *     (ROMANIAN=FAR/MOU/Contracted; GERMAN=HFlgTrp/MOU/Contracted; POLISH=LWL/MOU/Contracted etc.)
 *   - SAVE: tactical_evac_config added to medical_configuration buildUpdates.
 *   - LOAD: tactical_evac_config restored from medConfig on scenario load.
 *   - DERIVED: useEffect computes effective_air_sorties = nominal × adMod × wxMod.
 *   - medevacModule v5.5: reads tactical_evac_config.ad_threat/.weather in precedence order.
 * v4.7.0 (March 4, 2026) — DOCTRINAL-005: CSU/PHA/HLZ placement restricted by echelon doctrine
 *
 * ==============================================================================
 * v4.7.0 (March 4, 2026) — DOCTRINAL-005: CSU/PHA/HLZ placement doctrine enforcement
 *
 * Problem: generateAllocationOptions() offered ALL echelon levels (including Role 1)
 *   for every staging facility type. Planners could attach a PHA to a battalion BAS,
 *   which is doctrinally impossible (PHA = STRATEVAC staging for post-surgical patients
 *   awaiting strategic airlift; requires Role 3 infrastructure and airfield access).
 *
 * Fix: Added filterAllocationByType(options, facilityType) helper function.
 *   CSU → Role 2B / Role 2E / Role 3 only. Role 1 = non-holding CCP, not a CSU.
 *   PHA → Role 3 ONLY. Post-surgical STRATEVAC staging. AJP 4.10C.
 *   HLZ → Role 2B / Role 2E / Role 3 only. No helo ops at battalion BAS level.
 *
 * Allocation dropdown in renderStagingFacilityCard: filtered options rendered,
 *   doctrinal helper text per facility type, red Alert shown for existing violations.
 *
 * generateAllocationOptions() battalion-level comment corrected:
 *   "CSU near Role 1 for MASCAL collection points" → correct terminology is CCP.
 *   Battalion options remain in full list (used by other non-staging features)
 *   but are filtered out for CSU/PHA/HLZ at render time.
 *
 * ==============================================================================
 * v4.6.0 (March 1, 2026) — PSP_CRE_MASTER_FIX_PLAN_v2_2 Phase G G-03 (ARCH-010):
 * Aligns Tab3 CASEVAC UI with casualtyCalculator.js v5.6.0 (ARCH-010 fix).
 *
 * G-03a: CASEVAC main Alert text replaced with AJMedP-2 doctrinal statement.
 *   Old text described CASEVAC providing "no en-route medical care → higher mortality."
 *   This implied CASEVAC was a planning factor. New text: CASEVAC is unplanned/
 *   opportunistic and is NOT included in the medical estimate per AJMedP-2.
 *   UI controls retained for situational awareness tracking only.
 *
 * G-03b: DOW Impact panel removed from CASEVAC enabled block.
 *   Panel showed "+35% DOW multiplier" from casevac_config.dow_penalty.
 *   After ARCH-010, calculateOverflowDOW() treats ALL overflow as unevacuated
 *   at the unevacuated_dow_penalty (1.60×) regardless of CASEVAC availability.
 *   casevac_config.dow_penalty no longer drives any calculation.
 *   Displaying this value would mislead planners about actual DOW modelling.
 *
 * G-03c: "Calculator Integration" Alert replaced with ARCH-010-compliant text.
 *   Old text: "overflow casualties will be evacuated via CASEVAC with a 1.35x DOW
 *   multiplier." New text: overflow is modelled as unevacuated (1.60×) in the medical
 *   estimate. CASEVAC tracking displayed in Tab5 is informational only.
 *
 * G-03d: Cross-division caption updated.
 *   Removed claim that cross-division routing "reduces DOW rates" via CASEVAC avoidance.
 *   Cross-division MEDEVAC itself is correct doctrine; the DOW reduction claim was
 *   tied to CASEVAC-vs-MEDEVAC tier logic removed by ARCH-010.
 *
 * ==============================================================================
 * v4.5.0 (February 26, 2026) — PSP_CRE_MASTER_FIX_PLAN_v2_0 Phase C (ARCH-001):
 * - C-01: calculateLogisticsProjection call removed from handleSaveAndComplete.
 *         Tab3 Save & Continue now triggers ONLY: casualtyCalculator + onUpdate + onComplete.
 *         Logistics projection is Tab4's responsibility (ARCH-009 — data ownership).
 * - C-02: localStorage.setItem('psp_logistics_projection') removed from Tab3.
 *         Tab4 Save & Continue owns this write.
 * - C-03: Tab3 Save & Continue now saves ONLY: medical_configuration + casualty_projections.
 *         No logistics data written by Tab3.
 * - C-04: casualtyCalculator call preserved and untouched.
 * - C-05: ESLint compliance — calculateLogisticsProjection import removed (was unused).
 * - A-04: "Blue Light Protocol" UI labels relabeled to
 *         "MASCAL Escalation Protocol (AMedP-1.10)" per C-004 doctrinal correction.
 *         Internal constant names (BLUE_LIGHT_PROTOCOL, blueLightEnabled, etc.) unchanged.
 * - A-07: WBB doctrinal note added in Tab3 WBB display section per C-005 correction.
 *
 * v4.4.0 (February 26, 2026) — FIX_PLAN_LOGISTICS_DOS_v1_0 Phase L-3 [SUPERSEDED by v4.5.0]:
 * - L3-01 through L3-05: REMOVED in v4.5.0 (ARCH-001/ARCH-009 — logistics owned by Tab4).
 *
 * ==============================================================================
 * v4.3.10 (February 25, 2026) - BUG FIX: Replace alert() with MUI Dialog:
 * - PROBLEM: handleSaveAndComplete catch block used alert() which only shows a
 *   "Close" button. Message claimed "Click OK or Cancel" — no Cancel existed.
 * - FIX: Added showCalcErrorDialog + pendingErrorSave state. Catch block now
 *   opens a proper MUI Dialog with two real action buttons:
 *   "Fix Issues First" (stays on Tab3) vs "Save Configuration Only" (proceeds).
 * - No functional change to save logic — only the UI gate was broken.
 *
 * ==============================================================================
 * v4.3.9 (February 16, 2026) - ARCHITECTURAL FIX: Constants-Based Validation:
 * - ARCHITECTURAL: Validation now uses BATTALION_COMPOSITION_BY_BRIGADE_TYPE from unitTypes.js
 * - SINGLE SOURCE OF TRUTH: No hardcoded unit type lists in Tab3
 * - ROLE 1 CALCULATION: Uses template.role1_count ?? template.combatBattalions
 * - ROLE 2B CALCULATION: 1 per non-MEDICAL brigade (Medical brigades excluded)
 * - ROLE 2E CALCULATION: 1 per division
 * - ROLE 3 CALCULATION: 1 per corps
 * - MEDICAL BRIGADE SPECIAL CASE: Contributes 0 to Role 1/2B (self-sufficient providers)
 * - USER-FRIENDLY DISPLAY: Breakdown shows calculation with Medical brigade notation
 * - FUTURE-PROOF: Adding new brigade types only requires updating unitTypes.js
 * - BENEFITS: No fragile hardcoded filters, follows APP_RULES 2.1 (constants-based)
 * - LOCATION: Lines 6264-6360 (complete validation rewrite)
 * - IMPORT ADDED: BATTALION_COMPOSITION_BY_BRIGADE_TYPE from unitTypes.js
 *
 * v4.3.8 (February 15, 2026) - FACILITY VALIDATION FIX PART 3:
 * - FIXED: Added CAVALRY to combat maneuver units (CAVALRY = reconnaissance)
 * - PROBLEM: Filter looked for "RECONNAISSANCE" but actual unit_type is "CAVALRY"
 * - CAUSE: Different naming conventions (CAVALRY Squadron vs RECONNAISSANCE)
 * - RESULT: Expected 12 Role 1 (missed cavalry), should expect 18 (include cavalry)
 * - ACTUAL STRUCTURE: MECHANIZED (2) + ARMOR (1) + CAVALRY (1) = 4 combat per brigade
 * - CALCULATION: 6 brigades × 3 combat maneuver = 18 Role 1 facilities ✓
 * - EXCLUDED: ARTILLERY, ENGINEERS (combat support, no organic Role 1)
 * - EXCLUDED: SUSTAINMENT (CSS), HQ (administrative, no Role 1)
 * - CHANGED: From .includes() to === for exact matching (more precise)
 * - SUPERSEDED BY v4.3.9: Now uses constants instead of hardcoded filter
 *
 * v4.3.7 (February 15, 2026) - FACILITY VALIDATION FIX PART 2:
 * - FIXED: Role 1 expected count now filters for COMBAT battalions only
 * - PROBLEM: v4.3.6 counted ALL sub_units (including support units)
 * - CAUSE: MECHANIZED brigade has 8 sub_units, but only 3-4 are combat battalions
 * - RESULT: Expected 46 Role 1 (all sub_units), should expect ~18 (combat only)
 * - SOLUTION: Filter sub_units by unit_type to count only combat battalion types
 * - TYPES: MECHANIZED, ARMOR, INFANTRY, RECONNAISSANCE get Role 1
 * - NOT COUNTED: ARTILLERY, ENGINEER, AVIATION, CSS (support units, no organic Role 1)
 * - EXAMPLE: 6 brigades × 3 combat bns = 18 expected (matches 18 actual) ✓
 * - LOCATION: Lines 6247-6280 (added combat battalion filter)
 * - DOCTRINE: Role 1 is organic to combat maneuver battalions, not support units
 *
 * v4.3.6 (February 15, 2026) - FACILITY VALIDATION FIX PART 1:
 * - FIXED: Facility validation now uses ACTUAL force structure, not assumptions
 * - ROOT CAUSE: Used wrong path (scenario.force.corps.divisions vs forceBuilderResult.divisions)
 * - PROBLEM: Hardcoded fallback to 3 divisions (|| 3) caused false warnings
 * - SOLUTION: Count actual divisions, brigades, battalions from forceBuilderResult
 * - CHANGE: Role 1 expected count now sums actual sub_units from all brigades
 * - CHANGE: Role 2B expected count now uses actual brigades.length
 * - CHANGE: Role 2E expected count now uses actual divisions.length
 * - BENEFIT: Validation matches actual force composition (2 div shows 18 Role1, not 27)
 * - LOCATION: Lines 6220-6247 (validation panel calculation)
 * - ARCHITECTURAL: Zero-tolerance for assumptions - always count actual structure
 *
 * v4.3.5 (February 15, 2026) - DIAGNOSTIC LOGGING FOR CALCULATOR SAVE DEBUG:
 * - ADDED: Comprehensive diagnostic logging in handleSaveAndComplete
 * - PURPOSE: Debug why calculator results aren't saving to localStorage
 * - LOGS: Calculator return structure, keys, medical_requirements presence
 * - LOGS: Update object structure before passing to onUpdate
 * - LOCATION: handleSaveAndComplete (lines ~3410-3445)
 * - TEMPORARY: These logs will be removed after issue is identified
 * - NOTE: Calculator runs successfully but casualty_projections = undefined in localStorage
 *
 * v4.3.4 (February 13, 2026) - CALCULATOR SAFETY GUARD:
 * - SAFETY: Added operation_parameters check before calling calculator
 * - GUARD: handleSaveAndComplete now validates Tab2 completion
 * - CHECK: scenario?.operation_parameters?.duration_days must exist
 * - ERROR: Alert user "Tab 2 must be completed before Tab 3" if missing
 * - PREVENTION: Stops calculator from running if operation not configured
 * - LOCATION: handleSaveAndComplete (line 3377-3380)
 * - BENEFIT: Defensive programming prevents edge case failures
 * - NOTE: Tab navigation normally prevents this, but guard adds extra safety
 *
 * v4.3.3 (February 13, 2026) - EXTRACT LOCAL UNIT NAME PROPER IMPLEMENTATION:
 * - FIXED: Connected extractLocalUnitName() function (lines 956-1045, 90 lines)
 * - ROOT CAUSE: Function was written but never used - regex hack used instead (lines 6094-6097)
 * - OLD DISPLAY: "Battalion 3 / Brigade 2 / Division 1" (regex .match(/\d+/))
 * - NEW DISPLAY: "3rd Bn (Mechanized) / 2nd Bde (Artillery) / 1st Div / 1st Corps"
 * - BENEFITS:
 *   1. Shows ordinals (1st, 2nd, 3rd) instead of plain numbers
 *   2. Shows unit types for battalions (Mechanized, Armor, Infantry, etc.)
 *   3. Shows unit types for brigades (Artillery, Logistics, Medical, etc.)
 *   4. Robust pattern matching (handles "3/...", "Battalion 3", "3rd Bn", etc.)
 *   5. Uses well-written 90-line function instead of fragile inline regex
 * - LOCATION: Facility accordion "Position" section (line 6093-6127)
 * - USER BENEFIT: Now users see "3rd Mechanized Battalion" instead of "Battalion 3"
 * - ARCHITECTURE: Function extracts { number, ordinal, type, shortName } from full names
 * - Lines changed: 6094-6097 deleted (regex hack), 6094-6127 added (IIFE with proper parsing)
 * - ESLint: extractLocalUnitName no longer unused (was line 929 dead code)
 *
 * v4.3.2 (February 12, 2026) - BLUE LIGHT PROTOCOL COMPLETE IMPLEMENTATION:
 * - FEATURE: Automatic Blue Light Protocol detection and recommendation system
 * - ROOT CAUSE: Feature was marked "100% complete" in roadmap but only had manual toggle
 * - MISSING: Automatic detection, user decision workflow, reason tracking for reports
 * - IMPLEMENTED (Complete workflow):
 *   1. AUTOMATIC DETECTION: shouldActivateBlueLightProtocol() monitors conditions
 *      - Triggers on: High T1 casualties, Golden Hour violations, MTF overload, surgical backlog
 *      - Sets blueLightRecommended=true with trigger reason
 *   2. RECOMMENDATION ALERT: Shows error alert with medical recommendation
 *      - Explains why protocol activation recommended
 *      - Provides two options: "Activate Protocol" or "Dismiss (Provide Reason)"
 *   3. USER DECISION: Command decides whether to activate
 *      - ACTIVATE: Sets blueLightActive=true, timestamps activation
 *      - DISMISS: Opens dialog requiring operational reason (security, resources, etc.)
 *   4. REASON TRACKING: All decisions saved to scenario for Tab5 reports
 *      - blueLightRecommended, blueLightActive, blueLightTriggerReason
 *      - blueLightActivatedAt, blueLightDismissed, blueLightDismissReason
 *   5. STATUS DISPLAY: Active/Dismissed status shown in UI with details
 * - NEW HANDLERS: handleActivateBlueLight, handleOpenDismissDialog, handleCancelDismiss,
 *   handleConfirmDismiss, handleDeactivateBlueLight
 * - NEW DIALOG: Blue Light dismiss dialog with TextField for operational reason
 * - FIXED: Replaced unused shouldActivateBlueLightProtocol with actual implementation
 * - FIXED: Replaced unused testPatient with real metrics from scenario casualties
 * - MILITARY WORKFLOW: Medical recommends → Command decides → Decision documented
 * - TAB5 READY: All tracking data saved to scenario.medical_configuration.mascal_config
 * - Lines added: ~150 (state +7 fields, handlers +65, UI alerts +60, dialog +20)
 *
 * v4.3.1 (February 11, 2026) - ESLint cleanup:
 * - Removed unused terrainFactors imports (getTerrainFactor, calculateGroundEvacuationTime)
 * - Removed 3 unused helper functions (getFacilityId, brigadeHasRole1, generateFacilityDisplayName)
 * - Commented out 2 unused variables (mtfCapacity, blueLightTriggered)
 * - Net: -125 lines
 *
 * v4.3.0 (February 11, 2026) - SERVICE REFACTOR:
 * - Removed 1030-line inline functions block (extractMedicalFacilities,
 *   calculateEvacuationDistances, recalculateFacilityEvacuation, getDefaultPositionForRole)
 * - All three functions now imported from services/MedicalService.js (v2.0)
 * - Removed all console.log/warn statements (16 removed, 1 console.error kept)
 * - Zero behavior change: function signatures and call sites unchanged
 *
 * v4.2.1 (February 10, 2026) - Add Back to Tab 2 button, onNavigate prop
 * ==============================================================================
 * - ARCH: Added forwardRef + useImperativeHandle for toolbar navigation compatibility
 *   - App.jsx can now call tab3Ref.current.flushState() before toolbar tab switch
 *   - Tab3 is Pattern B: buffers state locally, async save on Save & Continue
 *   - flushState silently saves medical_configuration (sync, no calculator) without
 *     validation gate or onComplete navigation - preserves partial form state
 *   - buildUpdates() extracted: constructs medicalConfiguration from current state
 *   - handleSaveAndComplete: validate -> buildUpdates -> calculator -> onUpdate -> onComplete
 *   - flushState: buildUpdates -> onUpdateRef.current (sync path, no calculator)
 * - FIX: Resolved all 8 eslint-disable directives (zero eslint-disable policy):
 *   - facilityAssetOverrides: changed to [, setFacilityAssetOverrides] (setter-only)
 *   - handleFacilityAssetUpdate: removed (was unused, marked future-use)
 *   - blood useEffect: added bloodConfig.alertLevel to deps
 *   - surgical useEffect: removed stale eslint-disable comment (deps were complete)
 *   - MASCAL useEffect: removed stale eslint-disable comment (deps were complete)
 *   - init useEffect: loadConfigRef + initDefaultsRef pattern (stable function refs)
 *   - validation useEffect: validateConfigRef pattern (stable function ref)
 *   - cross-div useEffect: crossDivisionPairsRef pattern (avoids infinite loop)
 * - ENCODING FIX: Removed all non-ASCII characters from code lines (81 lines cleaned)
 *
 * ==============================================================================
 * Version: 4.1.0 - Calculator Integration on Save
 * 
 * v4.1.0 CALCULATOR INTEGRATION ON SAVE (February 7, 2026):
 * ============================================================================
 * FEATURE: Automatic Calculator Execution When Tab3 Completes
 * 
 * WHAT'S NEW:
 * - handleSaveAndComplete() now runs calculator automatically before saving
 * - Results saved to scenario.medical_requirements and scenario.casualty_projections
 * - Tab4 receives CALCULATED data immediately (not preliminary estimates)
 * - Eliminates Tab4 <-> Tab5 ping-pong workflow
 * 
 * USER WORKFLOW IMPROVEMENT:
 * OLD FLOW (BROKEN):
 *   Tab3 save -> Tab4 (preliminary) -> Tab5 (calculate) -> 
 *   Tab4 (modify) -> Tab5 (recalculate) -> Tab4 (verify) -> ...
 * 
 * NEW FLOW (FIXED):
 *   Tab3 save -> [Calculator runs automatically] -> 
 *   Tab4 (works with CALCULATED data) -> 
 *   Tab5 (review/export only)
 * 
 * IMPLEMENTATION DETAILS:
 * 1. Added calculator import: calculateCasualties from casualtyCalculator.js
 * 2. Added state: isCalculating, calculationError for UI feedback
 * 3. Modified handleSaveAndComplete():
 *    - Now async function
 *    - Runs calculator before saving
 *    - Saves calculator results alongside medical_configuration
 *    - Error handling: Calculator failure alerts user but still saves medical config
 * 
 * DATA SAVED TO SCENARIO:
 * - medical_configuration (existing)
 * - medical_requirements (NEW - from calculator)
 * - casualty_projections (NEW - from calculator)
 * - calculated_at (NEW - timestamp)
 * 
 * BENEFITS:
 *  Tab4 always has accurate calculated data
 *  No need to "go back" between tabs
 *  Linear workflow: Tab1 -> Tab2 -> Tab3 -> Tab4 -> Tab5
 *  Tab5 becomes final review/export (not required mid-workflow)
 *  Matches real military planning workflows
 * 
 * ERROR HANDLING:
 * - Calculator errors shown to user via alert
 * - User can choose to save medical config without calculator results
 * - Graceful degradation to preliminary estimates in Tab4
 * - Console logs for debugging
 * 
 * CONSOLE LOGGING:
 * - " Tab3: Running calculator automatically on save..."
 * - " Tab3: Calculator completed successfully"
 * - " Tab3: Total casualties: X"
 * - " Tab3: Saved medical configuration + calculator results"
 * - " Tab3: Tab4 will now have CALCULATED data (not preliminary)"
 * 
 * INTEGRATION:
 * - Requires Calculator v5.1.0 (reads from force.forceBuilderResult.brigades)
 * - Works with Tab2 v7.0 (single source of truth)
 * - Tab4 already supports loading calculator results (no changes needed)
 * - Tab5 already supports loading pre-calculated results (no changes needed)
 * 
 * ============================================================================
 * 
 * v4.0.1 VALIDATION RANGES FIX (February 6, 2026):
 * ============================================================================
 * BUG: Tab3 had HARDCODED validation ranges that didn't match any constants
 * SYMPTOM: Role 3 at 75km showed warning "outside NATO recommended range (100-150 km)"
 * ROOT CAUSE: Hardcoded ranges at line 665 violated constants-based architecture
 * 
 * THE FIX:
 * - Added import: MTF_DEFAULT_OFFSETS from mtfDefaultOffsets.js
 * - Replaced VALIDATION_RANGES.mtfDistances with constants-based values
 * - Role 1: min 3km, max 8km, recommended 5km (from ROLE1.typical_feba_distance_km)
 * - Role 2: min 10km, max 25km, recommended 10-25km (from ROLE2_BASIC.typical_feba_distance_km)
 * - Role 3: min 60km, max 150km, recommended 60-150km (from ROLE3.typical_feba_distance_km + user adjustment)
 * - Role 4: min 200km, max 1000km, recommended 300km (simplified for strategic level)
 * 
 * USER REQUIREMENTS APPLIED:
 * - Role 3 max extended to 150km (was 120km in constants) - wider operational flexibility
 * - Role 4 simplified to 300km recommended (strategic level, distance less critical)
 * 
 * RESULT:
 * - Role 3 at 75km: NOW VALID ✓ (within 60-150km range)
 * - Before: Warning at 75km (hardcoded 100-150km)
 * - After: Valid at 75km (constants-based 60-150km)
 * - Architectural compliance: Zero hardcoded validation values
 * 
 * ARCHITECTURAL PRINCIPLE RESTORED:
 * "All validation ranges MUST come from constants files, never hardcoded in components"
 * 
 * FILES USED:
 * - /constants/mtfDefaultOffsets.js -> typical_feba_distance_km for each role
 * - Provides min/max/typical distances based on NATO doctrine + Ukraine 2022-2024
 * 
 * ============================================================================
 * 
 * v4.0.0 PHASE 5 - SUB-UNIT MTF ASSIGNMENT (February 3, 2026):
 * ============================================================================
 * CRITICAL ENHANCEMENT: MTF Assignment Based on Actual Sub-Unit Composition
 * 
 * WHAT'S NEW:
 * - extractMedicalFacilities() now uses brigade.sub_units for MTF assignment
 * - Role 1 facilities mapped to actual sub-units (battalions/squadrons/companies)
 * - Only assigns Role 1 to sub-units that need them (excludes HQ, SIGNAL, MEDICAL)
 * - Uses actual sub-unit names instead of generic "Battalion 1, 2, 3..."
 * 
 * MODIFIED FUNCTIONS:
 * 1. extractMedicalFacilities() - Brigade Level (lines ~1973-2036)
 *    - Added v3.0 sub_units detection
 *    - Filters sub-units by type (only those needing Role 1)
 *    - Maps Role 1 facilities to actual sub-units
 *    - Tracks sub_unit_type, sub_unit_strength metadata
 *    - Legacy path for brigades without sub_units
 * 
 * 2. extractMedicalFacilities() - Division Level (lines ~2126-2200)
 *    - Iterates through brigades checking for sub_units
 *    - Correctly indexes Role 1 facilities across multiple brigades
 *    - Maps to sub-units within each brigade
 *    - Handles mixed scenarios (some brigades with sub_units, some without)
 * 
 * MTF ASSIGNMENT IMPROVEMENT:
 * Before: Role 1 assigned to generic "Battalion 1, 2, 3, 4"
 * After:  Role 1 assigned to actual sub-units:
 *   - "1st Mechanized Battalion Aid Station"
 *   - "1st Armor Battalion Aid Station"
 *   - "1st Artillery Battalion Aid Station"
 *   - "1st Engineers Battalion Aid Station"
 *   - "1st Sustainment Battalion Aid Station"
 *   - (NO Role 1 for HQ Company, Signal Company, etc.)
 * 
 * Example Display:
 * Role 1 Facilities (Brigade):
 * 1. 1st Mech Bn Aid Station - Type: MECHANIZED, 800 personnel
 * 2. 2nd Mech Bn Aid Station - Type: MECHANIZED, 800 personnel
 * 3. 1st Armor Bn Aid Station - Type: ARMOR, 800 personnel
 * 4. 1st Artillery Bn Aid Station - Type: ARTILLERY, 500 personnel
 * 5. 1st Engineers Bn Aid Station - Type: ENGINEER, 600 personnel
 * 6. 1st Support Bn Aid Station - Type: SUSTAINMENT, 700 personnel
 * 7. 1st Cavalry Squadron Aid Station - Type: CAVALRY, 400 personnel
 * (7 Role 1s instead of generic 8, HQ excluded)
 * 
 * NEW FIELDS ADDED TO FACILITIES:
 * - parent_unit_type: Actual unit type (MECHANIZED, ARMOR, etc.)
 * - sub_unit_strength: Personnel count from sub-unit
 * - sub_unit_type: Copy of unit_type for clarity
 * - is_from_doctrinal_composition: Boolean flag (true for v3.0 data)
 * - is_orphaned: Flag for Role 1s without matching sub-units (defensive)
 * 
 * BENEFITS:
 * - Accurate MTF-to-unit mapping for casualty routing
 * - Realistic facility counts (no Role 1 for HQ/Signal)
 * - Better UI display with actual unit names
 * - Foundation for future per-sub-unit MASCAL analysis
 * 
 * INTEGRATION:
 * - Requires unitTypes.js v3.0 (doctrinal compositions)
 * - Requires ForceService.js v3.0 (sub-unit generation)
 * - Backwards compatible with legacy brigades without sub_units
 * 
 * CONSOLE LOGGING:
 * - "[v3.0 Phase 5] Brigade 'X' has N sub-units - using doctrinal composition"
 * - "[v3.0 Phase 5] Brigade 'X' has NO sub-units - using legacy battalion array"
 * - "[v3.0 Phase 5] N sub-units need Role 1 (filtered from M)"
 * 
 * ============================================================================
 * 
 * Version: 3.24 - CRITICAL FIX: Section 3E data sync + constants architecture + rename
 * 
 * v3.24 SECTION 3E COMPREHENSIVE FIX (January 28, 2026):
 * - CRITICAL FIX: 3E.1 and 3E.2 data sync issue resolved
 *   - Problem 1: 3E.2 edited mtfDistances state (WRONG - not saved to calculator)
 *   - Problem 2: 3E.1 edited strategicEvac state (CORRECT - saved to calculator)
 *   - Result: User changes in 3E.2 were silently lost
 *   - Fix: Both sections now edit strategicEvac state (lines 5587, 5600)
 *   - Impact: Changes in either 3E.1 or 3E.2 now sync automatically and save correctly
 * 
 * - CONSTANTS ARCHITECTURE COMPLIANCE:
 *   - Removed ALL hardcoded fallback values throughout file
 *   - Lines 2764-2775: Removed "|| 30", "|| 40", "|| 50", "|| 500", "|| 800" from state init
 *   - Lines 5405, 5416: Removed "|| 50", "|| 40" from role2bToAirfield and role2eToAirfield
 *   - Lines 4695, 4706: Removed "|| 8", "|| 20" from read-only MTF distance displays
 *   - Now uses ONLY DEFAULT_STRATEGIC_DISTANCES from trainEvacuation.js constants
 *   - Follows project principle: "zero hardcoded values, all from constants files"
 * 
 * - DEAD CODE REMOVAL:
 *   - Removed handleMTFDistanceChange() function (line 3436-3441)
 *   - Function was defined but never used after 3E.2 fix
 *   - Cleanup maintains code quality and reduces confusion
 * 
 * - TIMELINE CALCULATIONS FIXED:
 *   - Lines 5613, 5619, 5623: Changed from mtfDistances to strategicEvac
 *   - Removed getFEBADefault() fallbacks (were using wrong values)
 *   - Timeline now calculates from actual user-configured distances
 *   - Updates in real-time when user changes distances in 3E.1 or 3E.2
 * 
 * - SECTION RENAMING (Option 2 - Clarity):
 *   - 3E.1: "Strategic Evacuation" -> "Strategic Evacuation Infrastructure"
 *   - 3E.2: "Strategic Evacuation (STRATEVAC) Configuration" -> "STRATEVAC Operations & Timeline"
 *   - Updated Alert descriptions to clarify purpose of each section
 *   - 3E.1 focus: Infrastructure setup (where are facilities, distances)
 *   - 3E.2 focus: Operational planning (timeline, coordination, aircraft)
 * 
 * - DATA FLOW VERIFICATION:
 *   - 3E.1 edits -> strategicEvac state -> React re-renders 3E.1 & 3E.2 -> Save passes to calculator ✓
 *   - 3E.2 edits -> strategicEvac state -> React re-renders 3E.1 & 3E.2 -> Save passes to calculator ✓
 *   - Timeline in 3E.2 -> reads strategicEvac -> recalculates on any distance change ✓
 * 
 * v3.23 FACILITY DISPLAY BUGS FIX (January 28, 2026):
 * - BUG FIX 1: Raw code displayed in Basic Information section
 *   - Issue: "[ facility.hierarchy.corps, ... ].filter(Boolean).join(' -> ')" shown as text
 *   - Cause: Missing opening `{` in JSX expression (line 6376)
 *   - Fix: Added `{` to wrap array expression, code now executes instead of displaying
 *   - Now shows: "1st Division -> 1st Brigade -> 1st Battalion"
 * - BUG FIX 2: Facility ID shows meaningless UUID
 *   - Issue: "ID: role1_div_bde1_bn1_co4a5l7cs" (technical UUID)
 *   - Fix: Replaced with human-readable position
 *   - Now shows: "Position: Battalion 1 / Brigade 1 / Division 1"
 *   - Falls back to facility name if hierarchy unavailable
 * 
 * v3.22 NATIONAL STANDARD DISPLAY FIX (January 28, 2026):
 * - BUG FIX: National Standard field showing empty in header
 * - Issue: Display read scenario.force.national_standard (snake_case)
 * - Problem: ForceBuilder stores as nationalStandard (camelCase) in different locations
 * - Fix: Use extractPrimaryNationalStandard() function that handles both formats
 * - Function checks: force.national_standard, fbResult.corps.nationalStandard, 
 *   fbResult.divisions[0].nationalStandard, force.division.nationalStandard, force.brigade.nationalStandard
 * - Now displays correctly for all echelon types and force structures
 * - Fixes empty National Standard display in Tab 3 header
 * 
 * v3.21 ROLE 2E DIVISION FIX (January 27, 2026):
 * - CRITICAL FIX: Role 2E extraction at DIVISION level
 * - Issue: "Role 2E: 0/1 (1 missing)" at division level
 * - Root cause: Code only checked result.role2_enhanced_facility/result.role2_enhanced
 * - Fix: Check array first (result.role2_enhanced_facilities[]), then division object, then legacy
 * - Added debug logging to identify where ForceBuilder stores R2E
 * - Updated R2E count calculation to check all three sources
 * - Matches CORPS-level extraction pattern
 * - Production-ready
 * 
 * v3.20 SYNTAX FIX (January 26, 2026):
 * - CRITICAL FIX: Fixed JSX parsing error in Blood Alert Levels
 * - Issue: "<1 DOS" was being parsed as JSX tag
 * - Solution: Used HTML entities (&lt;1 and &gt;5)
 * - File now compiles without errors
 * - All user-facing UTF-8 corruption still eliminated
 * - Production-ready
 * 
 * v3.19 UTF-8 COMPREHENSIVE FIX (January 26, 2026):
 * - COMPLETE FIX: All user-facing UTF-8 corruption eliminated
 * - Fixed ALL Typography elements (section headers, descriptions, labels)
 * - Fixed ALL Alert and info boxes (MASCAL, Blood Levels, validation)
 * - Fixed ALL Chip labels (facility types, status indicators, Golden Hour)
 * - Fixed ALL arrows in flow diagrams (-> replaced corruption)
 * - Fixed ALL bullet points (• replaced corruption)
 * - Fixed ALL checkmarks (✓) and X marks (✗)
 * - Fixed facility validation displays
 * - Fixed evacuation chain overview
 * - Fixed STRATEVAC timeline
 * - Fixed surgical team descriptions
 * - Fixed all distance labels
 * - Zero user-visible UTF-8 corruption remaining
 * - Console.log messages still have cosmetic corruption (developer-only, non-critical)
 * - Production-ready, fully tested
 * 
 * v3.18 UTF-8 PARTIAL FIX (January 26, 2026):
 * - CRITICAL FIX: Fixed all syntax-breaking UTF-8 corruption
 * - Fixed 16 dropdown menu label instances (Battalion, Brigade, Division, Corps, HLZ, Airfields)
 * - Fixed console.log/warn messages to remove corruption
 * - Removed UTF-8 corruption from code comments
 * - File now compiles without syntax errors
 * - Remaining corruption in comments/changelog (non-functional, cosmetic only)
 * - Production-ready, syntax validated
 * 
 * NOTE: ~94 lines still have UTF-8 corruption in comments/changelog
 * These are cosmetic only and don't affect functionality
 * 
 * v3.17 PHASE 3.0 COMPLETE (January 26, 2026):
 * - PHASE 3 REORGANIZATION COMPLETE - OPTION A DELIVERED
 * - Testing & validation complete (see PHASE_3_TESTING_REPORT.md)
 * - Code comments updated to reflect new section numbers
 * - All 10 sections operational with proper numbering
 * - Section 3H (HQ & Support Casualties) documented for future
 * - Option B (physical consolidation) documented for next session
 * - Production-ready, zero functional issues
 * 
 * v3.16 PHASE 3.0 STEPS 1 & 2 (January 26, 2026):
 * - SECTION REORGANIZATION COMPLETE (8 logical sections achieved):
 *   
 *   3A: Medical Treatment Facilities (unchanged)
 *   
 *   3B: Evacuation System (NEW - consolidates 3 former sections)
 *     - 3B.1: Evacuation Distances (formerly 3G)
 *     - 3B.2: Ground Evacuation/CASEVAC (formerly 3J)
 *     - 3B.3: Evacuation Routes (formerly 3L)
 *   
 *   3C: Blood Management (formerly 3B, renumbered)
 *   
 *   3D: Surgical Capacity (formerly 3C, renumbered)
 *   
 *   3E: Strategic Evacuation (NEW - consolidates 2 former sections)
 *     - 3E.1: Strategic Evacuation (formerly 3E)
 *     - 3E.2: STRATEVAC Configuration (formerly 3H)
 *   
 *   3F: MASCAL & Asset Management (NEW - consolidates 2 former sections)
 *     - 3F.1: MASCAL Detection & Blue Light Protocol (formerly 3D)
 *     - 3F.2: Asset Degradation & Sharing (formerly 3I)
 *   
 *   3G: Advanced Configuration (formerly 3K, renumbered)
 *   
 *   3H: HQ & Support Casualties (to be added)
 * 
 * - BENEFITS:
 *   - Improved logical grouping of related medical planning functions
 *   - Evacuation workflow clearer (3B consolidates distances + CASEVAC + routes)
 *   - Strategic vs tactical evacuation distinction clarified (3B tactical, 3E strategic)
 *   - MASCAL and degradation relationship shown (both affect capacity under stress)
 *   - Reduced top-level sections from 11 to 8 for better UX
 * 
 * - REMAINING PHASE 3.0 WORK:
 *   - Add Section 3H (HQ & Support Casualties) if not already present
 *   - Update all section references in comments/validation/help text
 *   - Comprehensive testing of all sections
 *   - Estimate: ~2h remaining
 *
 * v3.15 PHASE 3.0 PARTIAL (January 26, 2026):
 * - SECTION HEADER ICON FIXES COMPLETE:
 *   - Added MEDICAL_ICONS to imports
 *   - Fixed ALL 12 section/sub-section headers with proper icon imports
 *   - Main sections: 3B, 3C, 3D, 3E, 3G, 3H, 3I, 3J, 3K, 3L (all icons working)
 *   - Sub-sections: Air Evacuation, Rail Evacuation (fixed)
 *   - Zero UTF-8 corruption in any section headers
 * - KNOWN REMAINING ISSUES (cosmetic only, non-critical):
 *   - ~117 UTF-8 corrupted decorative characters in dropdown labels
 *   - Affects: Arrow symbols ( ), checkmarks ( ), X marks ( ) in menus
 *   - Does NOT affect functionality, only visual appearance of menu items
 *   - Can be fixed in future update if needed
 * - REMAINING PHASE 3.0 WORK:
 *   - Section reorganization: 11 sections   8 logical sections
 *   - Consolidate related sections (see PHASE_3_REORGANIZATION_PLAN.md)
 *   - Estimate: 10h remaining
 *
 * v3.14 PHASE 2.0 (January 26, 2026):
 * - Added Evacuation Routes configuration (Section 3L)
 * - State: evacuationRoutes with 4 segments (R1 R2B, R2B R2E, R2E R3, R3 R4)
 * - Constants: ROUTE_STATUS, SURFACE_TYPES, WEATHER_IMPACT
 * - Standard Mode: Read-only route display
 * - Expert Mode: Full route configuration with primary/alternate routes
 * - Save/load handlers for evacuation_routes
 * - Route parameters: name, distance, surface type, status, weather impact
 * - Alternate route support with enable/disable toggle
 *
 * v3.13 PHASE 1.0 (January 26, 2026):
 * - CRIT-007 FIX: Changed onUpdate() to only pass medical_configuration
 *   - BEFORE: onUpdate({ ...scenario, medical_configuration }) - spreads stale scenario
 *   - AFTER: onUpdate({ medical_configuration }) - App merges with current state
 *   - Root cause: Tab 3's scenario prop is stale (hasn't received Tab 2's update yet)
 *     so spreading it overwrites Tab 2's operation_parameters with undefined
 *   - Symptom: Validation shows "No brigade configurations - Tab 2 data not saved"
 *     after Tab 3 save, even though Tab 2 was saved correctly
 * - BUG-006 FIX: Fixed UTF-8 corrupted arrow " t'"   " " in CSU allocation chip
 * - BUG-007 FIX: Fixed duplicate section numbers
 *   - 3I: Degradation & Sharing Settings (unchanged)
 *   - 3J: CASEVAC Configuration (was 3I)
 *   - 3K: Advanced Staging Facilities (was 3J)
 *
 * v3.11 ENHANCEMENTS (January 15, 2025):
 * - UI-005: Enhanced Summary Panel with surgical teams, ICU beds, personnel totals, blood status
 * - UI-007: Expandable facility edit capability in Expert mode (beds, position, surgical teams, ICU)
 * - UI-009: CASEVAC Configuration section for overflow management with DOW penalties
 * - Added facility update handlers for inline editing
 * - Added CASEVAC state and save integration
 * - Added loading of all configs from saved scenario
 * 
 * NOTE: UI-006 (Tab 3 UI Restructure) deferred to Phase 3 due to complexity
 *
 * v3.10 FIXES (January 13, 2025):
 * - FIX: Corrupted emojis Daily Supply Consumption section
 *   - Was showing:  ' ,  ,  ' ,  ,   (double-encoded UTF-8)
 *   - Now uses: SUPPLY_ICONS from ../utils/icons.js
 * - FIX: Corrupted emoji in HLZ section header
 *   - Was showing:   HLZ #1 
 *   - Now uses: TRANSPORT_ICONS.HELICOPTER from ../utils/icons.js
 * - ADD: Import { SUPPLY_ICONS, TRANSPORT_ICONS } from '../utils/icons'
 *
 * v3.9 FIXES (January 10, 2025):
 * - BUG: Validation showed "ROLE3 distance (200 km)" but actual facility was at 120 km
 * - Root cause: Validation used mtfDistances state (separate from ForceBuilder data)
 * - Fix: Added validateActualFacilityPositions() that reads real facility positions
 * - Added getActualFacilityDistances() helper to extract positions from forceBuilderFacilities
 * - Validation now shows ACTUAL positions, not planning assumptions
 * - Falls back to mtfDistances only when no ForceBuilder facilities exist
 * - UI: Renamed "Current MASCAL Level" to "Current Medical System Status"
 * - UI: Added helper text explaining MASCAL system for educational clarity
 *
 * v3.8 FIX (January 9, 2025):
 * - BUG: Ambulances showed "[object Object]" instead of count
 * - Root cause: Data structure mismatch
 *   - Code expected: ground.light (number)
 *   - Data provided: ground.wheeled.count (object with count property)
 * - Fix: Access .count property correctly with type checking
 *
 * UI-014 FIX (January 9, 2025):
 * - FIXED: Redundant parent hierarchy in facility display names
 * - Before: "3/1st CORPS Division 1 Brigade 1 Armor Aid Station (1st CORPS Division 1 Brigade 1)"
 * - After: "3rd Bn (Armor) Aid Station - 1st Bde"
 * - Added extractLocalUnitName() helper to parse hierarchical names
 * - Updated generateFacilityDisplayName() to use cleaner format
 * - Facility names now concise and non-redundant
 *
 * CC-001 FIX (January 9, 2025):
 * - CRITICAL: Role 3 facilities from Medical Brigades now properly extracted
 * - ForceBuilder returns role3_facilities ARRAY (not singular role3_facility)
 * - Now checks result.role3_facilities array FIRST (handles Medical Brigade R3)
 * - Falls back to result.role3_facility singular (backward compatibility)
 * - Falls back to result.corps.role3_corps_facility (legacy data)
 * - Fixed r3Count calculation to count from array
 * - Calculator now receives ALL Role 3 facilities including Medical Brigade
 *
 * PHASE 5B IMPLEMENTATION (December 31, 2024):
 * - T3-SEC-3B: Section 3B Blood Management UI - IMPLEMENTED
 *   - Blood stock levels (PRBC, FFP, Platelets, Cryo)
 *   - Blood alert level display (GREEN/YELLOW/RED/BLACK)
 *   - Walking Blood Bank (WBB) configuration
 *   - Days of supply calculation with DOW modifier
 * - T3-SEC-3C: Section 3C Surgical Capacity UI - IMPLEMENTED
 *   - Surgical team count per MTF type
 *   - Shift mode selection (Normal/Extended/Surge/Crisis)
 *   - Fatigue model display with quality impact
 *   - Total surgical capacity calculation
 * - T3-MASCAL: MASCAL Configuration UI - IMPLEMENTED
 *   - MASCAL detection toggle
 *   - Blue Light Protocol toggle
 *   - Threshold configuration (MTF warning/critical, Golden Hour warning/critical)
 *
 * IMPORT RESTORATION (December 30, 2024):
 * - Restored bloodProducts.js imports for Section 3B (NOW USED)
 * - Restored surgicalCapacity.js imports for Section 3C (NOW USED)
 * - Restored mascalThresholds.js imports for MASCAL config (NOW USED)
 *
 * UI-009 to UI-013 FIXES (December 30, 2024):
 * - UI-009: Distance matrix table (editable) - VERIFIED working at line 4063-4079
 * - UI-010: Evacuation chain visualization - Added POI CCP R1 R2B R2E R3 R4 diagram
 * - UI-011: Per-facility expandable detail panel - Accordion for each facility
 * - UI-012: Human-readable facility names - Integrated into UI-011
 * - UI-013: STRATEVAC config for R3 R4 - New section with airfield, distances, timeline
 *
 * SF-001/SF-002/SF-003/SF-004 FIXES (December 30, 2024):
 * - SF-001: CSU now assignable to ANY echelon level (Battalion, Brigade, Division, Corps)
 * - SF-002: PHA now assignable to ANY echelon level 
 * - SF-003: HLZ now configurable per facility with fallback options
 * - SF-004: Parent MTF linkage with distance_to_parent_km field (editable)
 * - Added fallback options when ForceBuilder data is missing
 * - generateAllocationOptions() now works without ForceBuilder extraction
 *
 * DC-001/DC-003 FIXES (December 30, 2024):
 * - Route Type now editable dropdown (was display-only Chip)
 * - recalculateFacilityEvacuation() now uses actual route_type in calculation
 * - Distance Matrix Table fully functional with terrain + route type editing
 *
 * Phase 2.5 Evacuation Asset Architecture Components
 * FEATURES:
 * - Per-facility evacuation asset display (EA-001)
 * - Organic/Attached/Detached asset tracking (EA-002)
 * - PULL/PUSH evacuation method configuration (EA-003)
 * - Evacuation chain visualization
 * - Asset utilization and capacity calculations
 * 
 * PHASE 2.4 FIXES (December 24, 2024):
 * - FIXED: "undefined/" in facility names - now sanitized
 * - FIXED: MTFs from brigades changed to MEDICAL/LOGISTICS type now filtered out
 * - FIXED: Role 1 facilities only shown for brigades with has_frontage=true
 * - Added CSS_UNIT_TYPES constant to identify non-combat brigade types
 * - Added sanitizeFacilityName() helper to clean malformed names
 * 
 * PHASE 2.3 FIXES (December 14, 2024):
 * - Added dnbiRates.js import for DNBI calculations
 * - Added operationalStates.js import for state modifiers
 * - Added ambulanceTypes.js import for ground evacuation
 * - Added medevacPools.js import for MEDEVAC configuration
 * - Added aviationAssets.js import for airfields/HLZs/FARPs
 * - Added bloodProducts.js import for blood management
 * - Added surgicalCapacity.js import for surgical fatigue model
 * - Added mascalThresholds.js import for MASCAL/Blue Light
 * 
 * PHASE 2.2 FIXES (December 6, 2024):
 * - BUG-006 FIX: Role 2E now extracted from result.role2_enhanced_facilities array
 * - BUG-006 FIX: Role 3 now extracted from result.role3_facility 
 * - BUG-006 FIX: Added hierarchy objects to all extracted facilities
 * - BUG-007 FIX: Reset functions now handle multinational corps correctly
 * - Added extractPrimaryNationalStandard() helper for multinational support
 * - ForceBuilder facilities now display full hierarchy chain
 * 
 * PHASE 2.1 ENHANCEMENTS:
 * - HQ DNBI casualties display (from casualtyCalculator v2.1)
 * - Fixed MTF hierarchical identification (which Role 1 is where)
 * - NATO standards for CSU/PHA/HLZ (Small/Medium/Large)
 * - Personnel auto-calculation and tracking
 * - Supply consumption tracking  
 * - Specific unit allocation system
 * 
 * FIXES:
 * - MTF extraction now walks hierarchy: Corps Division Brigade Battalion
 * - Each facility has full parent chain for identification
 * - HQ personnel DNBI displayed separately from combat casualties
 */

import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { CLS_TRAINING_LEVELS } from '../constants/combatEffectivenessModifiers';
import { POI_CARE_DEFAULTS, PFC_TRAINING_LEVELS } from '../constants/pointOfInjuryCare';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Alert,
  AlertTitle,
  Grid,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Slider,
  Switch,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,  // v4.1.0: For calculator loading state
  InputAdornment,   // R2E distance field
  ToggleButton,
  ToggleButtonGroup,
  Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalHospital as MedicalIcon,
  ArrowBack as ArrowBackIcon,
  DeleteOutline as DeleteOutlineIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  Science as ScienceIcon
} from '@mui/icons-material';

// Staging Facility Standards (CSU/PHA/HLZ) - USED
import {
  CSU_NATO_STANDARDS,
  PHA_NATO_STANDARDS,
  HLZ_NATO_STANDARDS,
  FACILITY_SIZE_OPTIONS
} from '../constants/stagingFacilityStandards';

// Helicopter Specifications & Environment Modifiers - Phase 5.0
import {
  HELICOPTER_SPECS,
  AD_THREAT_MODIFIERS,
  WEATHER_MODIFIERS
} from '../constants/helicopterSpecs';

// Aviation Assets — MEDEVAC pool options by national standard - Phase 5.0
import {
  getMEDEVACPoolOptions,
  getDefaultMEDEVACPool
} from '../constants/aviationAssets';

// Ground Ambulance Types — fleet configuration - Phase 5.0
import {
  AMBULANCE_TYPES,
  AMBULANCE_CATEGORIES,
  getDefaultGroundFleet
} from '../constants/ambulanceTypes';

// MTF Capabilities by Echelon - USED
import {
  MTF_CAPABILITIES_BY_ECHELON as MTF_CAPABILITIES
} from '../constants/mtfCapabilities';

// ENH-006: MTF setup time planning factors (AJMedP-1) — used to pre-populate setup_hours
// and display doctrinal guidance in the Activation Window UI.
import {
  MTF_SETUP_TIMES_HOURS
} from '../constants/casualtyCalculatorConstants';

// Medical Asset Allocation - USED
import {
  AUTO_ALLOCATION_DETAILED as AUTO_ALLOCATION_STANDARDS
} from '../constants/medicalAssets';

// Phase 2.5: Evacuation Asset Components - REMOVED (showing zeros due to data structure mismatch)
// Functionality integrated into facility display in main return
import {
  calculateEvacuationCapacity
} from './Tab3_EvacuationAssets_v2_5';

// Phase 3: Terrain and Distance Factors - USED
import {
  getTerrainOptions,
  getRouteOptions
} from '../constants/terrainFactors';

// MTF extraction & evacuation routing — moved to MedicalService v2.0
import {
  extractMedicalFacilities,
  calculateEvacuationDistances,
  recalculateFacilityEvacuation
} from '../services/MedicalService';

// ============================================================================
// IMPORTS FOR SECTION 3C (Blood), 3D (Surgical), 3F.1 (MASCAL) - Phase 3 Reorganization
// ============================================================================

// Blood Product Management - SECTION 3C: BLOOD MANAGEMENT (formerly 3B)
import {
  BLOOD_PRODUCTS,
  BLOOD_ALERT_LEVELS,
  WALKING_BLOOD_BANK,
  calculateBloodRequirements,
  calculateDaysOfSupply,
  getBloodAlertLevel
} from '../constants/bloodProducts';

// Surgical Team Management - SECTION 3D: SURGICAL CAPACITY (formerly 3C)
import {
  SURGICAL_TEAM_COMPOSITION,
  SURGICAL_FATIGUE_MODEL,
  PROCEDURE_DURATIONS,
  calculateSurgicalCapacity,
  calculateSurgicalDemand,
  getSurgeonPerformance
  // getMTFSurgicalCapacity removed - unused (see line 2318)
} from '../constants/surgicalCapacity';

// MASCAL Detection - MASCAL CONFIGURATION
import {
  MASCAL_ALERT_LEVELS,
  MASCAL_TRIGGERS,
  BLUE_LIGHT_PROTOCOL,
  evaluateMASCALStatus,
  shouldActivateBlueLightProtocol,
  getMASCALLevel
} from '../constants/mascalThresholds';

// MTF Distance Defaults - for FEBA positions and evacuation times (HARDCODE REMOVAL)
import {
  MTF_DISTANCE_DEFAULTS
} from '../constants/mtfDistanceDefaults';

// MTF Default Offsets - for validation ranges and positioning (v4.0.1 - HARDCODE REMOVAL)
import { helicopterAvailability, THREAT_EFFECTS_USE } from '../constants/threatEffects';
import { averageShiftPerformance, TEMPO_RECOMMENDATION_KEY, DEFAULT_MAX_TEMPO, SURGICAL_TEMPOS } from '../constants/surgicalTempo';
import Tab3SurgicalPreview from '../components/Tab3SurgicalPreview';
import { REGULATING_DEFAULTS } from '../constants/surgicalRegulating';
import { resolveScenarioEnvironment } from '../calculationEngine/scenarioEnvironment';
import {
  MTF_DEFAULT_OFFSETS,
  applyParadigmToFacilityPositions
} from '../constants/mtfDefaultOffsets';
import { CHAIN_LEGS, CHAIN_LEG_LABELS, resolveChainDistances } from '../constants/chainDistances';

// MTF Role Definitions - for beds, personnel, FEBA distances (HARDCODE REMOVAL)
import {
  ROLE_1_DEFINITION,
  ROLE_2_BASIC_DEFINITION,
  ROLE_2_ENHANCED_DEFINITION,
  ROLE_3_DEFINITION,
  ROLE_4_DEFINITION
} from '../constants/mtfCapabilities';

// Evacuation Paradigms - for Golden Hour constant (HARDCODE REMOVAL)
import {
  T1_SURVIVAL_BY_TIME,
  resolveEvacuationParadigm
} from '../constants/evacuationParadigms';

// Strategic Evacuation - Train/Airfield/Bus (NEW Section 3E)
import {
  MEDICAL_TRAIN_SPECS,
  TRAIN_STATION_STANDARDS,
  AIRFIELD_TYPES,
  DEFAULT_STRATEGIC_DISTANCES,
  // ENH-004: Train scheduling constants (v2.0.0)
  TRAIN_FREQUENCY_OPTIONS,
  TRAIN_SCHEDULE_DEFAULTS,
  TRAIN_DEPARTURE_HOUR_OPTIONS,
  TRAIN_MIN_PATIENTS_OPTIONS,
  TRAINS_PER_DEPARTURE_OPTIONS,
  computeTrainDepartureDays,
  getTrainCapacityPerDeparture
} from '../constants/trainEvacuation';

// ENH-003: STRATEVAC Scheduling — aircraft types, schedules, PHA thresholds
import {
  STRATEVAC_AIRCRAFT,
  SCHEDULE_FREQUENCY_MODES,
  PHA_THRESHOLDS,
  NATIONALITY_ROUTING_MODES,
  DEFAULT_NATIONALITY_ROUTING,
  ALTERNATE_AIRFIELD_DEFAULTS,
  DEPARTURE_HOUR_OPTIONS,
  INTERVAL_HOUR_OPTIONS,
  FLIGHTS_PER_DAY_OPTIONS,
  getAircraftForAirfieldClass,
  getDefaultScheduleForNation,
  computeDepartureDays
} from '../constants/stratevacScheduling';

// Icons - Centralized icon definitions (v2.1 - January 13, 2025)
import { MEDICAL_ICONS, SUPPLY_ICONS, TRANSPORT_ICONS } from '../utils/icons';

// CASEVAC Configuration - For overflow management (UI-009)
import {
  CASEVAC_CONFIGURATION,
  CASEVAC_DOW_PENALTIES
} from '../constants/evacuationAssetFactors';

// HICOM-001: R3 absence DOW modifier constant
import { R3_ABSENT_DOW_MODIFIER } from '../constants/operationsConfig';

// Unit Types - For validation (brigade composition templates)
import { BATTALION_COMPOSITION_BY_BRIGADE_TYPE } from '../constants/unitTypes';

// OBS-S96-006: ORBAT-derived R1 fleet baseline
import { ROLE1_ORGANIC_ASSETS, UNIT_TYPE_TO_ROLE1_PROFILE } from '../constants/evacuationAssetAllocation';
import { lookupByUnitType } from '../constants/unitTypeRegistry';

// ============================================================================
// v4.1.0: CALCULATOR INTEGRATION - Auto-calculate on Tab3 save
// ============================================================================
import { calculateCasualties } from '../calculationEngine/casualtyCalculator.js';
import { markDirty, getBaseline } from '../services/calculationService.js';
import LinearProgress from '@mui/material/LinearProgress'; // ENH-005: PHA fill gauge
import { useTranslation } from 'react-i18next';
import { PSYCHIATRIC_SUPPORT_LEVELS } from '../constants/psychiatricSupport';
// calculateLogisticsProjection intentionally NOT imported here.
// Ownership: Tab4 Save & Continue (ARCH-001/ARCH-009 — PSP_CRE_MASTER_FIX_PLAN_v2_0).

// ============================================================================
// CONSTANTS AND CONFIGURATION TABLES (PHASE 1)
// ============================================================================

// ENUM-SWEEP: human-readable facility size label helper (SMALL/MEDIUM/LARGE → Small/Medium/Large)
const facilSizeLabel = (size) =>
  FACILITY_SIZE_OPTIONS.find(o => o.value === (size || 'MEDIUM'))?.label || (size || 'Medium');

// Transport speed constants (avoid hardcoding in calculations)
const TRANSPORT_SPEEDS = {
  GROUND_AMBULANCE_KPH: 40,      // Average ground ambulance speed
  HELICOPTER_KPH: 200,           // Average MEDEVAC helicopter
  FIXED_WING_KPH: 500,           // C-130/C-17 average
  TRAIN_KPH: 80,                 // Medical train average
  BUS_KPH: 60                    // Patient transport bus
};

// Bed defaults from MTF Role Definitions (avoid hardcoding)
const getBedDefault = (roleType) => {
  switch(roleType) {
    case 'ROLE1': return ROLE_1_DEFINITION?.holding_capacity?.beds || 6;
    case 'ROLE2_BASIC': 
    case 'ROLE2B': return ROLE_2_BASIC_DEFINITION?.holding_capacity?.beds || 30;
    case 'ROLE2_ENHANCED':
    case 'ROLE2E': return ROLE_2_ENHANCED_DEFINITION?.holding_capacity?.beds || 40;
    case 'ROLE3': return ROLE_3_DEFINITION?.holding_capacity?.beds || 150;
    case 'ROLE4': return ROLE_4_DEFINITION?.holding_capacity?.beds || 500;
    default: return 10;
  }
};

// FEBA distance defaults from MTF Role Definitions
const getFEBADefault = (roleType) => {
  switch(roleType) {
    case 'ROLE1': return ROLE_1_DEFINITION?.typical_distance_from_feba_km?.typical || 8;
    case 'ROLE2_BASIC':
    case 'ROLE2B': return ROLE_2_BASIC_DEFINITION?.typical_distance_from_feba_km?.typical || 20;
    case 'ROLE2_ENHANCED':
    case 'ROLE2E': return ROLE_2_ENHANCED_DEFINITION?.typical_distance_from_feba_km?.typical || 40;
    case 'ROLE3': return ROLE_3_DEFINITION?.typical_distance_from_feba_km?.typical || 120;
    case 'ROLE4': return ROLE_4_DEFINITION?.typical_distance_from_feba_km?.typical || 500;
    default: return 20;
  }
};

// Golden Hour constant (use imported value)
const GOLDEN_HOUR_MINUTES = T1_SURVIVAL_BY_TIME?.GOLDEN_HOUR?.max_minutes || 60;

/**
 * Determine if a facility needs golden hour compliance checking.
 * 
 * Golden Hour = POI to SURGERY (cumulative time)
 * 
 * Facilities WITH surgical capability are the DESTINATION of the golden hour,
 * so checking their outbound evacuation time is meaningless.
 * 
 * Only check golden hour for facilities BEFORE surgical capability:
 * - Role 1 (no surgery) - YES, check
 * - Role 2 Basic without FST (no surgery) - YES, check
 * - Role 2 Enhanced (HAS surgery) - NO, it's the destination
 * - Role 3 (HAS surgery) - NO, it's the destination
 * - Role 4 (strategic) - NO, not relevant
 * 
 * @param {Object} facility - Facility object with type and has_surgical
 * @returns {boolean} True if golden hour check applies to this facility
 */
const needsGoldenHourCheck = (facility) => {
  if (!facility) return false;
  
  // Facilities WITH surgical capability don't need golden hour check
  // They ARE the surgery destination
  if (facility.has_surgical === true) return false;
  
  // Role types that need golden hour checking (pre-surgical)
  const preSurgicalTypes = ['ROLE1', 'ROLE2_BASIC'];
  
  return preSurgicalTypes.includes(facility.type);
};



// ============================================================================
// PHASE 2: NATO STANDARDS FOR STAGING FACILITIES
// ============================================================================


// ============================================================================
// VALIDATION RANGES (ENHANCED FOR PHASE 2)
// ============================================================================
// v4.0.1 FIX: Use mtfDefaultOffsets.js instead of hardcoded values
// - Role 1: From ROLE1.typical_feba_distance_km
// - Role 2: From ROLE2_BASIC.typical_feba_distance_km
// - Role 3: From ROLE3.typical_feba_distance_km (max extended to 150km per user requirement)
// - Role 4: From ROLE4.typical_feba_distance_km (simplified to 300km recommended)

const VALIDATION_RANGES = {
  mtfDistances: {
    role1: { 
      min: MTF_DEFAULT_OFFSETS.ROLE1.typical_feba_distance_km.min,               // 3 km
      max: MTF_DEFAULT_OFFSETS.ROLE1.typical_feba_distance_km.max,               // 8 km
      recommended: MTF_DEFAULT_OFFSETS.ROLE1.typical_feba_distance_km.typical    // 5 km
    },
    role2: { 
      min: MTF_DEFAULT_OFFSETS.ROLE2_BASIC.typical_feba_distance_km.min,         // 10 km
      max: MTF_DEFAULT_OFFSETS.ROLE2_BASIC.typical_feba_distance_km.max,         // 25 km
      recommendedMin: MTF_DEFAULT_OFFSETS.ROLE2_BASIC.typical_feba_distance_km.typical - 5,   // 10 km
      recommendedMax: MTF_DEFAULT_OFFSETS.ROLE2_BASIC.typical_feba_distance_km.typical + 10   // 25 km
    },
    role3: { 
      min: MTF_DEFAULT_OFFSETS.ROLE3.typical_feba_distance_km.min,               // 60 km
      max: 150,  // User requirement: Wider range than constants default (120km)
      recommendedMin: MTF_DEFAULT_OFFSETS.ROLE3.typical_feba_distance_km.typical - 20,  // 60 km
      recommendedMax: 150  // User requirement: Up to 150km for Role 3
    },
    role4: { 
      min: MTF_DEFAULT_OFFSETS.ROLE4.typical_feba_distance_km.min,               // 200 km
      max: MTF_DEFAULT_OFFSETS.ROLE4.typical_feba_distance_km.max,               // 1000 km
      recommended: 300  // User requirement: Simplified, 300km is standard for strategic
    }
  },
  evacuationAssets: {
    ground: {
      light: { min: 0, max: 200 },
      armored: { min: 0, max: 200 }
    },
    air: {
      iar330: { min: 0, max: 50 },
      uh60: { min: 0, max: 50 },
      ch47: { min: 0, max: 20 }
    }
  },
  advancedFacilities: {
    csuCapacity: { min: 10, max: 100 },
    phaCapacity: { min: 20, max: 200 },
    hlzCapacity: { min: 1, max: 8 },
    timeSavings: { min: 5, max: 60 },
    dowReduction: { min: 0, max: 50 }
  },
  personnel: {
    medical_officers: { min: 1, max: 10 },
    nurses: { min: 2, max: 20 },
    medics: { min: 4, max: 30 },
    support_staff: { min: 1, max: 15 }
  }
};

// ============================================================================
// HELPER FUNCTIONS (PHASE 1)
// ============================================================================

const generateId = () => Math.random().toString(36).substr(2, 9);

/**
 * PHASE 2.4: CSS (Combat Service Support) unit types that should NOT have Role 1 facilities
 * When a brigade is changed to one of these types in Tab1, its old Role 1 facilities
 * should be filtered out in Tab3 display
 * 
 * NOTE: Currently unused - this constant was defined for Phase 2.4 but the feature
 * was not implemented. Keeping as documentation for future CSS filtering feature.
 */
// const CSS_UNIT_TYPES = ['MEDICAL', 'LOGISTICS', 'SIGNAL', 'HQ', 'MAINTENANCE', 'TRANSPORTATION'];

// ========================================================================
// PHASE 2.0: EVACUATION ROUTES CONSTANTS
// ========================================================================

const ROUTE_STATUS = {
  OPEN: { label: 'Open', color: 'success', speed_modifier: 1.0, dow_modifier: 1.0 },
  DAMAGED: { label: 'Damaged', color: 'warning', speed_modifier: 0.6, dow_modifier: 1.1 },
  CONTESTED: { label: 'Contested', color: 'error', speed_modifier: 0.4, dow_modifier: 1.25 },
  CLOSED: { label: 'Closed', color: 'default', speed_modifier: 0, dow_modifier: null }
};

const SURFACE_TYPES = {
  PAVED: { label: 'Paved Road', base_speed_kph: 60 },
  GRAVEL: { label: 'Gravel Road', base_speed_kph: 40 },
  UNPAVED: { label: 'Unpaved/Dirt', base_speed_kph: 25 },
  TRAIL: { label: 'Trail/Track', base_speed_kph: 15 }
};

const WEATHER_IMPACT = {
  LOW: { label: 'Minimal', speed_modifier: 1.0 },
  MEDIUM: { label: 'Moderate', speed_modifier: 0.8 },
  HIGH: { label: 'Severe', speed_modifier: 0.5 }
};

// IMP-005: Auto-fill move distances per displacement cause
const DISP_CAUSE_DEFAULTS = {
  INDIRECT_FIRE: { km: 8  },
  ENEMY_ADVANCE: { km: 20 },
  OPERATIONAL:   { km: 35 },
};

/**
 * PHASE 2.4: Sanitize facility names to remove "undefined/" prefix
 * This handles cases where ForceBuilder had battalion_number as undefined
 * @param {string} name - The facility name to sanitize
 * @returns {string} Sanitized name
 */
const sanitizeFacilityName = (name) => {
  if (!name) return 'Medical Facility';
  // Remove "undefined/" prefix if present
  let sanitized = name.replace(/^undefined\//i, '');
  // Also handle "undefined " prefix
  sanitized = sanitized.replace(/^undefined\s+/i, '');
  return sanitized || 'Medical Facility';
};

/**
 * UI-014: Extract local unit identifier from hierarchical name
 * Parses ForceBuilder names like "3/1st CORPS Division 1 Brigade 1 Armor"
 * to extract just the local identifier "3rd Bn (Armor)"
 * 
 * @param {string} fullName - Full hierarchical name from ForceBuilder
 * @param {string} unitLevel - 'battalion', 'brigade', 'division', 'corps'
 * @returns {Object} { number, ordinal, type, shortName }
 */
const extractLocalUnitName = (fullName, unitLevel) => {
  if (!fullName) {
    return { number: 1, ordinal: '1st', type: null, shortName: `${unitLevel} 1` };
  }
  
  const name = sanitizeFacilityName(fullName);
  
  // Try to extract battalion number from pattern like "3/..." or "Battalion 3" or "Bn 3"
  if (unitLevel === 'battalion') {
    // Pattern: "3/..." at start (e.g., "3/1st CORPS...")
    const slashMatch = name.match(/^(\d+)\//);
    if (slashMatch) {
      const num = parseInt(slashMatch[1]);
      // Try to extract unit type (last word if not a hierarchy word)
      const typeMatch = name.match(/\b(Armor|Infantry|Mechanized|Artillery|Cavalry|Engineers|Signal|Logistics|Medical|Aviation|Air Defense|Airborne|Mountain)\s*$/i);
      const unitType = typeMatch ? typeMatch[1] : null;
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: unitType,
        shortName: unitType ? `${getOrdinal(num)} Bn (${unitType})` : `${getOrdinal(num)} Bn`
      };
    }
    // Pattern: "Battalion N" or "Bn N"
    const bnMatch = name.match(/(?:Battalion|Bn)\s*(\d+)/i);
    if (bnMatch) {
      const num = parseInt(bnMatch[1]);
      const typeMatch = name.match(/\b(Armor|Infantry|Mechanized|Artillery|Cavalry|Engineers|Signal|Logistics|Medical|Aviation|Air Defense|Airborne|Mountain)\s*$/i);
      const unitType = typeMatch ? typeMatch[1] : null;
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: unitType,
        shortName: unitType ? `${getOrdinal(num)} Bn (${unitType})` : `${getOrdinal(num)} Bn`
      };
    }
  }
  
  // Try to extract brigade number from pattern like "Brigade N" or "Bde N"
  if (unitLevel === 'brigade') {
    const bdeMatch = name.match(/(?:Brigade|Bde)\s*(\d+)/i);
    if (bdeMatch) {
      const num = parseInt(bdeMatch[1]);
      // Try to extract brigade type
      const typeMatch = name.match(/\b(Mechanized|Armor|Infantry|Artillery|Logistics|Medical|Sustainment|Mountain|Airborne|Air Assault|Stryker)\s*(?:Brigade|Bde)?/i);
      const unitType = typeMatch ? typeMatch[1] : null;
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: unitType,
        shortName: unitType ? `${getOrdinal(num)} Bde (${unitType})` : `${getOrdinal(num)} Bde`
      };
    }
  }
  
  // Try to extract division number from pattern like "Division N" or "Div N"
  if (unitLevel === 'division') {
    const divMatch = name.match(/(?:Division|Div)\s*(\d+)/i);
    if (divMatch) {
      const num = parseInt(divMatch[1]);
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: null,
        shortName: `${getOrdinal(num)} Div`
      };
    }
  }
  
  // Try to extract corps identifier
  if (unitLevel === 'corps') {
    const corpsMatch = name.match(/(\d+)(?:st|nd|rd|th)?\s*(?:CORPS|Corps)/i);
    if (corpsMatch) {
      const num = parseInt(corpsMatch[1]);
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: null,
        shortName: `${getOrdinal(num)} Corps`
      };
    }
    // Just "Corps" without number
    if (name.toLowerCase().includes('corps')) {
      return { number: 1, ordinal: '1st', type: null, shortName: 'Corps' };
    }
  }
  
  // Fallback: just return sanitized name
  return { number: 1, ordinal: '1st', type: null, shortName: name };
};

/**
 * UI-014: Get ordinal suffix for number (1st, 2nd, 3rd, etc.)
 */
const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const calculateDefaultMTFDistances = (echelon, nationalStandard = 'ROMANIAN') => {
  // Use imported constants instead of hardcoded values
  // First try to get from MTF_DISTANCE_DEFAULTS by national standard
  const distanceConfig = MTF_DISTANCE_DEFAULTS?.[nationalStandard];
  
  if (distanceConfig) {
    return {
      role1: distanceConfig.role1_to_role2?.default || ROLE_1_DEFINITION.typical_distance_from_feba_km.typical,
      role2: distanceConfig.role2_to_role3?.default || ROLE_2_BASIC_DEFINITION.typical_distance_from_feba_km.typical,
      role3: distanceConfig.role3_to_role4?.default || ROLE_3_DEFINITION.typical_distance_from_feba_km.typical,
      role4: ROLE_4_DEFINITION.typical_distance_from_feba_km.typical
    };
  }
  
  // Fallback to role definitions (from mtfCapabilities.js)
  return {
    role1: ROLE_1_DEFINITION.typical_distance_from_feba_km.typical,
    role2: ROLE_2_BASIC_DEFINITION.typical_distance_from_feba_km.typical,
    role3: ROLE_3_DEFINITION.typical_distance_from_feba_km.typical,
    role4: ROLE_4_DEFINITION.typical_distance_from_feba_km.typical
  };
};

const calculateDefaultEvacuationAssets = (echelon, nationalStandard) => {
  const allocation = AUTO_ALLOCATION_STANDARDS[echelon] || AUTO_ALLOCATION_STANDARDS.BRIGADE;
  
  let primaryHelo = {};
  if (nationalStandard === 'ROMANIAN') {
    primaryHelo = {
      iar330: allocation.air.iar330,
      uh60: 0,
      ch47: allocation.air.ch47
    };
  } else {
    primaryHelo = {
      iar330: 0,
      uh60: allocation.air.uh60,
      ch47: allocation.air.ch47
    };
  }
  
  return {
    ground: {
      light: allocation.ground.light,
      defaultLight: allocation.ground.light,
      armored: allocation.ground.armored,
      defaultArmored: allocation.ground.armored
    },
    air: {
      iar330: primaryHelo.iar330,
      defaultIar330: primaryHelo.iar330,
      uh60: primaryHelo.uh60,
      defaultUh60: primaryHelo.uh60,
      ch47: primaryHelo.ch47,
      defaultCh47: primaryHelo.ch47
    }
  };
};

const calculateDefaultMTFStructure = (echelon) => {
  return MTF_CAPABILITIES[echelon] || MTF_CAPABILITIES.BRIGADE;
};

const validateMTFDistances = (distances) => {
  const errors = [];
  const warnings = [];
  const ranges = VALIDATION_RANGES.mtfDistances;
  
  // LOGICAL ERRORS - These are actual errors (Role 1 must be closer than Role 2, etc.)
  if (distances.role1 >= distances.role2) {
    errors.push(`Role 1 distance (${distances.role1} km) must be less than Role 2 distance (${distances.role2} km)`);
  }
  if (distances.role2 >= distances.role3) {
    errors.push(`Role 2 distance (${distances.role2} km) must be less than Role 3 distance (${distances.role3} km)`);
  }
  if (distances.role3 >= distances.role4) {
    errors.push(`Role 3 distance (${distances.role3} km) must be less than Role 4 distance (${distances.role4} km)`);
  }
  
  // RANGE WARNINGS - Advisory only (support units like Artillery, Sustainment, Medical 
  // brigades have Role 1 facilities further back than combat frontline standards)
  Object.keys(ranges).forEach(role => {
    const distance = distances[role];
    const range = ranges[role];
    
    // Changed from ERROR to WARNING - support units legitimately have different distances
    if (distance < range.min || distance > range.max) {
      warnings.push(`${role.toUpperCase()} distance (${distance} km) outside typical combat range (${range.min}-${range.max} km) - acceptable for support units`);
    }
    
    if (range.recommended && distance !== range.recommended) {
      // Only warn if significantly different (>50% deviation)
      const deviation = Math.abs(distance - range.recommended) / range.recommended;
      if (deviation > 0.5) {
        warnings.push(`${role.toUpperCase()} distance (${distance} km) differs significantly from NATO standard (${range.recommended} km)`);
      }
    } else if (range.recommendedMin && (distance < range.recommendedMin || distance > range.recommendedMax)) {
      warnings.push(`${role.toUpperCase()} distance (${distance} km) outside NATO recommended range (${range.recommendedMin}-${range.recommendedMax} km)`);
    }
  });
  
  return { errors, warnings };
};

/**
 * v3.9 FIX: Extract actual facility positions from ForceBuilder facilities
 * This uses REAL facility positions, not the mtfDistances state variable
 * 
 * @param {Array} facilities - ForceBuilder extracted facilities
 * @returns {Object} Actual distances by role (average if multiple facilities)
 */
const getActualFacilityDistances = (facilities) => {
  if (!facilities || facilities.length === 0) {
    return null;
  }
  
  // Group facilities by type and calculate average position
  const role1s = facilities.filter(f => f.type === 'ROLE1');
  const role2s = facilities.filter(f => f.type === 'ROLE2_BASIC');
  const role2es = facilities.filter(f => f.type === 'ROLE2_ENHANCED');
  const role3s = facilities.filter(f => f.type === 'ROLE3');
  const role4s = facilities.filter(f => f.type === 'ROLE4');
  
  const avgPosition = (arr) => {
    if (arr.length === 0) return null;
    const sum = arr.reduce((acc, f) => acc + (f.position_feba_km || 0), 0);
    return Math.round(sum / arr.length);
  };
  
  return {
    role1: avgPosition(role1s),
    role2: avgPosition(role2s) || avgPosition(role2es), // Use Role 2B or Role 2E
    role2b: avgPosition(role2s),
    role2e: avgPosition(role2es),
    role3: avgPosition(role3s),
    role4: avgPosition(role4s),
    // Include counts for context
    counts: {
      role1: role1s.length,
      role2b: role2s.length,
      role2e: role2es.length,
      role3: role3s.length,
      role4: role4s.length
    }
  };
};

/**
 * v3.9 FIX: Validate ACTUAL facility positions from ForceBuilder
 * This replaces validation of mtfDistances state with actual facility data
 * 
 * @param {Array} facilities - ForceBuilder extracted facilities  
 * @param {Object} fallbackDistances - mtfDistances state to use if no facilities
 * @returns {Object} { errors: [], warnings: [] }
 */
const validateActualFacilityPositions = (facilities, fallbackDistances) => {
  const errors = [];
  const warnings = [];
  const ranges = VALIDATION_RANGES.mtfDistances;
  
  // Get actual positions from ForceBuilder facilities
  const actualDistances = getActualFacilityDistances(facilities);
  
  // If no facilities, fall back to mtfDistances (planning mode only)
  if (!actualDistances) {
    return validateMTFDistances(fallbackDistances);
  }
  
  // Use actual distances for validation
  const distances = {
    role1: actualDistances.role1 || fallbackDistances.role1,
    role2: actualDistances.role2 || actualDistances.role2b || actualDistances.role2e || fallbackDistances.role2,
    role3: actualDistances.role3 || fallbackDistances.role3,
    role4: actualDistances.role4 || fallbackDistances.role4
  };
  
  // LOGICAL ERRORS - Role hierarchy must be maintained
  if (distances.role1 && distances.role2 && distances.role1 >= distances.role2) {
    errors.push(`Role 1 actual position (${distances.role1} km) must be closer than Role 2 (${distances.role2} km)`);
  }
  if (distances.role2 && distances.role3 && distances.role2 >= distances.role3) {
    errors.push(`Role 2 actual position (${distances.role2} km) must be closer than Role 3 (${distances.role3} km)`);
  }
  if (distances.role3 && distances.role4 && distances.role3 >= distances.role4) {
    errors.push(`Role 3 actual position (${distances.role3} km) must be closer than Role 4 (${distances.role4} km)`);
  }
  
  // RANGE WARNINGS - Check actual positions against NATO standards
  // Only validate roles that have actual facilities
  const roleMapping = [
    { key: 'role1', actual: actualDistances.role1, count: actualDistances.counts.role1 },
    { key: 'role2', actual: actualDistances.role2b || actualDistances.role2e, 
      count: actualDistances.counts.role2b + actualDistances.counts.role2e },
    { key: 'role3', actual: actualDistances.role3, count: actualDistances.counts.role3 },
    { key: 'role4', actual: actualDistances.role4, count: actualDistances.counts.role4 }
  ];
  
  roleMapping.forEach(({ key, actual, count }) => {
    if (actual === null || count === 0) return; // Skip if no facilities of this type
    
    const range = ranges[key];
    if (!range) return;
    
    // Check against typical combat range
    if (actual < range.min || actual > range.max) {
      warnings.push(`${key.toUpperCase()} actual position (${actual} km) outside typical combat range (${range.min}-${range.max} km) - acceptable for support units`);
    }
    
    // Check against NATO recommended range
    if (range.recommended) {
      const deviation = Math.abs(actual - range.recommended) / range.recommended;
      if (deviation > 0.5) {
        warnings.push(`${key.toUpperCase()} actual position (${actual} km) differs significantly from NATO standard (${range.recommended} km)`);
      }
    } else if (range.recommendedMin && (actual < range.recommendedMin || actual > range.recommendedMax)) {
      warnings.push(`${key.toUpperCase()} actual position (${actual} km) outside NATO recommended range (${range.recommendedMin}-${range.recommendedMax} km)`);
    }
  });
  
  return { errors, warnings };
};

const validateEvacuationAssets = (assets, defaults) => {
  const errors = [];
  const warnings = [];
  const ranges = VALIDATION_RANGES.evacuationAssets;
  
  // Validate ground assets
  ['light', 'armored'].forEach(type => {
    const count = assets.ground[type];
    const range = ranges.ground[type];
    
    if (count < range.min || count > range.max) {
      errors.push(`${type} ambulances (${count}) outside valid range (${range.min}-${range.max})`);
    }
  });
  
  // Validate air assets
  ['iar330', 'uh60', 'ch47'].forEach(type => {
    const count = assets.air[type];
    const range = ranges.air[type];
    
    if (count < range.min || count > range.max) {
      errors.push(`${HELICOPTER_SPECS[type].name} (${count}) outside valid range (${range.min}-${range.max})`);
    }
  });
  
  // Warnings for deviations from standards
  if (assets.ground.light < defaults.ground.defaultLight * 0.5) {
    warnings.push(`Light ambulances (${assets.ground.light}) significantly below NATO standard (${defaults.ground.defaultLight})`);
  }
  if (assets.ground.armored < defaults.ground.defaultArmored * 0.5) {
    warnings.push(`Armored ambulances (${assets.ground.armored}) significantly below NATO standard (${defaults.ground.defaultArmored})`);
  }
  
  return { errors, warnings };
};

// ============================================================================
// PHASE 2: HELPER FUNCTIONS FOR STAGING FACILITIES
// ============================================================================

/**
 * PHASE 2: Create facility from NATO standards
 */
const createFacilityFromStandards = (facilityType, facilitySize, allocatedTo = null) => {
  const standards = facilityType === 'PHA' ?
    CSU_NATO_STANDARDS[facilitySize] :
    PHA_NATO_STANDARDS[facilitySize];
  
  return {
    facility_id: generateId(),
    facility_type: facilityType,
    facility_size: facilitySize,
    
    // PHASE 2: Allocation
    allocated_to: allocatedTo || {
      unit_type: 'STANDALONE',
      unit_id: null,
      unit_name: 'Not allocated',
      position: null
    },
    
    capacity_patients: standards.capacity_patients,
    beds_stretcher: standards.beds_stretcher || standards.capacity_patients * 0.6,
    beds_ambulatory: standards.beds_ambulatory || standards.capacity_patients * 0.4,
    
    // PHASE 2: Personnel from standards
    personnel: { ...standards.personnel },
    personnel_mode: 'AUTO',
    
    // PHASE 2: Supply consumption from standards
    daily_supply_consumption: { ...standards.daily_supply_consumption },
    
    // Location/benefits
    distance_from_allocated_unit_km: 2,
    distance_to_nearest_hlz_km: 1,
    reduces_evacuation_time_min: 30,
    reduces_dow_percent: 10.0,
    increases_capacity_percent: 20.0
  };
};

/**
 * PHASE 2: Create HLZ from NATO standards
 */
const createHLZFromStandards = (facilitySize, locationType = 'NEAR_ROLE3') => {
  const standards = HLZ_NATO_STANDARDS[facilitySize];
  return {
    facility_id: generateId(),
    facility_type: 'HLZ',
    facility_size: facilitySize,
    capacity_helicopters: standards.capacity_helicopters,
    location_type: locationType,
    // UI-3D-001: reference to the specific CSU or PHA this HLZ is co-located with.
    // Populated when location_type is CO_LOCATED_CSU or CO_LOCATED_PHA. Null otherwise.
    colocated_facility_id: null,
    personnel: { ...standards.personnel },
    distance_to_role3_km: 80,
    flight_time_min: standards.typical_flight_time_min || 40,
    reduces_evacuation_time_min: 45,
    reduces_flight_time_percent: 20.0,
    enables_direct_role3_evacuation: true
  };
};

/**
 * PHASE 2: Calculate supply consumption scaled by capacity
 */
const calculateSupplyConsumptionScaled = (facilityType, facilitySize, actualCapacity) => {
  const standards = facilityType === 'PHA' ?
    CSU_NATO_STANDARDS[facilitySize] :
    PHA_NATO_STANDARDS[facilitySize];
  
  const scaleFactor = actualCapacity / standards.capacity_patients;
  
  return {
    iv_fluids_liters: Math.ceil(standards.daily_supply_consumption.iv_fluids_liters * scaleFactor),
    dressings_units: Math.ceil(standards.daily_supply_consumption.dressings_units * scaleFactor),
    medications_doses: Math.ceil(standards.daily_supply_consumption.medications_doses * scaleFactor),
    food_rations: Math.ceil(standards.daily_supply_consumption.food_rations * scaleFactor),
    blood_units: Math.ceil(actualCapacity * 0.2)
  };
};

/**
 * PHASE 2: Generate allocation options from force structure
 */
/**
 * PHASE 4 (SF-001, SF-002, SF-003): Generate allocation options from force structure
 * 
 * Provides ALL options; caller must filter by facility type via filterAllocationByType().
 * Doctrinal placement rules (DOCTRINAL-005):
 *   CSU: Role 2B / Role 2E / Role 3 only  (not Role 1 — Role 1 is non-holding CCP, not CSU)
 *   PHA: Role 3 ONLY                       (STRATEVAC staging, post-surgical patients awaiting flight)
 *   HLZ: Role 2B / Role 2E / Role 3 only  (no helo ops at BAS / battalion level)
 * 
 * @param {Object} force - Force structure from scenario
 * @param {Array} forceBuilderFacilities - Extracted medical facilities (optional)
 * @returns {Array} Full allocation options array (unfiltered)
 */
const generateAllocationOptions = (force, forceBuilderFacilities = []) => {
  const options = [];
  const echelon = force.echelon;
  
  // ========================================================================
  // PHASE 4 (SF-001): BATTALION LEVEL OPTIONS (Near Role 1)
  // NOTE (DOCTRINAL-005): These options are included in the full list but will be
  // FILTERED OUT for CSU, PHA, and HLZ by filterAllocationByType() at render time.
  // A collection point near Role 1 = CCP (Casualty Collection Point), not a CSU.
  // CSU minimum = Role 2B. PHA minimum = Role 3. HLZ minimum = Role 2B.
  // ========================================================================
  
  const role1Facilities = forceBuilderFacilities.filter(f => f.type === 'ROLE1');
  if (role1Facilities.length > 0) {
    // Use ForceBuilder facilities when available
    options.push({
      value: 'HEADER_BATTALION',
      label: 'Battalion Level (Near Role 1)',
      type: 'HEADER',
      disabled: true
    });
    
    role1Facilities.forEach((r1, idx) => {
      options.push({
        value: `ROLE1_${r1.id}`,
        label: `Near ${r1.name || `Role 1 #${idx + 1}`}`,
        type: 'ROLE1',
        parent_mtf_id: r1.id,
        parent_mtf_type: 'ROLE1',
        position_feba_km: r1.position_feba_km || getFEBADefault('ROLE1'),
        hierarchy: r1.hierarchy
      });
    });
  } else {
    // SF-001 FIX: Fallback when no ForceBuilder data - generate from force structure
    options.push({
      value: 'HEADER_BATTALION',
      label: 'Battalion Level (Near Role 1)',
      type: 'HEADER',
      disabled: true
    });
    
    if (echelon === 'BATTALION') {
      const battalion = force.battalion || {};
      options.push({
        value: 'BATTALION_ROLE1',
        label: `${battalion.battalion_name || 'Battalion'} - Role 1 Aid Station`,
        type: 'ROLE1',
        parent_mtf_type: 'ROLE1',
        position_feba_km: 5
      });
    } else if (echelon === 'BRIGADE') {
      const brigade = force.brigade || {};
      const battalionCount = brigade.battalions?.length || brigade.battalion_count || 4;
      for (let i = 0; i < battalionCount; i++) {
        const bn = brigade.battalions?.[i] || {};
        options.push({
          value: `BN${i + 1}_ROLE1`,
          label: `${bn.battalion_name || `Battalion ${i + 1}`} - Role 1 Aid Station`,
          type: 'ROLE1',
          parent_mtf_type: 'ROLE1',
          position_feba_km: 5
        });
      }
    } else if (echelon === 'DIVISION') {
      const division = force.division || {};
      const brigades = division.brigades || [];
      brigades.forEach((brigade, brgIdx) => {
        const battalionCount = brigade.battalions?.length || brigade.battalion_count || 4;
        for (let bnIdx = 0; bnIdx < battalionCount; bnIdx++) {
          const bn = brigade.battalions?.[bnIdx] || {};
          options.push({
            value: `BRG${brgIdx + 1}_BN${bnIdx + 1}_ROLE1`,
            label: `${brigade.brigade_name || `Bde ${brgIdx + 1}`} / ${bn.battalion_name || `Bn ${bnIdx + 1}`} - Role 1`,
            type: 'ROLE1',
            parent_mtf_type: 'ROLE1',
            position_feba_km: 5
          });
        }
      });
    } else if (echelon === 'CORPS') {
      const corps = force.corps || {};
      const divisions = corps.divisions || [];
      divisions.forEach((division, divIdx) => {
        const brigades = division.brigades || [];
        brigades.forEach((brigade, brgIdx) => {
          const battalionCount = brigade.battalions?.length || brigade.battalion_count || 4;
          for (let bnIdx = 0; bnIdx < battalionCount; bnIdx++) {
            options.push({
              value: `DIV${divIdx + 1}_BRG${brgIdx + 1}_BN${bnIdx + 1}_ROLE1`,
              label: `${division.division_name || `Div ${divIdx + 1}`} / ${brigade.brigade_name || `Bde ${brgIdx + 1}`} / Bn ${bnIdx + 1} - R1`,
              type: 'ROLE1',
              parent_mtf_type: 'ROLE1',
              position_feba_km: 5
            });
          }
        });
      });
    }
  }
  
  // ========================================================================
  // PHASE 4 (SF-002): BRIGADE LEVEL OPTIONS (Near Role 2 Basic)
  // CSU/PHA can be assigned near Role 2B for helicopter staging
  // ========================================================================
  
  const role2bFacilities = forceBuilderFacilities.filter(f => f.type === 'ROLE2_BASIC');
  if (role2bFacilities.length > 0) {
    // Use ForceBuilder facilities when available
    options.push({
      value: 'HEADER_BRIGADE',
      label: 'Brigade Level (Near Role 2 Basic)',
      type: 'HEADER',
      disabled: true
    });
    
    role2bFacilities.forEach((r2b, idx) => {
      options.push({
        value: `ROLE2B_${r2b.id}`,
        label: `Near ${r2b.name || `Role 2B #${idx + 1}`}`,
        type: 'ROLE2_BASIC',
        parent_mtf_id: r2b.id,
        parent_mtf_type: 'ROLE2_BASIC',
        position_feba_km: r2b.position_feba_km || getFEBADefault('ROLE2B'),
        hierarchy: r2b.hierarchy
      });
    });
  } else if (echelon === 'BRIGADE' || echelon === 'DIVISION' || echelon === 'CORPS') {
    // SF-002 FIX: Fallback when no ForceBuilder data
    options.push({
      value: 'HEADER_BRIGADE',
      label: 'Brigade Level (Near Role 2 Basic)',
      type: 'HEADER',
      disabled: true
    });
    
    if (echelon === 'BRIGADE') {
      const brigade = force.brigade || {};
      options.push({
        value: 'BRIGADE_ROLE2B',
        label: `${brigade.brigade_name || 'Brigade'} - Role 2 Basic (Medical Company)`,
        type: 'ROLE2_BASIC',
        parent_mtf_type: 'ROLE2_BASIC',
        position_feba_km: 20
      });
    } else if (echelon === 'DIVISION') {
      const division = force.division || {};
      const brigades = division.brigades || [];
      brigades.forEach((brigade, idx) => {
        options.push({
          value: `BRIGADE_${idx + 1}_ROLE2B`,
          label: `${brigade.brigade_name || `Brigade ${idx + 1}`} - Role 2 Basic`,
          type: 'ROLE2_BASIC',
          parent_mtf_type: 'ROLE2_BASIC',
          position_feba_km: 20
        });
      });
    } else if (echelon === 'CORPS') {
      const corps = force.corps || {};
      const divisions = corps.divisions || [];
      divisions.forEach((division, divIdx) => {
        const brigades = division.brigades || [];
        brigades.forEach((brigade, brgIdx) => {
          options.push({
            value: `DIV${divIdx + 1}_BRG${brgIdx + 1}_ROLE2B`,
            label: `${division.division_name || `Div ${divIdx + 1}`} / ${brigade.brigade_name || `Bde ${brgIdx + 1}`} - R2B`,
            type: 'ROLE2_BASIC',
            parent_mtf_type: 'ROLE2_BASIC',
            position_feba_km: 20
          });
        });
      });
    }
  }
  
  // ========================================================================
  // PHASE 4: DIVISION LEVEL OPTIONS (Near Role 2 Enhanced)
  // ========================================================================
  
  const role2eFacilities = forceBuilderFacilities.filter(f => f.type === 'ROLE2_ENHANCED');
  if (role2eFacilities.length > 0) {
    options.push({
      value: 'HEADER_DIVISION',
      label: 'Division Level',
      type: 'HEADER',
      disabled: true
    });
    
    role2eFacilities.forEach((r2e, idx) => {
      options.push({
        value: `ROLE2E_${r2e.id}`,
        label: `Near ${r2e.name || `Role 2E #${idx + 1}`}`,
        type: 'ROLE2_ENHANCED',
        parent_mtf_id: r2e.id,
        parent_mtf_type: 'ROLE2_ENHANCED',
        position_feba_km: r2e.position_feba_km || getFEBADefault('ROLE2E'),
        hierarchy: r2e.hierarchy
      });
    });
  } else if (echelon === 'BRIGADE') {
    // Legacy: Brigade without ForceBuilder data
    const brigade = force.brigade || {};
    options.push({
      value: 'HEADER_DIVISION',
      label: 'Division Level',
      type: 'HEADER',
      disabled: true
    });
    options.push({
      value: 'BRIGADE_ROLE2E',
      label: `${brigade.brigade_name || 'Brigade'} - Role 2 Enhanced`,
      type: 'ROLE2_ENHANCED',
      position: brigade.position || 1
    });
  } else if (echelon === 'DIVISION') {
    const division = force.division || {};
    const brigades = division.brigades || [];
    
    options.push({
      value: 'HEADER_DIVISION',
      label: 'Division Level',
      type: 'HEADER',
      disabled: true
    });
    
    brigades.forEach((brigade, idx) => {
      options.push({
        value: `BRIGADE_${idx + 1}_ROLE2E`,
        label: `${brigade.brigade_name || `Brigade ${idx+1}`} - Role 2 Enhanced`,
        type: 'ROLE2_ENHANCED',
        position: brigade.position || (idx + 1)
      });
    });
  } else if (echelon === 'CORPS') {
    const corps = force.corps || {};
    const divisions = corps.divisions || [];
    
    options.push({
      value: 'HEADER_DIVISION',
      label: 'Division Level',
      type: 'HEADER',
      disabled: true
    });
    
    divisions.forEach((division, divIdx) => {
      const brigades = division.brigades || [];
      brigades.forEach((brigade, brgIdx) => {
        options.push({
          value: `DIV${divIdx + 1}_BRG${brgIdx + 1}_ROLE2E`,
          label: `${division.division_name || `Div ${divIdx+1}`} - ${brigade.brigade_name || `Brg ${brgIdx+1}`} Role 2E`,
          type: 'ROLE2_ENHANCED',
          position: brigade.position || (brgIdx + 1)
        });
      });
    });
  }
  
  // ========================================================================
  // PHASE 4: CORPS LEVEL OPTIONS (Near Role 3)
  // ========================================================================
  
  const role3Facilities = forceBuilderFacilities.filter(f => f.type === 'ROLE3');
  if (role3Facilities.length > 0) {
    options.push({
      value: 'HEADER_CORPS',
      label: 'Corps Level',
      type: 'HEADER',
      disabled: true
    });
    
    role3Facilities.forEach((r3, idx) => {
      options.push({
        value: `ROLE3_${r3.id}`,
        label: `Near ${r3.name || `Role 3 CSH #${idx + 1}`}`,
        type: 'ROLE3',
        parent_mtf_id: r3.id,
        parent_mtf_type: 'ROLE3',
        position_feba_km: r3.position_feba_km || getFEBADefault('ROLE3'),
        hierarchy: r3.hierarchy
      });
    });
  } else if (echelon === 'DIVISION' || echelon === 'CORPS') {
    options.push({
      value: 'HEADER_CORPS',
      label: 'Corps Level',
      type: 'HEADER',
      disabled: true
    });
    options.push({
      value: 'CORPS_ROLE3_CSH',
      label: 'Corps Role 3 Combat Support Hospital',
      type: 'ROLE3',
      position: null
    });
  }
  
  // ========================================================================
  // PHASE 4 (SF-003): HLZ OPTIONS (for CSU/PHA near helicopter landing zones)
  // HLZ can be configured per facility level
  // ========================================================================
  
  const hlzFacilities = forceBuilderFacilities.filter(f => f.type === 'HLZ');
  if (hlzFacilities.length > 0) {
    options.push({
      value: 'HEADER_HLZ',
      label: 'Helicopter Landing Zones',
      type: 'HEADER',
      disabled: true
    });
    
    hlzFacilities.forEach((hlz, idx) => {
      options.push({
        value: `HLZ_${hlz.id}`,
        label: `Near ${hlz.name || `HLZ #${idx + 1}`}`,
        type: 'HLZ',
        parent_mtf_id: hlz.id,
        parent_mtf_type: 'HLZ'
      });
    });
  } else {
    // SF-003 FIX: Fallback - provide standard HLZ options at each level
    options.push({
      value: 'HEADER_HLZ',
      label: 'Helicopter Landing Zones',
      type: 'HEADER',
      disabled: true
    });
    
    // Standard HLZ locations based on echelon
    if (echelon === 'BATTALION') {
      options.push({
        value: 'HLZ_BATTALION_FWD',
        label: 'Battalion Forward HLZ',
        type: 'HLZ',
        parent_mtf_type: 'HLZ',
        position_feba_km: 3
      });
    } else if (echelon === 'BRIGADE') {
      options.push(
        {
          value: 'HLZ_BRIGADE_FWD',
          label: 'Brigade Forward HLZ (Near Role 1s)',
          type: 'HLZ',
          parent_mtf_type: 'HLZ',
          position_feba_km: 5
        },
        {
          value: 'HLZ_BRIGADE_MAIN',
          label: 'Brigade Main HLZ (Near Role 2B)',
          type: 'HLZ',
          parent_mtf_type: 'HLZ',
          position_feba_km: 20
        }
      );
    } else if (echelon === 'DIVISION') {
      const division = force.division || {};
      const brigades = division.brigades || [];
      brigades.forEach((brigade, idx) => {
        options.push({
          value: `HLZ_BDE${idx + 1}`,
          label: `${brigade.brigade_name || `Brigade ${idx + 1}`} HLZ`,
          type: 'HLZ',
          parent_mtf_type: 'HLZ',
          position_feba_km: 20
        });
      });
      options.push({
        value: 'HLZ_DIVISION_MAIN',
        label: 'Division Main HLZ (Near Role 2E)',
        type: 'HLZ',
        parent_mtf_type: 'HLZ',
        position_feba_km: 50
      });
    } else if (echelon === 'CORPS') {
      const corps = force.corps || {};
      const divisions = corps.divisions || [];
      divisions.forEach((division, divIdx) => {
        options.push({
          value: `HLZ_DIV${divIdx + 1}_MAIN`,
          label: `${division.division_name || `Division ${divIdx + 1}`} Main HLZ`,
          type: 'HLZ',
          parent_mtf_type: 'HLZ',
          position_feba_km: 50
        });
      });
      options.push({
        value: 'HLZ_CORPS_MAIN',
        label: 'Corps Main HLZ (Near Role 3)',
        type: 'HLZ',
        parent_mtf_type: 'HLZ',
        position_feba_km: 100
      });
    }
  }
  
  // ========================================================================
  // AIRFIELD AND STANDALONE OPTIONS (Always available)
  // ========================================================================
  
  options.push({
    value: 'HEADER_AIRFIELD',
    label: 'Airfields',
    type: 'HEADER',
    disabled: true
  });
  
  options.push(
    {
      value: 'AIRFIELD_MAIN',
      label: 'Main Airfield (Strategic Evacuation)',
      type: 'AIRFIELD',
      position: null
    },
    {
      value: 'AIRFIELD_FORWARD',
      label: 'Forward Airfield',
      type: 'AIRFIELD',
      position: null
    }
  );
  
  options.push({
    value: 'HEADER_OTHER',
    label: 'Other',
    type: 'HEADER',
    disabled: true
  });
  
  options.push({
    value: 'STANDALONE',
    label: 'Standalone (Not attached to facility)',
    type: 'STANDALONE',
    position: null
  });
  
  return options;
};

// ============================================================================
// DOCTRINAL-005 (March 4, 2026): Filter allocation options by facility type.
// generateAllocationOptions() returns ALL echelon options. This function filters
// to only doctrinally valid placement options per facility type.
//
// Doctrine sources:
//   PHA (Patient Holding Area): Minimum Role 2B. Role 1 is non-holding (4h max,
//     6 throughput positions). A collection point at Role 1 = CCP, not a PHA.
//     PHA = forward holding at brigade level for casualties awaiting evacuation or RTD.
//     Source: AJP-4.10; ATP 4-02.2 forward staging.
//
//   CSU (Casualty Staging Unit): Role 3 or STRATEVAC Airfield ONLY.
//     CSU = STRATEVAC staging buffer at APOD. Patients are post-surgical, stable,
//     cleared for strategic airlift, awaiting scheduled flight (C-130/C-17/A400M).
//     Co-location options:
//       (a) Near Role 3 MTF — patients moved to airfield on flight day.
//       (b) At STRATEVAC Airfield — when R3→airfield distance is large or flight
//           tempo is high; CSU physically at the terminal reduces ground transport.
//     A CSU near Role 1 or Role 2B is doctrinally impossible.
//     Source: AJMedP-1 para 3.18.6 (CSU at designated air bases / APOD).
//
//   HLZ (Helicopter Landing Zone): Minimum Role 2B. No tactical helo ops at BAS.
//     Battalion-level HLZs = POI pickup sites, not MTF-associated staging.
//     Source: AJMedP-2; FM 4-02.2 Air MEDEVAC.
// ============================================================================
const ALLOWED_OPTION_TYPES = {
  PHA: ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3', 'HLZ', 'HEADER'],
  CSU: ['ROLE3', 'AIRFIELD', 'HEADER'],
  HLZ: ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3', 'HLZ', 'HEADER'],
};

// Headers to keep: only those that precede at least one allowed non-header entry.
const filterAllocationByType = (options, facilityType) => {
  const allowed = ALLOWED_OPTION_TYPES[facilityType];
  if (!allowed) return options; // Unknown type — show all

  // First pass: collect all non-header values that pass the filter
  const allowedValues = new Set(
    options
      .filter(o => o.type !== 'HEADER' && allowed.includes(o.type))
      .map(o => o.value)
  );

  // Second pass: keep option if (a) it's a non-header in allowed set,
  // or (b) it's a header that has at least one allowed non-header sibling
  // in the same section (i.e. the next non-header option passes).
  const filtered = [];
  for (let i = 0; i < options.length; i++) {
    const opt = options[i];
    if (opt.type !== 'HEADER') {
      if (allowedValues.has(opt.value)) filtered.push(opt);
    } else {
      // Keep header only if there's at least one non-header that follows before next header
      let hasValidChild = false;
      for (let j = i + 1; j < options.length; j++) {
        if (options[j].type === 'HEADER') break;
        if (allowedValues.has(options[j].value)) { hasValidChild = true; break; }
      }
      if (hasValidChild) filtered.push(opt);
    }
  }
  return filtered;
};


// Functions moved to services/MedicalService.js (v2.0)
// Imported above: extractMedicalFacilities, calculateEvacuationDistances,
//                  recalculateFacilityEvacuation
// ============================================================================


/**
 * PHASE 2: Migrate Phase 1 facility to Phase 2 format
 */
const migrateStagingFacilities = (oldFacilities) => {
  return oldFacilities.map(facility => {
    // Determine size based on capacity
    const capacity = facility.capacity_patients || 50;
    let size = 'MEDIUM';
    if (capacity <= 30) size = 'SMALL';
    else if (capacity >= 80) size = 'LARGE';
    
    const facilityType = facility.facility_type || 'CSU';
    const standards = facilityType === 'PHA' ?
      CSU_NATO_STANDARDS[size] :
      PHA_NATO_STANDARDS[size];
    
    return {
      ...facility,
      facility_size: size,
      beds_stretcher: facility.beds_stretcher || Math.floor(capacity * 0.6),
      beds_ambulatory: facility.beds_ambulatory || Math.floor(capacity * 0.4),
      personnel: facility.personnel || standards.personnel,
      personnel_mode: 'AUTO',
      daily_supply_consumption: facility.daily_supply_consumption || 
        calculateSupplyConsumptionScaled(facilityType, size, capacity),
      allocated_to: facility.allocated_to || {
        unit_type: 'STANDALONE',
        unit_id: null,
        unit_name: 'Not allocated',
        position: null
      },
      increases_capacity_percent: facility.increases_capacity_percent || 20.0
    };
  });
};

/**
 * PHASE 2: Validate facility personnel
 */
const validateFacilityPersonnel = (personnel) => {
  const errors = [];
  const ranges = VALIDATION_RANGES.personnel;
  
  if (personnel.medical_officers < ranges.medical_officers.min) {
    errors.push(`Must have at least ${ranges.medical_officers.min} medical officer(s)`);
  }
  if (personnel.nurses < ranges.nurses.min) {
    errors.push(`Must have at least ${ranges.nurses.min} nurses`);
  }
  if (personnel.medics < ranges.medics.min) {
    errors.push(`Must have at least ${ranges.medics.min} medics`);
  }
  if (personnel.support_staff < ranges.support_staff.min) {
    errors.push(`Must have at least ${ranges.support_staff.min} support staff`);
  }
  
  return errors;
};

// ============================================================================
// AIRFIELD_CLASS_SPECS — Phase 6.0 module-level constant
// Airfield runway capability classification for STRATEVAC planning.
// TODO: Migrate to trainEvacuation.js in next constants session (APP_RULES 2.1).
// Source: ATP 4-25.13 Table 3-1; NATO STANAG 3224 airfield classification.
// ============================================================================
const AIRFIELD_CLASS_SPECS = {
  CLASS_A: {
    label: 'Class A — Heavy (C-17 / C-130 / A400M)',
    aircraft: ['C-17', 'C-130', 'A400M'],
    litter_capacity: 200,
    hourly_throughput: 50
  },
  CLASS_B: {
    label: 'Class B — Medium (C-130 / A400M)',
    aircraft: ['C-130', 'A400M'],
    litter_capacity: 120,
    hourly_throughput: 30
  },
  CLASS_C: {
    label: 'Class C — Light (C-27J / CN-235)',
    aircraft: ['C-27J', 'CN-235'],
    litter_capacity: 40,
    hourly_throughput: 10
  },
  CLASS_D: {
    label: 'Class D — Rotary wing only',
    aircraft: ['Helicopter only'],
    litter_capacity: 16,
    hourly_throughput: 4
  }
};

// ============================================================================
// ENV-003b: Tab2 → Tab3 environment mapping tables (module-level so both
// loadExistingConfiguration and the re-sync useEffect can share them).
// Source: operationsConfig.THREAT_LEVELS key styles + short-form variants.
// ============================================================================
const TAB2_TO_AD_THREAT = {
  // operationsConfig.THREAT_LEVELS key style (_THREAT suffix)
  LOW_THREAT: 'LOW', MINIMAL_THREAT: 'LOW',
  MODERATE_THREAT: 'MEDIUM',
  HIGH_THREAT: 'HIGH', EXTREME_THREAT: 'HIGH',
  FULL_COMBAT: 'HIGH', PEER_THREAT: 'HIGH',
  // Short-form variants (casualtyCalculator fallbacks)
  LOW: 'LOW', MINIMAL: 'LOW',
  MODERATE: 'MEDIUM',
  HIGH: 'HIGH', EXTREME: 'HIGH', PEER: 'HIGH', FULL: 'HIGH'
};
const TAB2_TO_WX = {
  CLEAR: 'VMC_CLEAR',
  LIGHT_RAIN: 'IMC_LIGHT_RAIN',
  HEAVY_RAIN: 'IMC_HEAVY_RAIN',
  SNOW: 'IMC_SNOW',
  FOG: 'IFR_FOG',
  STORM: 'GROUNDED_STORM'
};

// ============================================================================
// MAIN COMPONENT - STATE MANAGEMENT (ENHANCED FOR PHASE 2)
// ============================================================================

// BUG-200/201 FIX (March 25, 2026): Aggregate per-brigade fleets for MULTINATIONAL forces.
// Previous design called getDefaultGroundFleet('MULTINATIONAL') which returned a hardcoded
// minimum placeholder (SPRINTER=8, BOXER=4) regardless of actual brigade composition.
// For RO+FR+DE division the fleet was always SPRINTER+Boxer — French VAB/Griffon and
// Romanian TABC79/MLVM/Piranha were never shown; total count was 12 instead of ~58.
// Fix: when national standard is MULTINATIONAL, sum getDefaultGroundFleet() across each
// brigade's own nationalStandard. Vehicle codes that appear in multiple nations are summed
// (e.g. both NATO and GERMAN fleets use SPRINTER → counts add up correctly).
// Fallback to NATO defaults when brigade list is empty or all brigades are MULTINATIONAL.
// 2026-10-18 (Tab 3 review, Dr. Tatar): the shift length is no longer typed in; the operations tempo of each day is derived from the
// load and the planner sets the highest tempo allowed. true shows the former 'Shift Mode' list and 'Hours into shift' field,
// which the calculation does not use.
const SHOW_FORMER_SHIFT_INPUTS = false;

const buildAggregatedMNDFleet = (brigades) => {
  if (!brigades?.length) return getDefaultGroundFleet('NATO').vehicles;
  const aggregated = {};
  brigades.forEach(bde => {
    const bdeStd = bde.nationalStandard;
    if (!bdeStd || bdeStd === 'MULTINATIONAL') return;
    const bdeFleet = getDefaultGroundFleet(bdeStd);
    Object.entries(bdeFleet.vehicles).forEach(([code, counts]) => {
      if (!aggregated[code]) aggregated[code] = { count: 0, available: 0 };
      aggregated[code].count     += counts.count;
      aggregated[code].available += counts.available;
    });
  });
  return Object.keys(aggregated).length > 0
    ? aggregated
    : getDefaultGroundFleet('NATO').vehicles;
};

const Tab3_Medical = forwardRef(function Tab3_Medical({ scenario, mode, onUpdate, onComplete, onNavigate }, ref) {
  const { t } = useTranslation();

  // ============================================================================
  // TOOLBAR NAVIGATION ARCHITECTURE
  // ============================================================================

  // Stable ref for onUpdate - used by handleSaveAndComplete and flushState
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  // Ref to buildUpdates - populated after all state is available
  const buildUpdatesRef = useRef(null);

  // Stable function refs to avoid react-hooks/exhaustive-deps violations
  const loadConfigRef = useRef(null);
  const initDefaultsRef = useRef(null);
  const validateConfigRef = useRef(null);

  // ENV-003b: Tracks the Tab2 threat+weather values that Tab3 last synced from.
  // Initialized to null sentinel — updated by loadExistingConfiguration / initializeDefaults.
  // Re-sync alert fires when current Tab2 values diverge from this baseline.
  const lastKnownTab2Ref = useRef({ threat: null, weather: null });
  // ENH-027 / BUG-170: tracks the natStd that last populated the fleet.
  // useEffect guard compares against this — skip only if natStd truly hasn't changed.
  const prevFleetNatStdRef = useRef(null);

  // ENV-003b: Alert state — null = not showing; {suggestedAD, suggestedWX} = showing.
  const [tab2ResyncAlert, setTab2ResyncAlert] = useState(null);

  // Stable snapshot of crossDivisionPairs for the cross-div useEffect
  // (adding crossDivisionPairs to that effect's deps would cause infinite loop)
  const crossDivisionPairsRef = useRef([]);

  // Exposed to App.jsx via tab3Ref for toolbar navigation.
  // Tab3 is Pattern B (buffer locally, flush on Save & Continue).
  // flushState saves current medical_configuration WITHOUT running the calculator
  // (calculator is async - not suitable for toolbar nav) and WITHOUT validation gate.
  // Calculator results will be produced when user properly completes the tab.
  useImperativeHandle(ref, () => ({
    flushState: () => {
      if (buildUpdatesRef.current && onUpdateRef.current) {
        onUpdateRef.current(buildUpdatesRef.current());
        // BUG-066: markDirty required — toolbar nav bypasses Save & Continue handler.
        markDirty();
      }
    }
  }));

  // ========================================================================
  // STATE MANAGEMENT (PHASE 1)
  // ========================================================================
  
  // MTF Distances
  const [mtfDistances, setMtfDistances] = useState({
    role1: 8,
    role2: 20,
    role3: 100,
    role4: 500
  });
  
  // Evacuation Assets
  const [evacuationAssets, setEvacuationAssets] = useState({
    mode: mode === 'STANDARD' ? 'AUTO' : 'CUSTOM',
    ground: {
      light: 0,
      defaultLight: 0,
      armored: 0,
      defaultArmored: 0
    },
    air: {
      iar330: 0,
      defaultIar330: 0,
      uh60: 0,
      defaultUh60: 0,
      ch47: 0,
      defaultCh47: 0
    }
  });
  
  // MTF Structure
  const [mtfStructure, setMtfStructure] = useState({
    role1: { count: 0, beds: 0, surgical: false, holdTime: 0, personnel: 0 },
    role2: { count: 0, beds: 0, surgicalTeams: 0, surgical: false, holdTime: 0 },
    role3: { count: 0, beds: 0, surgicalTeams: 0, icuBeds: 0, holdTime: 0 }
  });
  
  // Advanced Staging (Expert Mode)
  const [useAdvancedStaging, setUseAdvancedStaging] = useState(false);
  const [stagingFacilities, setStagingFacilities] = useState([]);
  const [helicopterFacilities, setHelicopterFacilities] = useState([]);
  
  // INC-010: MSK surge threshold — configurable in Tab 3 Expert Mode (default 12 per NATO DES)
  const [mskSurgeThreshold, setMskSurgeThreshold] = useState(12);
  // 2026-10-18: surgical regulating (routing of the cases a Role 2 Basic cannot take); the defaults are shown and used
  const [surgicalRegulating, setSurgicalRegulating] = useState({ ...REGULATING_DEFAULTS });
  // Psychiatric support planning assumption ('' = not chosen: the reference level applies and is listed in the assumptions)
  const [psychiatricSupport, setPsychiatricSupport] = useState('');
  // INC-010: MSK info panel collapse state (component-level to avoid hooks-in-callback violation)
  const [showMskInfo, setShowMskInfo] = useState(false);
  // INC-010: Forward FH info panel — tracks which facility's info panel is open (null = none)
  const [fhInfoOpenId, setFhInfoOpenId] = useState(null);

  // Asset Degradation & Sharing (Expert Mode)
  const [enableAssetDegradation, setEnableAssetDegradation] = useState(false);
  const [expectedDailyLossRate, setExpectedDailyLossRate] = useState(2);
  const [enableCrossDivisionSharing, setEnableCrossDivisionSharing] = useState(false);
  
  // Phase 2: Cross-Division Support Configuration (Expert Mode)
  const [crossDivisionMascalOnly, setCrossDivisionMascalOnly] = useState(true);
  const [crossDivisionRespectCaveats, setCrossDivisionRespectCaveats] = useState(true);
  const [crossDivisionMaxCapacity, setCrossDivisionMaxCapacity] = useState(30); // Percentage
  const [crossDivisionPairs, setCrossDivisionPairs] = useState([]); // [{from_id, to_id, enabled, ...}]
  
  // Division Asset Allocations (Expert Mode, Corps only)
  const [divisionAllocations, setDivisionAllocations] = useState([]);
  
  // Validation
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [isValid, setIsValid] = useState(false);
  
  // v4.1.0: Calculator integration state
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState(null);
  // v4.3.10: BUG FIX — replace alert() (only shows Close, not OK/Cancel) with real MUI dialog
  const [showCalcErrorDialog, setShowCalcErrorDialog] = useState(false);
  const [pendingErrorSave, setPendingErrorSave] = useState(null); // () => void

  // ENH-002: Attach/Detach Evacuation Assets dialogs
  const [attachDialog, setAttachDialog] = useState({ open: false, facilityId: null });
  const [detachDialog, setDetachDialog] = useState({ open: false, facilityId: null });
  const [attachForm, setAttachForm] = useState({
    asset_type: 'HELICOPTER',
    model: 'IAR330',
    quantity: 1,
    attach_day: 1,
    detach_day: '',
    source_unit: '',
    mou_reference: ''
  });
  const [detachForm, setDetachForm] = useState({
    asset_type: 'HELICOPTER',
    quantity: 1,
    detach_day: 1,
    return_day: '',
    destination_unit: ''
  });

  // UI State - Reserved for future advanced sections
  // const [showAdvancedSection, setShowAdvancedSection] = useState(false);
  // const [showDivisionAllocation, setShowDivisionAllocation] = useState(false);
  
  // ========================================================================
  // PHASE 2: NEW STATE VARIABLES
  // ========================================================================
  
  // Facility size selection
  const [newFacilitySize, setNewFacilitySize] = useState('MEDIUM');
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const [pendingFacilityType, setPendingFacilityType] = useState('PHA');
  
  // Unit allocation options (from Tab 1 force structure)
  const [allocationOptions, setAllocationOptions] = useState([]);
  
  // Personnel customization tracking
  const [personnelEditMode, setPersonnelEditMode] = useState({});
  
  // ========================================================================
  // PHASE 2.5: FORCEBUILDER INTEGRATION STATE
  // ========================================================================
  
  // NEW: Detect if Tab 1 used ForceBuilder
  const [hasForceBuilderData, setHasForceBuilderData] = useState(false);
  const [forceBuilderFacilities, setForceBuilderFacilities] = useState([]);

// ========================================================================
// PHASE 2.5: EVACUATION ASSET STATE
// ========================================================================

// ========================================================================
// SECTION 3C: BLOOD MANAGEMENT STATE (Formerly 3B, reorganized in Phase 3)
// ========================================================================

// R1-07 (2026-10-25, Dr. Tatar): care at the point of injury is part of the medical force: the CLS training of the soldiers and the PFC training of the
// Role 1 medics (constants/pointOfInjuryCare.js). Saved in medical_configuration.point_of_injury_care.
const [poiCare, setPoiCare] = useState({ clsLevel: POI_CARE_DEFAULTS.cls_level, pfcTraining: POI_CARE_DEFAULTS.pfc_training });

const [bloodConfig, setBloodConfig] = useState({
  enabled: true,
  currentStock: {
    prbc_units: 100,
    ffp_units: 80,
    fdp_units: 0,       // Freeze-Dried Plasma — no cold chain, forward area asset
    plt_units: 20,
    cryo_pools: 10,
    whole_blood_units: 0  // Legacy field — WBB yield shown in WBB section
  },
  wbbEnabled: false,
  wbbDonors: 0,
  wbbLowTiterDonors: 0,        // PHASE 1.0: Low-titer O+ universal donors
  wbbActivationThreshold: 30,   // PHASE 1.0: Activation threshold (% of stock)
  alertLevel: 'GREEN',
  // Phase 6.0: MTP (Massive Transfusion Protocol)
  mtpEnabled: false,
  mtpProtocol: '1:1:1',         // '1:1:1' (equal ratios) or '2:1:1' (PRBC-heavy)
  // Phase 6.0: Shelf life tracking
  shelfLifeTracking: false       // Enables PLT 5-day expiry warning display
});

// ========================================================================
// SECTION 3C: SURGICAL CAPACITY STATE (REDESIGNED - All teams visible)
// ========================================================================

const [surgicalConfig, setSurgicalConfig] = useState({
  enabled: true,
  // Track ALL team types simultaneously (not dropdown selection)
  teams: {
    FST: { 
      count: 2, 
      available: 2,
      assigned: []  // Array of facility IDs where teams are assigned
    },
    FRT: { 
      count: 0, 
      available: 0,
      assigned: [] 
    },
    SURG_CO: { 
      count: 1, 
      available: 1,
      assigned: [] 
    }
  },
  // Legacy fields for backward compatibility
  teamType: 'FST',
  surgicalTeams: 2,
  shiftMode: 'NORMAL',  // NORMAL, EXTENDED, SURGE, CRISIS
  maxTempo: DEFAULT_MAX_TEMPO,  // 2026-10-18: highest operations tempo the planner allows (NORMAL, SURGE, CRISIS); the tempo of each day follows the load
  hoursWorked: 0,
  fatigueLevel: 0,
  dailyCapacity: 0
});

// ========================================================================
// SECTION 3E: STRATEGIC EVACUATION STATE (NEW)
// ========================================================================

const [strategicEvac, setStrategicEvac] = useState({
  // Train evacuation
  trainEnabled: false,
  stationType: TRAIN_SCHEDULE_DEFAULTS.station_type,
  trainType: TRAIN_SCHEDULE_DEFAULTS.train_type,
  role3ToStation: DEFAULT_STRATEGIC_DISTANCES?.ROLE3?.to_train_station_km?.default,
  stationToRole4: DEFAULT_STRATEGIC_DISTANCES?.TRAIN_TO_ROLE4?.distance_km?.default,
  // ENH-004: Train schedule fields
  trainFrequencyMode: TRAIN_SCHEDULE_DEFAULTS.frequency_mode,
  trainFirstDepartureDay: TRAIN_SCHEDULE_DEFAULTS.first_departure_day,
  trainDepartureHour: TRAIN_SCHEDULE_DEFAULTS.departure_hour,
  trainsPerDeparture: TRAIN_SCHEDULE_DEFAULTS.trains_per_departure,
  trainMinPatients: TRAIN_SCHEDULE_DEFAULTS.min_patients_for_departure,
  
  // Air evacuation
  airEnabled: true,
  
  // ENH-003: Airfields array — primary + optional alternate.
  // airfields[0]: primary, always active.
  // airfields[1]: alternate, is_active=false until planner enables in 3E.2.
  // schedule: null until planner configures in 3E.2.
  // coordinates: null until Tab 6 map. distance_source: 'MANUAL' always for Tab 3.
  airfields: [
    {
      id: 'airfield_primary',
      label: 'Primary STRATEVAC Airfield',
      class: 'CLASS_B',
      type: 'MILITARY',
      distances: {
        // BUG-099 FIX: ?? 0 fallback on all four distances — if DEFAULT_STRATEGIC_DISTANCES
        // does not expose ROLE2_BASIC/ROLE2_ENHANCED keys (or the path resolves undefined),
        // fields initialise to undefined which renders as blank in the TextField.
        // 0 is the correct sentinel: the field shows 0 km, planner edits to actual distance.
        r2b_to_airfield_km: DEFAULT_STRATEGIC_DISTANCES?.ROLE2_BASIC?.to_airfield_km?.default ?? 0,
        r2e_to_airfield_km: DEFAULT_STRATEGIC_DISTANCES?.ROLE2_ENHANCED?.to_airfield_km?.default ?? 0,
        r3_to_airfield_km:  DEFAULT_STRATEGIC_DISTANCES?.ROLE3?.to_airfield_km?.default ?? 0,
        airfield_to_r4_km:  DEFAULT_STRATEGIC_DISTANCES?.AIRFIELD_TO_ROLE4?.distance_km?.default ?? 0
      },
      pha_beds: 50,
      farp_enabled: false,
      is_primary: true,
      is_active: true,
      schedule: null,          // null = not yet configured; calculator throws if patients need STRATEVAC
      schedule_b: null,        // BUG-079: optional secondary aircraft on same airfield
      coordinates: null,
      distance_source: 'MANUAL'
    },
    {
      id: 'airfield_alternate',
      label: 'Alternate STRATEVAC Airfield',
      class: 'CLASS_B',
      type: 'MILITARY',
      distances: {
        // BUG-104 FIX: null → 0 for alternate airfield distances.
        // null renders as blank via (null ?? '') = ''. 0 is the correct sentinel —
        // planner edits to actual distance when alternate airfield is activated.
        r2b_to_airfield_km: 0,
        r2e_to_airfield_km: 0,
        r3_to_airfield_km:  0,
        airfield_to_r4_km:  0
      },
      pha_beds: ALTERNATE_AIRFIELD_DEFAULTS.pha_beds,
      farp_enabled: false,
      is_primary: false,
      is_active: false,        // toggled ON by planner in 3E.2
      schedule: null,
      schedule_b: null,        // BUG-079: optional secondary aircraft
      coordinates: null,
      distance_source: 'MANUAL'
    }
  ],
  
  // Bus transport (mass casualty)
  busEnabled: false,
  busCount: 5,
  
  // Calculated times (derived)
  trainEvacTimeHours: 0,
  airEvacTimeHours: 0
});

// ========================================================================
// HICOM-001: R3 ACCESSIBILITY STATE
// Toggle: R3 accessible (default) / R3 NOT accessible.
// V3 = warning flags only. V4 = dual calculation path.
// ========================================================================

const [r3Accessible, setR3Accessible] = useState(
  () => scenario?.medical_configuration?.r3_accessible ?? true
);

// BUG-NEXT-015: R2E ACCESSIBILITY STATE (brigade echelon only)
// At brigade echelon, R2E is a division asset — brigade planner cannot configure
// it in ORBAT. Toggle tells the model "division confirmed R2E is available".
// When ON + no R2E in force structure → model applies ROLE_2_ENHANCED_DEFINITION
// doctrinal defaults (40 beds). Only shown when echelon === 'BRIGADE'.
const [r2eAccessible, setR2eAccessible] = useState(
  () => scenario?.medical_configuration?.r2e_accessible ?? true
);
// R2E-DIST: Distance from R2B to the division R2E (km). Brigade planner enters the
// value communicated by division G4/surgeon. Used in evacuation timing when
// r2eAccessible=true and no R2E in ORBAT (doctrinal defaults path).
// Default: 40 km (typical R2B→R2E FEBA distance per AJMedP-1 planning norms).
const [r2eDistance, setR2eDistance] = useState(
  () => scenario?.medical_configuration?.r2e_distance_km ?? 40
);

// 2026-10-25: planner overrides of the chain legs (km per leg); a leg without an override follows the placed facilities (chainDistances.js)
const [distanceOverrides, setDistanceOverrides] = useState(
  () => scenario?.medical_configuration?.distance_overrides || {}
);

// ========================================================================
// MASCAL CONFIGURATION STATE
// ========================================================================

const [mascalConfig, setMascalConfig] = useState({
  enabled: true,
  currentLevel: 'NORMAL',
  blueLightEnabled: true,
  // Blue Light Protocol automatic detection and tracking (v4.3.2)
  blueLightRecommended: false,      // Automatic detection triggered
  blueLightActive: false,           // User activated protocol
  blueLightTriggerReason: null,     // Why automatic detection triggered
  blueLightActivatedAt: null,       // Timestamp when activated
  blueLightDismissed: false,        // User dismissed recommendation
  blueLightDismissReason: null,     // Why user dismissed
  thresholds: {
    mtf_warning: 80,
    mtf_critical: 95,
    surgical_queue_warning: 4,
    surgical_queue_critical: 8
  }
});

// ========================================================================
// BLUE LIGHT PROTOCOL DISMISS DIALOG STATE (v4.3.2)
// ========================================================================

const [blueLightDismissDialog, setBlueLightDismissDialog] = useState({
  open: false,
  reason: ''
});

// ========================================================================
// PHASE 5.0: TACTICAL EVACUATION CONFIGURATION STATE (Section 3B.0)
// Ground ambulance fleet + MEDEVAC air pool + AD threat + weather condition.
// This is the primary source for medevacModule v5.5 and evacuationCapacityCalculator v1.4.
// Pool options are filtered by the brigade/division national standard — a German unit
// sees Heeresfliegertruppe options; a Polish unit sees Wojska Lądowe options, etc.
// ========================================================================

const [tacticalEvacConfig, setTacticalEvacConfig] = useState(() => {
  // BUG-200/201 FIX (March 25, 2026): MULTINATIONAL forces now aggregate per-brigade fleets.
  // Previous code resolved initNatStd → for any MULTINATIONAL scenario,
  // extractPrimaryNationalStandard() short-circuits returning 'MULTINATIONAL', yielding
  // the hardcoded placeholder (SPRINTER=8, BOXER=4) regardless of actual brigade composition.
  // Fix: detect MULTINATIONAL and call buildAggregatedMNDFleet() to sum per-brigade fleets.
  const _fbr = scenario?.force?.forceBuilderResult;
  const _brigades = _fbr?.brigades || scenario?.force?.brigades || [];
  const initNatStd = scenario?.force?.corpsLeadNation ||
                     (scenario?.force?.national_standard !== 'MULTINATIONAL'
                       ? scenario?.force?.national_standard : null) ||
                     (_fbr?.corps?.nationalStandard !== 'MULTINATIONAL'
                       ? _fbr?.corps?.nationalStandard : null) ||
                     scenario?.force?.national_standard ||
                     'NATO';
  // OBS-S96-006 FIX: always aggregate per-brigade — single-nation forces need
  // N_brigades × per-brigade fleet, not a single brigade's worth.
  const initFleetVehicles = buildAggregatedMNDFleet(_brigades);
  const defaultPool = getDefaultMEDEVACPool(initNatStd);
  // BUG-HELI-ROU-001 FIX: use .find(p => p.code === defaultPool) to get label/aircraft_type
  // for the actual default pool. [0] returned the first alphabetical option (often NATO_NH90),
  // not the national default — so Romanian force showed NH90 even though RO_FAR was defaultPool.
  const defaultPoolObj = getMEDEVACPoolOptions(initNatStd).find(p => p.code === defaultPool)
                      || getMEDEVACPoolOptions(initNatStd)[0];
  return {
    fleet: initFleetVehicles,
    // IMP-S107-001: array of pool entries — supports mixed-fleet NATO MEDEVAC corps
    air_pools: [{
      pool_code:             defaultPool,
      pool_label:            defaultPoolObj?.label || defaultPool,
      nominal_daily_sorties: 6,
      aircraft_type:         defaultPoolObj?.primary_aircraft || 'IAR330'
    }],
    ad_threat: 'MEDIUM',
    weather:   'VMC_CLEAR',
    effective_air_sorties: null,
    combined_modifier:     null
  };
});

// ========================================================================
// UI-009: CASEVAC CONFIGURATION STATE (NEW)
// When MEDEVAC capacity is exceeded, CASEVAC may be used with DOW penalty
// ========================================================================

const [casevacConfig, setCasevacConfig] = useState({
  enabled: true,
  availability: 'ON_REQUEST',  // LIMITED, ON_REQUEST, DEDICATED, UNAVAILABLE
  availabilityPercent: 80,     // Derived from availability setting
  dowPenalty: 1.35,            // Ground CASEVAC DOW multiplier
  notes: ''                    // User notes about CASEVAC arrangements
});

// ========================================================================
// PHASE 2.0: EVACUATION ROUTES STATE
// ========================================================================

const [evacuationRoutes, setEvacuationRoutes] = useState({
  r1_to_r2b: {
    primary: {
      name: 'MSR Alpha',
      distance_km: 12,
      surface: 'PAVED',
      status: 'OPEN',
      weather_impact: 'LOW'
    },
    alternate: {
      name: 'Route Bravo',
      distance_km: 18,
      surface: 'GRAVEL',
      status: 'OPEN',
      weather_impact: 'MEDIUM'
    }
  },
  r2b_to_r2e: {
    primary: {
      name: '',
      distance_km: 30,
      surface: 'PAVED',
      status: 'OPEN',
      weather_impact: 'LOW'
    },
    alternate: null
  },
  r2e_to_r3: {
    primary: {
      name: '',
      distance_km: 70,
      surface: 'PAVED',
      status: 'OPEN',
      weather_impact: 'LOW'
    },
    alternate: {
      name: '',
      distance_km: 95,
      surface: 'UNPAVED',
      status: 'OPEN',
      weather_impact: 'MEDIUM'
    }
  },
  r3_to_r4: {
    primary: {
      name: '',
      distance_km: 500,
      surface: 'PAVED',
      status: 'OPEN',
      weather_impact: 'LOW'
    },
    alternate: null
  }
});

// ========================================================================
// SECTION 3B/3C/MASCAL: DERIVED CALCULATIONS
// Uses imported functions to compute real-time values
// ========================================================================

// Phase 5.0 / IMP-S107-001: Compute effective air sorties from AD threat × weather modifier.
// Sums nominal sorties across all air_pools entries (mixed-fleet support).
useEffect(() => {
  const adEntry = AD_THREAT_MODIFIERS[tacticalEvacConfig.ad_threat?.toUpperCase()];
  const wxEntry = WEATHER_MODIFIERS[tacticalEvacConfig.weather?.toUpperCase()];
  const adMod   = adEntry?.sortie_modifier ?? 1.0;
  const wxMod   = wxEntry?.sortie_modifier ?? 1.0;
  const combined = Math.round(adMod * wxMod * 100) / 100;
  const nominal  = (tacticalEvacConfig.air_pools || []).reduce((s, p) => s + (p.nominal_daily_sorties || 0), 0);
  const effective = Math.round(nominal * combined * 10) / 10;
  setTacticalEvacConfig(prev => ({
    ...prev,
    effective_air_sorties: effective,
    combined_modifier: combined
  }));
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [tacticalEvacConfig.ad_threat, tacticalEvacConfig.weather, tacticalEvacConfig.air_pools]);

// ENH-027 / BUG-170: Reset fleet and air pool when force national standard changes.
// Previous guard (pool validity check) was broken: getMEDEVACPoolOptions() always appends
// NATO_GENERIC, so currentPoolValid was always true → fleet never reset on natStd change.
// Fix: compare currentNatStd to prevFleetNatStdRef. Skip only if natStd truly hasn't changed.
// Load handler (loadMedicalConfig → setTacticalEvacConfig) writes state directly and always
// wins over this effect — saved user config is never overwritten.
useEffect(() => {
  const currentNatStd = extractPrimaryNationalStandard();
  // BUG-200/201 FIX (April 3, 2026): Guard was checking natStd only.
  //   For MULTINATIONAL corps, natStd = 'MULTINATIONAL' at mount time (before Tab1 populates brigades).
  //   Guard fired → effect skipped → buildAggregatedMNDFleet([]) used NATO minimum (12 vehicles).
  //   When Tab1 brigades load later, natStd string is still 'MULTINATIONAL' → guard skips again.
  //   Fix: track combined key of natStd + brigade count. Brigade load (0→9) changes the key → effect runs.
  const _brigades = scenario?.force?.forceBuilderResult?.brigades
                 || scenario?.force?.brigades || [];
  const currentKey = `${currentNatStd}:${_brigades.length}`;
  if (prevFleetNatStdRef.current === currentKey) return;
  prevFleetNatStdRef.current = currentKey;
  // OBS-S96-006 FIX: always aggregate per-brigade
  const fleetVehicles = buildAggregatedMNDFleet(_brigades);
  const poolOptions  = getMEDEVACPoolOptions(currentNatStd);
  const defaultPool  = getDefaultMEDEVACPool(currentNatStd);
  // BUG-HELI-ROU-001 FIX (second call site): match defaultPool code, not [0] position
  const defaultPoolObj = poolOptions.find(p => p.code === defaultPool) || poolOptions[0];
  // IMP-S107-001: Reset to single default pool entry; preserve user-set sorties from first entry
  setTacticalEvacConfig(prev => ({
    ...prev,
    fleet: fleetVehicles,
    air_pools: [{
      pool_code:             defaultPool,
      pool_label:            defaultPoolObj?.label || defaultPool,
      aircraft_type:         defaultPoolObj?.primary_aircraft || 'IAR330',
      nominal_daily_sorties: prev.air_pools?.[0]?.nominal_daily_sorties ?? 6
    }]
  }));
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [scenario?.force?.national_standard,
    scenario?.force?.corpsLeadNation,
    scenario?.force?.forceBuilderResult?.corps?.nationalStandard,
    scenario?.force?.forceBuilderResult?.divisions?.length,
    scenario?.force?.forceBuilderResult?.brigades?.length]);

// Calculate blood requirements and alert level when stock or casualties change
// Calculate blood supply metrics when blood config changes
useEffect(() => {
  if (!bloodConfig.enabled) return;
  
  // Get expected casualties from scenario
  const expectedCasualties = scenario?.casualty_projections?.totals || { t1: 10, t2: 30, t3: 50, t4: 5 };
  
  // Calculate blood requirements using imported function
  const bloodReqs = calculateBloodRequirements ? calculateBloodRequirements(expectedCasualties) : null;
  
  // Calculate days of supply using imported function
  const dailyConsumption = bloodReqs?.daily_total || 20;
  const dos = calculateDaysOfSupply ? 
    calculateDaysOfSupply(bloodConfig.currentStock, dailyConsumption) : 
    { prbc: bloodConfig.currentStock.prbc_units / 20, ffp: bloodConfig.currentStock.ffp_units / 15 };
  
  // Get alert level using imported function
  // NOTE: getBloodAlertLevel returns the whole object, we need the level KEY
  const minDOS = Math.min(dos.prbc || 5, dos.ffp || 5);
  const alertLevelObj = getBloodAlertLevel ? getBloodAlertLevel(minDOS) : null;
  const alertLevel = alertLevelObj?.level || 
    (minDOS >= 5 ? 'GREEN' : minDOS >= 3 ? 'YELLOW' : minDOS >= 1 ? 'RED' : 'BLACK');
  
  // Update state if alert level changed
  if (alertLevel !== bloodConfig.alertLevel) {
    setBloodConfig(prev => ({ ...prev, alertLevel }));
  }
}, [bloodConfig.currentStock, bloodConfig.enabled, bloodConfig.alertLevel, scenario?.casualty_projections]);

// 2026-10-18 (Dr. Tatar: Tab 4 owns the blood supply): the PRBC, FFP and platelet stock shown here is the initial stock of Tab 4
// (Logistics), read-only. The resupply and the days of supply are Tab 4 too.
const tab4InitialStock = scenario?.logistics?.medical_supplies?.initial_stock || null;
useEffect(() => {
  if (!tab4InitialStock) return;
  setBloodConfig(prev => {
    const next = {
      ...prev.currentStock,
      prbc_units: typeof tab4InitialStock.prbc_units === 'number' ? tab4InitialStock.prbc_units : prev.currentStock.prbc_units,
      ffp_units: typeof tab4InitialStock.ffp_units === 'number' ? tab4InitialStock.ffp_units : prev.currentStock.ffp_units,
      plt_units: typeof tab4InitialStock.platelets_units === 'number' ? tab4InitialStock.platelets_units : prev.currentStock.plt_units
    };
    if (next.prbc_units === prev.currentStock.prbc_units && next.ffp_units === prev.currentStock.ffp_units && next.plt_units === prev.currentStock.plt_units) return prev;
    return { ...prev, currentStock: next };
  });
}, [tab4InitialStock?.prbc_units, tab4InitialStock?.ffp_units, tab4InitialStock?.platelets_units]); // eslint-disable-line react-hooks/exhaustive-deps

// Calculate surgical capacity when team config changes
useEffect(() => {
  if (!surgicalConfig.enabled) return;
  
  // Calculate surgical capacity using imported function
  const capacityConfig = {
    teams: surgicalConfig.surgicalTeams,
    shiftMode: surgicalConfig.shiftMode,
    mtfType: 'ROLE_2_BASIC'
  };
  
  const capacity = calculateSurgicalCapacity ? calculateSurgicalCapacity(capacityConfig) : null;
  // const mtfCapacity = getMTFSurgicalCapacity ? getMTFSurgicalCapacity('ROLE_2_BASIC') : null; // UNUSED
  
  // Calculate demand from expected casualties
  const expectedCasualties = scenario?.casualty_projections?.totals || { t1: 10, t2: 30, t3: 50 };
  const demand = calculateSurgicalDemand ? calculateSurgicalDemand(expectedCasualties) : null;
  
  // Log for debugging
  if (capacity || demand) {
  }
}, [surgicalConfig.surgicalTeams, surgicalConfig.shiftMode, surgicalConfig.enabled, scenario?.casualty_projections]);

// Evaluate MASCAL status when metrics change
useEffect(() => {
  if (!mascalConfig.enabled) return;

  // BUG-120 FIX (March 16, 2026): MASCAL recommendation was firing at Tab 3 config time
  // because the metrics object used hardcoded placeholder values (mtf_utilization: 75,
  // surgical_queue_hours: 2) which passed the threshold check before any simulation ran.
  // Fix: require actual casualty_projections to exist before evaluating. Configuration-time
  // evaluation with placeholder metrics is doctrinally incorrect — AMedP-1.10 MASCAL
  // detection requires actual casualty data, not planning estimates.
  // The calculator produces projections after Tab 3 Save & Continue → Tab 5.
  const hasCalculatedProjections = scenario?.casualty_projections?.success === true ||
    (scenario?.casualty_projections?.totals?.total_all > 0);

  if (!hasCalculatedProjections) return;

  // Build metrics object for evaluation
  const metrics = {
    mtf_utilization: 75,  // Would come from actual MTF data
    surgical_queue_hours: 2,
    golden_hour_achievement: 85,
    blood_dos: (bloodConfig.alertLevel === 'GREEN' || bloodConfig.alertLevel?.level === 'GREEN') ? 5 : (bloodConfig.alertLevel === 'YELLOW' || bloodConfig.alertLevel?.level === 'YELLOW') ? 4 : 2
  };
  
  // Evaluate MASCAL status using imported function
  const mascalStatus = evaluateMASCALStatus ? evaluateMASCALStatus(metrics) : null;
  const mascalLevel = getMASCALLevel ? getMASCALLevel(metrics.mtf_utilization / 100) : null;
  
  // v4.3.2: Blue Light Protocol Automatic Detection
  // Detect if conditions warrant Blue Light Protocol activation
  if (mascalConfig.blueLightEnabled && !mascalConfig.blueLightActive && !mascalConfig.blueLightDismissed) {
    // Build patient profile for detection (from scenario casualties if available)
    const t1Casualties = scenario?.casualty_projections?.by_triage?.t1 || 0;
    const goldenHourCompliance = metrics.golden_hour_achievement || 85;
    
    // Check if Blue Light Protocol should be recommended
    const testMetrics = {
      t1_casualties: t1Casualties,
      golden_hour_compliance: goldenHourCompliance,
      mtf_utilization: metrics.mtf_utilization,
      surgical_queue_hours: metrics.surgical_queue_hours
    };
    
    const shouldTrigger = shouldActivateBlueLightProtocol ? 
      shouldActivateBlueLightProtocol(testMetrics) : false;
    
    if (shouldTrigger && !mascalConfig.blueLightRecommended) {
      // Trigger recommendation
      let triggerReason = 'MASCAL Escalation Protocol activation recommended: ';
      const reasons = [];
      
      if (t1Casualties > 20) reasons.push(`High T1 casualties (${t1Casualties})`);
      if (goldenHourCompliance < 80) reasons.push(`Golden Hour compliance low (${goldenHourCompliance}%)`);
      if (metrics.mtf_utilization > 90) reasons.push(`MTF utilization critical (${metrics.mtf_utilization}%)`);
      if (metrics.surgical_queue_hours > 4) reasons.push(`Surgical queue backlog (${metrics.surgical_queue_hours}h)`);
      
      triggerReason += reasons.length > 0 ? reasons.join(', ') : 'Multiple threshold violations detected';
      
      setMascalConfig(prev => ({ 
        ...prev, 
        blueLightRecommended: true,
        blueLightTriggerReason: triggerReason
      }));
    }
  }
  
  // Log for debugging
  if (mascalStatus || mascalLevel) {
  }
}, [mascalConfig.enabled, mascalConfig.blueLightEnabled, mascalConfig.blueLightActive, mascalConfig.blueLightDismissed, mascalConfig.blueLightRecommended, bloodConfig.alertLevel, scenario?.casualty_projections]);
  
  // ========================================================================
  // INITIALIZATION (ENHANCED FOR PHASE 2)
  // ========================================================================
  
  useEffect(() => {
    if (scenario && scenario.force) {
      // PHASE 2: Generate allocation options from Tab 1 force structure
      // NOTE: Moved AFTER ForceBuilder extraction in Phase 4
      
      // PHASE 2.5: Detect ForceBuilder results
      let extractedFacilities = [];
      if (scenario.force.forceBuilderResult) {
        setHasForceBuilderData(true);
        
        // Extract medical facilities from ForceBuilder
        // 2026-10-13: facility offset from the parent HQ follows the Tab 2 evacuation paradigm (dispersion: further under threat)
        const rawFacilities = applyParadigmToFacilityPositions(
          extractMedicalFacilities(scenario.force),
          resolveEvacuationParadigm(scenario).paradigm
        );
        
        // PHASE 3: Calculate evacuation distances for all facilities
        const defaultTerrain = scenario.operations?.terrain_type || 'ROLLING_HILLS';
        extractedFacilities = calculateEvacuationDistances(rawFacilities, defaultTerrain);

        // ENH-002: Merge saved attached_assets / detached_assets back from
        // medical_configuration.per_facility_evacuation_assets into the freshly extracted
        // facilities. extractMedicalFacilities() re-builds from force structure and has no
        // knowledge of user-configured attachments — we must re-apply them here.
        const savedPerFacility = scenario?.medical_configuration?.per_facility_evacuation_assets;
        if (Array.isArray(savedPerFacility) && savedPerFacility.length > 0) {
          extractedFacilities = extractedFacilities.map(f => {
            const fId = f.id || f.facility_id;
            const saved = savedPerFacility.find(s => s.facility_id === fId);
            if (!saved) return f;
            return {
              ...f,
              attached_assets:    Array.isArray(saved.attached_assets)    ? saved.attached_assets    : (f.attached_assets    || []),
              detached_assets:    Array.isArray(saved.detached_assets)    ? saved.detached_assets    : (f.detached_assets    || []),
              phase_displacements: Array.isArray(saved.phase_displacements) ? saved.phase_displacements : (f.phase_displacements || []),
              // INC-010: restore forward FH expert override (undefined=AUTO, true/false=override)
              forward_fh_override: saved.forward_fh_override
            };
          });
        }

        setForceBuilderFacilities(extractedFacilities);
      } else {
        setHasForceBuilderData(false);
        setForceBuilderFacilities([]);
      }
      
      // PHASE 4: Generate allocation options AFTER extracting facilities
      // This enables Battalion/Brigade level options for CSU/PHA/HLZ
      const options = generateAllocationOptions(scenario.force, extractedFacilities);
      setAllocationOptions(options);
    }
    
    if (scenario?.medical_configuration?.configured) {
      // Load existing configuration
      loadConfigRef.current();
    } else {
      // Auto-populate from Tab 1 force data
      initDefaultsRef.current();
    }
  }, [scenario]);
  
  const loadExistingConfiguration = () => {
    const medConfig = scenario.medical_configuration;
    
    if (medConfig.mtf_distances) {
      setMtfDistances({
        role1: medConfig.mtf_distances.role1_distance_km || 8,
        role2: medConfig.mtf_distances.role2_distance_km || 20,
        role3: medConfig.mtf_distances.role3_distance_km || 100,
        role4: medConfig.mtf_distances.role4_distance_km || 500
      });
    }
    
    if (medConfig.evacuation_assets) {
      const ea = medConfig.evacuation_assets;
      setEvacuationAssets({
        mode: ea.mode || (mode === 'STANDARD' ? 'AUTO' : 'CUSTOM'),
        ground: {
          light: ea.ground?.light_ambulances?.count || 0,
          defaultLight: ea.ground?.light_ambulances?.default_count || 0,
          armored: ea.ground?.armored_ambulances?.count || 0,
          defaultArmored: ea.ground?.armored_ambulances?.default_count || 0
        },
        air: {
          iar330: ea.air?.iar330_puma?.count || 0,
          defaultIar330: ea.air?.iar330_puma?.default_count || 0,
          uh60: ea.air?.uh60_blackhawk?.count || 0,
          defaultUh60: ea.air?.uh60_blackhawk?.default_count || 0,
          ch47: ea.air?.ch47_chinook?.count || 0,
          defaultCh47: ea.air?.ch47_chinook?.default_count || 0
        }
      });
    }
    
    if (medConfig.mtf_structure) {
      setMtfStructure(medConfig.mtf_structure);
    }
    
    setUseAdvancedStaging(medConfig.use_advanced_staging || false);
    
    // PHASE 2: Migrate Phase 1 facilities if needed
    const facilities = medConfig.staging_facilities || [];
    // DOCTRINAL-RENAME (session 105): Swap old CSU↔PHA labels to match AJMedP-1 para 3.18.6.
    // Old 'CSU' (Role 2B forward holding) → new 'PHA' (Patient Holding Area)
    // Old 'PHA' (STRATEVAC staging at APOD) → new 'CSU' (Casualty Staging Unit)
    const doctrineMigrated = facilities.map(f => {
      if (f.facility_type === 'CSU') return { ...f, facility_type: 'PHA' };
      if (f.facility_type === 'PHA') return { ...f, facility_type: 'CSU' };
      return f;
    });
    if (doctrineMigrated.length > 0 && !doctrineMigrated[0].facility_size) {
      const migrated = migrateStagingFacilities(doctrineMigrated);
      setStagingFacilities(migrated);
    } else {
      setStagingFacilities(doctrineMigrated);
    }
    
    setHelicopterFacilities(medConfig.helicopter_facilities || []);
    setEnableAssetDegradation(medConfig.enable_asset_degradation || false);
    setExpectedDailyLossRate((medConfig.expected_daily_loss_rate || 0.02) * 100);
    // INC-010: restore MSK surge threshold (default 12 if not saved)
    if (medConfig.msk_surge_threshold_per_day) setMskSurgeThreshold(medConfig.msk_surge_threshold_per_day);
    setPsychiatricSupport(medConfig.psychiatric_support || '');
    setEnableCrossDivisionSharing(medConfig.enable_cross_division_sharing || false);
    
    // Phase 2: Cross-Division Support Configuration
    const crossDivConfig = medConfig.cross_division_config || {};
    setCrossDivisionMascalOnly(crossDivConfig.mascal_only !== false); // Default true
    setCrossDivisionRespectCaveats(crossDivConfig.respect_national_caveats !== false); // Default true
    setCrossDivisionMaxCapacity(crossDivConfig.max_capacity_percent || 30);
    setCrossDivisionPairs(crossDivConfig.division_pairs || []);
    
    setDivisionAllocations(medConfig.division_asset_allocations || []);
    
    // ENV-001a / ENV-003b: Derive AD threat / weather defaults from Tab2 scenario data.
    // Mapping tables are at module level (TAB2_TO_AD_THREAT, TAB2_TO_WX) so the
    // ENV-003b re-sync useEffect can share them.
    const tab2RawThreat  = scenario?.environment?.threat_level
                        || scenario?.operation_parameters?.threat_level
                        || scenario?.operation_parameters?.environmental_factors?.threat_level;
    const tab2RawWeather = scenario?.environment?.weather
                        || scenario?.operation_parameters?.environmental_factors?.weather;
    const tab2AdDefault  = TAB2_TO_AD_THREAT[(tab2RawThreat || '').toUpperCase()] || 'MEDIUM';
    const tab2WxDefault  = TAB2_TO_WX[(tab2RawWeather || '').toUpperCase()]       || 'VMC_CLEAR';

    // Phase 5.0: Load Tactical Evacuation Configuration (Section 3B.0)
    if (medConfig.tactical_evac_config) {
      const tec = medConfig.tactical_evac_config;
      setTacticalEvacConfig(prev => ({
        ...prev,
        fleet:     tec.fleet    || prev.fleet,
        // IMP-S107-001: Migrate old single air_pool → array; prefer new air_pools field.
        air_pools: tec.air_pools
                    ? tec.air_pools
                    : tec.air_pool
                      ? [tec.air_pool]
                      : prev.air_pools,
        // ENV-001a: fall back to Tab2-derived default, not the hardcoded 'MEDIUM' prev state
        ad_threat: tec.ad_threat || tab2AdDefault,
        weather:   tec.weather   || tab2WxDefault
        // effective_air_sorties and combined_modifier recomputed by useEffect
      }));
    } else {
      // ENV-001a: No saved tactical_evac_config at all — seed from Tab2 defaults
      setTacticalEvacConfig(prev => ({
        ...prev,
        ad_threat: tab2AdDefault,
        weather:   tab2WxDefault
      }));
    }
    // ENV-003b: Record the Tab2 baseline used for this load so the re-sync
    // useEffect can detect future Tab2 changes vs this known-good sync point.
    lastKnownTab2Ref.current = { threat: tab2RawThreat, weather: tab2RawWeather };

    // UI-009: Load CASEVAC configuration
    if (medConfig.casevac_config) {
      const cc = medConfig.casevac_config;
      setCasevacConfig({
        enabled: cc.enabled !== false,
        availability: cc.availability || 'ON_REQUEST',
        availabilityPercent: cc.availability_percent || 80,
        dowPenalty: cc.dow_penalty || 1.35,
        notes: cc.notes || ''
      });
    }
    
    // PHASE 2.0: Load Evacuation Routes configuration
    if (medConfig.evacuation_routes) {
      const routes = medConfig.evacuation_routes;
      setEvacuationRoutes({
        r1_to_r2b: routes.r1_to_r2b || evacuationRoutes.r1_to_r2b,
        r2b_to_r2e: routes.r2b_to_r2e || evacuationRoutes.r2b_to_r2e,
        r2e_to_r3: routes.r2e_to_r3 || evacuationRoutes.r2e_to_r3,
        r3_to_r4: routes.r3_to_r4 || evacuationRoutes.r3_to_r4
      });
    } else {
      // LOC-001: No saved evacuation_routes — seed from Tab2 loc_config.
      // MSR/ASR physical condition → surface + base status.
      // LOC security → further constrains status (tactical situation overlays physical).
      const locCfg = scenario?.operation_parameters?.loc_config;
      if (locCfg) {
        // Map ROAD_STATUS key → { surface, status } for Tab3 route model.
        // ROAD_STATUS keys (locConfig.js): PAVED, GRAVEL, DIRT, DAMAGED, DESTROYED, FORD, UNPAVED
        const MSR_TO_ROUTE = {
          PAVED:     { surface: 'PAVED',   status: 'OPEN'     },
          GRAVEL:    { surface: 'GRAVEL',  status: 'OPEN'     },
          DIRT:      { surface: 'UNPAVED', status: 'OPEN'     },
          UNPAVED:   { surface: 'UNPAVED', status: 'OPEN'     },
          FORD:      { surface: 'UNPAVED', status: 'OPEN'     },
          DAMAGED:   { surface: 'UNPAVED', status: 'DAMAGED'  },
          DESTROYED: { surface: 'UNPAVED', status: 'CLOSED'   }
        };
        // Map LOC_SECURITY key → Tab3 ROUTE_STATUS key (tactical overlay).
        // SECURE/GUARDED: no restriction. CONTESTED: contested. INTERDICTED/DENIED: closed.
        const LOC_SEC_TO_STATUS = {
          SECURE:      'OPEN',
          GUARDED:     'OPEN',
          CONTESTED:   'CONTESTED',
          INTERDICTED: 'CONTESTED',
          DENIED:      'CLOSED'
        };
        // Tier order for "take the worse of physical + security"
        const STATUS_TIER = { OPEN: 0, DAMAGED: 1, CONTESTED: 2, CLOSED: 3 };
        const worstStatus = (physicalStatus, securityStatus) => {
          const t1 = STATUS_TIER[physicalStatus] ?? 0;
          const t2 = STATUS_TIER[securityStatus] ?? 0;
          return t1 >= t2 ? physicalStatus : securityStatus;
        };

        const msrKey = (locCfg.msr_status || 'PAVED').toUpperCase();
        const asrKey = (locCfg.asr_status || 'GRAVEL').toUpperCase();
        const secKey = (locCfg.loc_security || 'SECURE').toUpperCase();

        const msrRoute  = MSR_TO_ROUTE[msrKey] || { surface: 'PAVED',   status: 'OPEN' };
        const asrRoute  = MSR_TO_ROUTE[asrKey] || { surface: 'GRAVEL',  status: 'OPEN' };
        const secStatus = LOC_SEC_TO_STATUS[secKey] || 'OPEN';

        const primaryStatus  = worstStatus(msrRoute.status,  secStatus);
        const alternateStatus = worstStatus(asrRoute.status, secStatus);

        // Weather impact: derive from loc_config.weather if present, else LOW
        const locWeather = (locCfg.weather || '').toUpperCase();
        const weatherImpact = (locWeather === 'HEAVY_RAIN' || locWeather === 'SNOW' || locWeather === 'STORM')
          ? 'HIGH'
          : (locWeather === 'LIGHT_RAIN' || locWeather === 'FOG')
            ? 'MEDIUM'
            : 'LOW';

        setEvacuationRoutes(prev => ({
          r1_to_r2b: {
            ...prev.r1_to_r2b,
            primary: {
              ...prev.r1_to_r2b.primary,
              surface:        msrRoute.surface,
              status:         primaryStatus,
              weather_impact: weatherImpact
            },
            alternate: prev.r1_to_r2b.alternate ? {
              ...prev.r1_to_r2b.alternate,
              surface:        asrRoute.surface,
              status:         alternateStatus,
              weather_impact: weatherImpact
            } : prev.r1_to_r2b.alternate
          },
          r2b_to_r2e: {
            ...prev.r2b_to_r2e,
            primary: {
              ...prev.r2b_to_r2e.primary,
              surface:        msrRoute.surface,
              status:         primaryStatus,
              weather_impact: weatherImpact
            }
          },
          r2e_to_r3: {
            ...prev.r2e_to_r3,
            primary: {
              ...prev.r2e_to_r3.primary,
              surface:        msrRoute.surface,
              status:         primaryStatus,
              weather_impact: weatherImpact
            },
            alternate: prev.r2e_to_r3.alternate ? {
              ...prev.r2e_to_r3.alternate,
              surface:        asrRoute.surface,
              status:         alternateStatus,
              weather_impact: weatherImpact
            } : prev.r2e_to_r3.alternate
          },
          r3_to_r4: {
            ...prev.r3_to_r4,
            primary: {
              ...prev.r3_to_r4.primary,
              surface:        msrRoute.surface,
              status:         primaryStatus,
              weather_impact: weatherImpact
            }
          }
        }));
      }
    }
    
    // Load care at the point of injury (R1-07). Scenarios made before the card: CLS from the Tab 1 value, PFC training the default.
    if (medConfig.point_of_injury_care) {
      setPoiCare({
        clsLevel: medConfig.point_of_injury_care.cls_level || POI_CARE_DEFAULTS.cls_level,
        pfcTraining: medConfig.point_of_injury_care.pfc_training || POI_CARE_DEFAULTS.pfc_training
      });
    } else {
      const tab1Cls = scenario?.force?.force_quality?.cls_training_percentage;
      setPoiCare({ clsLevel: CLS_TRAINING_LEVELS[tab1Cls] ? tab1Cls : POI_CARE_DEFAULTS.cls_level, pfcTraining: POI_CARE_DEFAULTS.pfc_training });
    }

    // Load Blood configuration
    if (medConfig.blood_config) {
      const bc = medConfig.blood_config;
      setBloodConfig({
        enabled: bc.enabled !== false,
        currentStock: bc.current_stock || {
          prbc_units: 100,
          ffp_units: 80,
          fdp_units: 0,
          plt_units: 20,
          cryo_pools: 10,
          whole_blood_units: 0
        },
        wbbEnabled: bc.wbb_enabled || false,
        wbbDonors: bc.wbb_donors || 0,
        wbbLowTiterDonors: bc.wbb_low_titer_donors || 0,
        wbbActivationThreshold: bc.wbb_activation_threshold || 30,
        alertLevel: bc.alert_level || 'GREEN',
        // Phase 6.0: MTP + shelf life (backward compat: default false when missing)
        mtpEnabled: bc.mtp_enabled || false,
        mtpProtocol: bc.mtp_protocol || '1:1:1',
        shelfLifeTracking: bc.shelf_life_tracking || false
      });
    } else {
      // PHASE 1.0: Migration from old Tab 4 WBB data
      const oldWBB = scenario?.logistics?.walking_blood_bank;
      if (oldWBB) {
        setBloodConfig(prev => ({
          ...prev,
          wbbEnabled: oldWBB.enabled ?? false,
          wbbDonors: oldWBB.trained_donors || oldWBB.donors || 0,
          wbbLowTiterDonors: oldWBB.low_titer_donors || 0,
          wbbActivationThreshold: oldWBB.activation_threshold || 30
        }));
      }
    }
    
    // Load Surgical configuration
    if (medConfig.surgical_regulating) {
      setSurgicalRegulating({ ...REGULATING_DEFAULTS, ...medConfig.surgical_regulating });
    }
    if (medConfig.surgical_config) {
      const sc = medConfig.surgical_config;
      setSurgicalConfig(prev => ({
        ...prev,
        enabled: sc.enabled !== false,
        teams: sc.teams || prev.teams,
        shiftMode: sc.shift_mode || 'NORMAL',
        maxTempo: sc.max_tempo || DEFAULT_MAX_TEMPO,
        fatigueLevel: sc.fatigue_level || 0
      }));
    }
    
    // Load Strategic Evacuation configuration
    if (medConfig.strategic_evac_config) {
      const sec = medConfig.strategic_evac_config;
      // Phase 6.0 backward compat: load from airfields[] array (Phase 6+) OR flat keys (pre-Phase 6).
      // BUG-099/104 FIX: All distance fields default to 0, not undefined/null.
      // undefined renders blank (undefined ?? '' = ''); 0 is the correct sentinel value.
      const primaryAirfield = sec.airfields?.[0] ?? null;
      const loadedAirfield = primaryAirfield
        ? primaryAirfield
        : {
            id: 'airfield_primary',
            label: sec.airfield_label || 'Primary STRATEVAC Airfield',
            class: sec.airfield_class || 'CLASS_B',
            type: sec.airfield_type || 'MILITARY',
            distances: {
              r2b_to_airfield_km: sec.role2b_to_airfield ?? 0,
              r2e_to_airfield_km: sec.role2e_to_airfield ?? 0,
              r3_to_airfield_km:  sec.role3_to_airfield  ?? 0,
              airfield_to_r4_km:  sec.airfield_to_role4  ?? 0
            },
            pha_beds: sec.pha_beds ?? 50,
            farp_enabled: sec.farp_enabled || false,
            is_primary: true,
            coordinates: null,
            distance_source: 'MANUAL'
          };
      setStrategicEvac({
        trainEnabled: sec.train_enabled || false,
        stationType: sec.station_type || TRAIN_SCHEDULE_DEFAULTS.station_type,
        trainType: sec.train_type || TRAIN_SCHEDULE_DEFAULTS.train_type,
        role3ToStation: sec.role3_to_station,
        stationToRole4: sec.station_to_role4,
        // ENH-004: Train schedule fields — backward compat: default if not in saved scenario
        trainFrequencyMode: sec.train_schedule?.frequency_mode ?? TRAIN_SCHEDULE_DEFAULTS.frequency_mode,
        trainFirstDepartureDay: sec.train_schedule?.first_departure_day ?? TRAIN_SCHEDULE_DEFAULTS.first_departure_day,
        trainDepartureHour: sec.train_schedule?.departure_hour ?? TRAIN_SCHEDULE_DEFAULTS.departure_hour,
        trainsPerDeparture: sec.train_schedule?.trains_per_departure ?? TRAIN_SCHEDULE_DEFAULTS.trains_per_departure,
        trainMinPatients: sec.train_schedule?.min_patients_for_departure ?? TRAIN_SCHEDULE_DEFAULTS.min_patients_for_departure,
        airEnabled: sec.air_enabled !== false,
        airfields: [
          // airfields[0] — primary (always present)
          // BUG-099 FIX: patch distances from saved scenario — pre-fix saves may have
          // null or undefined on r2b/r2e fields. Spread loadedAirfield first, then
          // override distances with ?? 0 guards on each field.
          {
            ...loadedAirfield,
            distances: {
              r2b_to_airfield_km: loadedAirfield.distances?.r2b_to_airfield_km ?? 0,
              r2e_to_airfield_km: loadedAirfield.distances?.r2e_to_airfield_km ?? 0,
              r3_to_airfield_km:  loadedAirfield.distances?.r3_to_airfield_km  ?? 0,
              airfield_to_r4_km:  loadedAirfield.distances?.airfield_to_r4_km  ?? 0
            },
            is_active: true,
            schedule:   sec.airfields?.[0]?.schedule   ?? null,
            schedule_b: sec.airfields?.[0]?.schedule_b ?? null   // BUG-079: null on pre-v4.17 scenarios
          },
          // airfields[1] — alternate (loaded from saved state or default)
          sec.airfields?.[1]
            ? {
                ...sec.airfields[1],
                // BUG-104 FIX: patch alternate distances — pre-fix saves have null values
                distances: {
                  r2b_to_airfield_km: sec.airfields[1].distances?.r2b_to_airfield_km ?? 0,
                  r2e_to_airfield_km: sec.airfields[1].distances?.r2e_to_airfield_km ?? 0,
                  r3_to_airfield_km:  sec.airfields[1].distances?.r3_to_airfield_km  ?? 0,
                  airfield_to_r4_km:  sec.airfields[1].distances?.airfield_to_r4_km  ?? 0
                },
                is_active:  sec.airfields[1].is_active  ?? false,
                schedule:   sec.airfields[1].schedule   ?? null,
                schedule_b: sec.airfields[1].schedule_b ?? null  // BUG-079: null on pre-v4.17 scenarios
              }
            : {
                id: 'airfield_alternate',
                label: 'Alternate STRATEVAC Airfield',
                class: 'CLASS_B',
                type: 'MILITARY',
                // BUG-104 FIX: 0 not null — null ?? '' = '' renders blank TextField
                distances: { r2b_to_airfield_km: 0, r2e_to_airfield_km: 0, r3_to_airfield_km: 0, airfield_to_r4_km: 0 },
                pha_beds: ALTERNATE_AIRFIELD_DEFAULTS.pha_beds,
                farp_enabled: false,
                is_primary: false,
                is_active: false,
                schedule: null,
                schedule_b: null,
                coordinates: null,
                distance_source: 'MANUAL'
              }
        ],
        busEnabled: sec.bus_enabled || false,
        busCount: sec.bus_count || 5,
        trainEvacTimeHours: 0,
        airEvacTimeHours: 0
      });
    }
    
    // HICOM-001: Load R3 accessibility flag
    setR3Accessible(medConfig.r3_accessible ?? true);
    // BUG-NEXT-015: Load R2E accessibility flag (brigade echelon)
    setR2eAccessible(medConfig.r2e_accessible ?? true);
    // R2E-DIST: Load R2E distance (default 40 km)
    setR2eDistance(medConfig.r2e_distance_km ?? 40);
    setDistanceOverrides(medConfig.distance_overrides || {});

    // Load MASCAL configuration
    if (medConfig.mascal_config) {
      const mc = medConfig.mascal_config;
      setMascalConfig({
        enabled: mc.enabled !== false,
        currentLevel: mc.current_level || 'NORMAL',
        blueLightEnabled: mc.blue_light_enabled !== false,
        thresholds: mc.thresholds || {
          mtf_warning: 80,
          mtf_critical: 95,
          surgical_queue_warning: 4,
          surgical_queue_critical: 8
        },
        // v4.3.2: Load Blue Light Protocol tracking state
        blueLightRecommended: mc.blue_light_recommended || false,
        blueLightActive: mc.blue_light_active || false,
        blueLightTriggerReason: mc.blue_light_trigger_reason || null,
        blueLightActivatedAt: mc.blue_light_activated_at || null,
        blueLightDismissed: mc.blue_light_dismissed || false,
        blueLightDismissReason: mc.blue_light_dismiss_reason || null
      });
    }
  };

  // Keep ref current on every render so init useEffect calls latest version
  loadConfigRef.current = loadExistingConfiguration;
  
  // Helper: Extract primary national standard from force structure
  // Handles multinational corps/division where scenario.force.national_standard may not exist
  // BUG-116 FIX: MULTINATIONAL division — force.national_standard is not set at division level
  // (Tab 1 assigns national standards per brigade, not to the division itself). The previous
  // code fell through all checks and returned 'NATO'. Now detects via fbResult.brigades array.
  const extractPrimaryNationalStandard = () => {
    const force = scenario?.force;
    if (!force) return 'NATO';

    // BUG-097/101 FIX: Lead nation override (ENH-018F) — MULTINATIONAL corps with a named
    // lead nation. corpsLeadNation is null when no lead nation is selected (averaged data).
    if (force.corpsLeadNation) return force.corpsLeadNation;

    // Direct national_standard on force object (simple non-multinational forces)
    if (force.national_standard && force.national_standard !== 'MULTINATIONAL')
      return force.national_standard;

    // If explicitly set to MULTINATIONAL — return that for display
    if (force.national_standard === 'MULTINATIONAL') return 'MULTINATIONAL';

    const fbResult = force.forceBuilderResult;

    // ForceBuilder result - check corps (non-MULTINATIONAL)
    if (fbResult?.corps?.nationalStandard &&
        fbResult.corps.nationalStandard !== 'MULTINATIONAL')
      return fbResult.corps.nationalStandard;

    // Division array (CORPS echelon builds)
    if (fbResult?.divisions && fbResult.divisions.length > 0) {
      const divStd = fbResult.divisions[0].nationalStandard;
      if (divStd && divStd !== 'MULTINATIONAL') return divStd;
    }

    // BUG-116 FIX: DIVISION echelon — brigades are stored flat in fbResult.brigades[].
    // For MULTINATIONAL division, each brigade has its own nationalStandard.
    // If all brigades share one standard → return it. If mixed → return 'MULTINATIONAL'.
    if (fbResult?.brigades && fbResult.brigades.length > 0) {
      const standards = [...new Set(
        fbResult.brigades.map(b => b.nationalStandard).filter(Boolean)
      )];
      if (standards.length === 1 && standards[0] !== 'MULTINATIONAL') return standards[0];
      if (standards.length > 1) return 'MULTINATIONAL';
    }

    // Single division / brigade legacy paths
    if (force.division?.nationalStandard) return force.division.nationalStandard;
    if (force.brigade?.nationalStandard) return force.brigade.nationalStandard;

    return 'NATO';
  };
  
  const initializeDefaults = () => {
    if (!scenario?.force) return;
    
    const echelon = scenario.force.echelon;
    // FIXED: Use helper to extract national standard for multinational support
    const nationalStandard = extractPrimaryNationalStandard();
    
    const defaultDistances = calculateDefaultMTFDistances(echelon, nationalStandard);
    setMtfDistances(defaultDistances);
    
    const defaultAssets = calculateDefaultEvacuationAssets(echelon, nationalStandard);
    setEvacuationAssets({
      mode: mode === 'STANDARD' ? 'AUTO' : 'CUSTOM',
      ...defaultAssets
    });
    
    const defaultStructure = calculateDefaultMTFStructure(echelon);
    setMtfStructure(defaultStructure);
    
    // ForceBuilder corps with divisions
    const fbDivisions = scenario.force.forceBuilderResult?.divisions;
    if (echelon === 'CORPS' && fbDivisions && fbDivisions.length > 0) {
      initializeDivisionAllocations(fbDivisions, defaultAssets);
    }
    // Legacy corps structure
    else if (echelon === 'CORPS' && scenario.force.corps?.divisions) {
      initializeDivisionAllocations(scenario.force.corps.divisions, defaultAssets);
    }
    // 2026-10-18 (Tab 3 review T3-02): the surgical team counts of 3D now reach the calculation (capacity of the surgical
    // teams), so their starting values follow the Role 2 Basic facilities of Tab 1 (the organic surgical teams) and no
    // Surgical Company; before, they started at 2 Forward Surgical Teams and 1 Surgical Company whatever the force.
    try {
      const _organicTeams = extractMedicalFacilities(scenario.force)
        .filter(f => f.type === 'ROLE2_BASIC')
        .reduce((sum, f) => sum + (Number(f.surgical_teams) || 0), 0);
      if (_organicTeams > 0) {
        setSurgicalConfig(prev => ({
          ...prev,
          surgicalTeams: _organicTeams,
          teams: {
            ...prev.teams,
            FST: { count: _organicTeams, available: _organicTeams, assigned: [] },
            SURG_CO: { count: 0, available: 0, assigned: [] }
          }
        }));
      }
    } catch (e) {
      console.error('Tab 3: organic surgical teams could not be read from the Tab 1 facilities', e);
    }
    // ENV-003b: Record Tab2 baseline so re-sync useEffect ignores this initial seed.
    const _t2t = scenario?.operation_parameters?.threat_level || scenario?.environment?.threat_level;
    const _t2w = scenario?.operation_parameters?.environmental_factors?.weather || scenario?.environment?.weather;
    lastKnownTab2Ref.current = { threat: _t2t, weather: _t2w };
  };

  // Keep ref current on every render so init useEffect calls latest version
  initDefaultsRef.current = initializeDefaults;
  
  const initializeDivisionAllocations = (divisions, assets) => {
    const divCount = divisions.length;
    if (divCount === 0) return;
    
    const lightPerDiv = Math.floor(assets.ground.light / divCount);
    const armoredPerDiv = Math.floor(assets.ground.armored / divCount);
    
    // FIXED: Use extractPrimaryNationalStandard helper
    const primaryStandard = extractPrimaryNationalStandard();
    const heloType = primaryStandard === 'ROMANIAN' ? 'iar330' : 'uh60';
    const helosPerDiv = Math.floor(assets.air[heloType] / divCount);
    
    const allocations = divisions.map((div, idx) => ({
      division_id: div.id || div.division_id || `DIV_${idx + 1}`,
      division_name: div.name || div.division_name || `Division ${idx + 1}`,
      national_standard: div.nationalStandard || primaryStandard,
      light_ambulances: lightPerDiv,
      armored_ambulances: armoredPerDiv,
      helicopters: helosPerDiv
    }));
    
    setDivisionAllocations(allocations);
  };

  // Continue in Part 2...

  // ========================================================================
  // VALIDATION (ENHANCED FOR PHASE 2)
  // ========================================================================
  
  useEffect(() => {
    validateConfigRef.current();
  }, [mtfDistances, evacuationAssets, mtfStructure, stagingFacilities, divisionAllocations]);
  
  // Phase 2: Initialize cross-division pairs when divisions change
  // crossDivisionPairsRef used (instead of crossDivisionPairs directly) to avoid
  // adding it to deps which would cause an infinite loop (effect sets pairs -> pairs
  // changes -> effect fires -> sets pairs -> ...). Ref stays current via sync effect.
  useEffect(() => { crossDivisionPairsRef.current = crossDivisionPairs; }, [crossDivisionPairs]);
  
  useEffect(() => {
    const divisions = scenario?.force?.forceBuilderResult?.divisions || [];
    if (divisions.length < 2) {
      setCrossDivisionPairs([]);
      return;
    }
    
    // Generate all possible pairs (A B and B A are separate)
    const newPairs = [];
    divisions.forEach((fromDiv, fromIdx) => {
      divisions.forEach((toDiv, toIdx) => {
        if (fromIdx !== toIdx) {
          // Check if pair already exists (preserve user settings)
          const existingPair = crossDivisionPairsRef.current.find(
            p => p.from_id === fromDiv.id && p.to_id === toDiv.id
          );
          
          const sameNation = (fromDiv.nationalStandard || 'NATO') === (toDiv.nationalStandard || 'NATO');
          
          newPairs.push({
            from_id: fromDiv.id,
            from_name: fromDiv.name || `Division ${fromIdx + 1}`,
            from_nation: fromDiv.nationalStandard || 'NATO',
            to_id: toDiv.id,
            to_name: toDiv.name || `Division ${toIdx + 1}`,
            to_nation: toDiv.nationalStandard || 'NATO',
            same_nation: sameNation,
            // Preserve existing setting, or default: same nation = enabled
            enabled: existingPair !== undefined ? existingPair.enabled : sameNation
          });
        }
      });
    });
    
    setCrossDivisionPairs(newPairs);
  }, [scenario?.force?.forceBuilderResult?.divisions]);

  // ENV-003b: Detect Tab2 threat/weather changes after Tab3 is configured.
  // Fires when Tab2 is re-saved — compares new Tab2 values to the baseline
  // captured in lastKnownTab2Ref. Shows an inline Alert offering to re-sync.
  // Does NOT fire on initial load (ref is null sentinel until loadExistingConfiguration
  // or initializeDefaults runs and sets the baseline).
  useEffect(() => {
    // Guard 1: ref not yet initialised (first render before load completes)
    if (lastKnownTab2Ref.current.threat === null && lastKnownTab2Ref.current.weather === null) return;
    // Guard 2: Tab3 not yet saved — nothing meaningful to re-sync against
    if (!scenario?.medical_configuration?.configured) return;

    const currentTab2Threat  = scenario?.operation_parameters?.threat_level
                             || scenario?.environment?.threat_level
                             || null;
    const currentTab2Weather = scenario?.operation_parameters?.environmental_factors?.weather
                             || scenario?.environment?.weather
                             || null;

    const last = lastKnownTab2Ref.current;
    if (currentTab2Threat === last.threat && currentTab2Weather === last.weather) {
      // Tab2 has not changed since last sync — clear any stale alert
      setTab2ResyncAlert(null);
      return;
    }

    // Tab2 has changed — compute what Tab3 values should now be
    const suggestedAD = TAB2_TO_AD_THREAT[(currentTab2Threat || '').toUpperCase()] || 'MEDIUM';
    const suggestedWX = TAB2_TO_WX[(currentTab2Weather || '').toUpperCase()]       || 'VMC_CLEAR';
    setTab2ResyncAlert({ suggestedAD, suggestedWX, tab2Threat: currentTab2Threat, tab2Weather: currentTab2Weather });
  }, [
    scenario?.operation_parameters?.threat_level,
    scenario?.operation_parameters?.environmental_factors?.weather,
    scenario?.environment?.threat_level,
    scenario?.environment?.weather,
    scenario?.medical_configuration?.configured
  ]);
  
  // Cross-division pair helper functions
  const togglePairEnabled = (fromId, toId) => {
    setCrossDivisionPairs(pairs => pairs.map(p => 
      (p.from_id === fromId && p.to_id === toId) 
        ? { ...p, enabled: !p.enabled } 
        : p
    ));
  };
  
  const setAllPairsEnabled = (enabled) => {
    setCrossDivisionPairs(pairs => pairs.map(p => ({ ...p, enabled })));
  };
  
  const setSameNationOnly = () => {
    setCrossDivisionPairs(pairs => pairs.map(p => ({ ...p, enabled: p.same_nation })));
  };
  
  const validateConfiguration = () => {
    let allErrors = [];
    let allWarnings = [];
    
    // v3.9 FIX: Validate ACTUAL facility positions from ForceBuilder
    // Previously used mtfDistances state which could be out of sync with actual facilities
    // Now uses forceBuilderFacilities for real positions, falls back to mtfDistances if no facilities
    const distanceValidation = validateActualFacilityPositions(forceBuilderFacilities, mtfDistances);
    allErrors = [...allErrors, ...distanceValidation.errors];
    allWarnings = [...allWarnings, ...distanceValidation.warnings];
    
    // Validate evacuation assets
    const assetValidation = validateEvacuationAssets(evacuationAssets, evacuationAssets);
    allErrors = [...allErrors, ...assetValidation.errors];
    allWarnings = [...allWarnings, ...assetValidation.warnings];
    
    // Validate MTF structure
    if (mtfStructure.role1.beds < 4) {
      allWarnings.push('Role 1 beds below minimum recommended (4)');
    }
    if (mtfStructure.role2.count > 0 && mtfStructure.role2.beds < 20) {
      allWarnings.push('Role 2 beds below minimum recommended (20)');
    }
    if (mtfStructure.role3.count > 0) {
      if (mtfStructure.role3.beds < 30) {
        allWarnings.push('Role 3 beds below minimum recommended (30)');
      }
      if (mtfStructure.role3.icuBeds > mtfStructure.role3.beds) {
        allErrors.push('Role 3 ICU beds cannot exceed total beds');
      }
    }
    
    // PHASE 2: Validate staging facilities
    if (useAdvancedStaging) {
      stagingFacilities.forEach(facility => {
        // Validate personnel
        const personnelErrors = validateFacilityPersonnel(facility.personnel);
        allErrors = [...allErrors, ...personnelErrors.map(e => `${facility.facility_id}: ${e}`)];
        
        // Check capacity vs personnel ratio
        if (facility.personnel.total > 0) {
          const ratio = facility.capacity_patients / facility.personnel.total;
          if (ratio > 5) {
            allWarnings.push(`${facility.facility_id}: High patient-to-staff ratio (${ratio.toFixed(1)}:1)`);
          }
        }
        
        // Check allocation
        if (facility.allocated_to.unit_type === 'ROLE2E' || facility.allocated_to.unit_type === 'ROLE3') {
          if (!facility.allocated_to.unit_id) {
            allErrors.push(`${facility.facility_id}: Must specify which facility to allocate to`);
          }
        }
        
        // Check against NATO standards
        const standards = facility.facility_type === 'PHA' ?
          CSU_NATO_STANDARDS[facility.facility_size] :
          PHA_NATO_STANDARDS[facility.facility_size];
        
        if (standards) {
          if (facility.capacity_patients < standards.capacity_patients * 0.5) {
            allWarnings.push(`${facility.facility_id}: Capacity significantly below NATO standard (${standards.capacity_patients} patients)`);
          }
          if (facility.personnel.total < standards.personnel.total * 0.7) {
            allWarnings.push(`${facility.facility_id}: Personnel below 70% of NATO standard (${standards.personnel.total} staff)`);
          }
        }
      });
    }
    
    setValidationErrors(allErrors);
    setValidationWarnings(allWarnings);
    setIsValid(allErrors.length === 0);
  };

  // Keep ref current on every render so validation useEffect calls latest version
  validateConfigRef.current = validateConfiguration;
  
  // ========================================================================
  // EVENT HANDLERS - BLUE LIGHT PROTOCOL (v4.3.2)
  // ========================================================================
  
  /**
   * User decides to activate Blue Light Protocol
   */
  const handleActivateBlueLight = () => {
    setMascalConfig(prev => ({
      ...prev,
      blueLightActive: true,
      blueLightActivatedAt: new Date().toISOString(),
      blueLightRecommended: false  // Clear recommendation once activated
    }));
  };
  
  /**
   * User opens dismiss dialog to provide reason for not activating
   */
  const handleOpenDismissDialog = () => {
    setBlueLightDismissDialog({
      open: true,
      reason: ''
    });
  };
  
  /**
   * User cancels dismiss dialog
   */
  const handleCancelDismiss = () => {
    setBlueLightDismissDialog({
      open: false,
      reason: ''
    });
  };
  
  /**
   * User confirms dismissal with reason
   */
  const handleConfirmDismiss = () => {
    if (!blueLightDismissDialog.reason.trim()) {
      alert('Please provide a reason for not activating MASCAL Escalation Protocol (AMedP-1.10)');
      return;
    }
    
    setMascalConfig(prev => ({
      ...prev,
      blueLightDismissed: true,
      blueLightDismissReason: blueLightDismissDialog.reason,
      blueLightRecommended: false  // Clear recommendation
    }));
    
    setBlueLightDismissDialog({
      open: false,
      reason: ''
    });
  };
  
  /**
   * User manually deactivates Blue Light Protocol
   */
  const handleDeactivateBlueLight = () => {
    setMascalConfig(prev => ({
      ...prev,
      blueLightActive: false,
      blueLightActivatedAt: null
    }));
  };

  // ========================================================================
  // ENH-002: EVENT HANDLERS — ATTACH / DETACH EVACUATION ASSETS
  // ========================================================================

  const handleOpenAttachDialog = (facilityId) => {
    setAttachForm({
      asset_type: 'HELICOPTER', model: 'IAR330', quantity: 1,
      attach_day: 1, detach_day: '', source_unit: '', mou_reference: ''
    });
    setAttachDialog({ open: true, facilityId });
  };

  const handleCloseAttachDialog = () => setAttachDialog({ open: false, facilityId: null });

  const handleSaveAttachment = () => {
    const { facilityId } = attachDialog;
    if (!facilityId) return;
    const opDuration = scenario?.operation_parameters?.duration_days ?? 30;
    const attachDay  = parseInt(attachForm.attach_day)  || 1;
    const detachDay  = attachForm.detach_day !== '' ? (parseInt(attachForm.detach_day) || null) : null;
    if (attachDay < 1 || attachDay > opDuration) return;
    if (detachDay !== null && (detachDay < attachDay || detachDay > opDuration)) return;
    const spec = attachForm.asset_type === 'HELICOPTER'
      ? HELICOPTER_SPECS[attachForm.model]
      : null;
    const newRecord = {
      asset_type:           attachForm.asset_type,
      model:                attachForm.model,
      quantity:             Math.max(1, parseInt(attachForm.quantity) || 1),
      attach_day:           attachDay,
      detach_day:           detachDay,
      source_unit:          attachForm.source_unit.trim() || 'Unknown unit',
      mou_reference:        attachForm.mou_reference.trim() || null,
      capacity_per_aircraft: spec ? (spec.capacity?.stretchers ?? 0) : 0,
      speed_kmh:             spec ? (spec.performance?.cruise_speed_kmh ?? 0) : 0
    };
    setForceBuilderFacilities(prev => prev.map(f =>
      (f.id || f.facility_id) === facilityId
        ? { ...f, attached_assets: [...(Array.isArray(f.attached_assets) ? f.attached_assets : []), newRecord] }
        : f
    ));
    handleCloseAttachDialog();
  };

  const handleRemoveAttachment = (facilityId, idx) => {
    setForceBuilderFacilities(prev => prev.map(f =>
      (f.id || f.facility_id) === facilityId
        ? { ...f, attached_assets: (f.attached_assets || []).filter((_, i) => i !== idx) }
        : f
    ));
  };

  const handleOpenDetachDialog = (facilityId) => {
    setDetachForm({
      asset_type: 'HELICOPTER', quantity: 1,
      detach_day: 1, return_day: '', destination_unit: ''
    });
    setDetachDialog({ open: true, facilityId });
  };

  const handleCloseDetachDialog = () => setDetachDialog({ open: false, facilityId: null });

  const handleSaveDetachment = () => {
    const { facilityId } = detachDialog;
    if (!facilityId) return;
    const opDuration = scenario?.operation_parameters?.duration_days ?? 30;
    const detachDay  = parseInt(detachForm.detach_day)  || 1;
    const returnDay  = detachForm.return_day !== '' ? (parseInt(detachForm.return_day) || null) : null;
    if (detachDay < 1 || detachDay > opDuration) return;
    if (returnDay !== null && (returnDay <= detachDay || returnDay > opDuration)) return;
    const newRecord = {
      asset_type:       detachForm.asset_type,
      quantity:         Math.max(1, parseInt(detachForm.quantity) || 1),
      detach_day:       detachDay,
      return_day:       returnDay,
      destination_unit: detachForm.destination_unit.trim() || 'Unknown unit'
    };
    setForceBuilderFacilities(prev => prev.map(f =>
      (f.id || f.facility_id) === facilityId
        ? { ...f, detached_assets: [...(Array.isArray(f.detached_assets) ? f.detached_assets : []), newRecord] }
        : f
    ));
    handleCloseDetachDialog();
  };

  const handleRemoveDetachment = (facilityId, idx) => {
    setForceBuilderFacilities(prev => prev.map(f =>
      (f.id || f.facility_id) === facilityId
        ? { ...f, detached_assets: (f.detached_assets || []).filter((_, i) => i !== idx) }
        : f
    ));
  };

  // ENH-006: update activate_day / deactivate_day / setup_hours on a facility
  const handleFacilityActivationUpdate = (facilityId, field, rawValue) => {
    const value = rawValue === '' ? null : (parseInt(rawValue) || null);
    setForceBuilderFacilities(prev => prev.map(f => {
      if ((f.id || f.facility_id) !== facilityId) return f;
      const updated = { ...f, [field]: value };
      // ENH-006: when activate_day is first set and setup_hours is still null,
      // pre-populate setup_hours from the doctrinal standard for this facility type.
      if (field === 'activate_day' && value != null && f.setup_hours == null) {
        updated.setup_hours = getDoctrinalSetupHours(f.type || f.facility_type);
      }
      return updated;
    }));
  };

  // ENH-006: resolve doctrinal setup hours by facility type.
  // Values from MTF_SETUP_TIMES_HOURS (casualtyCalculatorConstants.js) — AJMedP-1.
  // Returns null if type unknown (planner must enter manually).
  const getDoctrinalSetupHours = (facilityType) => {
    const t = (facilityType || '').toUpperCase();
    if (t === 'ROLE1' || t === 'ROLE_1')                         return MTF_SETUP_TIMES_HOURS.ROLE_1  ?? 1;
    if (t === 'ROLE2_BASIC' || t === 'ROLE2B' || t === 'ROLE_2_BASIC') return MTF_SETUP_TIMES_HOURS.ROLE_2B ?? 12;
    if (t === 'ROLE2_ENHANCED' || t === 'ROLE2E')                return MTF_SETUP_TIMES_HOURS.ROLE_2E ?? 24;
    if (t === 'ROLE3' || t === 'ROLE_3')                         return MTF_SETUP_TIMES_HOURS.ROLE_3  ?? 48;
    return null;
  };

  // IMP-005: Update planned displacement entries for a facility/phase
  const handleFacilityDisplacementUpdate = (facilityId, phaseIndex, fields) => {
    setForceBuilderFacilities(prev => prev.map(f => {
      if ((f.id || f.facility_id) !== facilityId) return f;
      const existing = f.phase_displacements || [];
      let updated;
      if (fields === null) {
        updated = existing.filter(d => d.phase_index !== phaseIndex);
      } else {
        const idx = existing.findIndex(d => d.phase_index === phaseIndex);
        updated = idx >= 0
          ? existing.map((d, i) => i === idx ? { ...d, ...fields } : d)
          : [...existing, { phase_index: phaseIndex, ...fields }];
      }
      return { ...f, phase_displacements: updated };
    }));
  };

  // ========================================================================
  // EVENT HANDLERS - MTF DISTANCES
  // ========================================================================

  const handleResetDistances = () => {
    const echelon = scenario?.force?.echelon || 'BRIGADE';
    // FIXED: Use helper for multinational support
    const nationalStandard = extractPrimaryNationalStandard();
    const defaults = calculateDefaultMTFDistances(echelon, nationalStandard);
    setMtfDistances(defaults);
  };
  
  // ========================================================================
  // EVENT HANDLERS - EVACUATION ASSETS
  // ========================================================================
  
  const handleResetAssets = () => {
    const echelon = scenario?.force?.echelon || 'BRIGADE';
    // FIXED: Use helper for multinational support
    const nationalStandard = extractPrimaryNationalStandard();
    const defaults = calculateDefaultEvacuationAssets(echelon, nationalStandard);
    
    setEvacuationAssets(prev => ({
      ...prev,
      ...defaults
    }));
  };
  
  // ========================================================================
  // PHASE 2: EVENT HANDLERS - FACILITY SIZE SELECTION
  // ========================================================================
  
  const handleOpenSizeSelector = (facilityType) => {
    setPendingFacilityType(facilityType);
    setNewFacilitySize('MEDIUM');
    setShowSizeSelector(true);
  };
  
  const handleCloseSizeSelector = () => {
    setShowSizeSelector(false);
    setPendingFacilityType('PHA');
    setNewFacilitySize('MEDIUM');
  };

  const handleConfirmAddFacility = () => {
    if (pendingFacilityType === 'HLZ') {
      const newHLZ = createHLZFromStandards(newFacilitySize, 'NEAR_ROLE3');
      setHelicopterFacilities([...helicopterFacilities, newHLZ]);
    } else {
      const newFacility = createFacilityFromStandards(pendingFacilityType, newFacilitySize, null);
      setStagingFacilities([...stagingFacilities, newFacility]);
    }
    setShowSizeSelector(false);
  };
  
  // ========================================================================
  // PHASE 2: EVENT HANDLERS - UNIT ALLOCATION
  // ========================================================================
  
  const handleAllocationChange = (facilityId, allocationValue) => {
    const selectedOption = allocationOptions.find(opt => opt.value === allocationValue);
    
    if (!selectedOption) return;
    
    setStagingFacilities(stagingFacilities.map(facility => {
      if (facility.facility_id === facilityId) {
        // SF-004: Calculate distance to parent MTF based on facility type
        // CSU near Role 1: 0.5-2km, near Role 2B: 0.5-1km
        // PHA near Role 2E: 0.2-0.5km, near Role 3: 0.5km
        // HLZ: typically co-located (0km)
        let defaultDistanceToParent = 1.0; // km
        if (selectedOption.type === 'ROLE1') {
          defaultDistanceToParent = 1.5; // Forward CSU/HLZ near aid station
        } else if (selectedOption.type === 'ROLE2_BASIC') {
          defaultDistanceToParent = 0.5; // Near brigade medical company
        } else if (selectedOption.type === 'ROLE2_ENHANCED') {
          defaultDistanceToParent = 0.3; // Near division hospital
        } else if (selectedOption.type === 'ROLE3') {
          defaultDistanceToParent = 0.5; // Near corps CSH
        } else if (selectedOption.type === 'HLZ') {
          defaultDistanceToParent = 0; // Co-located with HLZ
        } else if (selectedOption.type === 'AIRFIELD') {
          defaultDistanceToParent = 0.2; // Adjacent to airfield
        }
        
        return {
          ...facility,
          allocated_to: {
            unit_type: selectedOption.type,
            unit_id: allocationValue,
            unit_name: selectedOption.label,
            position: selectedOption.position,
            // PHASE 4 (SF-004): Link to specific parent MTF with distance
            parent_mtf_id: selectedOption.parent_mtf_id || null,
            parent_mtf_type: selectedOption.parent_mtf_type || null,
            distance_to_parent_km: defaultDistanceToParent,
            position_feba_km: selectedOption.position_feba_km || null,
            hierarchy: selectedOption.hierarchy || null
          }
        };
      }
      return facility;
    }));
  };
  
  // ========================================================================
  // PHASE 2: EVENT HANDLERS - PERSONNEL CUSTOMIZATION
  // ========================================================================
  
  const handleTogglePersonnelMode = (facilityId, isCustom) => {
    setStagingFacilities(stagingFacilities.map(facility => {
      if (facility.facility_id === facilityId) {
        // If switching to AUTO, restore from standards
        const newPersonnel = isCustom ? facility.personnel :
          (facility.facility_type === 'CSU' ?
            { ...CSU_NATO_STANDARDS[facility.facility_size].personnel } :
            { ...PHA_NATO_STANDARDS[facility.facility_size].personnel });
        
        return {
          ...facility,
          personnel_mode: isCustom ? 'CUSTOM' : 'AUTO',
          personnel: newPersonnel
        };
      }
      return facility;
    }));
    
    setPersonnelEditMode({
      ...personnelEditMode,
      [facilityId]: isCustom
    });
  };
  
  const handlePersonnelChange = (facilityId, field, value) => {
    const numValue = parseInt(value) || 0;
    
    setStagingFacilities(stagingFacilities.map(facility => {
      if (facility.facility_id === facilityId) {
        const updatedPersonnel = {
          ...facility.personnel,
          [field]: numValue
        };
        
        // Recalculate total
        updatedPersonnel.total = 
          updatedPersonnel.medical_officers +
          updatedPersonnel.nurses +
          updatedPersonnel.medics +
          updatedPersonnel.support_staff;
        
        return {
          ...facility,
          personnel: updatedPersonnel
        };
      }
      return facility;
    }));
  };
  
  // ========================================================================
  // PHASE 2: EVENT HANDLERS - CAPACITY & SUPPLY RECALCULATION
  // ========================================================================
  
  const handleCapacityChange = (facilityId, newCapacity) => {
    setStagingFacilities(stagingFacilities.map(facility => {
      if (facility.facility_id === facilityId) {
        // Recalculate supplies if capacity changed
        const scaledSupplies = calculateSupplyConsumptionScaled(
          facility.facility_type,
          facility.facility_size,
          newCapacity
        );
        
        return {
          ...facility,
          capacity_patients: newCapacity,
          daily_supply_consumption: scaledSupplies
        };
      }
      return facility;
    }));
  };
  
  const handleFacilityFieldChange = (facilityId, field, value) => {
    setStagingFacilities(stagingFacilities.map(facility => {
      if (facility.facility_id === facilityId) {
        return {
          ...facility,
          [field]: typeof value === 'string' ? value : (parseFloat(value) || 0)
        };
      }
      return facility;
    }));
  };
  
  // ========================================================================
  // EVENT HANDLERS - STAGING FACILITIES (PHASE 1 HANDLERS)
  // ========================================================================
  
  const handleRemoveStagingFacility = (facilityId) => {
    setStagingFacilities(stagingFacilities.filter(f => f.facility_id !== facilityId));
  };
  
  // ========================================================================
  // EVENT HANDLERS - HLZ
  // ========================================================================
  
  // Reserved for future Add HLZ button implementation
  // const handleAddHLZ = () => {
  //   const newHLZ = createHLZFromStandards('MEDIUM', 'NEAR_ROLE3');
  //   setHelicopterFacilities([...helicopterFacilities, newHLZ]);
  //   console.log('[HLZ] Added MEDIUM HLZ');
  // };
  
  const handleRemoveHLZ = (facilityId) => {
    setHelicopterFacilities(helicopterFacilities.filter(h => h.facility_id !== facilityId));
  };
  
  const handleHLZFieldChange = (facilityId, field, value) => {
    setHelicopterFacilities(helicopterFacilities.map(hlz => {
      if (hlz.facility_id !== facilityId) return hlz;
      // String fields — preserve as-is
      if (field === 'location_type' || field === 'colocated_facility_id') {
        const updated = { ...hlz, [field]: value };
        // UI-3D-001: auto-clear colocated_facility_id when moving away from CO_LOCATED_* types
        if (field === 'location_type' && !value.startsWith('CO_LOCATED_')) {
          updated.colocated_facility_id = null;
        }
        return updated;
      }
      return { ...hlz, [field]: parseInt(value) || 0 };
    }));
  };
  
  // ========================================================================
  // EVENT HANDLERS - ASSET DEGRADATION
  // Reserved for future asset degradation UI implementation
  // ========================================================================
  
  // const handleToggleAssetDegradation = (enabled) => {
  //   setEnableAssetDegradation(enabled);
  //   if (!enabled) {
  //     setExpectedDailyLossRate(2);
  //   }
  // };
  
  // const handleDailyLossRateChange = (value) => {
  //   setExpectedDailyLossRate(Math.min(Math.max(0, value), 10));
  // };
  
  // ========================================================================
  // EVENT HANDLERS - DIVISION ALLOCATIONS
  // Reserved for future division allocation UI implementation
  // ========================================================================
  
  // const handleDivisionAllocationChange = (divisionId, field, value) => {
  //   setDivisionAllocations(divisionAllocations.map(div => {
  //     if (div.division_id === divisionId) {
  //       return {
  //         ...div,
  //         [field]: parseInt(value) || 0
  //       };
  //     }
  //     return div;
  //   }));
  // };

// ========================================================================
// UI-007: FACILITY PROPERTY UPDATE HANDLER (Expert Mode Inline Editing)
// ========================================================================

const handleFacilityPropertyUpdate = (facilityId, property, value) => {
  setForceBuilderFacilities(facilities => 
    facilities.map(f => {
      const fId = f.id || f.facility_id;
      if (fId === facilityId) {
        // Handle nested properties (e.g., "evacuation.distance_km")
        if (property.includes('.')) {
          const [parent, child] = property.split('.');
          return {
            ...f,
            [parent]: {
              ...(f[parent] || {}),
              [child]: value
            }
          };
        }
        // Handle direct properties
        return {
          ...f,
          [property]: value
        };
      }
      return f;
    })
  );
};

// ========================================================================
// UI-009: CASEVAC CONFIGURATION UPDATE HANDLER
// Uses constants from evacuationAssetFactors.js when available
// ========================================================================

const handleCasevacUpdate = (field, value) => {
  setCasevacConfig(prev => {
    const updated = { ...prev, [field]: value };
    
    // Auto-calculate availability percent based on setting
    if (field === 'availability') {
      // Try to use imported constants, fallback to hardcoded values
      const availabilityMap = CASEVAC_CONFIGURATION?.AVAILABILITY || {
        'LIMITED': { availability_percent: 50 },
        'ON_REQUEST': { availability_percent: 80 },
        'DEDICATED': { availability_percent: 95 },
        'UNAVAILABLE': { availability_percent: 10 }
      };
      const config = availabilityMap[value];
      updated.availabilityPercent = config?.availability_percent || 80;
      
      // Set DOW penalty based on availability
      // Use imported constants when available
      const dowPenalties = CASEVAC_DOW_PENALTIES || {
        'LIMITED': { dow_multiplier: 1.45 },
        'ON_REQUEST': { dow_multiplier: 1.35 },
        'DEDICATED': { dow_multiplier: 1.25 },
        'UNAVAILABLE': { dow_multiplier: 1.60 }
      };
      const dowConfig = dowPenalties[value] || dowPenalties.GROUND_CASEVAC;
      updated.dowPenalty = dowConfig?.dow_multiplier || 1.35;
    }
    
    return updated;
  });
};
  
  // 2026-10-25: the three legs of the medical chain: override, else the gap between the placed facilities, else the older typed value
  const chainLegs = resolveChainDistances({
    facilities: forceBuilderFacilities,
    distance_overrides: distanceOverrides,
    r2e_accessible: scenario?.force?.echelon === 'BRIGADE' ? r2eAccessible : true,
    r3_accessible: r3Accessible,
    mtf_distances: { role2_distance_km: mtfDistances.role2 },
    r2e_distance_km: r2eDistance,
    evacuation_routes: evacuationRoutes
  });
  const setLegOverrideKm = (leg, km) => setDistanceOverrides((prev) => {
    const next = { ...prev };
    if (Number.isFinite(km) && km > 0) next[leg] = Math.min(500, km); else delete next[leg];
    return next;
  });
  const SOURCE_LABEL = { OVERRIDE: 'Planner override', FACILITIES: 'From placed facilities', LEGACY: 'Older typed value (no placed facility)', GENERIC: 'Generic planning distance (no placed facility; type the real one if known)', NOT_AVAILABLE: 'Level marked not available (switch in the Role 2E / Role 3 card)', MISSING: 'Not set' };

  const renderChainDistancesCard = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Medical chain distances</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Each distance is the gap between the placed facilities (Role 1, Role 2 Basic, Role 2 Enhanced, Role 3 stand at their
          headquarters position plus an offset, see the facility positions below). To use a different distance, type it in the
          override field; empty means follow the facilities. These three values are the only chain distances the calculation reads.
        </Alert>
        <Grid container spacing={2}>
          {CHAIN_LEGS.map((leg) => {
            const l = chainLegs[leg];
            return (
              <Grid key={leg} size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={`${CHAIN_LEG_LABELS[leg]} (override)`}
                  value={distanceOverrides[leg] ?? ''}
                  placeholder={l.derived_km != null ? String(l.derived_km) : ''}
                  onChange={(e) => setLegOverrideKm(leg, e.target.value === '' ? NaN : Number(e.target.value))}
                  InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                  disabled={l.source === 'NOT_AVAILABLE'}
                  inputProps={{ min: 1, max: 500 }}
                  helperText={`${l.km != null ? `In use: ${l.km} km. ` : ''}${SOURCE_LABEL[l.source]}${l.source === 'OVERRIDE' && l.derived_km != null ? ` (facilities give ${l.derived_km} km)` : ''}`}
                />
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );

// ========================================================================
// BUILD UPDATES (shared by handleSaveAndComplete and flushState)
// Constructs medical_configuration from current local state.
// Synchronous - no calculator. Used by:
//   - handleSaveAndComplete: validate -> buildUpdates -> calculator -> onUpdate -> onComplete
//   - flushState: buildUpdates -> onUpdateRef.current (silent, no validation, no calculator)
// ========================================================================

  const buildUpdates = () => {
    const medicalConfiguration = {
      configured: true,
      mode: mode,
      mtf_distances: {
        role1_distance_km: mtfDistances.role1,
        role2_distance_km: mtfDistances.role2,
        role3_distance_km: mtfDistances.role3,
        role4_distance_km: mtfDistances.role4
      },
      evacuation_assets: {
        mode: evacuationAssets.mode,
        ground: {
          light_ambulances: {
            count: evacuationAssets.ground.light,
            default_count: evacuationAssets.ground.defaultLight
          },
          armored_ambulances: {
            count: evacuationAssets.ground.armored,
            default_count: evacuationAssets.ground.defaultArmored
          }
        },
        air: {
          iar330_puma: {
            count: evacuationAssets.air.iar330,
            default_count: evacuationAssets.air.defaultIar330
          },
          uh60_blackhawk: {
            count: evacuationAssets.air.uh60,
            default_count: evacuationAssets.air.defaultUh60
          },
          ch47_chinook: {
            count: evacuationAssets.air.ch47,
            default_count: evacuationAssets.air.defaultCh47
          }
        }
      },
      mtf_structure: mtfStructure,
      use_advanced_staging: useAdvancedStaging,
      staging_facilities: stagingFacilities,
      helicopter_facilities: helicopterFacilities,
      enable_asset_degradation: enableAssetDegradation,
      expected_daily_loss_rate: expectedDailyLossRate / 100,
      enable_cross_division_sharing: enableCrossDivisionSharing,
      cross_division_config: {
        enabled: enableCrossDivisionSharing,
        mascal_only: crossDivisionMascalOnly,
        respect_national_caveats: crossDivisionRespectCaveats,
        max_capacity_percent: crossDivisionMaxCapacity,
        division_pairs: crossDivisionPairs
      },
      division_asset_allocations: divisionAllocations,
      // INC-010: MSK surge threshold — stored in medical_configuration for calculator access
      msk_surge_threshold_per_day: mskSurgeThreshold || null,
      // Psychiatric support level (NONE / STANDARD / ENHANCED): lowers combat stress evacuation and restoration days
      psychiatric_support: psychiatricSupport || null,
      per_facility_evacuation_assets: forceBuilderFacilities.map(f => ({
        facility_id: f.id || f.facility_id,
        facility_type: f.type,
        facility_name: f.name || '',
        location: f.location || '',
        organic_assets: f.organic_assets,
        attached_assets: f.attached_assets,
        detached_assets: f.detached_assets,
        evacuation_config: f.evacuation_config,
        effective_capacity: calculateEvacuationCapacity(f),
        position_feba_km: f.position_feba_km,
        evacuation: f.evacuation,
        allocated_to: f.allocated_to || null,
        phase_displacements: f.phase_displacements || [],
        // INC-010: forward FH expert override — undefined=AUTO, true=FORCE FORWARD, false=FORCE STANDARD
        forward_fh_override: f.forward_fh_override
      })),
      evacuation_chain: scenario?.force?.forceBuilderResult?.evacuation_chain || null,
      // Phase 5.0: Tactical evacuation configuration (Section 3B.0)
      tactical_evac_config: {
        fleet:     tacticalEvacConfig.fleet,
        // IMP-S107-001: save as array (air_pools); old air_pool key dropped
        air_pools: tacticalEvacConfig.air_pools,
        ad_threat: tacticalEvacConfig.ad_threat,
        weather:   tacticalEvacConfig.weather
        // effective_air_sorties / combined_modifier are derived — not saved (recomputed on load)
      },
      casevac_config: {
        enabled: casevacConfig.enabled,
        availability: casevacConfig.availability,
        availability_percent: casevacConfig.availabilityPercent,
        dow_penalty: casevacConfig.dowPenalty,
        notes: casevacConfig.notes
      },
      evacuation_routes: {
        r1_to_r2b: evacuationRoutes.r1_to_r2b,
        r2b_to_r2e: evacuationRoutes.r2b_to_r2e,
        r2e_to_r3: evacuationRoutes.r2e_to_r3,
        r3_to_r4: evacuationRoutes.r3_to_r4
      },
      point_of_injury_care: { cls_level: poiCare.clsLevel, pfc_training: poiCare.pfcTraining },
      blood_config: {
        enabled: bloodConfig.enabled,
        current_stock: bloodConfig.currentStock,
        wbb_enabled: bloodConfig.wbbEnabled,
        wbb_donors: bloodConfig.wbbDonors,
        wbb_low_titer_donors: bloodConfig.wbbLowTiterDonors,
        wbb_activation_threshold: bloodConfig.wbbActivationThreshold,
        alert_level: bloodConfig.alertLevel,
        // Phase 6.0: MTP + shelf life
        mtp_enabled: bloodConfig.mtpEnabled,
        mtp_protocol: bloodConfig.mtpProtocol,
        shelf_life_tracking: bloodConfig.shelfLifeTracking
      },
      surgical_regulating: { ...surgicalRegulating },
      surgical_config: {
        enabled: surgicalConfig.enabled,
        teams: surgicalConfig.teams,
        shift_mode: surgicalConfig.shiftMode,
        max_tempo: surgicalConfig.maxTempo,
        fatigue_level: surgicalConfig.fatigueLevel
      },
      strategic_evac_config: {
        // Flat keys — backward compat for casualtyCalculator.js (reads these directly)
        train_enabled: strategicEvac.trainEnabled,
        station_type: strategicEvac.stationType,
        train_type: strategicEvac.trainType,
        role3_to_station: strategicEvac.role3ToStation,
        station_to_role4: strategicEvac.stationToRole4,
        // ENH-004: Train schedule — nested under train_schedule key
        train_schedule: {
          frequency_mode: strategicEvac.trainFrequencyMode,
          first_departure_day: strategicEvac.trainFirstDepartureDay,
          departure_hour: strategicEvac.trainDepartureHour,
          trains_per_departure: strategicEvac.trainsPerDeparture,
          min_patients_for_departure: strategicEvac.trainMinPatients
        },
        air_enabled: strategicEvac.airEnabled,
        // Phase 6.0: flatten from airfields[0] for calculator consumption
        airfield_type: strategicEvac.airfields[0]?.type,
        airfield_class: strategicEvac.airfields[0]?.class,
        airfield_label: strategicEvac.airfields[0]?.label,
        role3_to_airfield: strategicEvac.airfields[0]?.distances?.r3_to_airfield_km,
        airfield_to_role4: strategicEvac.airfields[0]?.distances?.airfield_to_r4_km,
        role2b_to_airfield: strategicEvac.airfields[0]?.distances?.r2b_to_airfield_km,
        role2e_to_airfield: strategicEvac.airfields[0]?.distances?.r2e_to_airfield_km,
        pha_beds: strategicEvac.airfields[0]?.pha_beds,
        farp_enabled: strategicEvac.airfields[0]?.farp_enabled,
        bus_enabled: strategicEvac.busEnabled,
        bus_count: strategicEvac.busCount,
        // Full array — for ENH-003 Phase 8 and Tab 6 map readiness
        airfields: strategicEvac.airfields
      },
      // HICOM-001: R3 accessibility flag (V3 = warning only; V4 = dual calc)
      r3_accessible: r3Accessible,
      // BUG-NEXT-015: R2E accessibility flag (brigade echelon — division asset)
      r2e_accessible: r2eAccessible,
      // R2E-DIST: Distance communicated by division — used in evacuation timing
      r2e_distance_km: chainLegs.r2b_to_r2e.km ?? r2eDistance,
      distance_overrides: distanceOverrides,
      mascal_config: {
        enabled: mascalConfig.enabled,
        current_level: mascalConfig.currentLevel,
        blue_light_enabled: mascalConfig.blueLightEnabled,
        thresholds: mascalConfig.thresholds,
        // v4.3.2: Blue Light Protocol automatic detection and decision tracking
        blue_light_recommended: mascalConfig.blueLightRecommended,
        blue_light_active: mascalConfig.blueLightActive,
        blue_light_trigger_reason: mascalConfig.blueLightTriggerReason,
        blue_light_activated_at: mascalConfig.blueLightActivatedAt,
        blue_light_dismissed: mascalConfig.blueLightDismissed,
        blue_light_dismiss_reason: mascalConfig.blueLightDismissReason
      },
      facilities: forceBuilderFacilities
    };
    return { medical_configuration: medicalConfiguration };
  };

  // Keep buildUpdatesRef current on every render so flushState always uses latest state
  buildUpdatesRef.current = buildUpdates;

  // ========================================================================
  // SAVE & COMPLETE
  // ========================================================================

  const handleSaveAndComplete = async () => {
    if (!isValid) {
      alert('Please fix all validation errors before completing this tab.');
      return;
    }

    // v4.3.4: Safety guard - ensure Tab2 (Operations) is complete before running calculator
    if (!scenario?.operation_parameters?.duration_days) {
      alert('Tab 2 (Operations Configuration) must be completed before Tab 3. Please complete Tab 2 first.');
      return;
    }

    setIsCalculating(true);
    setCalculationError(null);

    try {
      const updates = buildUpdates();
      const medicalConfiguration = updates.medical_configuration;

      // v4.1.0: Run calculator automatically on save

      // Build scenario with medical config for calculator input
      const scenarioWithMedical = {
        ...scenario,
        medical_configuration: medicalConfiguration
      };

      const calculatorResults = calculateCasualties(scenarioWithMedical);

      // 🔍 DIAGNOSTIC: Log calculator results

      if (!calculatorResults || calculatorResults.success === false) {
        console.error('❌ [TAB3 ERROR] Calculator validation failed:', calculatorResults);
        throw new Error('Calculator failed to produce valid results');
      }

      // Build update object
      const updateObject = {
        medical_configuration: medicalConfiguration,
        medical_requirements: calculatorResults.medical_requirements,
        casualty_projections: calculatorResults,
        calculated_at: new Date().toISOString()
      };

      // CRIT-007 FIX: Only pass Tab3-owned data, not entire scenario
      onUpdate(updateObject);

      // calculationService: medical config + preliminary casualties changed — Tab5 result is stale
      markDirty();

      // 🔍 DIAGNOSTIC: Confirm onUpdate was called successfully

      onComplete();

    } catch (error) {
      console.error('Tab3: Calculator error:', error);
      setCalculationError(error.message);

      // v4.3.10 FIX: Use real MUI dialog instead of alert() which only shows Close
      setPendingErrorSave(() => () => {
        onUpdate(buildUpdates());
        onComplete();
      });
      setShowCalcErrorDialog(true);

    } finally {
      setIsCalculating(false);
    }
  };

  // Continue in Part 3 with UI components...
  
  // ========================================================================
  // UI COMPONENT - FACILITY SIZE SELECTION DIALOG (PHASE 2)
  // ========================================================================
  
  const renderFacilitySizeDialog = () => (
    <Dialog 
      open={showSizeSelector} 
      onClose={handleCloseSizeSelector}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">
          Add {pendingFacilityType === 'CSU' ? 'Casualty Staging Unit' : 
               pendingFacilityType === 'PHA' ? 'Patient Holding Area' : 'Helicopter Landing Zone'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a NATO standard configuration size
        </Typography>
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
          <InputLabel>Facility Size</InputLabel>
          <Select
            value={newFacilitySize}
            onChange={(e) => setNewFacilitySize(e.target.value)}
            label={t('tab3.facility_size')}
          >
            {FACILITY_SIZE_OPTIONS.map(option => {
              const standards = pendingFacilityType === 'PHA' ?
                CSU_NATO_STANDARDS[option.value] :
                pendingFacilityType === 'CSU' ?
                PHA_NATO_STANDARDS[option.value] :
                HLZ_NATO_STANDARDS[option.value];
              
              return (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {option.label} 
                      {(pendingFacilityType === 'CSU' || pendingFacilityType === 'PHA') && 
                        ` (${standards.capacity_patients} beds)`
                      }
                      {pendingFacilityType === 'HLZ' && 
                        ` (${standards.capacity_helicopters} helicopters)`
                      }
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {standards.recommended_for}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        
        {/* Preview NATO Standard Configuration */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            NATO Standard Configuration for {newFacilitySize}:
          </Typography>
          {(pendingFacilityType === 'CSU' || pendingFacilityType === 'PHA') && (
            <>
              <Typography variant="body2">
                - Capacity: {(pendingFacilityType === 'PHA' ?
                  CSU_NATO_STANDARDS[newFacilitySize] :
                  PHA_NATO_STANDARDS[newFacilitySize]).capacity_patients} patients
              </Typography>
              <Typography variant="body2">
                - Stretcher beds: {(pendingFacilityType === 'PHA' ?
                  CSU_NATO_STANDARDS[newFacilitySize] :
                  PHA_NATO_STANDARDS[newFacilitySize]).beds_stretcher}
              </Typography>
              <Typography variant="body2">
                - Ambulatory beds: {(pendingFacilityType === 'PHA' ?
                  CSU_NATO_STANDARDS[newFacilitySize] :
                  PHA_NATO_STANDARDS[newFacilitySize]).beds_ambulatory}
              </Typography>
              <Typography variant="body2">
                - Personnel: {(pendingFacilityType === 'PHA' ?
                  CSU_NATO_STANDARDS[newFacilitySize] :
                  PHA_NATO_STANDARDS[newFacilitySize]).personnel.total} staff
                ({(pendingFacilityType === 'PHA' ?
                  CSU_NATO_STANDARDS[newFacilitySize] :
                  PHA_NATO_STANDARDS[newFacilitySize]).personnel.medical_officers} officers, 
                {(pendingFacilityType === 'PHA' ?
                  CSU_NATO_STANDARDS[newFacilitySize] :
                  PHA_NATO_STANDARDS[newFacilitySize]).personnel.nurses} nurses)
              </Typography>
              <Typography variant="body2">
                - Daily IV Fluids: {(pendingFacilityType === 'PHA' ?
                  CSU_NATO_STANDARDS[newFacilitySize] :
                  PHA_NATO_STANDARDS[newFacilitySize]).daily_supply_consumption.iv_fluids_liters} L
              </Typography>
              <Typography variant="body2">
                - Daily Medications: {(pendingFacilityType === 'PHA' ?
                  CSU_NATO_STANDARDS[newFacilitySize] :
                  PHA_NATO_STANDARDS[newFacilitySize]).daily_supply_consumption.medications_doses} doses
              </Typography>
            </>
          )}
          {pendingFacilityType === 'HLZ' && (
            <>
              <Typography variant="body2">
                - Helicopter Capacity: {HLZ_NATO_STANDARDS[newFacilitySize].capacity_helicopters} aircraft
              </Typography>
              <Typography variant="body2">
                - Personnel: {HLZ_NATO_STANDARDS[newFacilitySize].personnel.total} staff
              </Typography>
              <Typography variant="body2">
                - Typical Flight Time to Role 3: {HLZ_NATO_STANDARDS[newFacilitySize].typical_flight_time_min} minutes
              </Typography>
            </>
          )}
        </Alert>
        
        {mode === 'EXPERT' && allocationOptions.length > 0 && (
          <Alert severity="info">
            <Typography variant="body2">
              After creation, you can allocate this facility to a specific unit to provide medical support
              and increase effective capacity.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseSizeSelector}>Cancel</Button>
        <Button onClick={handleConfirmAddFacility} variant="contained" color="primary">
          Add {pendingFacilityType}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ========================================================================
  // UI COMPONENT - STAGING FACILITY CARD (PHASE 2 ENHANCED)
  // ========================================================================
  
  const renderStagingFacilityCard = (facility, index) => (
    <Card key={facility.facility_id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        {/* Header with Size Badge */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">
              {facility.facility_type} #{index + 1}
            </Typography>
            <Chip
              label={facilSizeLabel(facility.facility_size)}
              size="small"
              color="primary"
              variant="outlined"
            />
            {facility.allocated_to.unit_type !== 'STANDALONE' && (
              <Chip 
                label={`-> ${facility.allocated_to.unit_name}`}
                size="small" 
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
          {mode === 'EXPERT' && (
            <IconButton 
              onClick={() => handleRemoveStagingFacility(facility.facility_id)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
        
        {/* DOCTRINAL-005 (corrected per AJMedP-1 para 3.18.6): Allocation dropdown filtered by facility type.
            PHA (Patient Holding Area) → R2B/R2E/R3 only (not Role 1 — a collection point at
            battalion level is a CCP, not a PHA; AJP-4.10). CSU (Casualty Staging Unit) →
            R3 or STRATEVAC Airfield ONLY (AJMedP-1 para 3.18.6: CSU at designated air bases /
            APOD for strategic aeromedical evacuation staging). HLZ → R2B/R2E/R3. */}
        {mode === 'EXPERT' && allocationOptions.length > 0 && (() => {
          const filteredOptions = filterAllocationByType(allocationOptions, facility.facility_type);
          const currentAlloc = allocationOptions.find(
            o => o.value === (facility.allocated_to.unit_id || 'STANDALONE')
          );
          const isDoctrinalViolation =
            (facility.facility_type === 'CSU' && currentAlloc?.type && currentAlloc.type !== 'ROLE3' && currentAlloc.type !== 'AIRFIELD' && currentAlloc.type !== 'HEADER') ||
            (facility.facility_type === 'PHA' && currentAlloc?.type === 'ROLE1') ||
            (facility.facility_type === 'HLZ' && currentAlloc?.type === 'ROLE1');
          // OBS-S96-005: CSU standalone warning — CSU must be attached to an APOD or R3
          const isCsuStandalone = facility.facility_type === 'CSU' && currentAlloc?.type === 'STANDALONE';
          return (
            <>
              {isDoctrinalViolation && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Doctrinal placement error: </strong>
                    {facility.facility_type === 'CSU'
                      ? 'CSU must be at Role 3 or co-located at the STRATEVAC airfield (APOD). CSU = Casualty Staging Unit for strategic aeromedical evacuation staging — not a forward treatment facility. (AJMedP-1 para 3.18.6)'
                      : facility.facility_type === 'PHA'
                        ? 'PHA cannot be at Role 1. Role 1 is non-holding (4h max, 6 throughput positions). A battalion-level collection point is a CCP, not a PHA. PHA minimum = Role 2B. (AJP-4.10)'
                        : 'HLZ cannot be at battalion level. Tactical HLZs at BAS are POI pickup sites, not MTF-associated staging. (AJMedP-2)'}
                  </Typography>
                </Alert>
              )}
              {isCsuStandalone && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>CSU not attached to an APOD or Role 3.</strong> A CSU (Casualty Staging Unit) must be at a designated air base or APOD — it is the strategic aeromedical evacuation staging facility, not a forward holding unit. Attach to an Airfield or R3. (AJMedP-1 para 3.18.6)
                  </Typography>
                </Alert>
              )}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('tab3.allocate_to_unit')}</InputLabel>
                <Select
                  value={facility.allocated_to.unit_id || 'STANDALONE'}
                  onChange={(e) => handleAllocationChange(facility.facility_id, e.target.value)}
                  label={t('tab3.allocate_to_unit')}
                >
                  {filteredOptions.map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.type === 'HEADER'}
                      sx={option.type === 'HEADER' ? {
                        fontWeight: 'bold',
                        color: 'text.secondary',
                        backgroundColor: 'action.hover',
                        pointerEvents: 'none'
                      } : {}}
                    >
                      {option.type === 'HEADER' ? option.label : `  ${option.label}`}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {facility.facility_type === 'CSU'
                    ? 'CSU: Role 3 or STRATEVAC Airfield only — Casualty Staging Unit for strategic aeromedical evacuation (AJMedP-1 para 3.18.6)'
                    : facility.facility_type === 'PHA'
                      ? 'PHA: Role 2B minimum — Patient Holding Area for forward casualty management (AJP-4.10)'
                      : `${facility.facility_type} will support this facility, increasing its effective capacity by ${facility.increases_capacity_percent}%`}
                </FormHelperText>
              </FormControl>
            </>
          );
        })()}
        
        {/* SF-004: Distance to Parent MTF - Only show when allocated */}
        {mode === 'EXPERT' && facility.allocated_to.unit_type !== 'STANDALONE' && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('tab3.dist_parent_mtf')}
                value={facility.allocated_to.distance_to_parent_km || 0.5}
                onChange={(e) => {
                  const newDistance = parseFloat(e.target.value) || 0;
                  setStagingFacilities(stagingFacilities.map(f => {
                    if (f.facility_id === facility.facility_id) {
                      return {
                        ...f,
                        allocated_to: {
                          ...f.allocated_to,
                          distance_to_parent_km: newDistance
                        }
                      };
                    }
                    return f;
                  }));
                }}
                inputProps={{ min: 0, max: 10, step: 0.1 }}
                helperText={`Distance from ${facility.facility_type} to ${facility.allocated_to.parent_mtf_type || facility.allocated_to.unit_type}`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption">
                  <strong>Typical distances:</strong><br/>
                  CSU near Role 2B - R2E - R3: 0.5-2 km<br/>
                  PHA near Role 3 &airfield: 0.2-0.5 km<br/>
                  HLZ: Usually co-located (0 km)
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        )}
        
        {/* Capacity Configuration */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('tab3.capacity')}
              value={facility.capacity_patients}
              onChange={(e) => handleCapacityChange(facility.facility_id, parseInt(e.target.value))}
              disabled={mode === 'STANDARD'}
              inputProps={{ min: 10, max: 100 }}
              helperText={mode === 'EXPERT' ? 'Adjusting will scale supplies' : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('tab3.stretcher_beds')}
              value={facility.beds_stretcher}
              onChange={(e) => handleFacilityFieldChange(facility.facility_id, 'beds_stretcher', e.target.value)}
              disabled={mode === 'STANDARD'}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('tab3.ambulatory_beds')}
              value={facility.beds_ambulatory}
              onChange={(e) => handleFacilityFieldChange(facility.facility_id, 'beds_ambulatory', e.target.value)}
              disabled={mode === 'STANDARD'}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>
        
        {/* PHASE Personnel Staffing Accordion */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              {t('tab3.personnel_staffing', { count: facility.personnel.total })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {mode === 'EXPERT' && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={facility.personnel_mode === 'CUSTOM'}
                      onChange={(e) => handleTogglePersonnelMode(
                        facility.facility_id,
                        e.target.checked
                      )}
                    />
                  }
                  label={t('tab3.customize_personnel')}
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                  {facility.personnel_mode === 'AUTO' 
                    ? 'Personnel calculated from NATO standards'
                    : 'Custom personnel configuration'}
                </Typography>
              </Box>
            )}
            
            {facility.personnel_mode === 'AUTO' ? (
              /* Read-only table display for AUTO mode */
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Role</strong></TableCell>
                      <TableCell align="right"><strong>Count</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Medical Officers</TableCell>
                      <TableCell align="right">{facility.personnel.medical_officers}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Nurses</TableCell>
                      <TableCell align="right">{facility.personnel.nurses}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Medics</TableCell>
                      <TableCell align="right">{facility.personnel.medics}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Support Staff</TableCell>
                      <TableCell align="right">{facility.personnel.support_staff}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="right"><strong>{facility.personnel.total}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              /* Editable inputs for CUSTOM mode (Expert only) */
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab3.medical_officers')}
                    value={facility.personnel.medical_officers}
                    onChange={(e) => handlePersonnelChange(
                      facility.facility_id,
                      'medical_officers',
                      e.target.value
                    )}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab3.nurses')}
                    value={facility.personnel.nurses}
                    onChange={(e) => handlePersonnelChange(
                      facility.facility_id,
                      'nurses',
                      e.target.value
                    )}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab3.medics')}
                    value={facility.personnel.medics}
                    onChange={(e) => handlePersonnelChange(
                      facility.facility_id,
                      'medics',
                      e.target.value
                    )}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab3.support_staff')}
                    value={facility.personnel.support_staff}
                    onChange={(e) => handlePersonnelChange(
                      facility.facility_id,
                      'support_staff',
                      e.target.value
                    )}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Total Personnel:</strong> {facility.personnel.total}
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            )}
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>NATO Standard for {facilSizeLabel(facility.facility_size)} {facility.facility_type}:</strong> {' '}
                {facility.facility_type === 'CSU' ?
                  CSU_NATO_STANDARDS[facility.facility_size].personnel.total :
                  PHA_NATO_STANDARDS[facility.facility_size].personnel.total
                } personnel 
                ({facility.facility_type === 'CSU' ?
                  CSU_NATO_STANDARDS[facility.facility_size].personnel.medical_officers :
                  PHA_NATO_STANDARDS[facility.facility_size].personnel.medical_officers
                } officers, {' '}
                {facility.facility_type === 'CSU' ?
                  CSU_NATO_STANDARDS[facility.facility_size].personnel.nurses :
                  PHA_NATO_STANDARDS[facility.facility_size].personnel.nurses
                } nurses)
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>
        
        {/* PHASE Daily Supply Consumption Accordion */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              {t('tab3.daily_supply_consumption')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                These estimates are used by Tab 4 for logistics planning.
                Based on {facility.capacity_patients} patient capacity.
                {mode === 'EXPERT' && ' Automatically scales when capacity changes.'}
              </Typography>
            </Alert>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Supply Type</strong></TableCell>
                    <TableCell align="right"><strong>Daily Rate</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{SUPPLY_ICONS.IV_FLUIDS} IV Fluids</TableCell>
                    <TableCell align="right">
                      {facility.daily_supply_consumption.iv_fluids_liters} liters/day
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{SUPPLY_ICONS.DRESSINGS} Dressings</TableCell>
                    <TableCell align="right">
                      {facility.daily_supply_consumption.dressings_units} units/day
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{SUPPLY_ICONS.MEDICATIONS} Medications</TableCell>
                    <TableCell align="right">
                      {facility.daily_supply_consumption.medications_doses} doses/day
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{SUPPLY_ICONS.FOOD_RATIONS} Food Rations</TableCell>
                    <TableCell align="right">
                      {facility.daily_supply_consumption.food_rations} meals/day
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{SUPPLY_ICONS.BLOOD_PRODUCTS} Blood Products</TableCell>
                    <TableCell align="right">
                      {facility.daily_supply_consumption.blood_units} units/day
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            
            {mode === 'EXPERT' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Note:</strong> Supply consumption scales with actual usage in Tab 5 calculations.
                  These are baseline estimates for a facility operating at rated capacity.
                </Typography>
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
        
        {/* Location & Impact Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
             {t('tab3.location_impact')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab3.dist_allocated_unit')}
                  value={facility.distance_from_allocated_unit_km}
                  onChange={(e) => handleFacilityFieldChange(
                    facility.facility_id,
                    'distance_from_allocated_unit_km',
                    e.target.value
                  )}
                  disabled={mode === 'STANDARD'}
                  inputProps={{ min: 0, max: 50, step: 0.5 }}
                  helperText="Typical: 2-5 km for forward staging"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab3.dist_hlz')}
                  value={facility.distance_to_nearest_hlz_km}
                  onChange={(e) => handleFacilityFieldChange(
                    facility.facility_id,
                    'distance_to_nearest_hlz_km',
                    e.target.value
                  )}
                  disabled={mode === 'STANDARD'}
                  inputProps={{ min: 0, max: 20, step: 0.5 }}
                  helperText="Typical: 1-3 km"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab3.reduces_evac_time')}
                  value={facility.reduces_evacuation_time_min}
                  onChange={(e) => handleFacilityFieldChange(
                    facility.facility_id,
                    'reduces_evacuation_time_min',
                    e.target.value
                  )}
                  disabled={mode === 'STANDARD'}
                  inputProps={{ min: 0, max: 120, step: 5 }}
                  helperText="Time saved vs. direct evacuation"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab3.reduces_dow')}
                  value={facility.reduces_dow_percent}
                  onChange={(e) => handleFacilityFieldChange(
                    facility.facility_id,
                    'reduces_dow_percent',
                    e.target.value
                  )}
                  disabled={mode === 'STANDARD'}
                  inputProps={{ min: 0, max: 30, step: 1 }}
                  helperText="Died of Wounds reduction"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab3.increases_capacity')}
                  value={facility.increases_capacity_percent}
                  onChange={(e) => handleFacilityFieldChange(
                    facility.facility_id,
                    'increases_capacity_percent',
                    e.target.value
                  )}
                  disabled={mode === 'STANDARD'}
                  inputProps={{ min: 0, max: 50, step: 5 }}
                  helperText="Effective capacity increase for allocated facility"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );

  // ========================================================================
  // UI COMPONENT - HLZ FACILITY CARD (PHASE 2 ENHANCED)
  // ========================================================================
  
  const renderHLZCard = (hlz, index) => (
    <Card key={hlz.facility_id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">
              {TRANSPORT_ICONS.HELICOPTER} HLZ #{index + 1}
            </Typography>
            <Chip
              label={facilSizeLabel(hlz.facility_size)}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          {mode === 'EXPERT' && (
            <IconButton 
              onClick={() => handleRemoveHLZ(hlz.facility_id)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('tab3.helicopter_capacity')}
              value={hlz.capacity_helicopters}
              onChange={(e) => handleHLZFieldChange(hlz.facility_id, 'capacity_helicopters', e.target.value)}
              disabled={mode === 'STANDARD'}
              inputProps={{ min: 1, max: 6 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Location Type</InputLabel>
              <Select
                value={hlz.location_type}
                label={t('tab3.location_type')}
                onChange={(e) => handleHLZFieldChange(hlz.facility_id, 'location_type', e.target.value)}
                disabled={mode === 'STANDARD'}
              >
                <MenuItem value="CO_LOCATED_CSU">Co-located with CSU</MenuItem>
                <MenuItem value="CO_LOCATED_PHA">Co-located with PHA</MenuItem>
                <MenuItem value="NEAR_ROLE2E">Near Role 2E</MenuItem>
                <MenuItem value="NEAR_ROLE3">Near Role 3</MenuItem>
                <MenuItem value="STANDALONE">Standalone</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* UI-3D-001: Reference selector — which specific CSU or PHA this HLZ serves */}
          {(hlz.location_type === 'CO_LOCATED_CSU' || hlz.location_type === 'CO_LOCATED_PHA') && (() => {
            const targetType = hlz.location_type === 'CO_LOCATED_CSU' ? 'CSU' : 'PHA';
            const available  = stagingFacilities.filter(f => f.facility_type === targetType);
            if (available.length === 0) {
              return (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <Typography variant="body2">
                      No {targetType} facilities configured. Add a {targetType} in Section 3D above,
                      then select which one this HLZ is co-located with.
                    </Typography>
                  </Alert>
                </Grid>
              );
            }
            // Build per-type labels: CSU #1, CSU #2 / PHA #1, PHA #1
            let typeCount = 0;
            const options = available.map(f => {
              typeCount += 1;
              return { value: f.facility_id, label: `${targetType} #${typeCount} (${facilSizeLabel(f.facility_size)})` };
            });
            return (
              <Grid size={{ xs: 12 }}>
                {/* UX-S97-004: shrink InputLabel so it never overlaps the placeholder text in displayEmpty mode */}
                <FormControl fullWidth>
                  <InputLabel shrink>{targetType} Reference</InputLabel>
                  <Select
                    value={hlz.colocated_facility_id || ''}
                    label={`${targetType} Reference`}
                    notched
                    onChange={(e) => handleHLZFieldChange(hlz.facility_id, 'colocated_facility_id', e.target.value)}
                    disabled={mode === 'STANDARD'}
                    displayEmpty
                  >
                    <MenuItem value=""><em>— Select {targetType} —</em></MenuItem>
                    {options.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  {!hlz.colocated_facility_id && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, ml: 1 }}>
                      Reference required — calculator cannot determine which {targetType} this HLZ serves.
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            );
          })()}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('tab3.dist_to_r3')}
              value={hlz.distance_to_role3_km}
              onChange={(e) => handleHLZFieldChange(hlz.facility_id, 'distance_to_role3_km', e.target.value)}
              disabled={mode === 'STANDARD'}
              inputProps={{ min: 0, max: 200, step: 5 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('tab3.flight_time_r3')}
              value={hlz.flight_time_min}
              onChange={(e) => handleHLZFieldChange(hlz.facility_id, 'flight_time_min', e.target.value)}
              disabled={mode === 'STANDARD'}
              inputProps={{ min: 0, max: 180, step: 5 }}
            />
          </Grid>
        </Grid>
        
        {/* BUG-T19: HLZ distance/flight time consistency check (helicopter cruise ~150 km/h) */}
        {hlz.distance_to_role3_km > 0 && hlz.flight_time_min > 0 && (() => {
          const impliedSpeed = (hlz.distance_to_role3_km / hlz.flight_time_min) * 60;
          const tooFast = impliedSpeed > 280;
          const tooSlow = impliedSpeed < 60;
          return (tooFast || tooSlow) ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Implied air speed: {Math.round(impliedSpeed)} km/h
              {tooFast ? ' — too fast for helicopter (max ~280 km/h). Check distances or flight time.' : ' — very slow for helicopter (min ~60 km/h). Check values.'}
            </Alert>
          ) : null;
        })()}

        {/* Personnel Display for HLZ */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Personnel:</strong> {hlz.personnel?.total || 0} staff
            (NATO standard for {facilSizeLabel(hlz.facility_size)} HLZ: {HLZ_NATO_STANDARDS[hlz.facility_size]?.personnel.total || 0})
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );



  // ========================================================================
  // UI COMPONENTS - STANDARD MODE VIEW
  // ========================================================================
  
  // R1-07 (2026-10-25): care at the point of injury. Shown in the standard view (read only) and in the expert view (editable): the CLS training
  // of the soldiers is a NATO requirement for all personnel (STANAG 2122, AMedP-8.15 Edition B, 2022), so it has to surface in one of the tabs.
  const renderPoiCareCard = () => (
    <Card sx={{ mb: 3, border: '2px solid', borderColor: '#ef6c00', borderRadius: 2, boxShadow: 2 }} data-testid="poi-care-card">
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#ef6c00' }}>{t('tab3.poi_title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('tab3.poi_intro')}</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" disabled={mode === 'STANDARD'}>
              <InputLabel>{t('tab3.poi_cls')}</InputLabel>
              <Select
                value={poiCare.clsLevel}
                label={t('tab3.poi_cls')}
                onChange={(e) => setPoiCare(prev => ({ ...prev, clsLevel: e.target.value }))}
              >
                {Object.entries(CLS_TRAINING_LEVELS).map(([key, val]) => (
                  <MenuItem key={key} value={key}>{val.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{t('tab3.poi_cls_note')}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" disabled={mode === 'STANDARD'}>
              <InputLabel>{t('tab3.poi_pfc')}</InputLabel>
              <Select
                value={poiCare.pfcTraining}
                label={t('tab3.poi_pfc')}
                onChange={(e) => setPoiCare(prev => ({ ...prev, pfcTraining: e.target.value }))}
              >
                {Object.entries(PFC_TRAINING_LEVELS).map(([key, val]) => (
                  <MenuItem key={key} value={key}>{val.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{t('tab3.poi_pfc_note')}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderStandardModeView = () => (
    <Box>
      {/* 2026-10-25: chain distances follow the placed facilities; the planner may override each leg */}
      {renderChainDistancesCard()}
      
      {/* Evacuation Assets - Read Only */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Evacuation Assets
          </Typography>
          {/* OBS-S96-006: ORBAT organic baseline */}
          {orbatR1Fleet.total.all > 0 && (
            <Alert severity="info" icon={false} sx={{ mb: 1.5, py: 0.75 }}>
              <Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 0.25 }}>
                ORBAT organic baseline (Role 1 — battalion level):
              </Typography>
              <Typography variant="caption" display="block">
                {orbatR1Fleet.total.armored} armoured + {orbatR1Fleet.total.wheeled} wheeled = <strong>{orbatR1Fleet.total.all} ambulances</strong>
                {Object.keys(orbatR1Fleet.by_nation).length > 1 && (
                  <span> &nbsp;·&nbsp;
                    {Object.entries(orbatR1Fleet.by_nation).map(([n, c]) =>
                      `${n}: ${c.armored}a+${c.wheeled}w`
                    ).join(' | ')}
                  </span>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                Attach additional assets to individual Role 1 facilities below to supplement.
              </Typography>
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Asset counts are auto-configured based on force size and national standards.
          </Alert>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('tab3.ground_ambulances')}
                value={(evacuationAssets.ground?.light || 0) + (evacuationAssets.ground?.armored || 0)}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('tab3.medevac_heli')}
                value={(evacuationAssets.air?.iar330 || 0) + (evacuationAssets.air?.uh60 || 0)}
                disabled
                InputProps={{ readOnly: true }}
                helperText="Legacy field — air assets now managed in Section 3B.0"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('tab3.fixed_wing')}
                value={evacuationAssets.air?.ch47 || 0}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* R1-07: shown here too (read only): casualty care training is a NATO requirement for all personnel (STANAG 2122) */}
      {renderPoiCareCard()}

      <Alert severity="success">
        <Typography variant="body1">
          <strong>Standard Mode:</strong> All medical parameters are configured automatically.
          Review the configuration and click "Save & Continue" to proceed to Tab 4.
        </Typography>
      </Alert>
    </Box>
  );



  // ========================================================================
  // UI COMPONENTS - EXPERT MODE VIEW
  // ========================================================================
  
  const renderExpertModeView = () => (
    <Box>
{/* 3C SECTION */}
   
      {/* 2026-10-25: chain distances (placed facilities, planner override) */}
      {renderChainDistancesCard()}

      {/* R1-07: care at the point of injury (CLS training, PFC training of the Role 1 medics): the same card in both modes */}
      {renderPoiCareCard()}

      {/* ================================================================== */}
      {/* SECTION 3C: BLOOD MANAGEMENT - Mode-aware (Option C) */}
      {/* ================================================================== */}
      
      <Card sx={{ mb: 3, border: '3px solid', borderColor: '#f44336', borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6"gutterBottom sx={{ fontWeight: 600, color: '#f44336', mb: 3 }}>{MEDICAL_ICONS.BLOOD} {t('tab3.section_3c')}</Typography>

              <Chip 
                label={
                  typeof bloodConfig.alertLevel === 'string' 
                    ? (BLOOD_ALERT_LEVELS[bloodConfig.alertLevel]?.label || bloodConfig.alertLevel)
                    : (bloodConfig.alertLevel?.label || bloodConfig.alertLevel?.level || 'Unknown')
                }
                size="small"
                sx={{ 
                  bgcolor: typeof bloodConfig.alertLevel === 'string'
                    ? (BLOOD_ALERT_LEVELS[bloodConfig.alertLevel]?.color || '#4caf50')
                    : (bloodConfig.alertLevel?.color || '#4caf50'),
                  color: 'white'
                }}
              />
              {mode === 'STANDARD' && <Chip label={t('common.read_only')} size="small" variant="outlined" />}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={bloodConfig.enabled}
                  onChange={(e) => setBloodConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                  disabled={mode === 'STANDARD'}
                />
              }
              label={t('common.enable')}
            />
          </Box>
          
          {bloodConfig.enabled && (
            <>
              {/* STANDARD MODE: Read-only summary */}
              {mode === 'STANDARD' ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Blood configuration uses NATO defaults. Switch to Expert Mode to customize.
                    </Typography>
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography variant="caption" color="text.secondary">PRBC</Typography>
                      <Typography variant="h6">{bloodConfig.currentStock.prbc_units} units</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography variant="caption" color="text.secondary">FFP</Typography>
                      <Typography variant="h6">{bloodConfig.currentStock.ffp_units} units</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography variant="caption" color="text.secondary">FDP</Typography>
                      <Typography variant="h6">{bloodConfig.currentStock.fdp_units} units</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography variant="caption" color="text.secondary">Platelets</Typography>
                      <Typography variant="h6">{bloodConfig.currentStock.plt_units} units</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography variant="caption" color="text.secondary">Cryo</Typography>
                      <Typography variant="h6">{bloodConfig.currentStock.cryo_pools} pools</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <Typography variant="caption" color="text.secondary">WBB</Typography>
                      <Typography variant="h6">{bloodConfig.wbbEnabled ? `${bloodConfig.wbbDonors} donors` : 'Disabled'}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                /* EXPERT MODE: Full editable controls */
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      {t('tab3.blood_owner_note')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Object.keys(BLOOD_PRODUCTS).length} {t('tab3.blood_product_types')}
                    </Typography>
                  </Alert>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('tab3.current_depot')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t('tab3.blood_supply_chain_note')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          label={t('tab3.prbc')}
                          type="number"
                          size="small"
                          disabled
                          InputProps={{ readOnly: true }}
                          value={bloodConfig.currentStock.prbc_units}
                          onChange={(e) => setBloodConfig(prev => ({
                            ...prev,
                            currentStock: { ...prev.currentStock, prbc_units: parseInt(e.target.value) || 0 }
                          }))}
                          helperText={`${t('tab3.prbc_shelf_life')} | ${t(tab4InitialStock ? 'tab3.blood_from_tab4' : 'tab3.blood_no_tab4')}`}
                        />
                        <TextField
                          label={t('tab3.ffp')}
                          type="number"
                          size="small"
                          disabled
                          InputProps={{ readOnly: true }}
                          value={bloodConfig.currentStock.ffp_units}
                          onChange={(e) => setBloodConfig(prev => ({
                            ...prev,
                            currentStock: { ...prev.currentStock, ffp_units: parseInt(e.target.value) || 0 }
                          }))}
                          helperText={`${t('tab3.ffp_shelf_life')} | ${t(tab4InitialStock ? 'tab3.blood_from_tab4' : 'tab3.blood_no_tab4')}`}
                        />
                        <TextField
                          label={t('tab3.fdp')}
                          type="number"
                          size="small"
                          value={bloodConfig.currentStock.fdp_units}
                          onChange={(e) => setBloodConfig(prev => ({
                            ...prev,
                            currentStock: { ...prev.currentStock, fdp_units: parseInt(e.target.value) || 0 }
                          }))}
                          helperText={t('tab3.fdp_shelf_life')}
                        />
                        <TextField
                          label={t('tab3.plt')}
                          type="number"
                          size="small"
                          disabled
                          InputProps={{ readOnly: true }}
                          value={bloodConfig.currentStock.plt_units}
                          onChange={(e) => setBloodConfig(prev => ({
                            ...prev,
                            currentStock: { ...prev.currentStock, plt_units: parseInt(e.target.value) || 0 }
                          }))}
                          helperText={`${t('tab3.plt_shelf_life')} | ${t(tab4InitialStock ? 'tab3.blood_from_tab4' : 'tab3.blood_no_tab4')}`}
                        />
                        <TextField
                          label={t('tab3.cryo')}
                          type="number"
                          size="small"
                          value={bloodConfig.currentStock.cryo_pools}
                          onChange={(e) => setBloodConfig(prev => ({
                            ...prev,
                            currentStock: { ...prev.currentStock, cryo_pools: parseInt(e.target.value) || 0 }
                          }))}
                          helperText={t('tab3.cryo_shelf_life')}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>{t('tab3.mtp_title')}</Typography>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="caption">
                          {t('tab3.mtp_info')}
                        </Typography>
                      </Alert>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={bloodConfig.mtpEnabled}
                            onChange={(e) => setBloodConfig(prev => ({ ...prev, mtpEnabled: e.target.checked }))}
                          />
                        }
                        label={t('tab3.mtp_enabled')}
                      />
                      {bloodConfig.mtpEnabled && (
                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                          <InputLabel>{t('tab3.mtp_ratio')}</InputLabel>
                          <Select
                            value={bloodConfig.mtpProtocol}
                            label={t('tab3.mtp_ratio')}
                            onChange={(e) => setBloodConfig(prev => ({ ...prev, mtpProtocol: e.target.value }))}
                          >
                            <MenuItem value="1:1:1">{t('tab3.mtp_option_111')}</MenuItem>
                            <MenuItem value="2:1:1">{t('tab3.mtp_option_211')}</MenuItem>
                          </Select>
                          <FormHelperText>{t('tab3.mtp_phase_helper')}</FormHelperText>
                        </FormControl>
                      )}

                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>{t('tab3.shelf_life_tracking')}</Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={bloodConfig.shelfLifeTracking}
                            onChange={(e) => setBloodConfig(prev => ({ ...prev, shelfLifeTracking: e.target.checked }))}
                          />
                        }
                        label={t('tab3.enable_shelf_warnings')}
                      />
                      {bloodConfig.shelfLifeTracking && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <Typography variant="caption">
                            <strong>{t('tab3.shelf_life_critical_item')}</strong><br />
                            {t('tab3.shelf_life_all_items')}
                          </Typography>
                        </Alert>
                      )}
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>{t('tab3.wbb')}</Typography>
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        <Typography variant="caption">
                          {WALKING_BLOOD_BANK?.description || t('tab3.wbb_description_fallback')}
                        </Typography>
                      </Alert>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="caption">
                          <strong>{t('tab3.terminology_note_label')} </strong> {t('tab3.wbb_terminology_note')}
                        </Typography>
                      </Alert>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={bloodConfig.wbbEnabled}
                            onChange={(e) => setBloodConfig(prev => ({ ...prev, wbbEnabled: e.target.checked }))}
                          />
                        }
                        label={t('tab3.wbb_activated')}
                      />
                      {bloodConfig.wbbEnabled && (
                        <>
                          <TextField
                            label={t('tab3.registered_donors')}
                            type="number"
                            size="small"
                            fullWidth
                            sx={{ mt: 1 }}
                            value={bloodConfig.wbbDonors}
                            onChange={(e) => setBloodConfig(prev => ({ ...prev, wbbDonors: parseInt(e.target.value) || 0 }))}
                            helperText={`Standard: ${WALKING_BLOOD_BANK?.planning?.donors_per_1000_troops || 50} per 1000 troops`}
                            inputProps={{ min: 0, max: 5000 }}
                          />
                          <TextField
                            label={t('tab3.ltowb')}
                            type="number"
                            size="small"
                            fullWidth
                            sx={{ mt: 1 }}
                            value={bloodConfig.wbbLowTiterDonors}
                            onChange={(e) => setBloodConfig(prev => ({ ...prev, wbbLowTiterDonors: parseInt(e.target.value) || 0 }))}
                            helperText="Universal donors — can donate to any recipient regardless of blood type"
                            inputProps={{ min: 0, max: 5000 }}
                          />
                          <TextField
                            label={t('tab3.activation_threshold')}
                            type="number"
                            size="small"
                            fullWidth
                            sx={{ mt: 1 }}
                            value={bloodConfig.wbbActivationThreshold}
                            onChange={(e) => setBloodConfig(prev => ({ ...prev, wbbActivationThreshold: parseInt(e.target.value) || 30 }))}
                            helperText="WBB activates when stock falls below this % (default: 30%)"
                            inputProps={{ min: 0, max: 100 }}
                          />
                          {/* WBB projected yield: FWB and LTOWB — calculated from donor counts */}
                          {bloodConfig.wbbDonors > 0 && (() => {
                            const avail = WALKING_BLOOD_BANK?.planning?.availability_rate ?? 0.70;
                            const success = WALKING_BLOOD_BANK?.planning?.successful_collection_rate ?? 0.95;
                            const fwbYield = Math.round(bloodConfig.wbbDonors * avail * success);
                            const ltowbYield = Math.round(bloodConfig.wbbLowTiterDonors * avail * success);
                            return (
                              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(211,47,47,0.06)', borderRadius: 1, border: '1px solid rgba(211,47,47,0.2)' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  Projected WBB Yield per Activation
                                </Typography>
                                <Typography variant="caption" component="div" color="text.secondary">
                                  FWB (Fresh Whole Blood): <strong>{fwbYield} units</strong>
                                  <span style={{ marginLeft: 8, fontSize: '0.75em' }}>({avail * 100}% avail × {success * 100}% success)</span>
                                </Typography>
                                {bloodConfig.wbbLowTiterDonors > 0 && (
                                  <Typography variant="caption" component="div" color="text.secondary">
                                    LTOWB (Low-Titer O+): <strong>{ltowbYield} units</strong>
                                    <span style={{ marginLeft: 8, fontSize: '0.75em' }}>Universal — no crossmatch in emergency</span>
                                  </Typography>
                                )}
                                <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                  FWB shelf life: 24h | LTOWB shelf life: 21 days | Collection time: {WALKING_BLOOD_BANK?.collection?.total_time_minutes ?? 40} min/donor
                                </Typography>
                              </Box>
                            );
                          })()}
                        </>
                      )}
                      
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Blood Alert Levels:</strong><br/>
                          GREEN: &gt;5 DOS | YELLOW: 3-5 DOS | RED: 1-3 DOS | BLACK: &lt;1 DOS
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
    

{/* 3D SECTION */}

  {/* ================================================================== */}
      {/* SECTION 3D: SURGICAL CAPACITY - Mode-aware (Option C) */}
      {/* ================================================================== */}
      
      <Card sx={{ mb: 3, border: '3px solid', borderColor: '#9c27b0', borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6"gutterBottom sx={{ fontWeight: 600, color: '#9c27b0', mb: 2 }}>{MEDICAL_ICONS.STETHOSCOPE} {t('tab3.section_3d')}</Typography>
              <Chip 
                label={`${surgicalConfig.surgicalTeams} Teams`}
                size="small"
                color="secondary"
              />
              {mode === 'STANDARD' && <Chip label={t('common.read_only')} size="small" variant="outlined" />}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={surgicalConfig.enabled}
                  onChange={(e) => setSurgicalConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                  disabled={mode === 'STANDARD'}
                />
              }
              label={t('common.enable')}
            />
          </Box>
          
          {surgicalConfig.enabled && (
            <>
              {/* SURGICAL ASSETS OVERVIEW - All team types visible */}
              {/* BUG-3D-001 FIX: Removed phantom "auto-assigned" / "attachable assets" / facility-assignment
                  language. The assigned:[] array and facility selector never materialised (V3.1 scope).
                  Also corrected stale team-type names (OPCT/OHAT → FRT/SURG_CO) and removed "sliders"
                  reference (inputs are text fields in Expert mode, not sliders). */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {mode === 'STANDARD' ? t('tab3.surgical_standard_info') : t('tab3.surgical_expert_info')}
                </Typography>
              </Alert>
              
              {/* ALL THREE TEAM TYPES - Cards Side by Side */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* FST Card */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: '100%', borderColor: surgicalConfig.teams.FST.count > 0 ? 'success.main' : 'grey.300' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                          {SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.name || 'Forward Surgical Team'}
                        </Typography>
                        <Chip 
                          label={SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.code || 'FST'} 
                          size="small" 
                          color="primary"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {t('tab3.fst_role_subtitle', { personnel: SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.personnel?.total || 16 })}
                      </Typography>
                      
                      {mode === 'EXPERT' ? (
                        <TextField
                          label={t('tab3.team_count')}
                          type="number"
                          size="small"
                          fullWidth
                          value={surgicalConfig.teams.FST.count}
                          onChange={(e) => setSurgicalConfig(prev => ({
                            ...prev,
                            teams: {
                              ...prev.teams,
                              FST: { ...prev.teams.FST, count: parseInt(e.target.value) || 0 }
                            }
                          }))}
                          inputProps={{ min: 0, max: 10 }}
                        />
                      ) : (
                        <Typography variant="h4" color="primary.main" sx={{ textAlign: 'center', my: 1 }}>
                          {surgicalConfig.teams.FST.count}
                        </Typography>
                      )}
                      
                      <Divider sx={{ my: 1.5 }} />
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">{t('tab3.or_tables_cap')}</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {surgicalConfig.teams.FST.count * (SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.capability?.simultaneous_tables || 2)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">{t('tab3.proc_day')}</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {surgicalConfig.teams.FST.count * (SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.capability?.procedures_per_day || 10)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Chip label={t('tab3.dcs_capable')} color="success" size="small" sx={{ mt: 1 }} />
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* FRT Card */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: '100%', borderColor: surgicalConfig.teams.FRT.count > 0 ? 'info.main' : 'grey.300' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                          {t('tab3.frt_team_name')}
                        </Typography>
                        <Chip label="FRT" size="small" color="info" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {t('tab3.frt_role_subtitle', { personnel: SURGICAL_TEAM_COMPOSITION?.FORWARD_RESUSCITATION_TEAM?.personnel?.total || 8 })}
                      </Typography>
                      
                      {mode === 'EXPERT' ? (
                        <TextField
                          label={t('tab3.team_count')}
                          type="number"
                          size="small"
                          fullWidth
                          value={surgicalConfig.teams.FRT.count}
                          onChange={(e) => setSurgicalConfig(prev => ({
                            ...prev,
                            teams: {
                              ...prev.teams,
                              FRT: { ...prev.teams.FRT, count: parseInt(e.target.value) || 0 }
                            }
                          }))}
                          inputProps={{ min: 0, max: 10 }}
                        />
                      ) : (
                        <Typography variant="h4" color="info.main" sx={{ textAlign: 'center', my: 1 }}>
                          {surgicalConfig.teams.FRT.count}
                        </Typography>
                      )}
                      
                      <Divider sx={{ my: 1.5 }} />
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">{t('tab3.or_tables_cap')}</Typography>
                          <Typography variant="body2" fontWeight="bold">0</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">{t('tab3.capabilities_cap')}</Typography>
                          <Typography variant="body2" fontWeight="bold">{t('tab3.resuscitation')}</Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 1 }}>
                        <Chip label={t('tab3.blood_transfusion_chip')} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        <Chip label={t('tab3.airway_chip')} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        <Chip label={t('tab3.chest_tubes_chip')} size="small" sx={{ mb: 0.5 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* SURG_CO Card */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: '100%', borderColor: surgicalConfig.teams.SURG_CO.count > 0 ? 'secondary.main' : 'grey.300' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                          {SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.name || 'Surgical Company'}
                        </Typography>
                        <Chip 
                          label={SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.code || 'SURG_CO'} 
                          size="small" 
                          color="secondary"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {t('tab3.surg_co_role_subtitle', { personnel: SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.personnel?.total || 60 })}
                      </Typography>
                      
                      {mode === 'EXPERT' ? (
                        <TextField
                          label={t('tab3.team_count')}
                          type="number"
                          size="small"
                          fullWidth
                          value={surgicalConfig.teams.SURG_CO.count}
                          onChange={(e) => setSurgicalConfig(prev => ({
                            ...prev,
                            teams: {
                              ...prev.teams,
                              SURG_CO: { ...prev.teams.SURG_CO, count: parseInt(e.target.value) || 0 }
                            }
                          }))}
                          inputProps={{ min: 0, max: 5 }}
                        />
                      ) : (
                        <Typography variant="h4" color="secondary.main" sx={{ textAlign: 'center', my: 1 }}>
                          {surgicalConfig.teams.SURG_CO.count}
                        </Typography>
                      )}
                      
                      <Divider sx={{ my: 1.5 }} />
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">{t('tab3.or_tables_cap')}</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {surgicalConfig.teams.SURG_CO.count * (SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.capability?.simultaneous_tables || 4)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">{t('tab3.proc_day')}</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {surgicalConfig.teams.SURG_CO.count * (SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.capability?.procedures_per_day || 24)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Chip label={t('tab3.full_surgical_chip')} color="success" size="small" sx={{ mt: 1 }} />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              {/* TOTAL SURGICAL CAPACITY SUMMARY */}
              <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('tab3.total_surgical_summary')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">{t('tab3.total_teams')}</Typography>
                    <Typography variant="h5">
                      {surgicalConfig.teams.FST.count + surgicalConfig.teams.FRT.count + surgicalConfig.teams.SURG_CO.count}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">{t('tab3.total_personnel')}</Typography>
                    <Typography variant="h5">
                      {(surgicalConfig.teams.FST.count * (SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.personnel?.total || 16)) +
                       (surgicalConfig.teams.FRT.count * 8) +
                       (surgicalConfig.teams.SURG_CO.count * (SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.personnel?.total || 60))}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">{t('tab3.total_or')}</Typography>
                    <Typography variant="h5">
                      {(surgicalConfig.teams.FST.count * (SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.capability?.simultaneous_tables || 2)) +
                       (surgicalConfig.teams.SURG_CO.count * (SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.capability?.simultaneous_tables || 4))}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">{t('tab3.procedures_day')}</Typography>
                    <Typography variant="h5">
                      {(surgicalConfig.teams.FST.count * (SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.capability?.procedures_per_day || 10)) +
                       (surgicalConfig.teams.SURG_CO.count * (SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.capability?.procedures_per_day || 24))}
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* 2026-10-18: surgical regulating: where the cases that the Role 2 Basic cannot take go */}
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{t('tab3.regulating_title')}</Typography>
                    <FormControlLabel
                      control={<Switch checked={surgicalRegulating.enabled !== false} onChange={(e) => setSurgicalRegulating(prev => ({ ...prev, enabled: e.target.checked }))} />}
                      label={t('tab3.regulating_enabled')}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('tab3.regulating_help')}</Typography>
                  {surgicalRegulating.enabled !== false && (
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControlLabel
                          control={<Switch checked={surgicalRegulating.neighbour_r2b_enabled === true} onChange={(e) => setSurgicalRegulating(prev => ({ ...prev, neighbour_r2b_enabled: e.target.checked }))} />}
                          label={t('tab3.regulating_neighbour')}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 4 }}>
                        <TextField size="small" type="number" fullWidth label={t('tab3.regulating_neighbour_share')}
                          value={surgicalRegulating.neighbour_r2b_share_percent}
                          onChange={(e) => setSurgicalRegulating(prev => ({ ...prev, neighbour_r2b_share_percent: Math.min(100, Math.max(0, Number(e.target.value) || 0)) }))}
                          inputProps={{ min: 0, max: 100 }} disabled={surgicalRegulating.neighbour_r2b_enabled !== true} />
                      </Grid>
                      <Grid size={{ xs: 6, md: 4 }}>
                        <TextField size="small" type="number" fullWidth label={t('tab3.regulating_neighbour_km')}
                          value={surgicalRegulating.neighbour_r2b_distance_km}
                          onChange={(e) => setSurgicalRegulating(prev => ({ ...prev, neighbour_r2b_distance_km: Math.max(0, Number(e.target.value) || 0) }))}
                          inputProps={{ min: 0 }} disabled={surgicalRegulating.neighbour_r2b_enabled !== true} />
                      </Grid>
                      <Grid size={{ xs: 6, md: 4 }}>
                        <TextField size="small" type="number" fullWidth label={t('tab3.regulating_r2e_share')}
                          value={surgicalRegulating.r2e_share_percent}
                          onChange={(e) => setSurgicalRegulating(prev => ({ ...prev, r2e_share_percent: Math.min(100, Math.max(0, Number(e.target.value) || 0)) }))}
                          inputProps={{ min: 0, max: 100 }} disabled={!r2eAccessible} />
                      </Grid>
                      <Grid size={{ xs: 6, md: 4 }}>
                        <TextField size="small" type="number" fullWidth label={t('tab3.regulating_r3_share')}
                          value={surgicalRegulating.r3_share_percent}
                          onChange={(e) => setSurgicalRegulating(prev => ({ ...prev, r3_share_percent: Math.min(100, Math.max(0, Number(e.target.value) || 0)) }))}
                          inputProps={{ min: 0, max: 100 }} disabled={!r3Accessible} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">{t('tab3.regulating_access_note')}</Typography>
                      </Grid>
                    </Grid>
                  )}
                </Box>

                {/* 2026-10-18: live preview of the surgical load (calculation with the settings on screen, nothing saved) */}
                <Tab3SurgicalPreview
                  getScenario={() => {
                    // a single brigade in the summary view has no per-brigade list yet: the brigade is that of Tab 1 (as in the Tab 2 preview)
                    const _fbrBrigade = scenario?.force?.forceBuilderResult?.brigade;
                    const _brigades = (scenario?.force?.brigades && scenario.force.brigades.length > 0)
                      ? scenario.force.brigades
                      : (_fbrBrigade ? [_fbrBrigade] : []);
                    return {
                      ...scenario,
                      force: { ...scenario.force, brigades: _brigades },
                      medical_configuration: buildUpdatesRef.current().medical_configuration
                    };
                  }}
                />

                {/* Fatigue Model Display (Expert Mode Only) */}
                {mode === 'EXPERT' && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={2} alignItems="flex-start">
                      {/* 2026-10-18: policy = the highest tempo allowed; the tempo of each day follows the load */}
                      <Grid size={{ xs: 12, md: 8 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t('tab3.max_tempo_label')}</InputLabel>
                          <Select
                            value={surgicalConfig.maxTempo || DEFAULT_MAX_TEMPO}
                            label={t('tab3.max_tempo_label')}
                            onChange={(e) => setSurgicalConfig(prev => ({ ...prev, maxTempo: e.target.value }))}
                          >
                            {SURGICAL_TEMPOS.map((tempo) => {
                              const rec = SURGICAL_FATIGUE_MODEL?.SHIFT_RECOMMENDATIONS?.[TEMPO_RECOMMENDATION_KEY[tempo]];
                              return (
                                <MenuItem key={tempo} value={tempo}>
                                  {t('tab3.tempo_' + tempo.toLowerCase())} ({rec.shift_length_hours} h x {rec.shifts_per_day}) - {Math.round(averageShiftPerformance(rec.shift_length_hours) * 100)}% {t('tab3.tempo_average_quality')}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          <FormHelperText>{t('tab3.max_tempo_help')}</FormHelperText>
                        </FormControl>
                      </Grid>
                      {SHOW_FORMER_SHIFT_INPUTS && (
                        <>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Shift Mode</InputLabel>
                          <Select
                            value={surgicalConfig.shiftMode}
                            label={t('tab3.shift_mode')}
                            onChange={(e) => setSurgicalConfig(prev => ({ ...prev, shiftMode: e.target.value }))}
                          >
                            <MenuItem value="NORMAL">
                              Normal (8h) - {Math.round((SURGICAL_FATIGUE_MODEL?.PERFORMANCE_CURVE?.find(p => p.hours === 8)?.performance || 0.95) * 100)}% quality
                            </MenuItem>
                            <MenuItem value="EXTENDED">
                              Extended (12h) - {Math.round((SURGICAL_FATIGUE_MODEL?.PERFORMANCE_CURVE?.find(p => p.hours === 12)?.performance || 0.88) * 100)}% quality
                            </MenuItem>
                            <MenuItem value="SURGE">
                              Surge (16h) - {Math.round((SURGICAL_FATIGUE_MODEL?.PERFORMANCE_CURVE?.find(p => p.hours === 16)?.performance || 0.78) * 100)}% quality
                            </MenuItem>
                            <MenuItem value="CRISIS">
                              Crisis (24h) - {Math.round((SURGICAL_FATIGUE_MODEL?.PERFORMANCE_CURVE?.find(p => p.hours === 24)?.performance || 0.50) * 100)}% quality
                            </MenuItem>
                          </Select>
                          <FormHelperText>
                            Fatigue degrades procedures/day. During sustained MASCAL, reduced surgical throughput increases time-to-surgery and raises DoW rates in the calculator.
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label={t('tab3.hours_into_shift')}
                          type="number"
                          size="small"
                          fullWidth
                          value={surgicalConfig.hoursWorked}
                          onChange={(e) => setSurgicalConfig(prev => ({ 
                            ...prev, 
                            hoursWorked: Math.min(24, Math.max(0, parseInt(e.target.value) || 0))
                          }))}
                          inputProps={{ min: 0, max: 24 }}
                          helperText="Simulate fatigue: 0-8h normal, 12h+ degraded"
                        />
                      </Grid>
                        </>
                      )}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="subtitle2">{SHOW_FORMER_SHIFT_INPUTS ? 'Surgeon Performance' : t('tab3.tempo_performance_label')}</Typography>
                          {(() => {
                            const _tempoKey = surgicalConfig.maxTempo || DEFAULT_MAX_TEMPO;
                            const _rec = SURGICAL_FATIGUE_MODEL?.SHIFT_RECOMMENDATIONS?.[TEMPO_RECOMMENDATION_KEY[_tempoKey]];
                            const _perf = SHOW_FORMER_SHIFT_INPUTS
                              ? (getSurgeonPerformance?.(surgicalConfig.hoursWorked)?.performance || 1)
                              : averageShiftPerformance(_rec.shift_length_hours);
                            return (
                              <Typography variant="h5" color={_perf < 0.8 ? 'error.main' : 'success.main'}>
                                {`${Math.round(_perf * 100)}%`}
                              </Typography>
                            );
                          })()}
                        </Box>
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Typical DCS: {PROCEDURE_DURATIONS?.DAMAGE_CONTROL?.laparotomy?.duration_minutes?.typical || 60} min | 
                      Thoracotomy: {PROCEDURE_DURATIONS?.DAMAGE_CONTROL?.thoracotomy?.duration_minutes?.typical || 45} min |
                      Min rest: {SURGICAL_FATIGUE_MODEL?.RECOVERY?.minimum_rest_hours || 8}h
                    </Typography>
                  </Box>
                )}
              </Paper>
            </>
          )}
        </CardContent>
      </Card>

 
 {/* 3E SECTION */}

      <Card sx={{ mb: 3, border: '3px solid', borderColor: '#00bcd4', borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#00bcd4', mb: 3 }}>
            -> {t('tab3.section_3e')}
          </Typography>

      {/* ================================================================== */}
      {/* SECTION 3E: STRATEGIC EVACUATION (NEW)                            */}
      {/* Train/Airfield distances - Expert mode editable                   */}
      {/* ================================================================== */}
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">{MEDICAL_ICONS.HELICOPTER} {t('tab3.e1_infra')}</Typography>
              <Chip label="Role 3 -> Role 4" size="small" variant="outlined" />
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Configure infrastructure for strategic evacuation: airfield and train station locations, 
              ground transport distances to these facilities, and air/rail distances to Role 4 national hospitals.
              {mode === 'STANDARD' 
                ? ' Distances use NATO standard defaults. Switch to Expert Mode to customize.'
                : ' Configure air and rail evacuation infrastructure and enable/disable modes.'}
            </Typography>
          </Alert>
          
          {mode === 'STANDARD' ? (
            /* Standard Mode: Read-only summary */
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>{t('tab3.air_evac_primary')}</Typography>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Airfield</Typography>
                      <Typography variant="body1">{strategicEvac.airfields[0]?.label || 'Primary STRATEVAC Airfield'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Class</Typography>
                      <Typography variant="body1">{AIRFIELD_CLASS_SPECS[strategicEvac.airfields[0]?.class]?.label || AIRFIELD_CLASS_SPECS['CLASS_B']?.label || 'Class B'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Role 3 -> Airfield</Typography>
                      <Typography variant="body1">{strategicEvac.airfields[0]?.distances?.r3_to_airfield_km} km</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Airfield -> Role 4</Typography>
                      <Typography variant="body1">{strategicEvac.airfields[0]?.distances?.airfield_to_r4_km} km</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>{t('tab3.rail_evac_if_avail')}</Typography>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Role 3 -> Train Station</Typography>
                      <Typography variant="body1">{strategicEvac.role3ToStation} km</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Station -> Role 4</Typography>
                      <Typography variant="body1">{strategicEvac.stationToRole4} km</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            /* Expert Mode: Full editable configuration — E.1 is SOLE OWNER of all distances */
            <Grid container spacing={3}>
              {/* Air Evacuation Configuration */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">{MEDICAL_ICONS.HELICOPTER} {t('tab3.air_evac')}</Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={strategicEvac.airEnabled}
                            onChange={(e) => setStrategicEvac(prev => ({ ...prev, airEnabled: e.target.checked }))}
                          />
                        }
                        label={t('common.available')}
                      />
                    </Box>
                    
                    {strategicEvac.airEnabled && (
                      <>
                        {/* Airfield identity */}
                        <TextField
                          label={t('tab3.airfield_name')}
                          size="small"
                          fullWidth
                          sx={{ mb: 2 }}
                          value={strategicEvac.airfields[0]?.label || ''}
                          onChange={(e) => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [{ ...prev.airfields[0], label: e.target.value }]
                          }))}
                          helperText="e.g. Mihail Kogalniceanu, Ramstein AB, Host Nation Airfield"
                        />

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>{t('tab3.airfield_type')}</InputLabel>
                          <Select
                            value={strategicEvac.airfields[0]?.type || 'MILITARY'}
                            label={t('tab3.airfield_type')}
                            onChange={(e) => setStrategicEvac(prev => ({
                              ...prev,
                              airfields: [{ ...prev.airfields[0], type: e.target.value }]
                            }))}
                          >
                            {AIRFIELD_TYPES && Object.keys(AIRFIELD_TYPES).map(key => (
                              <MenuItem key={key} value={key}>
                                {AIRFIELD_TYPES[key]?.label || key}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>{t('tab3.airfield_class_label')}</InputLabel>
                          <Select
                            value={strategicEvac.airfields[0]?.class || 'CLASS_B'}
                            label={t('tab3.airfield_class_label')}
                            onChange={(e) => setStrategicEvac(prev => ({
                              ...prev,
                              airfields: [{ ...prev.airfields[0], class: e.target.value }]
                            }))}
                          >
                            {Object.entries(AIRFIELD_CLASS_SPECS).map(([key, spec]) => (
                              <MenuItem key={key} value={key}>{spec.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {AIRFIELD_CLASS_SPECS[strategicEvac.airfields[0]?.class]
                              ? `Aircraft: ${AIRFIELD_CLASS_SPECS[strategicEvac.airfields[0].class].aircraft.join(', ')} | Max litter: ${AIRFIELD_CLASS_SPECS[strategicEvac.airfields[0].class].litter_capacity} | Throughput: ${AIRFIELD_CLASS_SPECS[strategicEvac.airfields[0].class].hourly_throughput}/hr`
                              : 'Select airfield class to see capability'
                            }
                          </FormHelperText>
                        </FormControl>

                        {/* All 4 distances — E.1 is the sole owner */}
                        {/* BUG-T18: show error helper when distance is 0 (unset) */}
                        <TextField
                          label={t('tab3.dist_r2b_airfield')}
                          type="number"
                          size="small"
                          fullWidth
                          sx={{ mb: 2 }}
                          value={strategicEvac.airfields[0]?.distances?.r2b_to_airfield_km ?? ''}
                          onChange={(e) => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [{ ...prev.airfields[0], distances: { ...prev.airfields[0].distances, r2b_to_airfield_km: parseInt(e.target.value) || 0 } }]
                          }))}
                          inputProps={{ min: 10, max: 200 }}
                          error={!(strategicEvac.airfields[0]?.distances?.r2b_to_airfield_km > 0)}
                          helperText={!(strategicEvac.airfields[0]?.distances?.r2b_to_airfield_km > 0) ? 'Required — enter actual distance' : ''}
                        />

                        <TextField
                          label={t('tab3.dist_r2e_airfield')}
                          type="number"
                          size="small"
                          fullWidth
                          sx={{ mb: 2 }}
                          value={strategicEvac.airfields[0]?.distances?.r2e_to_airfield_km ?? ''}
                          onChange={(e) => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [{ ...prev.airfields[0], distances: { ...prev.airfields[0].distances, r2e_to_airfield_km: parseInt(e.target.value) || 0 } }]
                          }))}
                          inputProps={{ min: 10, max: 150 }}
                          error={!(strategicEvac.airfields[0]?.distances?.r2e_to_airfield_km > 0)}
                          helperText={!(strategicEvac.airfields[0]?.distances?.r2e_to_airfield_km > 0) ? 'Required — enter actual distance' : ''}
                        />

                        <TextField
                          label={t('tab3.dist_r3_airfield')}
                          type="number"
                          size="small"
                          fullWidth
                          sx={{ mb: 2 }}
                          value={strategicEvac.airfields[0]?.distances?.r3_to_airfield_km ?? ''}
                          onChange={(e) => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [{ ...prev.airfields[0], distances: { ...prev.airfields[0].distances, r3_to_airfield_km: parseInt(e.target.value) || 0 } }]
                          }))}
                          inputProps={{ min: 5, max: 100 }}
                          error={!(strategicEvac.airfields[0]?.distances?.r3_to_airfield_km > 0)}
                          helperText={!(strategicEvac.airfields[0]?.distances?.r3_to_airfield_km > 0) ? 'Required — enter actual distance' : ''}
                        />

                        <TextField
                          label={t('tab3.dist_airfield_r4')}
                          type="number"
                          size="small"
                          fullWidth
                          sx={{ mb: 2 }}
                          value={strategicEvac.airfields[0]?.distances?.airfield_to_r4_km ?? ''}
                          onChange={(e) => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [{ ...prev.airfields[0], distances: { ...prev.airfields[0].distances, airfield_to_r4_km: parseInt(e.target.value) || 0 } }]
                          }))}
                          inputProps={{ min: 100, max: 5000 }}
                          error={!(strategicEvac.airfields[0]?.distances?.airfield_to_r4_km > 0)}
                          helperText={!(strategicEvac.airfields[0]?.distances?.airfield_to_r4_km > 0) ? 'Required — air distance to national/allied Role 4 hospital' : 'Air distance to national/allied Role 4 hospital'}
                        />

                        {/* PHA beds — pre-wire for ENH-003 */}
                        <TextField
                          label={t('tab3.pha_beds_label')}
                          type="number"
                          size="small"
                          fullWidth
                          sx={{ mb: 2 }}
                          value={strategicEvac.airfields[0]?.pha_beds ?? 50}
                          onChange={(e) => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [{ ...prev.airfields[0], pha_beds: parseInt(e.target.value) || 0 }]
                          }))}
                          inputProps={{ min: 0, max: 500 }}
                          helperText="PHA is at Role 3 ONLY (post-surgical, cleared for strategic airlift). Used by ENH-003 scheduling."
                        />

                        {/* FARP toggle */}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={strategicEvac.airfields[0]?.farp_enabled || false}
                              onChange={(e) => setStrategicEvac(prev => ({
                                ...prev,
                                airfields: [{ ...prev.airfields[0], farp_enabled: e.target.checked }]
                              }))}
                            />
                          }
                          label={t('tab3.farp_label')}
                        />
                        {strategicEvac.airfields[0]?.farp_enabled && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                            FARP increases rotary-wing STRATEVAC sorties by ~25%.
                          </Typography>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Train Evacuation Configuration */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box>
                        <Typography variant="h6">{MEDICAL_ICONS.HOSPITAL} {t('tab3.rail_evac')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ukraine-derived capability — not a NATO STANAG
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={strategicEvac.trainEnabled}
                            onChange={(e) => setStrategicEvac(prev => ({ ...prev, trainEnabled: e.target.checked }))}
                          />
                        }
                        label={t('common.available')}
                      />
                    </Box>

                    {strategicEvac.trainEnabled && (
                      <>
                        {/* Infrastructure */}
                        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>
                          {t('tab3.infrastructure')}
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>{t('tab3.station_size')}</InputLabel>
                          <Select
                            value={strategicEvac.stationType}
                            label={t('tab3.station_size')}
                            onChange={(e) => setStrategicEvac(prev => ({ ...prev, stationType: e.target.value }))}
                          >
                            {TRAIN_STATION_STANDARDS && Object.keys(TRAIN_STATION_STANDARDS).map(key => (
                              <MenuItem key={key} value={key}>
                                {TRAIN_STATION_STANDARDS[key]?.label || key} ({TRAIN_STATION_STANDARDS[key]?.capacity?.patients_per_day || 200}/day)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>{t('tab3.train_type')}</InputLabel>
                          <Select
                            value={strategicEvac.trainType}
                            label={t('tab3.train_type')}
                            onChange={(e) => setStrategicEvac(prev => ({ ...prev, trainType: e.target.value }))}
                          >
                            {MEDICAL_TRAIN_SPECS && Object.keys(MEDICAL_TRAIN_SPECS).map(key => (
                              <MenuItem key={key} value={key}>
                                {MEDICAL_TRAIN_SPECS[key]?.name || key} ({MEDICAL_TRAIN_SPECS[key]?.composition?.total_capacity || 144} patients)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          label={t('tab3.dist_r3_train')}
                          type="number"
                          size="small"
                          fullWidth
                          value={strategicEvac.role3ToStation ?? ''}
                          onChange={(e) => setStrategicEvac(prev => ({ ...prev, role3ToStation: parseInt(e.target.value) || 0 }))}
                          sx={{ mb: 2 }}
                          inputProps={{ min: 10, max: 150 }}
                          helperText="Corps-level asset — R3 to station only"
                        />

                        <TextField
                          label={t('tab3.dist_station_r4')}
                          type="number"
                          size="small"
                          fullWidth
                          value={strategicEvac.stationToRole4 ?? ''}
                          onChange={(e) => setStrategicEvac(prev => ({ ...prev, stationToRole4: parseInt(e.target.value) || 0 }))}
                          sx={{ mb: 2 }}
                          inputProps={{ min: 100, max: 2000 }}
                          helperText="Rail distance to national Role 4 hospital"
                        />

                        {/* Schedule */}
                        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>
                          {t('tab3.departure_schedule')}
                        </Typography>

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>{t('tab3.frequency')}</InputLabel>
                          <Select
                            value={strategicEvac.trainFrequencyMode}
                            label={t('tab3.frequency')}
                            onChange={(e) => setStrategicEvac(prev => ({ ...prev, trainFrequencyMode: e.target.value }))}
                          >
                            {Object.keys(TRAIN_FREQUENCY_OPTIONS).map(key => (
                              <MenuItem key={key} value={key}>
                                {TRAIN_FREQUENCY_OPTIONS[key].label}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 0.5 }}>
                            {TRAIN_FREQUENCY_OPTIONS[strategicEvac.trainFrequencyMode]?.description}
                          </Typography>
                        </FormControl>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid size={{ xs: 6 }}>
                            <TextField
                              label={t('tab3.first_dep_day')}
                              type="number"
                              size="small"
                              fullWidth
                              value={strategicEvac.trainFirstDepartureDay}
                              onChange={(e) => setStrategicEvac(prev => ({
                                ...prev,
                                trainFirstDepartureDay: Math.max(1, parseInt(e.target.value) || 1)
                              }))}
                              inputProps={{ min: 1, max: 14 }}
                              helperText="Day 1 = operation start"
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('tab3.dep_hour')}</InputLabel>
                              <Select
                                value={strategicEvac.trainDepartureHour}
                                label={t('tab3.dep_hour')}
                                onChange={(e) => setStrategicEvac(prev => ({ ...prev, trainDepartureHour: e.target.value }))}
                              >
                                {TRAIN_DEPARTURE_HOUR_OPTIONS.map(opt => (
                                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>{t('tab3.trains_per_dep')}</InputLabel>
                          <Select
                            value={strategicEvac.trainsPerDeparture}
                            label={t('tab3.trains_per_dep')}
                            onChange={(e) => setStrategicEvac(prev => ({ ...prev, trainsPerDeparture: e.target.value }))}
                          >
                            {TRAINS_PER_DEPARTURE_OPTIONS.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {strategicEvac.trainFrequencyMode === 'ON_DEMAND' && (
                          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>{t('tab3.min_patients')}</InputLabel>
                            <Select
                              value={strategicEvac.trainMinPatients}
                              label={t('tab3.min_patients')}
                              onChange={(e) => setStrategicEvac(prev => ({ ...prev, trainMinPatients: e.target.value }))}
                            >
                              {TRAIN_MIN_PATIENTS_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 0.5 }}>
                              Train departs when this many patients are waiting
                            </Typography>
                          </FormControl>
                        )}

                        {/* Schedule preview — BUG-100 FIX: use actual scenario duration, not hardcoded 14 */}
                        {strategicEvac.trainFrequencyMode !== 'ON_DEMAND' && (() => {
                          const opDays = scenario?.operation_parameters?.duration_days ?? 10;
                          const departureDays = computeTrainDepartureDays(
                            { frequency_mode: strategicEvac.trainFrequencyMode, first_departure_day: strategicEvac.trainFirstDepartureDay },
                            opDays
                          );
                          const capPerDep = getTrainCapacityPerDeparture({
                            train_type: strategicEvac.trainType,
                            trains_per_departure: strategicEvac.trainsPerDeparture
                          });
                          return (
                            <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                <strong>Schedule preview ({opDays}-day operation):</strong>
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Departure days: {departureDays.length > 0 ? departureDays.map(d => `D${d}`).join(', ') : 'None'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Capacity per departure: {capPerDep} patients
                                {strategicEvac.trainsPerDeparture > 1 ? ` (${strategicEvac.trainsPerDeparture} trains × ${MEDICAL_TRAIN_SPECS[strategicEvac.trainType]?.composition?.total_capacity ?? 0})` : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Total capacity: {departureDays.length * capPerDep} patients over {opDays} days
                              </Typography>
                            </Box>
                          );
                        })()}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Estimated STRATEVAC Timeline */}
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
                  <Typography variant="subtitle2" gutterBottom>Estimated STRATEVAC Timeline</Typography>
                  <Grid container spacing={2}>
                    {strategicEvac.airEnabled && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" fontWeight="bold">Air Route (Role 3 -> Role 4)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ground to airfield: ~{Math.round((strategicEvac.airfields[0]?.distances?.r3_to_airfield_km || 0) / TRANSPORT_SPEEDS.GROUND_AMBULANCE_KPH * 60)} min<br/>
                          Staging/loading: ~90 min<br/>
                          Flight time: ~{Math.round((strategicEvac.airfields[0]?.distances?.airfield_to_r4_km || 0) / TRANSPORT_SPEEDS.FIXED_WING_KPH * 60)} min<br/>
                          <strong>Total: ~{Math.round((strategicEvac.airfields[0]?.distances?.r3_to_airfield_km || 0) / TRANSPORT_SPEEDS.GROUND_AMBULANCE_KPH * 60) + 90 + Math.round((strategicEvac.airfields[0]?.distances?.airfield_to_r4_km || 0) / TRANSPORT_SPEEDS.FIXED_WING_KPH * 60)} min</strong>
                        </Typography>
                      </Grid>
                    )}
                    {strategicEvac.trainEnabled && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" fontWeight="bold">Rail Route (Role 3 -> Role 4)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ground to station: ~{Math.round((strategicEvac.role3ToStation || 0) / TRANSPORT_SPEEDS.GROUND_AMBULANCE_KPH * 60)} min<br/>
                          Loading/staging: ~120 min<br/>
                          Train travel: ~{Math.round((strategicEvac.stationToRole4 || 0) / TRANSPORT_SPEEDS.TRAIN_KPH * 60)} min<br/>
                          <strong>Total: ~{Math.round((strategicEvac.role3ToStation || 0) / TRANSPORT_SPEEDS.GROUND_AMBULANCE_KPH * 60) + 120 + Math.round((strategicEvac.stationToRole4 || 0) / TRANSPORT_SPEEDS.TRAIN_KPH * 60)} min ({(((strategicEvac.role3ToStation || 0) / TRANSPORT_SPEEDS.GROUND_AMBULANCE_KPH + 2 + (strategicEvac.stationToRole4 || 0) / TRANSPORT_SPEEDS.TRAIN_KPH)).toFixed(1)} hours)</strong>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    

    {/* ========================================================================
        PHASE 3 REORGANIZATION: SECTION 3E - STRATEGIC EVACUATION
        
        Consolidates strategic-level evacuation planning:
        - 3E.1: Strategic Evacuation (Basic configuration, in Standard Mode Cards)
        - 3E.2: STRATEVAC Configuration (Detailed R3->R4 settings)
        ======================================================================== */}
 {/* UI-013: STRATEVAC Operations & Timeline */}
    {mode === 'EXPERT' && (
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{MEDICAL_ICONS.HELICOPTER} {t('tab3.e2_stratevac')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Operational Planning:</strong> STRATEVAC timeline and national coordination requirements.
              All distances are configured in E.1 (Infrastructure). This section displays derived timelines only.
              STRATEVAC is a national responsibility under NATO agreements — Romanian forces coordinate with
              RoAF, US forces use the TCCET system.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            {/* Read-only STRATEVAC Timeline — reads from E.1 airfields[0] */}
            {strategicEvac.airEnabled && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {MEDICAL_ICONS.HELICOPTER} Air STRATEVAC Timeline
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Airfield: <strong>{strategicEvac.airfields[0]?.label || 'Not configured'}</strong>
                    {' | Class: '}<strong>{strategicEvac.airfields[0]?.class || '—'}</strong>
                    {strategicEvac.airfields[0]?.farp_enabled ? ' | FARP active' : ''}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption">
                      R3 to airfield: ~{Math.round((strategicEvac.airfields[0]?.distances?.r3_to_airfield_km || 0) / TRANSPORT_SPEEDS.GROUND_AMBULANCE_KPH * 60)} min (ground)
                    </Typography>
                    <Typography variant="caption">
                      Airfield staging: ~60–90 min (patient prep + loading)
                    </Typography>
                    <Typography variant="caption">
                      Flight to Role 4: ~{Math.round((strategicEvac.airfields[0]?.distances?.airfield_to_r4_km || 0) / TRANSPORT_SPEEDS.FIXED_WING_KPH * 60)} min ({TRANSPORT_SPEEDS.FIXED_WING_KPH} km/h)
                    </Typography>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="body2" fontWeight="bold">
                      Total: ~{Math.round((strategicEvac.airfields[0]?.distances?.r3_to_airfield_km || 0) / TRANSPORT_SPEEDS.GROUND_AMBULANCE_KPH * 60) + 90 + Math.round((strategicEvac.airfields[0]?.distances?.airfield_to_r4_km || 0) / TRANSPORT_SPEEDS.FIXED_WING_KPH * 60)} min
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PHA capacity: {strategicEvac.airfields[0]?.pha_beds || 0} beds (Role 3 — post-surgical staging)
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* National Liaison note */}
            <Grid size={{ xs: strategicEvac.airEnabled ? 12 : 12, md: strategicEvac.airEnabled ? 6 : 12 }}>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>National Responsibility:</strong> STRATEVAC is a national responsibility under
                  NATO agreements. Romanian forces coordinate with RoAF (C-130H / C-27J Spartan).
                  US forces use the TCCET (Theater Critical Care Evacuation Team) system.
                  Ensure coordination with your national STRATEVAC liaison before operation.
                </Typography>
              </Alert>
            </Grid>

            {/* ── ENH-003: FLIGHT SCHEDULE CONFIGURATION ─────────────────── */}
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 2, border: '1px solid', borderColor: 'primary.light' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {MEDICAL_ICONS.HELICOPTER} Flight Schedule — Primary Airfield
                </Typography>

                {/* ── Aircraft + Frequency Mode ── */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Aircraft Type</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={strategicEvac.airfields[0]?.schedule?.aircraft_type ?? ''}
                      displayEmpty
                      onChange={e => setStrategicEvac(prev => ({
                        ...prev,
                        airfields: [
                          {
                            ...prev.airfields[0],
                            schedule: { ...(prev.airfields[0].schedule ?? {}), aircraft_type: e.target.value }
                          },
                          prev.airfields[1]
                        ]
                      }))}
                    >
                      <MenuItem value=""><em>— Select aircraft —</em></MenuItem>
                      {getAircraftForAirfieldClass(strategicEvac.airfields[0]?.class ?? 'CLASS_B').map(code => (
                        <MenuItem key={code} value={code}>
                          {STRATEVAC_AIRCRAFT[code]?.label ?? code}
                          {' — '}
                          {STRATEVAC_AIRCRAFT[code]?.litter_capacity} litter
                          {STRATEVAC_AIRCRAFT[code]?.icu_capable ? ' · ICU' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Schedule Mode</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={strategicEvac.airfields[0]?.schedule?.frequency_mode ?? 'FIXED_DAYS'}
                      onChange={e => setStrategicEvac(prev => ({
                        ...prev,
                        airfields: [
                          {
                            ...prev.airfields[0],
                            schedule: { ...(prev.airfields[0].schedule ?? {}), frequency_mode: e.target.value, fixed_days: [], interval_hours: 48, first_departure_day: 1 }
                          },
                          prev.airfields[1]
                        ]
                      }))}
                    >
                      {Object.entries(SCHEDULE_FREQUENCY_MODES)
                        .filter(([, v]) => v.planning_valid)
                        .map(([key, val]) => (
                          <MenuItem key={key} value={key}>{val.label}</MenuItem>
                        ))}
                    </Select>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Departure Hour (UTC)</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={strategicEvac.airfields[0]?.schedule?.departure_hour ?? 6}
                      onChange={e => setStrategicEvac(prev => ({
                        ...prev,
                        airfields: [
                          {
                            ...prev.airfields[0],
                            schedule: { ...(prev.airfields[0].schedule ?? {}), departure_hour: e.target.value }
                          },
                          prev.airfields[1]
                        ]
                      }))}
                    >
                      {DEPARTURE_HOUR_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  {/* ENH-STRAT-001: Flights per departure day */}
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Sorties / Departure Day</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={strategicEvac.airfields[0]?.schedule?.flights_per_day ?? 1}
                      onChange={e => setStrategicEvac(prev => ({
                        ...prev,
                        airfields: [
                          {
                            ...prev.airfields[0],
                            schedule: { ...(prev.airfields[0].schedule ?? {}), flights_per_day: e.target.value }
                          },
                          prev.airfields[1]
                        ]
                      }))}
                    >
                      {FLIGHTS_PER_DAY_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>

                {/* ── FIXED_DAYS: day checkboxes ── */}
                {/* BUG-STRATEVAC-001: also show when frequency_mode absent (default is FIXED_DAYS) */}
                {(strategicEvac.airfields[0]?.schedule?.frequency_mode ?? 'FIXED_DAYS') === 'FIXED_DAYS' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Primary Aircraft Departure Days (select all days the primary aircraft departs):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {Array.from(
                        { length: scenario?.operation_parameters?.duration_days ?? 10 },
                        (_, i) => i + 1
                      ).map(day => {
                        const selectedDays = strategicEvac.airfields[0]?.schedule?.fixed_days ?? [];
                        const isSelected = selectedDays.includes(day);
                        return (
                          <Chip
                            key={day}
                            label={`D${day}`}
                            size="small"
                            color={isSelected ? 'primary' : 'default'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            onClick={() => {
                              const next = isSelected
                                ? selectedDays.filter(d => d !== day)
                                : [...selectedDays, day].sort((a, b) => a - b);
                              setStrategicEvac(prev => ({
                                ...prev,
                                airfields: [
                                  {
                                    ...prev.airfields[0],
                                    schedule: { ...(prev.airfields[0].schedule ?? {}), fixed_days: next }
                                  },
                                  prev.airfields[1]
                                ]
                              }));
                            }}
                          />
                        );
                      })}
                    </Box>
                    {(() => {
                      const sched = strategicEvac.airfields[0]?.schedule;
                      if (sched?.aircraft_type && sched?.fixed_days?.length > 0) {
                        const aircraft = STRATEVAC_AIRCRAFT[sched.aircraft_type];
                        const fpd = sched.flights_per_day ?? 1;
                        const days = computeDepartureDays(
                          { ...sched, frequency_mode: 'FIXED_DAYS' },
                          scenario?.operation_parameters?.duration_days ?? 10
                        );
                        const effectiveCap = (aircraft?.litter_capacity ?? 0) * fpd;
                        return (
                          <Typography variant="caption" color="primary.main" sx={{ mt: 0.5, display: 'block' }}>
                            {days.length} departure days × {fpd > 1 ? `${fpd} sorties × ` : ''}{aircraft?.litter_capacity ?? '?'} litter
                            {' = '}{days.length * effectiveCap} total patient slots
                          </Typography>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                )}

                {/* ── INTERVAL_HOURS: first day + interval ── */}
                {strategicEvac.airfields[0]?.schedule?.frequency_mode === 'INTERVAL_HOURS' && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary">First Departure Day</Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={strategicEvac.airfields[0]?.schedule?.first_departure_day ?? 1}
                        onChange={e => setStrategicEvac(prev => ({
                          ...prev,
                          airfields: [
                            {
                              ...prev.airfields[0],
                              schedule: { ...(prev.airfields[0].schedule ?? {}), first_departure_day: e.target.value }
                            },
                            prev.airfields[1]
                          ]
                        }))}
                      >
                        {Array.from({ length: scenario?.operation_parameters?.duration_days ?? 10 }, (_, i) => i + 1).map(d => (
                          <MenuItem key={d} value={d}>Day {d}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary">Repeat Interval</Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={strategicEvac.airfields[0]?.schedule?.interval_hours ?? 48}
                        onChange={e => setStrategicEvac(prev => ({
                          ...prev,
                          airfields: [
                            {
                              ...prev.airfields[0],
                              schedule: { ...(prev.airfields[0].schedule ?? {}), interval_hours: e.target.value }
                            },
                            prev.airfields[1]
                          ]
                        }))}
                      >
                        {INTERVAL_HOUR_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    {(() => {
                      const sched = strategicEvac.airfields[0]?.schedule;
                      if (sched?.aircraft_type) {
                        const aircraft = STRATEVAC_AIRCRAFT[sched.aircraft_type];
                        const fpd = sched.flights_per_day ?? 1;
                        const days = computeDepartureDays(
                          { ...sched, frequency_mode: 'INTERVAL_HOURS' },
                          scenario?.operation_parameters?.duration_days ?? 10
                        );
                        const effectiveCap = (aircraft?.litter_capacity ?? 0) * fpd;
                        return (
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="primary.main">
                              Departs: {days.map(d => `D${d}`).join(', ')}
                              {' — '}{days.length} departure days × {fpd > 1 ? `${fpd} sorties × ` : ''}{aircraft?.litter_capacity ?? '?'} litter
                              {' = '}{days.length * effectiveCap} total slots
                            </Typography>
                          </Grid>
                        );
                      }
                      return null;
                    })()}
                  </Grid>
                )}

                {/* ── DAILY: first day only ── */}
                {strategicEvac.airfields[0]?.schedule?.frequency_mode === 'DAILY' && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary">First Departure Day</Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={strategicEvac.airfields[0]?.schedule?.first_departure_day ?? 1}
                        onChange={e => setStrategicEvac(prev => ({
                          ...prev,
                          airfields: [
                            {
                              ...prev.airfields[0],
                              schedule: { ...(prev.airfields[0].schedule ?? {}), first_departure_day: e.target.value }
                            },
                            prev.airfields[1]
                          ]
                        }))}
                      >
                        {Array.from({ length: scenario?.operation_parameters?.duration_days ?? 10 }, (_, i) => i + 1).map(d => (
                          <MenuItem key={d} value={d}>Day {d}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                )}

                {/* ── Nationality Routing ── */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">Nationality Routing</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={
                        strategicEvac.airfields[0]?.schedule?.nationality_routing ??
                        DEFAULT_NATIONALITY_ROUTING[scenario?.force?.national_standard] ??
                        'OWN_NATION_ONLY'
                      }
                      onChange={e => setStrategicEvac(prev => ({
                        ...prev,
                        airfields: [
                          {
                            ...prev.airfields[0],
                            schedule: { ...(prev.airfields[0].schedule ?? {}), nationality_routing: e.target.value }
                          },
                          prev.airfields[1]
                        ]
                      }))}
                    >
                      {Object.entries(NATIONALITY_ROUTING_MODES).map(([key, val]) => (
                        <MenuItem key={key} value={key}>
                          {val.label}
                          {val.requires_mou ? ' (MOU required)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  {/* Load nation template button */}
                  <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        try {
                          const natStd = scenario?.force?.national_standard ?? 'NATO';
                          const template = getDefaultScheduleForNation(natStd);
                          setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              { ...prev.airfields[0], schedule: template },
                              prev.airfields[1]
                            ]
                          }));
                        } catch (err) {
                          // Nation has no template — user must configure manually
                          console.warn('stratevacScheduling: no template for nation', err.message);
                        }
                      }}
                    >
                      Load National Template
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                      Pre-fills schedule from national doctrine defaults
                    </Typography>
                  </Grid>
                </Grid>

                {/* BUG-STRATEVAC-003 FIX: use ?? 'FIXED_DAYS' fallback so warning fires even when
                    frequency_mode is undefined (i.e. user selected aircraft but never changed
                    schedule mode from the default — stored as undefined, displayed as FIXED_DAYS).
                    Previously used strict === 'FIXED_DAYS' which silently passed when mode was
                    undefined, giving a 'success' alert even with no departure days configured. */}
                {/* PHA summary */}
                {strategicEvac.airfields[0]?.schedule?.aircraft_type && (
                  <Alert
                    severity={
                      (strategicEvac.airfields[0]?.schedule?.frequency_mode ?? 'FIXED_DAYS') === 'FIXED_DAYS' &&
                      (strategicEvac.airfields[0]?.schedule?.fixed_days?.length ?? 0) === 0
                        ? 'warning'
                        : 'success'
                    }
                    sx={{ mt: 1 }}
                  >
                    {(strategicEvac.airfields[0]?.schedule?.frequency_mode ?? 'FIXED_DAYS') === 'FIXED_DAYS' &&
                     (strategicEvac.airfields[0]?.schedule?.fixed_days?.length ?? 0) === 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>⚠ No departure days selected.</strong> Click the D1–D{scenario?.operation_parameters?.duration_days ?? 10} chips above to set which days this aircraft operates. Tab 6 will show "no active schedule" until at least one day is selected.
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>PHA capacity:</strong> {strategicEvac.airfields[0]?.pha_beds ?? 50} beds
                      {' | '}
                      <strong>Aircraft:</strong> {STRATEVAC_AIRCRAFT[strategicEvac.airfields[0].schedule.aircraft_type]?.label}
                      {STRATEVAC_AIRCRAFT[strategicEvac.airfields[0].schedule.aircraft_type]?.icu_capable
                        ? ' (ICU-capable)'
                        : ''}
                      {' | '}
                      <strong>Sorties/day:</strong> {strategicEvac.airfields[0]?.schedule?.flights_per_day ?? 1}
                      {' | '}
                      <strong>Effective capacity/departure day:</strong>{' '}
                      {(STRATEVAC_AIRCRAFT[strategicEvac.airfields[0].schedule.aircraft_type]?.litter_capacity ?? 0)
                        * (strategicEvac.airfields[0]?.schedule?.flights_per_day ?? 1)} litter
                      {' | '}
                      <strong>Turnaround:</strong> {STRATEVAC_AIRCRAFT[strategicEvac.airfields[0].schedule.aircraft_type]?.turnaround_hours}h
                      {' | '}
                      <strong>PHA MASCAL threshold:</strong> {Math.round((PHA_THRESHOLDS.MASCAL.min_fraction) * (strategicEvac.airfields[0]?.pha_beds ?? 50))} patients
                    </Typography>
                  </Alert>
                )}
                {/* ── BUG-079 FIX (March 13, 2026): Secondary Aircraft on Primary Airfield ── */}
                <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Secondary Aircraft (Mixed-Fleet)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add a second aircraft type operating from the same airfield on different days.
                        Example: C-130 on odd days (RoAF) + C-17 on even days (USAF MOU).
                        Both schedules draw from the same PHA queue.
                      </Typography>
                    </Box>
                    <Switch
                      checked={strategicEvac.airfields[0]?.schedule_b != null}
                      onChange={e => setStrategicEvac(prev => ({
                        ...prev,
                        airfields: [
                          {
                            ...prev.airfields[0],
                            schedule_b: e.target.checked
                              ? { aircraft_type: '', frequency_mode: 'FIXED_DAYS', fixed_days: [], departure_hour: 6, flights_per_day: 1, interval_hours: 48, first_departure_day: 1 }
                              : null
                          },
                          prev.airfields[1]
                        ]
                      }))}
                    />
                  </Box>

                  {strategicEvac.airfields[0]?.schedule_b != null && (
                    <Grid container spacing={2}>
                      {/* Aircraft type B */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Secondary Aircraft Type</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[0].schedule_b.aircraft_type ?? ''}
                          displayEmpty
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              { ...prev.airfields[0], schedule_b: { ...prev.airfields[0].schedule_b, aircraft_type: e.target.value } },
                              prev.airfields[1]
                            ]
                          }))}
                        >
                          <MenuItem value=""><em>— Select aircraft —</em></MenuItem>
                          {getAircraftForAirfieldClass(strategicEvac.airfields[0]?.class ?? 'CLASS_B').map(code => (
                            <MenuItem key={code} value={code}>
                              {STRATEVAC_AIRCRAFT[code]?.label ?? code}
                              {' — '}
                              {STRATEVAC_AIRCRAFT[code]?.litter_capacity} litter
                              {STRATEVAC_AIRCRAFT[code]?.icu_capable ? ' · ICU' : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>

                      {/* Schedule mode B */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Schedule Mode</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[0].schedule_b.frequency_mode ?? 'FIXED_DAYS'}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              { ...prev.airfields[0], schedule_b: { ...prev.airfields[0].schedule_b, frequency_mode: e.target.value, fixed_days: [], interval_hours: 48, first_departure_day: 1 } },
                              prev.airfields[1]
                            ]
                          }))}
                        >
                          {Object.entries(SCHEDULE_FREQUENCY_MODES)
                            .filter(([, v]) => v.planning_valid)
                            .map(([key, val]) => (
                              <MenuItem key={key} value={key}>{val.label}</MenuItem>
                            ))}
                        </Select>
                      </Grid>

                      {/* Sorties/day B */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Sorties / Departure Day</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[0].schedule_b.flights_per_day ?? 1}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              { ...prev.airfields[0], schedule_b: { ...prev.airfields[0].schedule_b, flights_per_day: e.target.value } },
                              prev.airfields[1]
                            ]
                          }))}
                        >
                          {FLIGHTS_PER_DAY_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </Grid>

                      {/* FIXED_DAYS departure day chips for schedule_b */}
                      {/* BUG-STRATEVAC-001: label clarified to distinguish from primary aircraft days */}
                      {(strategicEvac.airfields[0].schedule_b.frequency_mode ?? 'FIXED_DAYS') === 'FIXED_DAYS' && (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="caption" color="text.secondary">
                            Secondary Aircraft Departure Days (days this aircraft flies — must differ from primary):
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {Array.from(
                              { length: scenario?.operation_parameters?.duration_days ?? 10 },
                              (_, i) => i + 1
                            ).map(day => {
                              const selDays = strategicEvac.airfields[0].schedule_b.fixed_days ?? [];
                              const isSel = selDays.includes(day);
                              // Warn if primary and secondary both scheduled on same day
                              const primaryDays = strategicEvac.airfields[0]?.schedule?.fixed_days ?? [];
                              const overlap = primaryDays.includes(day);
                              return (
                                <Tooltip
                                  key={day}
                                  title={overlap ? 'Primary aircraft also departs this day' : ''}
                                >
                                  <Chip
                                    label={`D${day}`}
                                    size="small"
                                    color={isSel ? 'secondary' : 'default'}
                                    variant={isSel ? 'filled' : 'outlined'}
                                    sx={overlap && isSel ? { border: '2px solid', borderColor: 'warning.main' } : {}}
                                    onClick={() => {
                                      const next = isSel
                                        ? selDays.filter(d => d !== day)
                                        : [...selDays, day].sort((a, b) => a - b);
                                      setStrategicEvac(prev => ({
                                        ...prev,
                                        airfields: [
                                          { ...prev.airfields[0], schedule_b: { ...prev.airfields[0].schedule_b, fixed_days: next } },
                                          prev.airfields[1]
                                        ]
                                      }));
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                          {/* Capacity preview for schedule_b */}
                          {(() => {
                            const sb = strategicEvac.airfields[0].schedule_b;
                            if (sb?.aircraft_type && (sb?.fixed_days?.length ?? 0) > 0) {
                              const acB = STRATEVAC_AIRCRAFT[sb.aircraft_type];
                              const fpd = sb.flights_per_day ?? 1;
                              const slots = sb.fixed_days.length * (acB?.litter_capacity ?? 0) * fpd;
                              return (
                                <Typography variant="caption" color="secondary.main" sx={{ mt: 0.5, display: 'block' }}>
                                  {sb.fixed_days.length} departure days × {fpd > 1 ? `${fpd} sorties × ` : ''}{acB?.litter_capacity ?? '?'} litter = {slots} total patient slots
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </Grid>
                      )}

                      {/* INTERVAL_HOURS for schedule_b */}
                      {strategicEvac.airfields[0].schedule_b.frequency_mode === 'INTERVAL_HOURS' && (
                        <>
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="text.secondary">First Departure Day</Typography>
                            <Select
                              fullWidth
                              size="small"
                              value={strategicEvac.airfields[0].schedule_b.first_departure_day ?? 1}
                              onChange={e => setStrategicEvac(prev => ({
                                ...prev,
                                airfields: [
                                  { ...prev.airfields[0], schedule_b: { ...prev.airfields[0].schedule_b, first_departure_day: e.target.value } },
                                  prev.airfields[1]
                                ]
                              }))}
                            >
                              {Array.from({ length: scenario?.operation_parameters?.duration_days ?? 10 }, (_, i) => i + 1).map(d => (
                                <MenuItem key={d} value={d}>Day {d}</MenuItem>
                              ))}
                            </Select>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="text.secondary">Repeat Interval</Typography>
                            <Select
                              fullWidth
                              size="small"
                              value={strategicEvac.airfields[0].schedule_b.interval_hours ?? 48}
                              onChange={e => setStrategicEvac(prev => ({
                                ...prev,
                                airfields: [
                                  { ...prev.airfields[0], schedule_b: { ...prev.airfields[0].schedule_b, interval_hours: e.target.value } },
                                  prev.airfields[1]
                                ]
                              }))}
                            >
                              {INTERVAL_HOUR_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* ── ENH-003: ALTERNATE AIRFIELD TOGGLE + CONFIG ──────────────── */}
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 2, border: '1px solid', borderColor: 'grey.300' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: strategicEvac.airfields[1]?.is_active ? 2 : 0 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Alternate Airfield (Exercise Planning)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enable a secondary airfield for overflow or primary degradation planning.
                      Degradation events are Tab 6 (Exercise Mode) only.
                    </Typography>
                  </Box>
                  <Switch
                    checked={strategicEvac.airfields[1]?.is_active ?? false}
                    onChange={e => setStrategicEvac(prev => ({
                      ...prev,
                      airfields: [
                        prev.airfields[0],
                        { ...prev.airfields[1], is_active: e.target.checked }
                      ]
                    }))}
                  />
                </Box>

                {strategicEvac.airfields[1]?.is_active && (
                  <>
                    {/* Alternate airfield label */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">Airfield Name</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[1]?.label ?? ''}
                          placeholder="Alternate STRATEVAC Airfield"
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], label: e.target.value }
                            ]
                          }))}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Class</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[1]?.class ?? 'CLASS_B'}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], class: e.target.value, schedule: null }
                            ]
                          }))}
                        >
                          {Object.entries(AIRFIELD_CLASS_SPECS).map(([key, spec]) => (
                            <MenuItem key={key} value={key}>{spec.label}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">PHA Beds</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={strategicEvac.airfields[1]?.pha_beds ?? 30}
                          inputProps={{ min: 0, max: 200 }}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], pha_beds: parseInt(e.target.value) || 0 }
                            ]
                          }))}
                        />
                      </Grid>
                    </Grid>

                    {/* Alternate R3→airfield distance */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">R3 → Airfield (km)</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={strategicEvac.airfields[1]?.distances?.r3_to_airfield_km ?? ''}
                          inputProps={{ min: 0, max: 2000 }}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], distances: { ...prev.airfields[1].distances, r3_to_airfield_km: parseInt(e.target.value) || 0 } }
                            ]
                          }))}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Airfield → R4 (km)</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={strategicEvac.airfields[1]?.distances?.airfield_to_r4_km ?? ''}
                          inputProps={{ min: 0, max: 2000 }}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], distances: { ...prev.airfields[1].distances, airfield_to_r4_km: parseInt(e.target.value) || 0 } }
                            ]
                          }))}
                        />
                      </Grid>
                    </Grid>

                    {/* Alternate aircraft + schedule */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Aircraft Type</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[1]?.schedule?.aircraft_type ?? ''}
                          displayEmpty
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], schedule: { ...(prev.airfields[1].schedule ?? {}), aircraft_type: e.target.value } }
                            ]
                          }))}
                        >
                          <MenuItem value=""><em>— Select aircraft —</em></MenuItem>
                          {getAircraftForAirfieldClass(strategicEvac.airfields[1]?.class ?? 'CLASS_B').map(code => (
                            <MenuItem key={code} value={code}>
                              {STRATEVAC_AIRCRAFT[code]?.label ?? code}
                              {' — '}{STRATEVAC_AIRCRAFT[code]?.litter_capacity} litter
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Schedule Mode</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[1]?.schedule?.frequency_mode ?? 'FIXED_DAYS'}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], schedule: { ...(prev.airfields[1].schedule ?? {}), frequency_mode: e.target.value, fixed_days: [], interval_hours: 48, first_departure_day: 1 } }
                            ]
                          }))}
                        >
                          {Object.entries(SCHEDULE_FREQUENCY_MODES)
                            .filter(([, v]) => v.planning_valid)
                            .map(([key, val]) => (
                              <MenuItem key={key} value={key}>{val.label}</MenuItem>
                            ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Departure Hour (UTC)</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[1]?.schedule?.departure_hour ?? 8}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], schedule: { ...(prev.airfields[1].schedule ?? {}), departure_hour: e.target.value } }
                            ]
                          }))}
                        >
                          {DEPARTURE_HOUR_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </Grid>

                      {/* ENH-STRAT-001: Flights per departure day — alternate airfield */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Sorties / Departure Day</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={strategicEvac.airfields[1]?.schedule?.flights_per_day ?? 1}
                          onChange={e => setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], schedule: { ...(prev.airfields[1].schedule ?? {}), flights_per_day: e.target.value } }
                            ]
                          }))}
                        >
                          {FLIGHTS_PER_DAY_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>

                    {/* Alternate FIXED_DAYS */}
                    {strategicEvac.airfields[1]?.schedule?.frequency_mode === 'FIXED_DAYS' && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">Departure Days:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {Array.from(
                            { length: scenario?.operation_parameters?.duration_days ?? 10 },
                            (_, i) => i + 1
                          ).map(day => {
                            const selectedDays = strategicEvac.airfields[1]?.schedule?.fixed_days ?? [];
                            const isSelected = selectedDays.includes(day);
                            return (
                              <Chip
                                key={day}
                                label={`D${day}`}
                                size="small"
                                color={isSelected ? 'secondary' : 'default'}
                                variant={isSelected ? 'filled' : 'outlined'}
                                onClick={() => {
                                  const next = isSelected
                                    ? selectedDays.filter(d => d !== day)
                                    : [...selectedDays, day].sort((a, b) => a - b);
                                  setStrategicEvac(prev => ({
                                    ...prev,
                                    airfields: [
                                      prev.airfields[0],
                                      { ...prev.airfields[1], schedule: { ...(prev.airfields[1].schedule ?? {}), fixed_days: next } }
                                    ]
                                  }));
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Alternate INTERVAL_HOURS */}
                    {strategicEvac.airfields[1]?.schedule?.frequency_mode === 'INTERVAL_HOURS' && (
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">First Departure Day</Typography>
                          <Select
                            fullWidth
                            size="small"
                            value={strategicEvac.airfields[1]?.schedule?.first_departure_day ?? 1}
                            onChange={e => setStrategicEvac(prev => ({
                              ...prev,
                              airfields: [
                                prev.airfields[0],
                                { ...prev.airfields[1], schedule: { ...(prev.airfields[1].schedule ?? {}), first_departure_day: e.target.value } }
                              ]
                            }))}
                          >
                            {Array.from({ length: scenario?.operation_parameters?.duration_days ?? 10 }, (_, i) => i + 1).map(d => (
                              <MenuItem key={d} value={d}>Day {d}</MenuItem>
                            ))}
                          </Select>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">Repeat Interval</Typography>
                          <Select
                            fullWidth
                            size="small"
                            value={strategicEvac.airfields[1]?.schedule?.interval_hours ?? 48}
                            onChange={e => setStrategicEvac(prev => ({
                              ...prev,
                              airfields: [
                                prev.airfields[0],
                                { ...prev.airfields[1], schedule: { ...(prev.airfields[1].schedule ?? {}), interval_hours: e.target.value } }
                              ]
                            }))}
                          >
                            {INTERVAL_HOUR_OPTIONS.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </Grid>
                      </Grid>
                    )}

                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Alternate active.</strong> Calculator will model both airfields.
                        Tab 5 STRATEVAC report shows per-airfield PHA occupancy and patient flow.
                        Primary degradation events (enemy fires, weather) are Tab 6 (Exercise Mode) only.
                      </Typography>
                    </Alert>

                    {/* Load template for alternate */}
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        try {
                          const natStd = scenario?.force?.national_standard ?? 'NATO';
                          const template = getDefaultScheduleForNation(natStd);
                          setStrategicEvac(prev => ({
                            ...prev,
                            airfields: [
                              prev.airfields[0],
                              { ...prev.airfields[1], schedule: template }
                            ]
                          }));
                        } catch (err) {
                          console.warn('stratevacScheduling: no template for nation', err.message);
                        }
                      }}
                    >
                      Load National Template (Alternate)
                    </Button>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    )}


        </CardContent>
      </Card>

{/* 3F SECTION */}
      <Card sx={{ mb: 3, border: '3px solid', borderColor: '#4caf50', borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#4caf50', mb: 3 }}>
            {MEDICAL_ICONS.RED_CROSS} {t('tab3.section_3f')}
          </Typography>
   
      {/* ================================================================== */}
      {/* MASCAL CONFIGURATION - Mode-aware (Option C) */}
      {/* ================================================================== */}
      
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">{MEDICAL_ICONS.RED_CROSS} {t('tab3.mascal_f1')}</Typography>
              <Chip 
                label={MASCAL_ALERT_LEVELS[mascalConfig.currentLevel]?.label || mascalConfig.currentLevel}
                size="small"
                sx={{ 
                  bgcolor: MASCAL_ALERT_LEVELS[mascalConfig.currentLevel]?.color || '#4caf50',
                  color: 'white'
                }}
              />
              {mode === 'STANDARD' && <Chip label={t('common.read_only')} size="small" variant="outlined" />}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={mascalConfig.enabled}
                  onChange={(e) => setMascalConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                  disabled={mode === 'STANDARD'}
                />
              }
              label={t('common.enable')}
            />
          </Box>
          
          {/* v4.3.2: Blue Light Protocol Automatic Recommendation */}
          {mascalConfig.blueLightRecommended && !mascalConfig.blueLightActive && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('tab3.mascal_protocol_recommended')}
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {mascalConfig.blueLightTriggerReason}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                <strong>Medical Recommendation:</strong> Conditions warrant MASCAL Escalation Protocol (AMedP-1.10) activation.
                <br />
                <strong>Command Decision Required:</strong> Evaluate operational constraints before activating.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="error"
                  startIcon={<MedicalIcon />}
                  onClick={handleActivateBlueLight}
                  size="small"
                >
                  {t('tab3.activate_protocol')}
                </Button>
                <Button 
                  variant="outlined"
                  onClick={handleOpenDismissDialog}
                  size="small"
                >
                  {t('tab3.dismiss_with_reason')}
                </Button>
              </Box>
            </Alert>
          )}
          
          {/* v4.3.2: Blue Light Protocol Active Status */}
          {mascalConfig.blueLightActive && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>
                <MedicalIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('tab3.mascal_protocol_active')}
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Activated at: {mascalConfig.blueLightActivatedAt ? new Date(mascalConfig.blueLightActivatedAt).toLocaleString() : 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {t('tab3.max_runtime', { h: BLUE_LIGHT_PROTOCOL?.max_runtime_hours || 4 })} |
                {t('tab3.cooldown_text', { m: BLUE_LIGHT_PROTOCOL?.cooldown_minutes || 120 })}
              </Typography>
              <Button 
                variant="outlined" 
                color="error"
                size="small"
                onClick={handleDeactivateBlueLight}
              >
                {t('tab3.deactivate_protocol')}
              </Button>
            </Alert>
          )}
          
          {/* v4.3.2: Blue Light Protocol Dismissed Status */}
          {mascalConfig.blueLightDismissed && !mascalConfig.blueLightActive && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>{t('tab3.mascal_protocol_dismissed')}</AlertTitle>
              <Typography variant="body2">
                <strong>Reason:</strong> {mascalConfig.blueLightDismissReason}
              </Typography>
            </Alert>
          )}
          
          {mascalConfig.enabled && (
            <>
              {/* STANDARD MODE: Read-only summary */}
              {mode === 'STANDARD' ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      MASCAL configuration uses NATO defaults. Switch to Expert Mode to customize.
                    </Typography>
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Current Level</Typography>
                      <Typography variant="h6">{mascalConfig.currentLevel}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">MASCAL Protocol</Typography>
                      <Typography variant="h6">{mascalConfig.blueLightEnabled ? 'Enabled' : 'Disabled'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">MTF Warning</Typography>
                      <Typography variant="h6">{mascalConfig.thresholds?.mtf_warning ?? MASCAL_TRIGGERS?.mtf_utilization?.warning ?? 80}%</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">MTF Critical</Typography>
                      <Typography variant="h6">{mascalConfig.thresholds?.mtf_critical ?? MASCAL_TRIGGERS?.mtf_utilization?.critical ?? 95}%</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                /* EXPERT MODE: Full editable controls */
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      {t('tab3.mascal_detection_desc')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {t('tab3.mascal_stanag_note')}
                    </Typography>
                  </Alert>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('tab3.current_med_status')}</InputLabel>
                        <Select
                          value={mascalConfig.currentLevel}
                          label={t('tab3.current_med_status')}
                          onChange={(e) => setMascalConfig(prev => ({ ...prev, currentLevel: e.target.value }))}
                        >
                          {Object.entries(MASCAL_ALERT_LEVELS).map(([key, val]) => (
                            <MenuItem key={key} value={key}>
                              {val.label || key} - {val.description || ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {/* v3.9: Helper text for educational clarity */}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {t('tab3.med_status_helper')}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box sx={{ p: 1, bgcolor: mascalConfig.blueLightEnabled ? 'info.50' : 'grey.100', borderRadius: 1, border: mascalConfig.blueLightEnabled ? '2px solid' : 'none', borderColor: 'info.main' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={mascalConfig.blueLightEnabled}
                              onChange={(e) => setMascalConfig(prev => ({ ...prev, blueLightEnabled: e.target.checked }))}
                              color="info"
                            />
                          }
                          label={<Typography variant="subtitle2">{t('tab3.mascal_protocol_label')}</Typography>}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('tab3.max_runtime', { h: BLUE_LIGHT_PROTOCOL?.max_runtime_hours || 4 })}<br/>
                          {t('tab3.cooldown_text', { m: BLUE_LIGHT_PROTOCOL?.cooldown_minutes || 120 })}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2">{t('tab3.mascal_triggers')}</Typography>
                          <Tooltip title="Reset to NATO doctrine defaults (AMedP-1.10)">
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', py: 0, px: 0.75, minWidth: 0 }}
                              onClick={() => setMascalConfig(prev => ({
                                ...prev,
                                thresholds: {
                                  mtf_warning:              MASCAL_TRIGGERS?.mtf_utilization?.warning        ?? 80,
                                  mtf_critical:             MASCAL_TRIGGERS?.mtf_utilization?.critical       ?? 95,
                                  surgical_queue_warning:   MASCAL_TRIGGERS?.surgical_queue_hours?.warning   ?? 4,
                                  surgical_queue_critical:  MASCAL_TRIGGERS?.surgical_queue_hours?.critical  ?? 8
                                }
                              }))}
                            >
                              Reset
                            </Button>
                          </Tooltip>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 6 }}>
                            <TextField
                              label={t('tab3.mtf_warning_pct')}
                              type="number"
                              size="small"
                              fullWidth
                              value={mascalConfig.thresholds?.mtf_warning ?? 80}
                              onChange={(e) => {
                                const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 80));
                                setMascalConfig(prev => ({ ...prev, thresholds: { ...prev.thresholds, mtf_warning: v } }));
                              }}
                              inputProps={{ min: 1, max: 100 }}
                              helperText={t('tab3.nato_default_80')}
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <TextField
                              label={t('tab3.mtf_critical_pct')}
                              type="number"
                              size="small"
                              fullWidth
                              value={mascalConfig.thresholds?.mtf_critical ?? 95}
                              onChange={(e) => {
                                const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 95));
                                setMascalConfig(prev => ({ ...prev, thresholds: { ...prev.thresholds, mtf_critical: v } }));
                              }}
                              inputProps={{ min: 1, max: 100 }}
                              helperText={t('tab3.nato_default_95')}
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <TextField
                              label={t('tab3.surg_queue_warn')}
                              type="number"
                              size="small"
                              fullWidth
                              value={mascalConfig.thresholds?.surgical_queue_warning ?? 4}
                              onChange={(e) => {
                                const v = Math.min(48, Math.max(1, parseInt(e.target.value) || 4));
                                setMascalConfig(prev => ({ ...prev, thresholds: { ...prev.thresholds, surgical_queue_warning: v } }));
                              }}
                              inputProps={{ min: 1, max: 48 }}
                              helperText={t('tab3.nato_default_4h')}
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <TextField
                              label={t('tab3.surg_queue_crit')}
                              type="number"
                              size="small"
                              fullWidth
                              value={mascalConfig.thresholds?.surgical_queue_critical ?? 8}
                              onChange={(e) => {
                                const v = Math.min(48, Math.max(1, parseInt(e.target.value) || 8));
                                setMascalConfig(prev => ({ ...prev, thresholds: { ...prev.thresholds, surgical_queue_critical: v } }));
                              }}
                              inputProps={{ min: 1, max: 48 }}
                              helperText={t('tab3.nato_default_8h')}
                            />
                          </Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {t('tab3.mascal_source_citation')}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>{t('tab3.current_response_actions')}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {mascalConfig.currentLevel === 'NORMAL' ? (
                            <Chip label="Normal Operations" color="success" size="small" />
                          ) : (
                            <>
                              <Chip label="Triage Adjustment Active" color="warning" size="small" />
                              <Chip label="Extended Shifts" color="warning" size="small" />
                              {mascalConfig.currentLevel !== 'ELEVATED' && (
                                <>
                                  <Chip label="Recall Off-Duty Staff" color="error" size="small" />
                                  <Chip label="Defer Elective Procedures" color="error" size="small" />
                                </>
                              )}
                              {mascalConfig.blueLightEnabled && (
                                <Chip label="MASCAL Protocol Available" color="info" size="small" />
                              )}
                            </>
                       )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          )}   

    {/* ========================================================================
        PHASE 3 REORGANIZATION: SECTION 3F - MASCAL & ASSET MANAGEMENT
        
        Consolidates medical system capacity management under stress:
        - 3F.1: MASCAL Detection & Blue Light Protocol (in Standard Mode Cards)
        - 3F.2: Asset Degradation & Sharing (combat attrition)
        ======================================================================== */}
      {/* Asset Degradation & Cross-Division Sharing */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{MEDICAL_ICONS.HEARTBEAT} {t('tab3.f2_title')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableAssetDegradation}
                    onChange={(e) => setEnableAssetDegradation(e.target.checked)}
                  />
                }
                label={t('tab3.enable_degradation')}
              />
              <Typography variant="caption" display="block" sx={{ ml: 4, mt: 1 }}>
                Simulate ambulances/helicopters lost to combat operations over time
              </Typography>
              
              {enableAssetDegradation && (
                <Box sx={{ mt: 2, ml: 4 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Expected Daily Loss Rate
                  </Typography>
                  <Slider
                    value={expectedDailyLossRate}
                    onChange={(e, value) => setExpectedDailyLossRate(value)}
                    min={0}
                    max={10}
                    step={0.5}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 2, label: '2%' },
                      { value: 5, label: '5%' },
                      { value: 10, label: '10%' }
                    ]}
                    valueLabelDisplay="on"
                    valueLabelFormat={(value) => `${value}%`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Recommended: 2-3% for moderate intensity, 5%+ for high intensity operations
                  </Typography>
                </Box>
              )}
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableCrossDivisionSharing}
                    onChange={(e) => setEnableCrossDivisionSharing(e.target.checked)}
                  />
                }
                label={t('tab3.cross_div_support')}
              />
              <Typography variant="caption" display="block" sx={{ ml: 4, mt: 1 }}>
                When MEDEVAC capacity is exceeded, route overflow to adjacent divisions before treating casualties as unevacuated
              </Typography>
              
              {enableCrossDivisionSharing && (
                  <Alert severity="info" sx={{ mt: 2, ml: 4 }}>
                  <strong>Cross-Division Support Active:</strong> When a division's MEDEVAC capacity is overwhelmed, 
                  up to {crossDivisionMaxCapacity}% additional MEDEVAC capacity from adjacent divisions will be used.
                  Casualties handled by cross-division MEDEVAC receive full en-route care (base DOW rate).
                  Casualties that still cannot be evacuated are treated as unevacuated (1.60× DOW) in the medical estimate.
                </Alert>
              )}
              
              {/* Phase 2: Expert Mode Cross-Division Configuration */}
              {enableCrossDivisionSharing && mode === 'EXPERT' && (
                <Box sx={{ mt: 3, ml: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Advanced Cross-Division Settings
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* MASCAL-Only Toggle */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={crossDivisionMascalOnly}
                            onChange={(e) => setCrossDivisionMascalOnly(e.target.checked)}
                            size="small"
                          />
                        }
                        label={t('tab3.mascal_only_label')}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Only activate cross-division support during MASCAL conditions
                      </Typography>
                    </Grid>
                    
                    {/* Respect National Caveats Toggle */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={crossDivisionRespectCaveats}
                            onChange={(e) => setCrossDivisionRespectCaveats(e.target.checked)}
                            size="small"
                          />
                        }
                        label={t('tab3.respect_caveats')}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Honor NATIONAL_MEDEVAC and NATIONAL_TREATMENT restrictions
                      </Typography>
                    </Grid>
                    
                    {/* Max Capacity Slider */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" gutterBottom>
                        Maximum Cross-Division Capacity: {crossDivisionMaxCapacity}%
                      </Typography>
                      <Slider
                        value={crossDivisionMaxCapacity}
                        onChange={(e, newValue) => setCrossDivisionMaxCapacity(newValue)}
                        min={10}
                        max={50}
                        step={5}
                        marks={[
                          { value: 10, label: '10%' },
                          { value: 30, label: '30%' },
                          { value: 50, label: '50%' }
                        ]}
                        valueLabelDisplay="auto"
                        size="small"
                        sx={{ maxWidth: 400 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Percentage of adjacent division's capacity available for overflow
                      </Typography>
                    </Grid>
                    
                    {/* Division Support Matrix */}
                    {crossDivisionPairs.length > 0 && (
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Division Support Matrix
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                          Configure which divisions can send casualties to which. Rows = sender, Columns = receiver.
                        </Typography>
                        
                        {/* Helper Buttons */}
                        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            onClick={() => setAllPairsEnabled(true)}
                          >
                            Select All
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            onClick={() => setAllPairsEnabled(false)}
                          >
                            Clear All
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                            onClick={setSameNationOnly}
                          >
                            Same-Nation Only
                          </Button>
                        </Box>
                        
                        {/* Matrix Table */}
                        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 600 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                                  From / To ->
                                </TableCell>
                                {(() => {
                                  // Get unique divisions for columns
                                  const divisions = scenario?.force?.forceBuilderResult?.divisions || [];
                                  return divisions.map((div, idx) => (
                                    <TableCell 
                                      key={div.id} 
                                      align="center"
                                      sx={{ 
                                        fontWeight: 'bold', 
                                        bgcolor: 'grey.100',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      <Tooltip title={`${div.name} (${div.nationalStandard || 'NATO'})`}>
                                        <span>
                                          {div.nationalStandard || 'NATO'}
                                          <br />
                                          Div {idx + 1}
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  ));
                                })()}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(() => {
                                const divisions = scenario?.force?.forceBuilderResult?.divisions || [];
                                return divisions.map((fromDiv, fromIdx) => (
                                  <TableRow key={fromDiv.id}>
                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50', fontSize: '0.75rem' }}>
                                      <Tooltip title={`${fromDiv.name} (${fromDiv.nationalStandard || 'NATO'})`}>
                                        <span>
                                          {fromDiv.nationalStandard || 'NATO'} Div {fromIdx + 1}
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                    {divisions.map((toDiv, toIdx) => {
                                      if (fromIdx === toIdx) {
                                        // BUG-102 FIX: diagonal = self, was showing ? (corrupted unicode) — render dash
                                        return (
                                          <TableCell key={toDiv.id} align="center" sx={{ bgcolor: 'grey.200', color: 'text.disabled' }}>
                                            -
                                          </TableCell>
                                        );
                                      }
                                      
                                      const pair = crossDivisionPairs.find(
                                        p => p.from_id === fromDiv.id && p.to_id === toDiv.id
                                      );
                                      const isEnabled = pair?.enabled ?? false;
                                      const sameNation = pair?.same_nation ?? false;
                                      
                                      return (
                                        <TableCell 
                                          key={toDiv.id} 
                                          align="center"
                                          sx={{ 
                                            bgcolor: sameNation ? 'success.50' : 'warning.50',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: sameNation ? 'success.100' : 'warning.100' }
                                          }}
                                          onClick={() => togglePairEnabled(fromDiv.id, toDiv.id)}
                                        >
                                          {/* BUG-102 FIX: MUI icons replace corrupted unicode that showed as ? */}
                                          <Tooltip title={`${fromDiv.name} -> ${toDiv.name}: ${isEnabled ? "Allowed" : "Blocked"}`}>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                                              {isEnabled
                                                ? <CheckCircleIcon fontSize="small" color="success" />
                                                : <CancelIcon fontSize="small" color="disabled" />
                                              }
                                            </Box>
                                          </Tooltip>
                                        </TableCell>
                                      );
                                    })}
                                  </TableRow>
                                ));
                              })()}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        {/* Legend */}
                        <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Legend:</Typography>
                          {/* BUG-102 FIX: legend uses same MUI icons as matrix cells */}
                          <CheckCircleIcon fontSize="small" color="success" />
                          <Typography variant="caption">Allowed</Typography>
                          <CancelIcon fontSize="small" color="disabled" />
                          <Typography variant="caption">Blocked</Typography>
                          <Box sx={{ width: 16, height: 16, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }} />
                          <Typography variant="caption">Same nation</Typography>
                          <Box sx={{ width: 16, height: 16, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }} />
                          <Typography variant="caption">Different nation</Typography>
                        </Box>
                        
                        {/* Summary */}
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            <strong>Active Pairs:</strong> {crossDivisionPairs.filter(p => p.enabled).length} of {crossDivisionPairs.length} allowed
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ================================================================== */}
      {/* ENH-005: SECTION F.3 — STRATEVAC BOTTLENECK MONITOR                */}
      {/* Reads last calculated baseline from calculationService.getBaseline() */}
      {/* Shows PHA gauges, departure schedule, wait time, MASCAL alert       */}
      {/* ================================================================== */}
      {(() => {
        // BUG-168 FIX: getBaseline()?.results is only populated after Tab5 mounts (storeBaseline).
        // scenario.casualty_projections is the identical results object — written by Tab3 Save & Continue.
        // Checking it as fallback lets F.3 show data immediately after Tab3 Save & Continue,
        // without requiring the planner to navigate to Tab5 first.
        const baselineResults = getBaseline()?.results ?? scenario?.casualty_projections;
        const ssa = baselineResults?.mascal_analysis?.stratevac_schedule_analysis;
        // BUG-153 FIX: Read airEnabled from local React state (strategicEvac), NOT from
        // the scenario prop. The scenario prop only updates after Save & Continue; the
        // local state is live the moment the toggle fires. Using the prop caused F.3 to
        // show "not enabled" even when the planner had turned the toggle ON but not yet
        // navigated away to trigger a save.
        // BUG-133 FIX preserved: when airfields array absent, trust airEnabled alone.
        const airEnabled = strategicEvac.airEnabled && (
          !strategicEvac.airfields ||
          strategicEvac.airfields.some(af => af?.is_active)
        );

        // Section header always visible in Section 3F
        return (
          <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  {MEDICAL_ICONS.HELICOPTER || 'F.3:'} {t('tab3.f3_title')}
                </Typography>
                {ssa?.summary?.any_mascal && (
                  <Chip label="PHA OVERFLOW" size="small" color="error" />
                )}
                {ssa?.summary?.total_backflow_to_r3 > 0 && !ssa?.summary?.any_mascal && (
                  <Chip label="BACKFLOW" size="small" color="warning" />
                )}
                {!ssa && airEnabled && (
                  <Chip label={t('tab3.save_continue_chip')} size="small" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>

              {/* No STRATEVAC configured */}
              {!airEnabled && (
                <Alert severity="info">
                  <Typography variant="body2">
                    STRATEVAC air evacuation is not enabled. Configure airfield schedules
                    in Section 3E to activate this monitor.
                  </Typography>
                </Alert>
              )}

              {/* STRATEVAC enabled but no calculated results yet */}
              {airEnabled && !ssa && (
                <Alert severity="info">
                  <Typography variant="body2">
                    STRATEVAC is configured. <strong>Save &amp; Continue</strong> (or run Tab 5 Calculate)
                    to see PHA bottleneck status, departure schedule, and Corps MASCAL risk.
                  </Typography>
                </Alert>
              )}

              {/* STRATEVAC schedule analysis available */}
              {airEnabled && ssa && (
                <>
                  {/* Corps MASCAL alert — auto-declared when PHA overflows to R3 */}
                  {(ssa.summary?.any_mascal || ssa.summary?.total_backflow_to_r3 > 0) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <AlertTitle>
                        <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        STRATEVAC Bottleneck — Corps MASCAL Risk
                      </AlertTitle>
                      <Typography variant="body2">
                        {ssa.summary?.total_backflow_to_r3 > 0 && (
                          <>{ssa.summary.total_backflow_to_r3} patients projected to backflow
                          from PHA to Role 3. Role 3 overflow triggers Corps-level MASCAL
                          declaration (AJMedP-1). </>
                        )}
                        {ssa.summary?.total_wait_dow_penalty > 0 && (
                          <>Wait-time DOW penalty: +{ssa.summary.total_wait_dow_penalty.toFixed(1)} patients
                          (patients held &gt;72h at PHA — AJMedP-1 §4). </>
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Corrective actions:</strong> Add a flight on the most congested day,
                        increase PHA/CSU bed capacity, or add a second aircraft on peak days (schedule_b).
                      </Typography>
                    </Alert>
                  )}

                  {/* Per-airfield PHA gauges */}
                  {(ssa.airfields ?? []).map((af, afIdx) => {
                    if (af.error) return (
                      <Alert key={afIdx} severity="warning" sx={{ mb: 1 }}>
                        {af.airfield_label}: {af.error}
                      </Alert>
                    );
                    const timeline  = af.timeline ?? [];
                    const afSummary = af.summary ?? {};

                    // Peak PHA fill across all days
                    const peakFrac = timeline.reduce(
                      (mx, t) => Math.max(mx, t.pha_fraction ?? 0), 0
                    );
                    const peakDay  = timeline.find(
                      t => (t.pha_fraction ?? 0) === peakFrac
                    )?.day ?? null;
                    const peakPct  = Math.round(peakFrac * 100);

                    // Gauge colour
                    const gaugeColor = peakFrac >= 1.0 ? 'error'
                                     : peakFrac >= 0.75 ? 'warning'
                                     : peakFrac >= 0.50 ? 'info'
                                     : 'success';

                    // Next departure after the worst day
                    const opDays = scenario?.operation_parameters?.duration_days ?? 14;
                    const departureDays = af.departure_days ?? [];
                    const futureDeps = departureDays.filter(d => d <= opDays);

                    // Worst-case wait time
                    const maxWait = timeline.reduce(
                      (mx, t) => Math.max(mx, t.wait_hours ?? 0), 0
                    );

                    return (
                      <Box key={afIdx} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2">
                            {af.is_primary ? 'Primary' : 'Alternate'}: {af.airfield_label}
                          </Typography>
                          <Chip size="small" label={af.aircraft_label || af.aircraft_type} variant="outlined" color="info" />
                          <Chip size="small" label={`${af.pha_beds} CSU beds`} variant="outlined" />
                          {afSummary.mascal_days > 0 && (
                            <Chip size="small" label={`${afSummary.mascal_days}d MASCAL`} color="error" />
                          )}
                          {afSummary.warning_days > 0 && !afSummary.mascal_days && (
                            <Chip size="small" label={`${afSummary.warning_days}d WARNING`} color="warning" />
                          )}
                        </Box>

                        {/* PHA fill gauge */}
                        <Box sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">
                              Peak PHA fill{peakDay != null ? ` (Day ${peakDay})` : ''}
                            </Typography>
                            <Typography variant="caption" fontWeight={600}
                              color={peakFrac >= 1 ? 'error.main' : peakFrac >= 0.75 ? 'warning.main' : 'success.main'}>
                              {peakPct}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, peakPct)}
                            color={gaugeColor}
                            sx={{ height: 8, borderRadius: 1 }}
                          />
                        </Box>

                        {/* Stats row */}
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Arrived: <strong>{afSummary.total_arrived ?? '--'}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Departed: <strong>{afSummary.total_departed ?? '--'}</strong>
                          </Typography>
                          {(afSummary.total_backflow ?? 0) > 0 && (
                            <Typography variant="caption" color="error.main">
                              Backflow: <strong>{afSummary.total_backflow}</strong>
                            </Typography>
                          )}
                          {maxWait > 0 && (
                            <Typography variant="caption"
                              color={maxWait > 72 ? 'error.main' : maxWait > 48 ? 'warning.main' : 'text.secondary'}>
                              Max wait: <strong>{maxWait}h</strong>
                              {maxWait > 72 && ' (DOW penalty)'}
                            </Typography>
                          )}
                        </Box>

                        {/* Departure schedule */}
                        {futureDeps.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">Flights:</Typography>
                            {futureDeps.map(d => {
                              const dayData = timeline.find(t => t.day === d);
                              const isProblematic = (dayData?.pha_queue ?? 0) > af.pha_beds * 0.75;
                              return (
                                <Chip
                                  key={d}
                                  size="small"
                                  label={`D${d} (${dayData?.departed ?? '?'} pts)`}
                                  color={isProblematic ? 'warning' : 'default'}
                                  variant={isProblematic ? 'filled' : 'outlined'}
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })}

                  {/* Doctrinal footer */}
                  <Typography variant="caption" color="text.secondary">
                    PHA thresholds: &lt;50% GREEN · 50-74% ELEVATED · 75-99% WARNING · &ge;100% MASCAL (AJMedP-1).
                    Backflow to Role 3 triggers Corps-level MASCAL declaration. Wait &gt;72h incurs DOW penalty.
                    Results from last calculation — re-run after modifying STRATEVAC schedule.
                  </Typography>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })()}

      {/* ================================================================== */}
      {/* INC-010: SECTION F.4 — MSK SURGE THRESHOLD (Expert Mode)           */}
      {/* Configures the T1/day threshold that triggers blood surge multipliers*/}
      {/* NATO DES Framework 2024: PRBC×1.5, FFP×1.3, PLT×1.2 above threshold*/}
      {/* ================================================================== */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">🩸 {t('tab3.f4_title')}</Typography>
            {mskSurgeThreshold !== 12 && (
              <Chip label={`Custom: ${mskSurgeThreshold}/day`} size="small" color="warning" variant="outlined" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  When T1 casualties exceed this threshold per day, surgical demand overwhelms normal
                  throughput — blood requirements spike. Default 12/day per NATO DES Framework 2024.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label={t('tab3.t1_surge_threshold')}
                    type="number"
                    size="small"
                    value={mskSurgeThreshold}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 6 && val <= 24) setMskSurgeThreshold(val);
                    }}
                    inputProps={{ min: 6, max: 24, step: 1 }}
                    sx={{ width: 220 }}
                    helperText="Range: 6–24 T1/day"
                  />
                  {mskSurgeThreshold !== 12 && (
                    <Button size="small" variant="outlined" onClick={() => setMskSurgeThreshold(12)}>
                      Reset to default (12)
                    </Button>
                  )}
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Chip
                    size="small"
                    label={mskSurgeThreshold < 12 ? `Surge triggers earlier — higher blood demand` : mskSurgeThreshold === 12 ? `NATO DES baseline — standard blood demand` : `Surge triggers later — lower blood demand`}
                    color={mskSurgeThreshold < 10 ? 'error' : mskSurgeThreshold === 12 ? 'success' : 'warning'}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Box>

                <Button
                  size="small"
                  variant="text"
                  startIcon={<InfoOutlinedIcon fontSize="small" />}
                  onClick={() => setShowMskInfo(s => !s)}
                  sx={{ mb: 1, fontSize: '0.75rem' }}
                >
                  {showMskInfo ? 'Hide' : 'Show'} doctrine &amp; guidance
                </Button>

                <Collapse in={showMskInfo}>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', fontSize: '0.78rem', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      MSK SURGE THRESHOLD — Multi-Trauma Overwhelm
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                      When T1 casualties exceed this number per day, surgical demand overwhelms normal
                      throughput — operating time, blood, and anaesthesia all spike.
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 1, mb: 0.5 }}>
                      EFFECTS WHEN THRESHOLD EXCEEDED
                    </Typography>
                    <Typography variant="caption" display="block">• Blood requirements: PRBC ×1.5 | FFP ×1.3 | Platelets ×1.2</Typography>
                    <Typography variant="caption" display="block">• Mechanism: Surgical backlog → delayed treatment → worse coagulopathy → more blood</Typography>
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>• Source: NATO DES Framework for M&amp;S of Medical Support (2024)</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 1, mb: 0.5 }}>
                      GUIDANCE — THRESHOLD SELECTION
                    </Typography>
                    <Typography variant="caption" display="block">• 12/day (default) — NATO DES baseline; peer adversary, mixed terrain</Typography>
                    <Typography variant="caption" display="block">• 8–10/day — High-intensity peer warfare, urban combat, massed fire</Typography>
                    <Typography variant="caption" display="block">• 14–18/day — Limited conflict, sub-peer adversary, dispersed engagement</Typography>
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>• &gt;18/day — Counter-insurgency, low-kinetic scenario</Typography>
                    <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                      <Typography variant="caption">
                        ⚠ Lowering this threshold increases blood requirements. Verify supply chain
                        capacity in Tab 4 before reducing below 10.
                      </Typography>
                    </Alert>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      Reference: NATO ACT/STO — Framework for M&amp;S of Medical Support (2024) · MSK-001
                    </Typography>
                  </Paper>
                </Collapse>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ================================================================== */}
      {/* SECTION F.5 — PSYCHIATRIC SUPPORT (planning assumption)            */}
      {/* Dr. Tatar 2026-10-10: some psychiatric support lowers the          */}
      {/* evacuation and the days a casualty needs treatment.                */}
      {/* ================================================================== */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{t('tab3.f5_title')}</Typography>
            {psychiatricSupport && (
              <Chip label={PSYCHIATRIC_SUPPORT_LEVELS[psychiatricSupport]?.label || psychiatricSupport} size="small" color="primary" variant="outlined" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('tab3.f5_intro')}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 320 }}>
            <InputLabel>{t('tab3.f5_level')}</InputLabel>
            <Select
              value={psychiatricSupport}
              label={t('tab3.f5_level')}
              onChange={(e) => setPsychiatricSupport(e.target.value)}
            >
              <MenuItem value="">{t('tab3.f5_not_chosen')}</MenuItem>
              {Object.entries(PSYCHIATRIC_SUPPORT_LEVELS).map(([key, lvl]) => (
                <MenuItem key={key} value={key}>{lvl.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(psychiatricSupport || 'STANDARD') && (
            <Typography variant="caption" display="block" sx={{ mt: 1.5 }}>
              {PSYCHIATRIC_SUPPORT_LEVELS[psychiatricSupport || 'STANDARD'].description}
            </Typography>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            {t('tab3.f5_effect', {
              rtd: PSYCHIATRIC_SUPPORT_LEVELS[psychiatricSupport || 'STANDARD'].rtd72Multiplier,
              days: PSYCHIATRIC_SUPPORT_LEVELS[psychiatricSupport || 'STANDARD'].treatmentDaysMultiplier
            })}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            {t('tab3.f5_reference')}
          </Typography>
        </AccordionDetails>
      </Accordion>

        </CardContent>
      </Card>

{/* 3G SECTION */}

      <Card sx={{ mb: 3, border: '3px solid', borderColor: '#757575', borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#757575', mb: 3 }}>
            {t('tab3.section_3g')} {MEDICAL_ICONS.TENT}
          </Typography>

      {/* Advanced Staging Facilities - PHASE 2 ENHANCED */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{MEDICAL_ICONS.TENT} {t('tab3.advanced_staging_title')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={useAdvancedStaging}
                onChange={(e) => setUseAdvancedStaging(e.target.checked)}
              />
            }
            label={t('tab3.enable_adv_staging')}
          />
          
          {useAdvancedStaging && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Advanced staging facilities:</strong>  use NATO standard configurations
                  with automatic personnel and supply calculations. Select facility size when adding new facilities.
                </Typography>
              </Alert>
              
              {/* Casualty Staging Units & Patient Holding Areas */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    PHA &amp; CSU — Patient Holding Areas &amp; Casualty Staging Units
                  </Typography>
                  <Box>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenSizeSelector('PHA')}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    >
                      Add PHA
                    </Button>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenSizeSelector('CSU')}
                      size="small"
                      variant="outlined"
                    >
                      Add CSU
                    </Button>
                  </Box>
                </Box>

                {stagingFacilities.length === 0 && (
                  <Alert severity="info">
                    No staging facilities configured. Click "Add PHA" (Patient Holding Area, Role 2B forward holding)
                    or "Add CSU" (Casualty Staging Unit, STRATEVAC airfield staging) to create facilities
                    with NATO standard configurations (Small/Medium/Large).
                  </Alert>
                )}
                
                {stagingFacilities.map((facility, index) => {
                  // UI-3D-002: per-type index so CSU #1, CSU #2, PHA #1 instead of CSU #1, CSU #2, PHA #3
                  const typeIndex = stagingFacilities
                    .slice(0, index)
                    .filter(f => f.facility_type === facility.facility_type).length;
                  return renderStagingFacilityCard(facility, typeIndex);
                })}
              </Box>
              
              {/* Helicopter Landing Zones */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Helicopter Landing Zones (HLZ)
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenSizeSelector('HLZ')}
                    size="small"
                    variant="outlined"
                  >
                    Add HLZ
                  </Button>
                </Box>
                
                {helicopterFacilities.length === 0 && (
                  <Alert severity="info">
                    No HLZ facilities configured. Click "Add HLZ" to create a helicopter landing zone
                    with NATO standard configuration.
                  </Alert>
                )}
                
                {helicopterFacilities.map((hlz, index) => renderHLZCard(hlz, index))}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

        </CardContent>
      </Card>
    </Box>
  );
  
  // ========================================================================
  // UI COMPONENTS - VALIDATION DISPLAY
  // ========================================================================
  
  const renderValidation = () => (
    <Box sx={{ mb: 3 }}>
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Validation Errors ({validationErrors.length})</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, idx) => (
              <li key={idx}><Typography variant="body2">{error}</Typography></li>
            ))}
          </ul>
        </Alert>
      )}
      
      {validationWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Validation Warnings ({validationWarnings.length})</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationWarnings.map((warning, idx) => (
              <li key={idx}><Typography variant="body2">{warning}</Typography></li>
            ))}
          </ul>
        </Alert>
      )}
      
      {validationErrors.length === 0 && validationWarnings.length === 0 && (
        <Alert severity="success">
          <AlertTitle>{t('tab3.config_valid')}</AlertTitle>
          {t('tab3.config_valid_msg')}
        </Alert>
      )}
    </Box>
  );
  
  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  // IMP-005: Corps phases for planned displacement UI
  const corpsPhases = scenario?.operation_parameters?.corps_config?.phases || [];

  // OBS-S96-006: ORBAT-derived R1 baseline — armoured + wheeled per nation
  const orbatR1Fleet = useMemo(() => {
    const brigades = scenario?.force?.forceBuilderResult?.brigades
                  || scenario?.force?.brigades || [];
    const byNation = {};
    let totalWheeled = 0, totalArmored = 0;
    brigades.forEach(brigade => {
      const nation = brigade.national_standard || brigade.nationalStandard || 'UNKNOWN';
      if (!byNation[nation]) byNation[nation] = { wheeled: 0, armored: 0 };
      const subUnits = brigade.sub_units || [];
      subUnits.forEach(su => {
        const profile = lookupByUnitType(UNIT_TYPE_TO_ROLE1_PROFILE, su.unit_type, 'UNIT_TYPE_TO_ROLE1_PROFILE');
        const assets  = ROLE1_ORGANIC_ASSETS[profile];
        if (!assets) return;
        const w = assets.ground_ambulances?.wheeled?.count || 0;
        const a = assets.ground_ambulances?.armored?.count || 0;
        byNation[nation].wheeled += w;
        byNation[nation].armored += a;
        totalWheeled += w;
        totalArmored += a;
      });
    });
    return { total: { wheeled: totalWheeled, armored: totalArmored, all: totalWheeled + totalArmored }, by_nation: byNation };
  }, [scenario?.force?.forceBuilderResult?.brigades, scenario?.force?.brigades]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
         <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: '#f44336', mb: 3 }}>
          {t('tab3.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('tab3.subtitle')}
        </Typography>
      </Box>

      {/* Progress Indicator */}
      {scenario?.force && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('tab3.force_echelon')}</Typography>
              <Typography variant="h6">{scenario.force.echelon}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('tab3.national_standard')}</Typography>
              <Typography variant="h6">{extractPrimaryNationalStandard()}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('tab3.config_mode')}</Typography>
              <Typography variant="h6">{mode}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('tab3.config_status')}</Typography>
              <Chip label={t('tab3.nato_standards')} color="success" size="small" />
            </Grid>
          </Grid>
        </Paper>
      )}
   
      {/* PHASE 2.5: ForceBuilder Medical Facilities Summary */}
      {/* UI-011: Per-facility expandable detail panel */}
      {hasForceBuilderData && forceBuilderFacilities.length > 0 && (
        <Card sx={{ mb: 3, border: '3px solid', borderColor: '#2196f3', borderRadius: 2, boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
             
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2196f3', mb: 3 }}>{MEDICAL_ICONS.RED_CROSS}
                {t('tab3.section_3a')}
              </Typography>
              <Chip 
                label={`${forceBuilderFacilities.length} total`} 
                size="small" 
                color="primary" 
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('tab3.click_expand_info')}
            </Typography>
            
            {/* Facility Type Tabs */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`${t('tab3.role1')}: ${forceBuilderFacilities.filter(f => f.type === 'ROLE1').length}`}
                size="small"
                sx={{ bgcolor: '#e3f2fd' }}
              />
              <Chip
                label={`${t('tab3.role2b')}: ${forceBuilderFacilities.filter(f => f.type === 'ROLE2_BASIC').length}`}
                size="small"
                sx={{ bgcolor: '#f3e5f5' }}
              />
              <Chip
                label={`${t('tab3.role2e')}: ${forceBuilderFacilities.filter(f => f.type === 'ROLE2_ENHANCED').length}`}
                size="small"
                sx={{ bgcolor: '#fff3e0' }}
              />
              {forceBuilderFacilities.some(f => f.type === 'ROLE3') && (
                <Chip
                  label={`${t('tab3.role3')}: ${forceBuilderFacilities.filter(f => f.type === 'ROLE3').length}`}
                  size="small"
                  sx={{ bgcolor: '#e8f5e9' }}
                />
              )}
            </Box>
            
            {/* Expandable Facility Accordions */}
            {forceBuilderFacilities.map((facility, index) => (
              <Accordion 
                key={facility.id}
                sx={{ 
                  mb: 1,
                  '&:before': { display: 'none' },
                  bgcolor: facility.type === 'ROLE1' ? '#e3f2fd' : 
                           facility.type === 'ROLE2_BASIC' ? '#f3e5f5' :
                           facility.type === 'ROLE2_ENHANCED' ? '#fff3e0' :
                           facility.type === 'ROLE3' ? '#e8f5e9' : '#fce4ec',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {/* UI-012: Human-readable facility name */}
                    <Typography variant="body2" fontWeight="bold">
                      {facility.name}
                    </Typography>
                    {/* ENUM-SWEEP FIX: human-readable facility type labels. */}
                    <Chip
                      label={({ ROLE1: 'Role 1', ROLE2_BASIC: 'Role 2 Basic', ROLE2_ENHANCED: 'Role 2 Enhanced', ROLE3: 'Role 3' }[facility.type] ?? facility.type)}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 'auto', mr: 2 }}
                    />
                    {/* Only show golden hour warning for pre-surgical facilities (Role 1, Role 2 Basic) */}
                    {needsGoldenHourCheck(facility) && facility.evacuation?.golden_hour_compliant === false && (
                      <Chip 
                        icon={<WarningIcon />}
                        label={`Warning: >${GOLDEN_HOUR_MINUTES}min`}
                        size="small" 
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* Basic Info - Enhanced with UI-007 Expert Mode Editing */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>Basic Information</Typography>
                      <Box sx={{ pl: 1 }}>
                        {facility.hierarchy && !facility.hierarchy.legacy && (
                          <Typography variant="caption" display="block" color="primary">
                            {[
                              facility.hierarchy.corps,
                              facility.hierarchy.division,
                              facility.hierarchy.brigade,
                              facility.hierarchy.battalion
                            ].filter(Boolean).join(" -> ")}
                          </Typography>
                        )}
                        
                        {/* UI-007: Expert Mode Inline Editing */}
                        {mode === 'EXPERT' ? (
                          <Box sx={{ mt: 1 }}>
                            <Grid container spacing={1}>
                              <Grid size={{ xs: 6 }}>
                                <TextField
                                  label={t('tab3.beds')}
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={facility.beds || 0}
                                  onChange={(e) => handleFacilityPropertyUpdate(
                                    facility.id || facility.facility_id,
                                    'beds',
                                    parseInt(e.target.value) || 0
                                  )}
                                  inputProps={{ min: 0, max: 500 }}
                                />
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <TextField
                                  label={t('tab3.position_km')}
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={facility.position_feba_km || 0}
                                  onChange={(e) => handleFacilityPropertyUpdate(
                                    facility.id || facility.facility_id,
                                    'position_feba_km',
                                    parseInt(e.target.value) || 0
                                  )}
                                  inputProps={{ min: 0, max: 500 }}
                                  helperText="Distance from FEBA"
                                />
                              </Grid>
                              {facility.has_surgical && (
                                <Grid size={{ xs: 6 }}>
                                  <TextField
                                    label={t('tab3.surgical_teams')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={facility.surgical_teams || 0}
                                    onChange={(e) => handleFacilityPropertyUpdate(
                                      facility.id || facility.facility_id,
                                      'surgical_teams',
                                      parseInt(e.target.value) || 0
                                    )}
                                    inputProps={{ min: 0, max: 20 }}
                                  />
                                </Grid>
                              )}
                              {(facility.type === 'ROLE2_ENHANCED' || facility.type === 'ROLE3') && (
                                <Grid size={{ xs: 6 }}>
                                  <TextField
                                    label={t('tab3.icu_beds')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={facility.icu_beds || 0}
                                    onChange={(e) => handleFacilityPropertyUpdate(
                                      facility.id || facility.facility_id,
                                      'icu_beds',
                                      parseInt(e.target.value) || 0
                                    )}
                                    inputProps={{ min: 0, max: 50 }}
                                  />
                                </Grid>
                              )}

                              {/* INC-010: Forward Field Hospital positioning override — ROLE2_ENHANCED only */}
                              {facility.type === 'ROLE2_ENHANCED' && (() => {
                                const fId = facility.id || facility.facility_id;
                                const posKm = facility.position_feba_km || 0;
                                const threshold = 40;
                                const autoIsForward = posKm > 0 && posKm < threshold;
                                const override = facility.forward_fh_override; // undefined | true | false
                                const showFHInfo = fhInfoOpenId === fId;
                                return (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                                      {/* Header row */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <ScienceIcon fontSize="small" color="primary" />
                                        <Typography variant="caption" sx={{ fontWeight: 700, flexGrow: 1 }}>
                                          Forward Field Hospital Mode
                                        </Typography>
                                        <IconButton size="small" onClick={() => setFhInfoOpenId(showFHInfo ? null : fId)}>
                                          <InfoOutlinedIcon fontSize="small" color={showFHInfo ? 'primary' : 'action'} />
                                        </IconButton>
                                      </Box>

                                      {/* Auto-detection status line */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <Chip
                                          size="small"
                                          label={override !== undefined
                                            ? (override ? '🔬 Expert Override: FORWARD' : '🔬 Expert Override: STANDARD')
                                            : (autoIsForward ? '● Auto: FORWARD' : posKm > 0 ? '○ Auto: STANDARD' : '○ Auto: NO DATA')}
                                          color={override === true || (override === undefined && autoIsForward) ? 'success' : 'default'}
                                          variant={override !== undefined ? 'filled' : 'outlined'}
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                        {override === undefined && posKm > 0 && (
                                          <Typography variant="caption" color="text.secondary">
                                            {posKm}km {autoIsForward ? '<' : '≥'} {threshold}km threshold
                                          </Typography>
                                        )}
                                        {override === undefined && posKm === 0 && (
                                          <Typography variant="caption" color="text.secondary">
                                            Set position above to enable auto-detection
                                          </Typography>
                                        )}
                                      </Box>

                                      {/* Three-state toggle */}
                                      <ToggleButtonGroup
                                        value={override === undefined ? 'auto' : override ? 'forward' : 'standard'}
                                        exclusive
                                        size="small"
                                        onChange={(_, val) => {
                                          if (!val) return;
                                          handleFacilityPropertyUpdate(fId, 'forward_fh_override',
                                            val === 'auto' ? undefined : val === 'forward');
                                        }}
                                        sx={{ width: '100%' }}
                                      >
                                        <ToggleButton value="auto" sx={{ flex: 1, fontSize: '0.7rem', py: 0.5 }}>
                                          AUTO
                                        </ToggleButton>
                                        <ToggleButton value="forward" sx={{ flex: 1, fontSize: '0.7rem', py: 0.5 }}>
                                          FORCE FORWARD
                                        </ToggleButton>
                                        <ToggleButton value="standard" sx={{ flex: 1, fontSize: '0.7rem', py: 0.5 }}>
                                          FORCE STANDARD
                                        </ToggleButton>
                                      </ToggleButtonGroup>

                                      {/* Collapsible doctrine InfoPanel */}
                                      <Collapse in={showFHInfo}>
                                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.72rem' }}>
                                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                            FORWARD FIELD HOSPITAL — AJMedP-1 / NATO Role 2E Doctrine
                                          </Typography>
                                          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                            A surgical facility positioned to provide damage control surgery within the Golden Hour
                                            of wounding — typically within <strong>40km of the FEBA</strong> to enable direct helicopter
                                            evacuation from point of injury.
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                                            EFFECT ON CALCULATIONS
                                          </Typography>
                                          <Typography variant="caption" display="block">
                                            • <strong>−19% Died-of-Wounds rate</strong> (NATO DES Framework 2024: 49.5% vs 61% mortality at 12 T1/day)
                                          </Typography>
                                          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                            • <strong>+Golden Hour compliance bonus</strong> — faster surgical access improves achievement %
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                                            WHEN TO USE EACH MODE
                                          </Typography>
                                          <Typography variant="caption" display="block">
                                            <strong>AUTO</strong> — recommended. Tool reads your configured position and applies the 40km threshold automatically.
                                          </Typography>
                                          <Typography variant="caption" display="block">
                                            <strong>FORCE FORWARD</strong> — position data is estimated; excellent MEDEVAC coverage compensates for &gt;40km distance; modelling a &quot;push R2E forward&quot; CoA.
                                          </Typography>
                                          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                            <strong>FORCE STANDARD</strong> — facility is displacing or under threat; position &lt;40km is a planning default not the actual posture; coalition facility with limited surgical throughput.
                                          </Typography>
                                          <Typography variant="caption" color="warning.main" display="block">
                                            ⚠ Override applies to the entire exercise duration. For phase-specific degradation, use <strong>Planned Displacements</strong> below (visible once Corps HQ phases are configured).
                                          </Typography>
                                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                                            Ref: NATO ACT/STO — Framework for M&S of Medical Support (2024) · AJMedP-1 Ch.4
                                          </Typography>
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  </Grid>
                                );
                              })()}
                              {['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3'].includes(facility.type) && (
                                <Grid size={{ xs: 12 }}>
                                  <TextField
                                    label={t('tab3.location_grid')}
                                    size="small"
                                    fullWidth
                                    placeholder="e.g. VIC CP BRAVO, 33TYN 4521 8832"
                                    value={facility.location || ''}
                                    onChange={(e) => handleFacilityPropertyUpdate(
                                      facility.id || facility.facility_id,
                                      'location',
                                      e.target.value
                                    )}
                                    helperText="Optional — appears in MEDSITREP and sketch map"
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        ) : (
                          /* Standard Mode: Read-only display */
                          <>
                            <Typography variant="caption" display="block">
                              <strong>Beds:</strong> {facility.beds} |
                              <strong> Personnel:</strong> {
                                typeof facility.personnel === 'object'
                                  ? Object.values(facility.personnel).reduce((sum, val) => sum + val, 0)
                                  : facility.personnel
                              }
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>FEBA Distance:</strong> {facility.position_feba_km || 'N/A'} km
                            </Typography>
                            {['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3'].includes(facility.type) && facility.location && (
                              <Typography variant="caption" display="block">
                                <strong>Location:</strong> {facility.location}
                              </Typography>
                            )}
                            {/* BUG-T17 fix: only show if surgical_teams > 0; || 2 fallback was misleading */}
                            {facility.has_surgical && (facility.surgical_teams || 0) > 0 && (
                              <Chip
                                label={`${facility.surgical_teams} Surgical Teams`}
                                size="small"
                                color="secondary"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    </Grid>
                    
                    {/* Evacuation Info */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>Evacuation Route</Typography>
                      <Box sx={{ pl: 1 }}>
                        {facility.evacuation?.receiving_facility_name ? (
                          <>
                            <Typography variant="caption" display="block">
                              <strong>Evacuates to:</strong> {facility.evacuation.receiving_facility_name}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Distance:</strong> {facility.evacuation.distance_km} km | 
                              <strong> Time:</strong> {facility.evacuation.estimated_time_min} min
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Terrain:</strong> {facility.evacuation.terrain_type} | 
                              <strong> Route:</strong> {facility.evacuation.route_type}
                            </Typography>
                            <Chip 
                              label={!needsGoldenHourCheck(facility) ? "Has Surgical Capability" : (facility.evacuation.golden_hour_compliant ? "Golden Hour OK" : `EXCEEDS ${GOLDEN_HOUR_MINUTES} min`)}
                              size="small" 
                              color={!needsGoldenHourCheck(facility) ? 'info' : (facility.evacuation.golden_hour_compliant ? 'success' : 'error')}
                              sx={{ mt: 0.5 }}
                              title={!needsGoldenHourCheck(facility) ? 'This facility has organic surgical capability — it IS the surgical destination. Golden Hour compliance is measured for pre-surgical facilities (Role 1, Role 2B) only.' : ''}
                            />
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No evacuation route configured (top-level facility)
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    
                    {/* Organic Assets (if available) */}
                    {facility.organic_assets && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" gutterBottom>Organic Assets</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 1 }}>
                          {facility.organic_assets.ambulances_wheeled > 0 && (
                            <Chip label={`${facility.organic_assets.ambulances_wheeled} Wheeled Amb`} size="small" variant="outlined" />
                          )}
                          {facility.organic_assets.ambulances_armored > 0 && (
                            <Chip label={`${facility.organic_assets.ambulances_armored} Armored Amb`} size="small" variant="outlined" />
                          )}
                          {facility.organic_assets.medevac_helicopters > 0 && (
                            <Chip label={`${facility.organic_assets.medevac_helicopters} MEDEVAC Heli`} size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      </Grid>
                    )}

                    {/* ENH-002: Attached Assets */}
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="subtitle2">Attached Assets</Typography>
                        <Tooltip title="Attach external assets (helicopters or ambulances) via MOU/SOFA for a specific day range">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenAttachDialog(facility.id || facility.facility_id)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {Array.isArray(facility.attached_assets) && facility.attached_assets.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1 }}>
                          {facility.attached_assets.map((a, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={`${a.quantity}x ${a.model} D${a.attach_day}${a.detach_day != null ? `-D${a.detach_day}` : '+'}`}
                              onDelete={() => handleRemoveAttachment(facility.id || facility.facility_id, idx)}
                              title={`${a.source_unit}${a.mou_reference ? ' / ' + a.mou_reference : ''}`}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                          No attached assets — organic only
                        </Typography>
                      )}
                    </Grid>

                    {/* ENH-002: Detached Assets */}
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="subtitle2">Detached Assets</Typography>
                        <Tooltip title="Record assets temporarily assigned to another unit (reduces effective capacity for that day range)">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDetachDialog(facility.id || facility.facility_id)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {Array.isArray(facility.detached_assets) && facility.detached_assets.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1 }}>
                          {facility.detached_assets.map((d, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`-${d.quantity}x ${d.asset_type} D${d.detach_day}${d.return_day != null ? `-D${d.return_day}` : '+'}`}
                              onDelete={() => handleRemoveDetachment(facility.id || facility.facility_id, idx)}
                              title={`To: ${d.destination_unit}`}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                          No detachments recorded
                        </Typography>
                      )}
                    </Grid>

                    {/* ENH-006: Activation Window */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('tab3.activation_window')}
                        <Tooltip title="AJMedP-1 doctrinal setup times: Role 1 = 1h, Role 2B = 12h, Role 2E = 24h, Role 3 = 48h. Set Activate Day to model a facility that deploys mid-operation. Setup Hours auto-fills from doctrine when you set Activate Day. Blank fields = always active, instant setup, full capacity from Day 1.">
                          <InfoIcon sx={{ fontSize: '0.9rem', ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                        </Tooltip>
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 4 }}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            label={t('tab3.activate_day')}
                            placeholder="1"
                            inputProps={{ min: 1, max: scenario?.operation_parameters?.duration_days ?? 30 }}
                            value={facility.activate_day ?? ''}
                            onChange={e => handleFacilityActivationUpdate(
                              facility.id || facility.facility_id, 'activate_day', e.target.value
                            )}
                            helperText="Blank = Day 1"
                          />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            label={t('tab3.deactivate_day')}
                            placeholder="—"
                            inputProps={{ min: 1, max: scenario?.operation_parameters?.duration_days ?? 30 }}
                            value={facility.deactivate_day ?? ''}
                            onChange={e => handleFacilityActivationUpdate(
                              facility.id || facility.facility_id, 'deactivate_day', e.target.value
                            )}
                            helperText="Blank = end of op"
                          />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            label={t('tab3.setup_hours')}
                            placeholder="—"
                            inputProps={{ min: 0, max: 168 }}
                            value={facility.setup_hours ?? ''}
                            onChange={e => handleFacilityActivationUpdate(
                              facility.id || facility.facility_id, 'setup_hours', e.target.value
                            )}
                            helperText={(() => {
                              const doctrinal = getDoctrinalSetupHours(facility.type || facility.facility_type);
                              return doctrinal != null
                                ? `AJMedP-1 default: ${doctrinal}h`
                                : 'Blank = instant';
                            })()}
                          />
                        </Grid>
                      </Grid>
                      {/* Doctrinal reference note — always visible */}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        AJMedP-1 setup times: Role 1 = {MTF_SETUP_TIMES_HOURS.ROLE_1 ?? 1}h
                        &nbsp;&bull;&nbsp;Role 2B = {MTF_SETUP_TIMES_HOURS.ROLE_2B ?? 12}h
                        &nbsp;&bull;&nbsp;Role 2E = {MTF_SETUP_TIMES_HOURS.ROLE_2E ?? 24}h
                        &nbsp;&bull;&nbsp;Role 3 = {MTF_SETUP_TIMES_HOURS.ROLE_3 ?? 48}h
                      </Typography>
                      {/* IMP-005: Planned displacements per phase (AJMedP-1) */}
                      {corpsPhases.length > 0 && ['ROLE2_BASIC', 'ROLE2_ENHANCED'].includes(facility.type) && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Planned Displacements (AJMedP-1)
                          </Typography>
                          {(facility.phase_displacements || []).map(disp => {
                            const ph = corpsPhases[disp.phase_index];
                            const phLabel = `Ph.${ph?.phase_number || disp.phase_index + 1}`;
                            const fId = facility.id || facility.facility_id;
                            return (
                              <Box key={disp.phase_index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Chip size="small" label={phLabel} variant="outlined" color="primary" sx={{ fontSize: '0.7rem', height: 22 }} />
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                  <Select value={disp.cause}
                                    onChange={e => handleFacilityDisplacementUpdate(fId, disp.phase_index, {
                                      cause: e.target.value,
                                      move_distance_km: DISP_CAUSE_DEFAULTS[e.target.value]?.km ?? disp.move_distance_km
                                    })}>
                                    <MenuItem value="INDIRECT_FIRE">Indirect fire threat</MenuItem>
                                    <MenuItem value="ENEMY_ADVANCE">Enemy advance</MenuItem>
                                    <MenuItem value="OPERATIONAL">Operational redeployment</MenuItem>
                                  </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 90 }}>
                                  <Select value={disp.gap_hrs}
                                    onChange={e => handleFacilityDisplacementUpdate(fId, disp.phase_index, { gap_hrs: Number(e.target.value) })}>
                                    {[2, 4, 6, 8, 12, 24].map(h => <MenuItem key={h} value={h}>{h}h gap</MenuItem>)}
                                  </Select>
                                </FormControl>
                                {disp.gap_hrs > 2  && <Chip size="small" color="warning" label="Blood chain" sx={{ fontSize: '0.65rem', height: 20 }} />}
                                {disp.gap_hrs > 4  && <Chip size="small" color="error"   label="Surgical gap" sx={{ fontSize: '0.65rem', height: 20 }} />}
                                <IconButton size="small" onClick={() => handleFacilityDisplacementUpdate(fId, disp.phase_index, null)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            );
                          })}
                          {corpsPhases.map((ph, phIdx) => {
                            const configured = (facility.phase_displacements || []).some(d => d.phase_index === phIdx);
                            if (configured) return null;
                            const fId = facility.id || facility.facility_id;
                            return (
                              <Button key={phIdx} size="small" variant="text" startIcon={<AddIcon />}
                                onClick={() => handleFacilityDisplacementUpdate(fId, phIdx, { cause: 'INDIRECT_FIRE', gap_hrs: 4, move_distance_km: 8 })}
                                sx={{ fontSize: '0.7rem', mt: 0.25 }}>
                                + Displace on Ph.{ph.phase_number || phIdx + 1}
                              </Button>
                            );
                          })}
                        </Box>
                      )}
                      {facility.activate_day != null && (
                        <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                          <Typography variant="caption">
                            This facility is <strong>inactive before Day {facility.activate_day}</strong>.
                            {facility.setup_hours != null && facility.setup_hours > 0
                              ? ` Setup ramp: ${facility.setup_hours}h — capacity reaches 100% on Day ${facility.activate_day + Math.ceil(facility.setup_hours / 24)} (AJMedP-1).`
                              : ' Instant activation — full capacity on activate day.'}
                            {facility.deactivate_day != null
                              ? ` Deactivates after Day ${facility.deactivate_day}.`
                              : ''}
                          </Typography>
                        </Alert>
                      )}
                    </Grid>

                    {/* Facility position for reference */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary">
                        {facility.hierarchy && !facility.hierarchy.legacy ? (
                          <>
                            <strong>Position:</strong>{' '}
                            {(() => {
                              // v4.3.3: Use extractLocalUnitName instead of regex hack
                              const parts = [];
                              
                              if (facility.hierarchy.battalion) {
                                const bn = extractLocalUnitName(facility.hierarchy.battalion, 'battalion');
                                parts.push(bn.type ? `${bn.ordinal} Bn (${bn.type})` : `${bn.ordinal} Bn`);
                              }
                              
                              if (facility.hierarchy.brigade) {
                                const bde = extractLocalUnitName(facility.hierarchy.brigade, 'brigade');
                                parts.push(bde.type ? `${bde.ordinal} Bde (${bde.type})` : `${bde.ordinal} Bde`);
                              }
                              
                              if (facility.hierarchy.division) {
                                const div = extractLocalUnitName(facility.hierarchy.division, 'division');
                                parts.push(`${div.ordinal} Div`);
                              }
                              
                              if (facility.hierarchy.corps) {
                                const corps = extractLocalUnitName(facility.hierarchy.corps, 'corps');
                                parts.push(`${corps.ordinal} Corps`);
                              }
                              
                              return parts.join(' / ');
                            })()}
                          </>
                        ) : (
                          <>
                            <strong>Facility:</strong> {facility.name}
                          </>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
   {/* A2 subsection start */}
  
    {/* PHASE 5 (UI-004, UI-005): Medical Facilities Summary & Validation */}
    {hasForceBuilderData && forceBuilderFacilities.length > 0 && (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2196f3', mb: 3 }}>
            <MedicalIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('tab3.a2_title')}
          </Typography>

 		<Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <InfoIcon sx={{ fontSize: '0.9rem' }} />
                <strong>{t('tab3.integration_status')}</strong> {t('tab3.integration_status_body')}
              </Typography>
            </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Validation Summary Panel (UI-004) */}
          {(() => {
            const role1Count = forceBuilderFacilities.filter(f => f.type === 'ROLE1').length;
            const role2bCount = forceBuilderFacilities.filter(f => f.type === 'ROLE2_BASIC').length;
            const role2eCount = forceBuilderFacilities.filter(f => f.type === 'ROLE2_ENHANCED').length;
            const role3Count = forceBuilderFacilities.filter(f => f.type === 'ROLE3').length;
            const role4Count = forceBuilderFacilities.filter(f => f.type === 'ROLE4').length;
            
            // Calculate expected based on ACTUAL force structure using unitTypes templates
            const echelon = scenario?.force?.echelon;
            const forceBuilderResult = scenario?.force?.forceBuilderResult;
            let expectedRole1 = 0, expectedRole2b = 0, expectedRole2e = 0, expectedRole3 = 0;
            let medicalBrigadeCount = 0;  // Track medical brigades for breakdown display
            
            if (echelon === 'BATTALION') {
              expectedRole1 = 1;
            } else if (echelon === 'BRIGADE') {
              const brigades = forceBuilderResult?.brigades || [];
              
              brigades.forEach(bde => {
                // Medical brigades: self-sufficient, no Role 1/2B needed
                if (bde.unit_type === 'MEDICAL') {
                  medicalBrigadeCount++;
                  return;
                }
                
                // Use brigade template from unitTypes.js (single source of truth)
                const template = BATTALION_COMPOSITION_BY_BRIGADE_TYPE[bde.unit_type];
                const role1ForThisBrigade = template?.role1_count ?? template?.combatBattalions ?? 0;
                
                expectedRole1 += role1ForThisBrigade;
                expectedRole2b += 1;  // Each non-medical brigade gets Role 2B
              });
            } else if (echelon === 'DIVISION') {
              const brigades = forceBuilderResult?.brigades || [];
              
              brigades.forEach(bde => {
                if (bde.unit_type === 'MEDICAL') {
                  medicalBrigadeCount++;
                  return;
                }
                
                const template = BATTALION_COMPOSITION_BY_BRIGADE_TYPE[bde.unit_type];
                const role1ForThisBrigade = template?.role1_count ?? template?.combatBattalions ?? 0;
                
                expectedRole1 += role1ForThisBrigade;
                expectedRole2b += 1;
              });
              
              expectedRole2e = 1;
            } else if (echelon === 'CORPS') {
              const divisions = forceBuilderResult?.divisions || [];
              // BUG-S102-010 FIX: at CORPS echelon, brigades are NESTED inside
              // forceBuilderResult.divisions[i].brigades — NOT in forceBuilderResult.brigades
              // (which is empty). Flatten all division brigade arrays so medical brigade
              // detection works and the explanatory note is visible when a brigade has
              // unit_type === 'MEDICAL'.
              const brigades = divisions.length > 0
                ? divisions.flatMap(div => div.brigades || [])
                : (forceBuilderResult?.brigades || []);

              brigades.forEach(bde => {
                if (bde.unit_type === 'MEDICAL') {
                  medicalBrigadeCount++;
                  return;
                }

                const template = BATTALION_COMPOSITION_BY_BRIGADE_TYPE[bde.unit_type];
                const role1ForThisBrigade = template?.role1_count ?? template?.combatBattalions ?? 0;

                expectedRole1 += role1ForThisBrigade;
                expectedRole2b += 1;
              });
              
              expectedRole2e = divisions.length;
              expectedRole3 = 1;
            }
            
            const role1Match = role1Count >= expectedRole1;
            const role2bMatch = role2bCount >= expectedRole2b;
            const role2eMatch = role2eCount >= expectedRole2e;
            const role3Match = role3Count >= expectedRole3;
            const allMatch = role1Match && role2bMatch && role2eMatch && role3Match;
            
            // Calculate totals
            const totalBeds = forceBuilderFacilities.reduce((sum, f) => sum + (f.beds || 0), 0);
            // v3.8 FIX: Ambulance data structure is { wheeled: {count, ...}, armored: {count, ...} }
            // Previously accessed .light/.armored directly (returned object, caused [object Object] display)
            const totalAmbulances = forceBuilderFacilities.reduce((sum, f) => {
              const ground = f.organic_assets?.ground_ambulances;
              // Access .count property from each ambulance type object
              const wheeled = ground?.wheeled?.count || ground?.light?.count || ground?.light || 0;
              const armored = ground?.armored?.count || ground?.armored || 0;
              const tracked = ground?.tracked?.count || ground?.tracked || 0;
              // Ensure we're adding numbers, not objects
              const wheeledNum = typeof wheeled === 'number' ? wheeled : 0;
              const armoredNum = typeof armored === 'number' ? armored : 0;
              const trackedNum = typeof tracked === 'number' ? tracked : 0;
              return sum + wheeledNum + armoredNum + trackedNum;
            }, 0);
            // BUG-T14 fix: totalHelicopters (organic_assets.helicopters.allocated) is always 0
            // and is no longer used in the display chip. Variable removed to avoid lint warning.
            // BUG-114 FIX: Tactical MEDEVAC pool sorties — organic_assets.helicopters.allocated
            // is always 0 (facility-level organic helicopters, not the configured MEDEVAC pool).
            // Read nominal daily sorties from tacticalEvacConfig.air_pool instead, which is what
            // the planner actually configures in Section 3B.0.
            // IMP-S107-001: sum nominal sorties across all pool entries
            const medevacSorties = (tacticalEvacConfig?.air_pools || []).reduce((s, p) => s + (p.nominal_daily_sorties || 0), 0);
            
            // UI-005: Additional totals for enhanced summary panel
            const totalSurgicalTeams = forceBuilderFacilities.reduce((sum, f) => {
              return sum + (f.surgical_teams || 0);
            }, 0);
            const totalIcuBeds = forceBuilderFacilities.reduce((sum, f) => {
              return sum + (f.icu_beds || 0);
            }, 0);
            const totalPersonnel = forceBuilderFacilities.reduce((sum, f) => {
              if (typeof f.personnel === 'object') {
                return sum + Object.values(f.personnel).reduce((pSum, val) => pSum + (typeof val === 'number' ? val : 0), 0);
              }
              return sum + (typeof f.personnel === 'number' ? f.personnel : 0);
            }, 0);
            
            return (
              <>
                {/* Validation Alert */}
                <Alert severity={allMatch ? "success" : "warning"} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>{t('tab3.facility_validation', { echelon })}</strong>
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="body2">
                        Role 1: {role1Count}/{expectedRole1}
                        {role1Match ? " " : ` (${expectedRole1 - role1Count} missing)`}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="body2">
                        Role 2B: {role2bCount}/{expectedRole2b}
                        {role2bMatch ? " " : ` (${expectedRole2b - role2bCount} missing)`}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="body2">
                        Role 2E: {role2eCount}/{expectedRole2e}
                        {role2eMatch ? " " : ` (${expectedRole2e - role2eCount} missing)`}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="body2">
                        Role 3: {role3Count}/{expectedRole3}
                        {role3Match ? " " : ` (${expectedRole3 - role3Count} missing)`}
                      </Typography>
                    </Grid>
                  </Grid>
                  {medicalBrigadeCount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                      {medicalBrigadeCount} Medical Brigade{medicalBrigadeCount > 1 ? 's' : ''} excluded from Role 1/2B counts — self-sufficient formations that augment R2E/R3 capacity.
                    </Typography>
                  )}
                </Alert>

                {/* BUG-NEXT-004: explicit prerequisite warning when no Role 2B is configured */}
                {role2bCount === 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <strong>Tab 5 prerequisite not met:</strong> No Role 2B facility is configured.
                    Tab 5 Medical Estimate requires at least 1 Role 2B to compute surgical capacity and blood requirements.
                    Add a Role 2B facility in the MTF Builder above before proceeding.
                  </Alert>
                )}

                {/* Summary Totals Panel (UI-005 ENHANCED) */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                  <Chip 
                    icon={<MedicalIcon />}
                    label={`Total Facilities: ${forceBuilderFacilities.length}`}
                    color="primary"
                  />
                  <Chip 
                    label={`Total Beds: ${totalBeds}`}
                    color="secondary"
                  />
                  <Chip 
                    label={`Surgical Teams: ${totalSurgicalTeams}`}
                    color="error"
                    variant={totalSurgicalTeams > 0 ? "filled" : "outlined"}
                  />
                  <Chip 
                    label={`ICU Beds: ${totalIcuBeds}`}
                    color="warning"
                    variant={totalIcuBeds > 0 ? "filled" : "outlined"}
                  />
                  <Chip 
                    label={`Personnel: ${totalPersonnel}`}
                    color="info"
                  />
                  <Chip 
                    label={`Ambulances: ${totalAmbulances}`}
                    color="default"
                  />
                  {/* BUG-T14 fix: totalHelicopters reads organic_assets.helicopters.allocated (always 0).
                      Only display the meaningful MEDEVAC sorties figure from air_pool. */}
                  <Chip
                    label={medevacSorties > 0 ? `Air MEDEVAC: ${medevacSorties} sorties/day` : 'Air MEDEVAC: not configured'}
                    color={medevacSorties > 0 ? "default" : "warning"}
                    variant={medevacSorties > 0 ? "filled" : "outlined"}
                  />
                  {role4Count > 0 && (
                    <Chip 
                      label={`Role 4 (Strategic): ${role4Count}`}
                      color="info"
                    />
                  )}
                  {/* Blood Status Indicator */}
                  <Chip 
                    label={`Blood: ${bloodConfig.alertLevel}`}
                    color={
                      bloodConfig.alertLevel === 'GREEN' ? 'success' :
                      bloodConfig.alertLevel === 'YELLOW' ? 'warning' :
                      bloodConfig.alertLevel === 'RED' ? 'error' : 'error'
                    }
                    variant={bloodConfig.alertLevel === 'GREEN' ? 'outlined' : 'filled'}
                  />
                </Box>
              </>
            );
          })()}
          
          {/* Hierarchical Tree View (UI-002) */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
            {t('tab3.mtf_hierarchy_title')}
          </Typography>
          
          <Box sx={{ 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 1, 
            p: 2,
            maxHeight: 500,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            bgcolor: 'background.paper'
          }}>
            {(() => {
              // Group facilities by hierarchy
              const role3s = forceBuilderFacilities.filter(f => f.type === 'ROLE3');
              const role2es = forceBuilderFacilities.filter(f => f.type === 'ROLE2_ENHANCED');
              const role2bs = forceBuilderFacilities.filter(f => f.type === 'ROLE2_BASIC');
              const role1s = forceBuilderFacilities.filter(f => f.type === 'ROLE1');
              
              // Group Role 1s by parent brigade
              const role1sByBrigade = {};
              role1s.forEach(r1 => {
                const brigadeKey = r1.hierarchy?.brigade || r1.parent_brigade || 'Unknown Brigade';
                if (!role1sByBrigade[brigadeKey]) role1sByBrigade[brigadeKey] = [];
                role1sByBrigade[brigadeKey].push(r1);
              });
              
              // Group Role 2Bs by parent division
              const role2bsByDivision = {};
              role2bs.forEach(r2b => {
                const divKey = r2b.hierarchy?.division || r2b.parent_division || 'Unknown Division';
                if (!role2bsByDivision[divKey]) role2bsByDivision[divKey] = [];
                role2bsByDivision[divKey].push(r2b);
              });
              
              return (
                <Box>
                  {/* Corps Level - Role 3 */}
                  {role3s.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        CORPS LEVEL
                      </Typography>
                      {role3s.map(r3 => (
                        <Box key={r3.id} sx={{ ml: 2, my: 0.5 }}>
                          <Typography component="span" sx={{ color: 'error.main' }}>
                            [ROLE 3]
                          </Typography>
                          {' '}{r3.name} - {r3.beds || getBedDefault('ROLE3')} beds | FEBA: {r3.position_feba_km}km
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  {/* Division Level - Role 2E */}
                  {role2es.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                        DIVISION LEVEL
                      </Typography>
                      {role2es.map(r2e => (
                        <Box key={r2e.id} sx={{ ml: 2, my: 0.5 }}>
                          <Typography component="span" sx={{ color: 'warning.main' }}>
                            [ROLE 2E]
                          </Typography>
                          {' '}{r2e.name} - {r2e.beds || getBedDefault('ROLE2E')} beds | FEBA: {r2e.position_feba_km}km
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  {/* Brigade Level - Role 2B with Role 1s */}
                  {role2bs.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        {t('tab3.brigade_level_label')}
                      </Typography>
                      {role2bs.map(r2b => {
                        // Find Role 1s that belong to this brigade
                        const brigadeKey = r2b.hierarchy?.brigade || r2b.parent_brigade || r2b.name;
                        const childRole1s = role1sByBrigade[brigadeKey] || [];
                        
                        return (
                          <Box key={r2b.id} sx={{ ml: 2, my: 1 }}>
                            <Typography component="span" sx={{ color: 'secondary.main' }}>
                              [ROLE 2B]
                            </Typography>
                            {' '}{r2b.name} - {r2b.beds || getBedDefault('ROLE2B')} beds | FEBA: {r2b.position_feba_km}km
                            
                            {/* Child Role 1s */}
                            {childRole1s.length > 0 && (
                              <Box sx={{ ml: 3, borderLeft: '2px solid', borderColor: 'divider', pl: 1, mt: 0.5 }}>
                                {childRole1s.map(r1 => (
                                  <Box key={r1.id} sx={{ my: 0.25 }}>
                                    <Typography component="span" sx={{ color: 'primary.main', fontSize: '0.8rem' }}>
                                      [R1]
                                    </Typography>
                                    {' '}{r1.name} - {r1.beds || getBedDefault('ROLE1')} beds | FEBA: {r1.position_feba_km}km
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  
                  {/* Orphaned Role 1s (not under any Role 2B) */}
                  {(() => {
                    const assignedRole1Ids = new Set();
                    role2bs.forEach(r2b => {
                      const brigadeKey = r2b.hierarchy?.brigade || r2b.parent_brigade || r2b.name;
                      (role1sByBrigade[brigadeKey] || []).forEach(r1 => assignedRole1Ids.add(r1.id));
                    });
                    const orphanedRole1s = role1s.filter(r1 => !assignedRole1Ids.has(r1.id));
                    
                    if (orphanedRole1s.length === 0) return null;
                    
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                          BATTALION LEVEL (Standalone)
                        </Typography>
                        {orphanedRole1s.map(r1 => (
                          <Box key={r1.id} sx={{ ml: 2, my: 0.25 }}>
                            <Typography component="span" sx={{ color: 'primary.main' }}>
                              [ROLE 1]
                            </Typography>
                            {' '}{r1.name} - {r1.beds || getBedDefault('ROLE1')} beds | FEBA: {r1.position_feba_km}km
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}
                </Box>
              );
            })()}
          </Box>
          
          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              [ROLE 3] = Combat Support Hospital (Corps)
            </Typography>
            <Typography variant="caption" sx={{ color: 'warning.main' }}>
              [ROLE 2E] = Field Hospital (Division)
            </Typography>
            <Typography variant="caption" sx={{ color: 'secondary.main' }}>
              [ROLE 2B] = Medical Company (Brigade)
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.main' }}>
              [ROLE 1] = Battalion Aid Station
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    )}
          </CardContent>
        </Card>
      )}
   
{/* ====================================================================
     HICOM-001: R3 ACCESSIBILITY CONFIRMATION BLOCK
     Placed between Section 3A (MTF) and Section 3B (Evacuation).
     V3: Warning flags only. V4: dual calculation path.
     ==================================================================== */}
      {/* BUG-NEXT-015: R2E Accessibility card — brigade echelon only.
          Division planners own R2E (in force structure). Brigade planners reference it. */}
      {scenario?.force?.echelon === 'BRIGADE' && (
        <Card sx={{ mb: 3, border: '3px solid', borderColor: r2eAccessible ? '#1976d2' : '#f44336', borderRadius: 2, boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: r2eAccessible ? '#1565c0' : '#c62828', flexGrow: 1 }}>
                {t('tab3.r2e_accessibility')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={r2eAccessible}
                    onChange={e => setR2eAccessible(e.target.checked)}
                    color={r2eAccessible ? 'primary' : 'error'}
                  />
                }
                label={r2eAccessible ? t('tab3.r2e_accessible') : t('tab3.r2e_not_accessible')}
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('tab3.r2e_accessible_help')}
            </Typography>
            {r2eAccessible ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <TextField
                    label={t('tab3.dist_r2e')}
                    type="number"
                    size="small"
                    value={chainLegs.r2b_to_r2e.km ?? r2eDistance}
                    onChange={e => {
                      const v = Math.max(1, Math.min(500, Number(e.target.value) || 40));
                      setR2eDistance(v);
                      setLegOverrideKm('r2b_to_r2e', v);   // typing here is the override of the Role 2 Basic to Role 2 Enhanced leg
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">km</InputAdornment>
                    }}
                    inputProps={{ min: 1, max: 500 }}
                    sx={{ width: 160 }}
                    helperText={chainLegs.r2b_to_r2e.source === 'OVERRIDE' ? 'R2B → R2E: planner override (see Medical chain distances)' : 'R2B → R2E: follows the placed facilities; type to override'}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {(chainLegs.r2b_to_r2e.km ?? r2eDistance) <= 20
                      ? 'Close support — favourable evacuation times'
                      : (chainLegs.r2b_to_r2e.km ?? r2eDistance) <= 50
                        ? 'Standard depth — normal evacuation corridor'
                        : 'Extended corridor — increased DOW risk en route'}
                  </Typography>
                </Box>
                <Alert severity="info">
                  R2E accessible — division has confirmed R2E support. If no R2E facility exists
                  in your force structure, the model applies NATO doctrinal defaults (40 beds,
                  4 OR tables) at the distance entered above. Division asset — not configurable at brigade level.
                </Alert>
              </>
            ) : (
              <Alert severity="error">
                <strong>R2E NOT accessible</strong> — overflow from R2B routes directly to R3
                or STRATEVAC. DOW rates increase; surgical queue pressure moves to brigade level.
                Notify higher HQ immediately and request R2E support or CASEVAC re-routing.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Card sx={{
        mb: 3,
        border: '3px solid',
        borderColor: r3Accessible ? '#4caf50' : '#f44336',
        borderRadius: 2,
        boxShadow: 3
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: r3Accessible ? '#388e3c' : '#c62828', flexGrow: 1 }}>
              {t('tab3.r3_accessibility')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={r3Accessible}
                  onChange={e => setR3Accessible(e.target.checked)}
                  color={r3Accessible ? 'success' : 'error'}
                />
              }
              label={r3Accessible ? t('tab3.r3_accessible') : t('tab3.r3_not_accessible')}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confirm whether Role 3 (Combat Support Hospital) is within operational range and available to receive patients.
            This affects DOW rates, supply chain planning, TPRP ownership, and MASCAL thresholds.
          </Typography>

          {!r3Accessible && (
            <Box>
              <Alert severity="error" sx={{ mb: 1 }}>
                <strong>DOW Rate Impact:</strong> Without R3 definitive surgical capability, borderline non-survivable WIA
                convert to KIA at higher rates. DOW multiplier ×{R3_ABSENT_DOW_MODIFIER.toFixed(2)} is applied
                automatically by the calculator (ECHELON-DUAL mode). Tab 5 results already reflect R3-absent DOW rates.
              </Alert>
              <Alert severity="error" sx={{ mb: 1 }}>
                <strong>TPRP Ownership Shift:</strong> Strategic TPRP (Theatre Patient Reception Plan) is JFC/COMMED
                level — NOT brigade or division. Without R3, TPRP ownership shifts from R3 to R2E capacity model.
                Notify higher HQ immediately. Do not assume brigade-level TPRP authority.
              </Alert>
              <Alert severity="warning" sx={{ mb: 1 }}>
                <strong>Blood Product Crisis Risk:</strong> R3 is the primary PLT redistribution hub.
                Without R3, PLT shelf-life crisis risk is HIGH — 5-day PLT product vs 3–5 day FMSS→R2E
                pipeline leaves zero safety margin. WBB/EBC becomes primary PLT source. Increase PLT DOS target.
              </Alert>
              <Alert severity="warning" sx={{ mb: 1 }}>
                <strong>CSU / PHA Planning:</strong> CSU and PHA staging assume stable post-surgical patients
                from R3. Without R3, patients arriving from R2E are less stable — expect higher nursing ratio
                and increased aircraft requirements for STRATEVAC.
              </Alert>
              <Alert severity="warning" sx={{ mb: 1 }}>
                <strong>MASCAL Threshold Reduced:</strong> Without R3 as overflow capacity, the effective MASCAL
                threshold is lower. R2B becomes the functional cap — saturates faster. Review MASCAL thresholds
                in Section 3E below.
              </Alert>
              <Alert severity="info" sx={{ mt: 1 }}>
                <strong>ECHELON-DUAL active (V4):</strong> The calculator automatically applies the R3-absent
                DOW multiplier (x{R3_ABSENT_DOW_MODIFIER.toFixed(2)}), disables STRATEVAC drain, increases
                blood DOS consumption by 15%, and computes TPRP requirements. Results shown in Tab 5.
              </Alert>
            </Box>
          )}

          {r3Accessible && (
            <Alert severity="success">
              R3 accessible — standard planning assumptions apply. DOW rates, TPRP ownership, blood supply
              chain, and MASCAL thresholds are as calculated. If no Role 3 facility exists in your force
              structure (Tab 1), the model applies NATO doctrinal CSH defaults (150 beds) for overflow
              routing — corps asset, not configurable at division level.
            </Alert>
          )}
        </CardContent>
      </Card>

{/* 3B SECTION */}

      <Card sx={{ mb: 3, border: '3px solid', borderColor: '#ff9800', borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#ff9800', mb: 3 }}>
           {MEDICAL_ICONS.AMBULANCE} {t('tab3.section_3b')}
          </Typography>


  {/* ========================================================================
        PHASE 3 REORGANIZATION: SECTION 3B - EVACUATION SYSTEM
        
        This section consolidates all evacuation-related configuration:
        - 3B.1: Evacuation Distances (formerly 3G)
        - 3B.2: Ground Evacuation/CASEVAC (formerly 3J)  
        - 3B.3: Evacuation Routes (formerly 3L)
        
        These settings directly affect Golden Hour achievement and DOW rates.
        ======================================================================== */}

    {/* ================================================================== */}
    {/* SUB-SECTION 3B.0: TACTICAL MEDEVAC CONFIGURATION (Phase 5.0)       */}
    {/* Ground ambulance fleet + MEDEVAC air pool + AD threat + weather     */}
    {/* Pool options filtered by brigade/division national standard         */}
    {/* ================================================================== */}
    {(() => {
      const natStd = extractPrimaryNationalStandard();
      const poolOptions = getMEDEVACPoolOptions(natStd);
      const adOptions   = Object.values(AD_THREAT_MODIFIERS);
      const wxOptions   = Object.values(WEATHER_MODIFIERS);

      // Expand aviation acronyms for student comprehension
      const expandWeatherLabel = (desc) => {
        if (!desc) return desc;
        return desc
          .replace(/\bVMC\b/g, 'VMC (Visual Flight)')
          .replace(/\bIMC\b/g, 'IMC (Instrument/Reduced Visibility)')
          .replace(/\bIFR\b/g, 'IFR (Instrument Flight Rules)');
      };

      const handleFleetChange = (typeCode, field, value) => {
        setTacticalEvacConfig(prev => ({
          ...prev,
          fleet: {
            ...prev.fleet,
            [typeCode]: { ...prev.fleet[typeCode], [field]: Math.max(0, parseInt(value) || 0) }
          }
        }));
      };

      const handleAddFleetType = (typeCode) => {
        setTacticalEvacConfig(prev => {
          if (prev.fleet[typeCode]) return prev;
          return { ...prev, fleet: { ...prev.fleet, [typeCode]: { count: 0, available: 0 } } };
        });
      };

      const handleRemoveFleetType = (typeCode) => {
        setTacticalEvacConfig(prev => {
          const next = { ...prev.fleet };
          delete next[typeCode];
          return { ...prev, fleet: next };
        });
      };

      // IMP-S107-001: handlers for multi-pool air fleet
      const handleAirPoolEntry = (idx, field, value) => {
        setTacticalEvacConfig(prev => {
          const pools = prev.air_pools.map((p, i) => {
            if (i !== idx) return p;
            if (field === 'pool_code') {
              const chosen = poolOptions.find(opt => opt.code === value);
              return {
                ...p,
                pool_code:    value,
                pool_label:   chosen?.label || value,
                aircraft_type: chosen?.primary_aircraft || p.aircraft_type
              };
            }
            return { ...p, [field]: value };
          });
          return { ...prev, air_pools: pools };
        });
      };

      const handleAddAirPool = () => {
        setTacticalEvacConfig(prev => {
          const defaultPoolObj2 = poolOptions[0] || {};
          return {
            ...prev,
            air_pools: [
              ...prev.air_pools,
              {
                pool_code:             defaultPoolObj2.code || 'NATO_GENERIC',
                pool_label:            defaultPoolObj2.label || 'NATO Generic',
                nominal_daily_sorties: 0,
                aircraft_type:         defaultPoolObj2.primary_aircraft || 'IAR330'
              }
            ]
          };
        });
      };

      const handleRemoveAirPool = (idx) => {
        setTacticalEvacConfig(prev => ({
          ...prev,
          air_pools: prev.air_pools.filter((_, i) => i !== idx)
        }));
      };

      const allAmbulanceTypes  = Object.values(AMBULANCE_TYPES);
      const currentFleetCodes  = Object.keys(tacticalEvacConfig.fleet);
      const availableToAdd     = allAmbulanceTypes.filter(a => !currentFleetCodes.includes(a.code));

      // 2026-10-13 (Tab 2 plan 7.9): with an enemy air defense set in Tab 2 the engine uses ONE helicopter availability
      // (air situation x enemy air defense x operation weather); the AD and weather selectors below do not count then.
      const tab2EnemyAd = scenario?.operation_parameters?.threat_assessment?.enemy_ad;
      const tab2AirModel = (THREAT_EFFECTS_USE && tab2EnemyAd)
        ? helicopterAvailability({
            airSituationLevel: scenario?.operation_parameters?.air_situation_config?.level ?? 'PARITY',
            enemyAd: tab2EnemyAd,
            weather: resolveScenarioEnvironment(scenario).weather
          })
        : null;

      const adEntry   = AD_THREAT_MODIFIERS[tacticalEvacConfig.ad_threat?.toUpperCase()] || AD_THREAT_MODIFIERS.MEDIUM;
      const wxEntry   = WEATHER_MODIFIERS[tacticalEvacConfig.weather?.toUpperCase()]     || WEATHER_MODIFIERS.VMC_CLEAR;
      const combined  = tab2AirModel
        ? Math.round(tab2AirModel.value * 100)
        : Math.round((adEntry.sortie_modifier ?? 1) * (wxEntry.sortie_modifier ?? 1) * 100);
      // IMP-S107-001: sum across all pool entries
      const nominalSorties   = (tacticalEvacConfig.air_pools || []).reduce((s, p) => s + (p.nominal_daily_sorties || 0), 0);
      const effectiveSorties = Math.round(nominalSorties * combined / 10) / 10;
      const combinedColor    = combined >= 80 ? 'success' : combined >= 50 ? 'warning' : 'error';

      // ENV-001b: Check whether Tab3 AD threat is consistent with Tab2 threat_level.
      // If Tab2 says FULL/HIGH combat intensity but Tab3 still shows LOW/MEDIUM AD → warn.
      const ENV001_TAB2_TO_TIER = { FULL:'HIGH', EXTREME:'HIGH', PEER:'HIGH', HIGH:'HIGH', MODERATE:'MEDIUM', LOW:'LOW', MINIMAL:'LOW' };
      const ENV001_TIER_ORDER   = { LOW: 0, MEDIUM: 1, HIGH: 2 };
      const tab2ThreatRaw = scenario?.environment?.threat_level || scenario?.operation_parameters?.threat_level;
      const tab2ImpliedAD = ENV001_TAB2_TO_TIER[(tab2ThreatRaw || '').toUpperCase()] || 'MEDIUM';
      const tab3AD        = (tacticalEvacConfig.ad_threat || 'MEDIUM').toUpperCase();
      const adMismatch    = !tab2AirModel && ENV001_TIER_ORDER[tab3AD] < ENV001_TIER_ORDER[tab2ImpliedAD];

      // UI-3B-001: Fleet totals for total row and ground chip.
      const fleetTotals = Object.entries(tacticalEvacConfig.fleet).reduce(
        (acc, [typeCode, vals]) => {
          const spec = AMBULANCE_TYPES[typeCode];
          const litterCap = spec?.capacity?.stretchers ?? spec?.litter_capacity ?? 0;
          acc.onStrength += vals.count     || 0;
          acc.available  += vals.available || 0;
          acc.litter     += litterCap * (vals.count || 0);
          return acc;
        },
        { onStrength: 0, available: 0, litter: 0 }
      );
      const groundPct   = fleetTotals.onStrength > 0
        ? Math.round((fleetTotals.available / fleetTotals.onStrength) * 100)
        : 0;
      const groundColor = groundPct >= 80 ? 'success' : groundPct >= 50 ? 'warning' : 'error';

      // ENV-003b: human-readable labels for the re-sync alert
      const adLabel = { LOW: 'LOW (PERMISSIVE)', MEDIUM: 'MEDIUM (MANPADS)', HIGH: 'HIGH (DENIED)' };
      const wxLabel = {
        VMC_CLEAR: 'VMC CLEAR', IMC_LIGHT_RAIN: 'IMC Light Rain',
        IMC_HEAVY_RAIN: 'IMC Heavy Rain', IMC_SNOW: 'IMC Snow',
        IFR_FOG: 'IFR Fog', GROUNDED_STORM: 'GROUNDED Storm'
      };

      return (
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6">{MEDICAL_ICONS.AMBULANCE} {t('tab3.b0_tactical_medevac')}</Typography>
              <Chip label={`${natStd} force`} size="small" variant="outlined" />
              {/* UI-3B-001: ground fleet chip */}
              <Chip
                label={`Ground: ${fleetTotals.available}/${fleetTotals.onStrength}`}
                size="small"
                color={fleetTotals.onStrength > 0 ? groundColor : 'default'}
                variant="filled"
              />
              <Chip
                label={`Air: ${combined}%`}
                size="small"
                color={combinedColor}
                variant="filled"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>

            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Tactical MEDEVAC — AJP-4.10C / APP_RULES 8.2</AlertTitle>
              <Typography variant="body2">
                Configure the <strong>ground ambulance fleet</strong> and <strong>MEDEVAC air pool</strong> for this force.
                Air pool options are <strong>filtered by national standard ({natStd})</strong> — each nation has its own
                doctrine for helicopter sourcing. AD threat and weather modifiers degrade effective sortie rates.
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                These values feed directly into Golden Hour compliance and DOW calculations in Tab 5.
              </Typography>
            </Alert>

            {/* ENV-001b: Warn when Tab3 AD threat is less restrictive than Tab2 scenario implies */}
            {adMismatch && tab2ThreatRaw && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>AD Environment Mismatch</AlertTitle>
                <Typography variant="body2">
                  Tab 2 sets threat level <strong>{tab2ThreatRaw.toUpperCase()}</strong>, which implies
                  AD environment <strong>{tab2ImpliedAD}</strong>.
                  Tab 3 is currently configured as <strong>{tab3AD}</strong> — more permissive than the
                  operational scenario suggests.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  If Tab 3 AD threat is intentionally different (e.g. local suppression, dedicated AD assets),
                  this warning can be ignored. Otherwise update the AD Threat Level dropdown below.
                </Typography>
              </Alert>
            )}

            {/* ENV-003b: Tab2 re-save re-sync alert */}
            {tab2ResyncAlert && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setTacticalEvacConfig(prev => ({
                          ...prev,
                          ad_threat: tab2ResyncAlert.suggestedAD,
                          weather:   tab2ResyncAlert.suggestedWX
                        }));
                        lastKnownTab2Ref.current = {
                          threat:  tab2ResyncAlert.tab2Threat,
                          weather: tab2ResyncAlert.tab2Weather
                        };
                        setTab2ResyncAlert(null);
                      }}
                    >
                      Apply Tab 2 defaults
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        // Acknowledge the change — keep current Tab3 settings as-is
                        lastKnownTab2Ref.current = {
                          threat:  tab2ResyncAlert.tab2Threat,
                          weather: tab2ResyncAlert.tab2Weather
                        };
                        setTab2ResyncAlert(null);
                      }}
                    >
                      Keep current
                    </Button>
                  </Box>
                }
              >
                <AlertTitle>Tab 2 scenario has changed</AlertTitle>
                <Typography variant="body2">
                  Tab 2 now implies <strong>AD: {adLabel[tab2ResyncAlert.suggestedAD] ?? tab2ResyncAlert.suggestedAD}</strong>
                  {' and '}
                  <strong>Weather: {wxLabel[tab2ResyncAlert.suggestedWX] ?? tab2ResyncAlert.suggestedWX}</strong>.
                  Tab 3 is still configured to the previous values.
                  Apply to update, or keep your current settings if the difference is intentional.
                </Typography>
              </Alert>
            )}

            <Grid container spacing={3}>

              {/* ---- GROUND AMBULANCE FLEET ---- */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {TRANSPORT_ICONS.TRUCK} {t('tab3.ground_ambulance_fleet')}
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell><strong>Vehicle Type</strong></TableCell>
                        <TableCell align="center"><strong>Category</strong></TableCell>
                        {/* BUG-T16 fix: clarify column header — this is stretcher spaces per vehicle × count */}
                        <TableCell align="center">
                          <Tooltip title="Total stretcher (litter) spaces: litter capacity per vehicle × vehicle count. Represents simultaneous patient-carrying capacity of the entire fleet, not per sortie." arrow>
                            <Box sx={{ cursor: 'help' }}><strong>Litter Spaces</strong>
                              <Typography variant="caption" display="block" color="text.secondary">fleet total</Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="On Strength = authorized fleet size (full establishment). The total number of vehicles allocated to this unit by table of organisation." arrow>
                            <Box sx={{ cursor: 'help' }}><strong>On Strength</strong></Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Available = operationally ready vehicles after maintenance, breakdown, and attrition. The calculator uses this figure only — not On Strength. Shortfalls reduce effective evacuation capacity." arrow>
                            <Box sx={{ cursor: 'help' }}><strong>Available</strong></Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(tacticalEvacConfig.fleet).map(([typeCode, vals]) => {
                        const spec = AMBULANCE_TYPES[typeCode];
                        return (
                          <TableRow key={typeCode}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {spec?.name || typeCode}
                              </Typography>
                              {spec?.educational?.key_point && (
                                <Typography variant="caption" color="text.secondary">
                                  {spec.educational.key_point}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={spec?.category || 'UNKNOWN'}
                                size="small"
                                variant="outlined"
                                color={spec?.category === 'ARMORED' ? 'warning' : spec?.category === 'HEAVY_ARMORED' ? 'error' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="center">{spec?.capacity?.stretchers ?? spec?.litter_capacity ?? '?'}</TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                value={vals.count}
                                onChange={(e) => handleFleetChange(typeCode, 'count', e.target.value)}
                                inputProps={{ min: 0, max: 99 }}
                                size="small"
                                sx={{ width: 70 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                value={vals.available}
                                onChange={(e) => handleFleetChange(typeCode, 'available', e.target.value)}
                                inputProps={{ min: 0, max: vals.count }}
                                size="small"
                                sx={{ width: 70 }}
                                error={vals.available > vals.count}
                                helperText={vals.available > vals.count ? '>count' : ''}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button size="small" color="error" onClick={() => handleRemoveFleetType(typeCode)} sx={{ minWidth: 0, px: 1 }}>
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* UI-3B-001: Total row — BUG-T16 fix: label clarifies what is summed */}
                      {Object.keys(tacticalEvacConfig.fleet).length > 0 && (
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Fleet Total</Typography>
                            <Typography variant="caption" color="text.secondary">all vehicle types</Typography>
                          </TableCell>
                          <TableCell align="center" />
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{fleetTotals.litter}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{fleetTotals.onStrength}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: groundPct >= 80 ? 'success.main' : groundPct >= 50 ? 'warning.main' : 'error.main' }}
                            >
                              {fleetTotals.available}
                              {fleetTotals.onStrength > 0 && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  ({groundPct}%)
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {availableToAdd.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel>{t('tab3.add_vehicle_type')}</InputLabel>
                    <Select value="" label={t('tab3.add_vehicle_type')} onChange={(e) => handleAddFleetType(e.target.value)}>
                      {Object.entries(AMBULANCE_CATEGORIES).map(([cat, codes]) => [
                        <MenuItem key={`h-${cat}`} value="" disabled sx={{ fontWeight: 600, opacity: 1, color: 'text.primary' }}>
                          {cat}
                        </MenuItem>,
                        ...codes
                          .filter(code => AMBULANCE_TYPES[code] && !currentFleetCodes.includes(code))
                          .map(code => (
                            <MenuItem key={code} value={code}>{AMBULANCE_TYPES[code]?.name || code}</MenuItem>
                          ))
                      ])}
                    </Select>
                  </FormControl>
                )}
                {/* Reset to TOE defaults button — restores national standard brigade fleet */}
                <Tooltip title={`Reset to ${natStd} brigade medical company TOE (from GROUND_FLEET_BY_NATION constants)`}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{ ml: 1, height: 40, alignSelf: 'flex-end' }}
                    onClick={() => {
                      // BUG-200/201 FIX (March 25, 2026): aggregate per-brigade fleets for MULTINATIONAL
                      const _brigades = scenario?.force?.forceBuilderResult?.brigades
                                     || scenario?.force?.brigades || [];
                      // OBS-S96-006 FIX: always aggregate per-brigade
                      const toeVehicles = buildAggregatedMNDFleet(_brigades);
                      setTacticalEvacConfig(prev => ({ ...prev, fleet: toeVehicles }));
                    }}
                  >
                    Reset to TOE defaults
                  </Button>
                </Tooltip>
              </Grid>

              {/* ---- MEDEVAC AIR POOL (IMP-S107-001: multi-pool) ---- */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {TRANSPORT_ICONS.HELICOPTER} {t('tab3.medevac_air_pool', { standard: natStd })}
                  </Typography>
                  <Chip
                    label={`${(tacticalEvacConfig.air_pools || []).length} pool${(tacticalEvacConfig.air_pools || []).length !== 1 ? 's' : ''}`}
                    size="small"
                    color={(tacticalEvacConfig.air_pools || []).length > 1 ? 'secondary' : 'default'}
                    variant="outlined"
                  />
                </Box>
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="caption">
                    Real NATO corps MEDEVAC fleets are rarely mono-type. Add a second pool for MOU/contracted assets
                    (e.g. national IAR-330s + NATO NH90 MOU). AD/weather modifiers apply to the combined sortie total.
                  </Typography>
                </Alert>

                {(tacticalEvacConfig.air_pools || []).map((pool, idx) => {
                  const poolSpec = HELICOPTER_SPECS[pool.aircraft_type];
                  const poolEffective = Math.round((pool.nominal_daily_sorties || 0) * combined / 10) / 10;
                  const poolMeta = poolOptions.find(p => p.code === pool.pool_code);
                  return (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1.5, position: 'relative' }}>
                      {/* Pool index badge + remove button */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Chip
                          label={`Pool ${idx + 1}`}
                          size="small"
                          color={idx === 0 ? 'primary' : 'secondary'}
                          sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                        />
                        {(tacticalEvacConfig.air_pools || []).length > 1 && (
                          <Tooltip title="Remove this pool entry">
                            <IconButton size="small" color="error" onClick={() => handleRemoveAirPool(idx)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Pool selector */}
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>{t('tab3.air_pool_label')}</InputLabel>
                        <Select
                          value={pool.pool_code}
                          label={t('tab3.air_pool_label')}
                          onChange={(e) => handleAirPoolEntry(idx, 'pool_code', e.target.value)}
                        >
                          {poolOptions.map(opt => (
                            <MenuItem key={opt.code} value={opt.code}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.label}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {opt.description}
                                </Typography>
                                {opt.requires_mou && (
                                  <Chip label="MOU required" size="small" color="warning" variant="outlined" sx={{ mt: 0.25 }} />
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {poolMeta?.educational && (
                          <FormHelperText>{poolMeta.educational}</FormHelperText>
                        )}
                      </FormControl>

                      {/* Aircraft type */}
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>{t('tab3.helicopter_type')}</InputLabel>
                        <Select
                          value={pool.aircraft_type || ''}
                          label={t('tab3.helicopter_type')}
                          onChange={(e) => handleAirPoolEntry(idx, 'aircraft_type', e.target.value)}
                        >
                          {Object.entries(HELICOPTER_SPECS).map(([code, spec]) => (
                            <MenuItem key={code} value={code}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{spec.name || code}</Typography>
                                <Chip label={`${spec.capacity?.stretchers ?? '?'} litter`} size="small" variant="outlined" />
                                {spec.cruise_speed_kph && (
                                  <Chip label={`${spec.cruise_speed_kph} km/h`} size="small" variant="outlined" color="info" />
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {poolSpec && (
                          <FormHelperText>
                            Litter capacity: {poolSpec.capacity?.stretchers ?? '?'} — per-sortie patient load
                          </FormHelperText>
                        )}
                      </FormControl>

                      {/* Sorties input + effective readout */}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          label={t('tab3.nominal_sorties_day')}
                          type="number"
                          size="small"
                          sx={{ flex: 1 }}
                          value={pool.nominal_daily_sorties}
                          onChange={(e) => handleAirPoolEntry(idx, 'nominal_daily_sorties', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: 50 }}
                          helperText="Before AD/weather degradation"
                        />
                        <Chip
                          label={`→ ${poolEffective} eff.`}
                          size="small"
                          color={combined >= 80 ? 'success' : combined >= 50 ? 'warning' : 'error'}
                          sx={{ height: 32, fontSize: '0.72rem', fontWeight: 600 }}
                        />
                      </Box>

                      {/* Romanian mismatch warning — per-pool */}
                      {natStd === 'ROMANIAN' && pool.pool_code !== 'RO_FAR' && pool.pool_code !== 'RO_SMURD' && idx === 0 && (
                        <Alert severity="info" sx={{ mt: 1, py: 0.25 }}>
                          <Typography variant="caption">
                            Romanian force: consider <strong>FAR (IAR-330 Puma)</strong> as primary pool. Current pool is NATO/multinational — verify MOU activation.
                          </Typography>
                        </Alert>
                      )}
                    </Paper>
                  );
                })}

                {/* Add pool button */}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddAirPool}
                  sx={{ mt: 0.5 }}
                >
                  Add pool entry
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Use for MOU assets, contracted helicopters, or a second nation's MEDEVAC pool.
                </Typography>
              </Grid>

              {/* ---- AD THREAT + WEATHER ---- */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Environment Modifiers
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('tab3.ad_threat_level')}</InputLabel>
                  <Select
                    value={tacticalEvacConfig.ad_threat}
                    label={t('tab3.ad_threat_level')}
                    disabled={Boolean(tab2AirModel)}
                    onChange={(e) => setTacticalEvacConfig(prev => ({ ...prev, ad_threat: e.target.value }))}
                  >
                    {adOptions.map(opt => (
                      <MenuItem key={opt.code} value={opt.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.description}</Typography>
                          <Chip label={`${Math.round(opt.sortie_modifier * 100)}%`} size="small"
                            sx={{ bgcolor: opt.color, color: '#fff', fontWeight: 700 }} />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {tab2AirModel
                      ? `Set in Tab 2 (threat assessment: enemy air defense ${tab2EnemyAd}, with the air situation). This field is not used while Tab 2 sets the enemy air defense.`
                      : (adEntry?.educational?.teaching_note || '')}
                  </FormHelperText>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('tab3.weather_conditions')}</InputLabel>
                  <Select
                    value={tacticalEvacConfig.weather}
                    label={t('tab3.weather_conditions')}
                    disabled={Boolean(tab2AirModel)}
                    onChange={(e) => setTacticalEvacConfig(prev => ({ ...prev, weather: e.target.value }))}
                  >
                    {wxOptions.map(opt => (
                      <MenuItem key={opt.code} value={opt.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{expandWeatherLabel(opt.description)}</Typography>
                          <Chip label={`${Math.round(opt.sortie_modifier * 100)}%`} size="small"
                            sx={{ bgcolor: opt.color, color: '#fff', fontWeight: 700 }} />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {tab2AirModel
                    ? <FormHelperText>Weather of the operation is set in Tab 2 (one weather for road and air); this field is not used.</FormHelperText>
                    : (wxEntry?.notes && <FormHelperText sx={{ color: 'error.main' }}>{wxEntry.notes}</FormHelperText>)}
                </FormControl>

                <Alert severity={combinedColor === 'success' ? 'success' : combinedColor === 'warning' ? 'warning' : 'error'}>
                  <Typography variant="body2">
                    <strong>Effective air availability: {combined}%</strong>
                    {tab2AirModel
                      ? ` (air situation ${Math.round(tab2AirModel.parts.air_situation * 100)}% x enemy air defense ${Math.round(tab2AirModel.parts.enemy_air_defense * 100)}% x weather ${Math.round(tab2AirModel.parts.weather * 100)}%, from Tab 2)`
                      : <>{' '}(AD {Math.round((adEntry?.sortie_modifier ?? 1) * 100)}%
                    {' '}x Weather {Math.round((wxEntry?.sortie_modifier ?? 1) * 100)}%)</>}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Nominal {nominalSorties} sorties/day
                    {' '}&rarr; <strong>Effective: {effectiveSorties} sorties/day</strong>
                  </Typography>
                  {combined === 0 && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                      No air MEDEVAC possible. Ground evacuation only.
                    </Typography>
                  )}
                </Alert>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      );
    })()}

    {/* Sub-Section B.1: Evacuation Distances (formerly 3G) */}
    {hasForceBuilderData && forceBuilderFacilities.length > 0 && mode === 'EXPERT' && (
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{MEDICAL_ICONS.AMBULANCE} {t('tab3.b1_evac_distances')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Configure actual evacuation distances and terrain factors for each facility. 
              Distances are auto-calculated from FEBA positions but can be manually adjusted.
            </Typography>
          </Alert>
          
          {/* UI-010: Evacuation Chain Visualization */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('tab3.evac_chain_overview')}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 1,
              py: 2
            }}>
              {/* POI */}
              <Chip label="POI" size="small" sx={{ bgcolor: '#ffcdd2' }} />
              <Typography variant="body2" color="text.secondary">-></Typography>
              
              {/* CCP */}
              <Tooltip title="Casualty Collection Point - TCCC performed here">
                <Chip label="CCP" size="small" sx={{ bgcolor: '#ffcdd2' }} />
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
 <em style={{ fontSize: "0.75rem" }}>PULL</em> ->
              </Typography>
              
              {/* Role 1 */}
              <Tooltip title={`${forceBuilderFacilities.filter(f => f.type === 'ROLE1').length} Role 1 Facilities`}>
                <Chip 
                  label={`R1 (${forceBuilderFacilities.filter(f => f.type === 'ROLE1').length})`} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
 <em style={{ fontSize: "0.75rem" }}>PULL</em> ->
              </Typography>
              
              {/* Role 2 Basic */}
              <Tooltip title={`${forceBuilderFacilities.filter(f => f.type === 'ROLE2_BASIC').length} Role 2 Basic Facilities - First surgical capability`}>
                <Chip 
                  label={`R2B (${forceBuilderFacilities.filter(f => f.type === 'ROLE2_BASIC').length})`} 
                  size="small" 
                  color="secondary"
                  variant="outlined"
                />
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
 <em style={{ fontSize: "0.75rem" }}>AUTO</em> ->
              </Typography>
              
              {/* Role 2 Enhanced */}
              <Tooltip title={`${forceBuilderFacilities.filter(f => f.type === 'ROLE2_ENHANCED').length} Role 2 Enhanced Facilities`}>
                <Chip 
                  label={`R2E (${forceBuilderFacilities.filter(f => f.type === 'ROLE2_ENHANCED').length})`} 
                  size="small" 
                  sx={{ bgcolor: '#fff3e0', borderColor: '#ffb74d', border: '1px solid' }}
                />
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
 <em style={{ fontSize: "0.75rem" }}>AUTO</em> ->
              </Typography>
              
              {/* Role 3 */}
              <Tooltip title={`${forceBuilderFacilities.filter(f => f.type === 'ROLE3').length} Role 3 Combat Support Hospital`}>
                <Chip 
                  label={`R3 (${forceBuilderFacilities.filter(f => f.type === 'ROLE3').length})`} 
                  size="small" 
                  color="success"
                  variant="outlined"
                />
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
 <em style={{ fontSize: "0.75rem" }}>STRATEVAC</em> ->
              </Typography>
              
              {/* Role 4 */}
              <Tooltip title="Role 4 - Strategic evacuation to national/allied hospitals">
                <Chip 
                  label="R4" 
                  size="small" 
                  sx={{ bgcolor: '#e8f5e9', borderColor: '#81c784', border: '1px solid' }}
                />
              </Tooltip>
            </Box>
            
            {/* Golden Hour Summary - Only for pre-surgical facilities (Role 1, Role 2 Basic) */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="success.main">
                  {forceBuilderFacilities.filter(f => needsGoldenHourCheck(f) && f.evacuation?.golden_hour_compliant).length}
                </Typography>
                <Typography variant="caption" color="text.secondary">Golden Hour OK</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="error.main">
                  {forceBuilderFacilities.filter(f => needsGoldenHourCheck(f) && f.evacuation && !f.evacuation.golden_hour_compliant && f.evacuation.receiving_facility_id).length}
                </Typography>
                <Typography variant="caption" color="text.secondary">Exceeds {GOLDEN_HOUR_MINUTES} min</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  {Math.round(forceBuilderFacilities.filter(f => f.evacuation?.receiving_facility_id).reduce((sum, f) => sum + (f.evacuation?.estimated_time_min || 0), 0) / Math.max(forceBuilderFacilities.filter(f => f.evacuation?.receiving_facility_id).length, 1))} min
                </Typography>
                <Typography variant="caption" color="text.secondary">Avg Evac Time</Typography>
              </Box>
            </Box>
          </Paper>
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>From Facility</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>To Facility</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 100 }} align="center">Distance (km)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Est. Time</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 100 }} align="center">Golden Hour</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Terrain</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Route Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forceBuilderFacilities
                  .filter(f => f.evacuation && f.evacuation.receiving_facility_id)
                  .map((facility) => (
                    <TableRow 
                      key={facility.id}
                      sx={{ 
                        '&:hover': { bgcolor: 'action.hover' },
                        // Only highlight red for pre-surgical facilities (R1, R2B) that exceed golden hour
                        bgcolor: (needsGoldenHourCheck(facility) && !facility.evacuation?.golden_hour_compliant) ? 'error.50' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {facility.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {/* ENUM-SWEEP FIX */}
                            {({ ROLE1: 'Role 1', ROLE2_BASIC: 'Role 2 Basic', ROLE2_ENHANCED: 'Role 2 Enhanced', ROLE3: 'Role 3' }[facility.type] ?? facility.type)} | FEBA: {facility.position_feba_km}km
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {facility.evacuation?.receiving_facility_name || 'Not configured'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {facility.evacuation?.receiving_facility_type}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          value={facility.evacuation?.distance_km || 0}
                          onChange={(e) => {
                            const newDistance = parseInt(e.target.value) || 0;
                            const updatedFacilities = forceBuilderFacilities.map(f => {
                              if (f.id === facility.id) {
                                return recalculateFacilityEvacuation(f, { distance_km: newDistance }, forceBuilderFacilities);
                              }
                              return f;
                            });
                            setForceBuilderFacilities(updatedFacilities);
                          }}
                          inputProps={{ min: 1, max: 500, style: { textAlign: 'center' } }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={`${facility.evacuation?.estimated_time_min || 0} min`}
                          color={!needsGoldenHourCheck(facility) ? 'default' : (facility.evacuation?.golden_hour_compliant ? 'success' : 'error')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {!needsGoldenHourCheck(facility) ? (
                          <Chip size="small" label="N/A" color="default" title="Facility has surgical capability - Golden Hour check not applicable" />
                        ) : facility.evacuation?.golden_hour_compliant ? (
                          <Chip size="small" icon={<CheckCircleIcon />} label="OK" color="success" />
                        ) : (
                          <Chip size="small" icon={<WarningIcon />} label="EXCEEDED" color="error" />
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={facility.evacuation?.terrain_type || 'ROLLING_HILLS'}
                            onChange={(e) => {
                              const newTerrain = e.target.value;
                              const updatedFacilities = forceBuilderFacilities.map(f => {
                                if (f.id === facility.id) {
                                  return recalculateFacilityEvacuation(
                                    f, 
                                    { 
                                      distance_km: f.evacuation.distance_km,
                                      terrain_type: newTerrain 
                                    }, 
                                    forceBuilderFacilities
                                  );
                                }
                                return f;
                              });
                              setForceBuilderFacilities(updatedFacilities);
                            }}
                          >
                            {getTerrainOptions().map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={facility.evacuation?.route_type || 'PAVED_SECONDARY'}
                            onChange={(e) => {
                              const newRouteType = e.target.value;
                              const updatedFacilities = forceBuilderFacilities.map(f => {
                                if (f.id === facility.id) {
                                  return recalculateFacilityEvacuation(
                                    f, 
                                    { 
                                      distance_km: f.evacuation.distance_km,
                                      terrain_type: f.evacuation.terrain_type,
                                      route_type: newRouteType 
                                    }, 
                                    forceBuilderFacilities
                                  );
                                }
                                return f;
                              });
                              setForceBuilderFacilities(updatedFacilities);
                            }}
                          >
                            {getRouteOptions().map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Summary Statistics - Golden Hour only applies to pre-surgical facilities */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Total Routes: ${forceBuilderFacilities.filter(f => f.evacuation?.receiving_facility_id).length}`}
              color="primary"
            />
            <Chip 
              label={`Golden Hour Compliant: ${forceBuilderFacilities.filter(f => needsGoldenHourCheck(f) && f.evacuation?.golden_hour_compliant).length}`}
              color="success"
            />
            <Chip 
              label={`Exceeds Golden Hour: ${forceBuilderFacilities.filter(f => needsGoldenHourCheck(f) && f.evacuation && !f.evacuation.golden_hour_compliant && f.evacuation.receiving_facility_id).length}`}
              color="error"
            />
            <Chip 
              label={`Surgical Facilities (N/A): ${forceBuilderFacilities.filter(f => !needsGoldenHourCheck(f) && f.evacuation?.receiving_facility_id).length}`}
              color="default"
              title="Role 2E/Role 3 have surgery - Golden Hour check not applicable"
            />
          </Box>
          
          {/* Warning for golden hour violations - Only for pre-surgical facilities */}
          {forceBuilderFacilities.some(f => needsGoldenHourCheck(f) && f.evacuation && !f.evacuation.golden_hour_compliant && f.evacuation.receiving_facility_id) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> Some Role 1/Role 2 Basic evacuation routes exceed the {GOLDEN_HOUR_MINUTES}-minute "Golden Hour" threshold. 
                Consider repositioning facilities, using helicopter evacuation, or establishing forward staging areas.
              </Typography>
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>
    )}
{/* Sub-Section 3B.2: Ground Evacuation/CASEVAC (formerly 3J) */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{TRANSPORT_ICONS.TRUCK} {t('tab3.b2_casevac_ground')}</Typography>
            <Chip 
              label={casevacConfig.enabled ? `${casevacConfig.availability} (${casevacConfig.availabilityPercent}%)` : 'Disabled'} 
              size="small" 
              color={casevacConfig.enabled ? 'warning' : 'default'}
              variant="outlined"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>CASEVAC — Informational Tracking Only (AJMedP-2)</AlertTitle>
            <Typography variant="body2">
              <strong>CASEVAC</strong> (Casualty Evacuation) uses non-medical vehicles (combat vehicles, utility trucks)
              to move casualties.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Per <strong>AJMedP-2</strong> and <strong>AJP 4.10C</strong>: <em>"CASEVAC is unplanned and
              opportunistic. This type of movement will inevitably occur but is <strong>NOT</strong> a factor in
              planning of the medical footprint and therefore cannot have influence on the medical estimate
              process."</em>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Configuration saved here is for <strong>situational awareness and reporting only</strong>.
            </Typography>
          </Alert>
          
          <FormControlLabel
            control={
              <Switch
                checked={casevacConfig.enabled}
                onChange={(e) => handleCasevacUpdate('enabled', e.target.checked)}
              />
            }
            label={t('tab3.enable_casevac_overflow')}
          />
          
          {casevacConfig.enabled && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Availability Selection */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('tab3.casevac_availability')}</InputLabel>
                    <Select
                      value={casevacConfig.availability}
                      label={t('tab3.casevac_availability')}
                      onChange={(e) => handleCasevacUpdate('availability', e.target.value)}
                    >
                      <MenuItem value="UNAVAILABLE">
                        <Box>
                          <Typography>Unavailable (10%)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Combat units fully committed, no vehicles available
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="LIMITED">
                        <Box>
                          <Typography>Limited (50%)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Some combat vehicles can be tasked when needed
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="ON_REQUEST">
                        <Box>
                          <Typography>On Request (80%) - Default</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Standard availability, request through chain of command
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="DEDICATED">
                        <Box>
                          <Typography>Dedicated (95%)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Vehicles pre-assigned for CASEVAC mission
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                    <FormHelperText>
                      Percentage of overflow casualties that can be evacuated by CASEVAC
                    </FormHelperText>
                  </FormControl>
                </Grid>
                
                {/* Notes */}
                {mode === 'EXPERT' && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={t('tab3.casevac_notes')}
                      multiline
                      rows={2}
                      fullWidth
                      value={casevacConfig.notes}
                      onChange={(e) => handleCasevacUpdate('notes', e.target.value)}
                      placeholder="E.g., 'Coordinated with 3rd Bn for Bradley ambulance support' or 'Host nation civil ambulances available'"
                      helperText="Document CASEVAC arrangements and coordination details"
                    />
                  </Grid>
                )}
                
                {/* Summary Alert — ARCH-010 compliant */}
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    <AlertTitle>Medical Estimate — AJMedP-2</AlertTitle>
                    <Typography variant="body2">
                      <strong>DOW modelling:</strong> All MEDEVAC overflow is treated as unevacuated
                      in the medical estimate (<strong>1.60× DOW penalty</strong>), regardless of
                      CASEVAC availability. CASEVAC is unplanned and cannot influence the medical estimate.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>What this config does:</strong> CASEVAC tracking (vehicles used, casualties
                      moved) will appear in reports as <em>situational awareness data only </em> —
                      it does not reduce the projected DOW count.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
      
      
      {/* ================================================================== */}
      {/* SUB-SECTION 3B.3: EVACUATION ROUTES (Part of Section 3B - Evacuation System) */}
      {/* ================================================================== */}
      
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <Typography variant="h6">{TRANSPORT_ICONS.ROAD} {t('tab3.b3_evac_routes')}</Typography>
              {mode === 'STANDARD' && <Chip label={t('common.read_only')} size="small" variant="outlined" />}
            </Box>
          </Box>

          {/* BUG-B1B3-001: Priority hierarchy notice — B.1 per-facility distances override B.3 */}
          {hasForceBuilderData && forceBuilderFacilities.length > 0 && mode === 'EXPERT' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {t('tab3.b1_active_info')}
              </Typography>
            </Alert>
          )}

          {/* LOC-001: Warn when Tab3 route status is more optimistic than Tab2 loc_config implies */}
          {(() => {
            const locCfg2 = scenario?.operation_parameters?.loc_config;
            if (!locCfg2) return null;
            const LOC_SEC_ORDER    = { SECURE: 0, GUARDED: 1, CONTESTED: 2, INTERDICTED: 3, DENIED: 4 };
            const ROUTE_STATUS_ORDER = { OPEN: 0, DAMAGED: 1, CONTESTED: 2, CLOSED: 3 };
            const LOC_SEC_TO_MIN_STATUS = {
              SECURE: 'OPEN', GUARDED: 'OPEN',
              CONTESTED: 'CONTESTED', INTERDICTED: 'CONTESTED', DENIED: 'CLOSED'
            };
            const secKey2       = (locCfg2.loc_security || 'SECURE').toUpperCase();
            const impliedStatus = LOC_SEC_TO_MIN_STATUS[secKey2] || 'OPEN';
            const tab3Primary   = (evacuationRoutes.r1_to_r2b?.primary?.status || 'OPEN').toUpperCase();
            const locMismatch   = LOC_SEC_ORDER[secKey2] >= 2 &&
              ROUTE_STATUS_ORDER[tab3Primary] < ROUTE_STATUS_ORDER[impliedStatus];
            if (!locMismatch) return null;
            return (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>LOC / Evacuation Route Mismatch</AlertTitle>
                <Typography variant="body2">
                  Tab 2 LOC security is <strong>{secKey2}</strong>, implying route status
                  at least <strong>{impliedStatus}</strong>.
                  Tab 3 primary route (R1→R2B) shows <strong>{tab3Primary}</strong> — more permissive
                  than the LOC situation suggests.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  If the route is intentionally secured (dedicated escorts, alternate corridor),
                  this warning can be ignored. Otherwise update route status in Expert Mode,
                  or re-save Tab 2 to re-seed these values.
                </Typography>
              </Alert>
            );
          })()}

          {mode === 'STANDARD' ? (
            <>
              {/* STANDARD MODE - Read-only display */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Evacuation routes configured with NATO standard assumptions. Switch to Expert Mode to customize route conditions, surface types, and weather impact.
                </Typography>
              </Alert>
              
              <Grid container spacing={2}>
                {Object.entries(evacuationRoutes).map(([segmentId, segment]) => {
                  const segmentNames = {
                    r1_to_r2b: 'Role 1 -> Role 2 Basic',
                    r2b_to_r2e: 'Role 2 Basic -> Role 2 Enhanced',
                    r2e_to_r3: 'Role 2 Enhanced -> Role 3',
                    r3_to_r4: 'Role 3 -> Role 4'
                  };
                  
                  return (
                    <Grid size={{ xs: 12, sm: 6 }} key={segmentId}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom>{segmentNames[segmentId]}</Typography>
                        <Typography variant="body2">
                          Primary: {CHAIN_LEGS.includes(segmentId) ? (chainLegs[segmentId].km ?? segment.primary.distance_km) : segment.primary.distance_km} km ({SURFACE_TYPES[segment.primary.surface]?.label || segment.primary.surface})
                        </Typography>
                        <Chip 
                          label={ROUTE_STATUS[segment.primary.status]?.label || segment.primary.status}
                          size="small"
                          color={ROUTE_STATUS[segment.primary.status]?.color || 'default'}
                          sx={{ mt: 0.5 }}
                        />
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <>
              {/* EXPERT MODE - Full configuration */}
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {t('tab3.evac_route_configure_info')}
                </Typography>
              </Alert>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {t('tab3.evac_route_tab2_note')}
                </Typography>
              </Alert>
              
              {Object.entries(evacuationRoutes).map(([segmentId, segment]) => {
                const segmentNames = {
                  r1_to_r2b: t('tab3.seg_r1_r2b'),
                  r2b_to_r2e: t('tab3.seg_r2b_r2e'),
                  r2e_to_r3: t('tab3.seg_r2e_r3'),
                  r3_to_r4: t('tab3.seg_r3_r4')
                };
                
                return (
                  <Accordion key={segmentId} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography>{segmentNames[segmentId]}</Typography>
                        <Chip 
                          label={`${CHAIN_LEGS.includes(segmentId) ? (chainLegs[segmentId].km ?? segment.primary.distance_km) : segment.primary.distance_km} km`}
                          size="small"
                          color={ROUTE_STATUS[segment.primary.status]?.color || 'default'}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* Primary Route */}
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Primary Route
                          </Typography>
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label={t('tab3.route_name')}
                            value={segment.primary.name}
                            onChange={(e) => setEvacuationRoutes(prev => ({
                              ...prev,
                              [segmentId]: {
                                ...prev[segmentId],
                                primary: { ...prev[segmentId].primary, name: e.target.value }
                              }
                            }))}
                            size="small"
                            helperText="e.g., MSR Alpha, Highway 1"
                          />
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label={t('tab3.distance_km')}
                            type="number"
                            value={CHAIN_LEGS.includes(segmentId) ? (chainLegs[segmentId].km ?? segment.primary.distance_km) : segment.primary.distance_km}
                            disabled={CHAIN_LEGS.includes(segmentId)}
                            helperText={CHAIN_LEGS.includes(segmentId) ? 'Set in Medical chain distances (placed facilities or override)' : undefined}
                            onChange={(e) => setEvacuationRoutes(prev => ({
                              ...prev,
                              [segmentId]: {
                                ...prev[segmentId],
                                primary: { ...prev[segmentId].primary, distance_km: parseInt(e.target.value) || 0 }
                              }
                            }))}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t('tab3.surface_type')}</InputLabel>
                            <Select
                              value={segment.primary.surface}
                              onChange={(e) => setEvacuationRoutes(prev => ({
                                ...prev,
                                [segmentId]: {
                                  ...prev[segmentId],
                                  primary: { ...prev[segmentId].primary, surface: e.target.value }
                                }
                              }))}
                              label={t('tab3.surface_type')}
                            >
                              {Object.entries(SURFACE_TYPES).map(([key, val]) => (
                                <MenuItem key={key} value={key}>{val.label} ({val.base_speed_kph} km/h)</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t('tab3.route_status')}</InputLabel>
                            <Select
                              value={segment.primary.status}
                              onChange={(e) => setEvacuationRoutes(prev => ({
                                ...prev,
                                [segmentId]: {
                                  ...prev[segmentId],
                                  primary: { ...prev[segmentId].primary, status: e.target.value }
                                }
                              }))}
                              label={t('tab3.route_status')}
                            >
                              {Object.entries(ROUTE_STATUS).map(([key, val]) => (
                                <MenuItem key={key} value={key}>{val.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t('tab3.weather_impact')}</InputLabel>
                            <Select
                              value={segment.primary.weather_impact}
                              onChange={(e) => setEvacuationRoutes(prev => ({
                                ...prev,
                                [segmentId]: {
                                  ...prev[segmentId],
                                  primary: { ...prev[segmentId].primary, weather_impact: e.target.value }
                                }
                              }))}
                              label={t('tab3.weather_impact')}
                            >
                              {Object.entries(WEATHER_IMPACT).map(([key, val]) => (
                                <MenuItem key={key} value={key}>{val.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        {/* Alternate Route Toggle */}
                        <Grid size={{ xs: 12 }}>
                          <Divider sx={{ my: 1 }} />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={segment.alternate !== null}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEvacuationRoutes(prev => ({
                                      ...prev,
                                      [segmentId]: {
                                        ...prev[segmentId],
                                        alternate: {
                                          name: '',
                                          distance_km: segment.primary.distance_km + 10,
                                          surface: 'GRAVEL',
                                          status: 'OPEN',
                                          weather_impact: 'MEDIUM'
                                        }
                                      }
                                    }));
                                  } else {
                                    setEvacuationRoutes(prev => ({
                                      ...prev,
                                      [segmentId]: {
                                        ...prev[segmentId],
                                        alternate: null
                                      }
                                    }));
                                  }
                                }}
                              />
                            }
                            label={t('tab3.configure_alt_route')}
                          />
                        </Grid>
                        
                        {/* Alternate Route Configuration */}
                        {segment.alternate && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                Alternate Route
                              </Typography>
                            </Grid>
                            
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField
                                fullWidth
                                label={t('tab3.route_name')}
                                value={segment.alternate.name}
                                onChange={(e) => setEvacuationRoutes(prev => ({
                                  ...prev,
                                  [segmentId]: {
                                    ...prev[segmentId],
                                    alternate: { ...prev[segmentId].alternate, name: e.target.value }
                                  }
                                }))}
                                size="small"
                                helperText="Backup route name"
                              />
                            </Grid>
                            
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField
                                fullWidth
                                label={t('tab3.distance_km')}
                                type="number"
                                value={segment.alternate.distance_km}
                                onChange={(e) => setEvacuationRoutes(prev => ({
                                  ...prev,
                                  [segmentId]: {
                                    ...prev[segmentId],
                                    alternate: { ...prev[segmentId].alternate, distance_km: parseInt(e.target.value) || 0 }
                                  }
                                }))}
                                size="small"
                              />
                            </Grid>
                            
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>{t('tab3.surface_type')}</InputLabel>
                                <Select
                                  value={segment.alternate.surface}
                                  onChange={(e) => setEvacuationRoutes(prev => ({
                                    ...prev,
                                    [segmentId]: {
                                      ...prev[segmentId],
                                      alternate: { ...prev[segmentId].alternate, surface: e.target.value }
                                    }
                                  }))}
                                  label={t('tab3.surface_type')}
                                >
                                  {Object.entries(SURFACE_TYPES).map(([key, val]) => (
                                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>{t('tab3.route_status')}</InputLabel>
                                <Select
                                  value={segment.alternate.status}
                                  onChange={(e) => setEvacuationRoutes(prev => ({
                                    ...prev,
                                    [segmentId]: {
                                      ...prev[segmentId],
                                      alternate: { ...prev[segmentId].alternate, status: e.target.value }
                                    }
                                  }))}
                                  label={t('tab3.route_status')}
                                >
                                  {Object.entries(ROUTE_STATUS).map(([key, val]) => (
                                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>{t('tab3.weather_impact')}</InputLabel>
                                <Select
                                  value={segment.alternate.weather_impact}
                                  onChange={(e) => setEvacuationRoutes(prev => ({
                                    ...prev,
                                    [segmentId]: {
                                      ...prev[segmentId],
                                      alternate: { ...prev[segmentId].alternate, weather_impact: e.target.value }
                                    }
                                  }))}
                                  label={t('tab3.weather_impact')}
                                >
                                  {Object.entries(WEATHER_IMPACT).map(([key, val]) => (
                                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
              
              {/* Route Impact Summary */}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>{t('tab3.route_modifiers')}</strong><br/>
                  {t('tab3.route_modifier_status_legend')}<br/>
                  {t('tab3.route_modifier_weather_legend')}
                </Typography>
              </Alert>
            </>
          )}
      
        </CardContent>
      </Card>
      
      {/* Mode-specific content */}
      {mode === 'STANDARD' ? renderStandardModeView() : renderExpertModeView()}
      
      {/* Validation Display */}
      {renderValidation()}
      
      {/* v4.1.0: Calculator Status */}
      {isCalculating && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <AlertTitle>Running Calculator...</AlertTitle>
          Automatically calculating casualties and medical requirements based on your configuration.
          This will provide Tab 4 with accurate data immediately.
        </Alert>
      )}
      
      {calculationError && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <AlertTitle>Calculator Error</AlertTitle>
          {calculationError}
          <br/>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Medical configuration will still be saved. Tab 4 will use preliminary estimates instead.
          </Typography>
        </Alert>
      )}

      {/* v4.3.10: Calculator Error Dialog — replaces alert() to provide real OK/Cancel choice */}
      <Dialog
        open={showCalcErrorDialog}
        onClose={() => setShowCalcErrorDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          Calculator Error
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontSize: '0.8rem', wordBreak: 'break-word' }}>
            {calculationError}
          </Typography>
          <Typography variant="body2">
            The medical configuration is valid and can be saved. Tab 4 will use preliminary estimates until
            the calculator issue is resolved.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            <strong>Save Configuration Only</strong> — saves Tab 3 without calculator results and proceeds to Tab 4.<br />
            <strong>Fix Issues First</strong> — stays on Tab 3 so you can correct the problem.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCalcErrorDialog(false)}
            color="inherit"
          >
            Fix Issues First
          </Button>
          <Button
            onClick={() => {
              setShowCalcErrorDialog(false);
              if (pendingErrorSave) pendingErrorSave();
            }}
            variant="contained"
            color="warning"
          >
            Save Configuration Only
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => onNavigate && onNavigate(1)}
        >
          {t('tab3.back_to_tab2')}
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (mode === 'EXPERT') {
                handleResetDistances();
                handleResetAssets();
              }
            }}
            disabled={mode === 'STANDARD'}
          >
            {t('tab3.reset_to_defaults')}
          </Button>
          
          <Button
            variant="contained"
            size="large"
            onClick={handleSaveAndComplete}
            disabled={!isValid || isCalculating}
            startIcon={
              isCalculating ? <CircularProgress size={20} /> :
              isValid ? <CheckCircleIcon /> : 
              <WarningIcon />
            }
          >
            {isCalculating ? t('common.calculating') :
             isValid ? t('tab3.save_continue') :
             t('tab3.fix_errors')}
          </Button>
        </Box>
      </Box>
      
      {/* Help Text */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 1, fontSize: '1rem' }} /> {t('tab3.config_help')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Standard Mode:</strong> All values are auto-configured based on NATO standards. Review the configuration
          and click "Save & Continue" to proceed to logistics planning.<br/>
          <strong>Expert Mode:</strong> Customize all medical parameters. Use sliders and inputs to adjust distances,
          asset counts, and enable advanced staging facilities with NATO standard sizes (Small/Medium/Large). 
          Personnel and supplies are automatically calculated based on facility size.<br/>
          Available features: Facility size selection, unit allocation, personnel tracking, and 
          supply consumption estimation for Tab 4 integration.
        </Typography>
      </Box>
      
      {/* PHASE 2: Facility Size Selection Dialog */}
      {renderFacilitySizeDialog()}
      
      {/* ENH-002: Attach Asset Dialog */}
      <Dialog
        open={attachDialog.open}
        onClose={handleCloseAttachDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Attach Evacuation Asset</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Attached assets augment this facility's organic capacity for the specified day range.
            Use MOU/SOFA reference to document the authorisation (AJP-4.10C §5.4).
          </Alert>

          {/* Asset type */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('tab3.asset_type')}</InputLabel>
            <Select
              value={attachForm.asset_type}
              label={t('tab3.asset_type')}
              onChange={e => setAttachForm(prev => ({
                ...prev,
                asset_type: e.target.value,
                model: e.target.value === 'HELICOPTER' ? 'IAR330' : 'TABC79'
              }))}
            >
              <MenuItem value="HELICOPTER">Helicopter (MEDEVAC)</MenuItem>
              <MenuItem value="AMBULANCE">Ground Ambulance</MenuItem>
            </Select>
          </FormControl>

          {/* Model */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('tab3.model')}</InputLabel>
            <Select
              value={attachForm.model}
              label={t('tab3.model')}
              onChange={e => setAttachForm(prev => ({ ...prev, model: e.target.value }))}
            >
              {attachForm.asset_type === 'HELICOPTER'
                ? Object.entries(HELICOPTER_SPECS)
                    .filter(([, spec]) => spec.code && spec.name)
                    .map(([key, spec]) => (
                      <MenuItem key={key} value={key}>
                        {spec.name}
                        {spec.capacity?.stretchers != null && (
                          <Chip
                            label={`${spec.capacity.stretchers} litter`}
                            size="small"
                            sx={{ ml: 1 }}
                            variant="outlined"
                          />
                        )}
                      </MenuItem>
                    ))
                : Object.entries(AMBULANCE_TYPES)
                    // BUG-154 FIX: AMBULANCE_TYPES entries use .name not .label.
                    // spec.label was always undefined → filter returned [] → empty dropdown.
                    .filter(([, spec]) => spec.code && spec.name)
                    .map(([key, spec]) => (
                      <MenuItem key={key} value={key}>
                        {spec.name}
                        {(spec.litter_capacity ?? spec.capacity?.stretchers) != null && (
                          <Chip
                            label={`${spec.litter_capacity ?? spec.capacity?.stretchers} litter`}
                            size="small"
                            sx={{ ml: 1 }}
                            variant="outlined"
                          />
                        )}
                      </MenuItem>
                    ))
              }
            </Select>
          </FormControl>

          {/* Quantity + day range on one row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('tab3.quantity')}
                inputProps={{ min: 1, max: 20 }}
                value={attachForm.quantity}
                onChange={e => setAttachForm(prev => ({ ...prev, quantity: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('tab3.attach_day')}
                inputProps={{ min: 1, max: scenario?.operation_parameters?.duration_days ?? 30 }}
                value={attachForm.attach_day}
                onChange={e => setAttachForm(prev => ({ ...prev, attach_day: e.target.value }))}
                helperText="Day attachment begins"
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              {(() => {
                const attachDayVal = parseInt(attachForm.attach_day) || 1;
                const detachDayVal = attachForm.detach_day !== '' ? parseInt(attachForm.detach_day) : null;
                const detachDayError = detachDayVal !== null && detachDayVal <= attachDayVal;
                return (
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab3.detach_day')}
                    inputProps={{
                      min: attachDayVal + 1,
                      max: scenario?.operation_parameters?.duration_days ?? 30
                    }}
                    value={attachForm.detach_day}
                    onChange={e => setAttachForm(prev => ({ ...prev, detach_day: e.target.value }))}
                    error={detachDayError}
                    helperText={
                      detachDayError
                        ? `Must be after Attach Day (D+${attachDayVal})`
                        : 'Leave blank = end of op'
                    }
                  />
                );
              })()}
            </Grid>
          </Grid>

          {/* Source unit + MOU */}
          <TextField
            fullWidth
            label={t('tab3.source_unit')}
            placeholder="e.g. Romanian Air Force 71st Air Base"
            value={attachForm.source_unit}
            onChange={e => setAttachForm(prev => ({ ...prev, source_unit: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('tab3.mou_sofa_ref')}
            placeholder="e.g. RO-NATO MOU 2024-MEDEVAC-001 (optional)"
            value={attachForm.mou_reference}
            onChange={e => setAttachForm(prev => ({ ...prev, mou_reference: e.target.value }))}
            helperText="Document the legal authorisation for reporting purposes (AJP-4.10C §5.4)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttachDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAttachment}
            disabled={(() => {
              if (!attachForm.source_unit.trim()) return true;
              const attachDayVal = parseInt(attachForm.attach_day) || 1;
              const detachDayVal = attachForm.detach_day !== '' ? parseInt(attachForm.detach_day) : null;
              if (detachDayVal !== null && detachDayVal <= attachDayVal) return true;
              return false;
            })()}
          >
            Attach Asset
          </Button>
        </DialogActions>
      </Dialog>

      {/* ENH-002: Detach Asset Dialog */}
      <Dialog
        open={detachDialog.open}
        onClose={handleCloseDetachDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record Asset Detachment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Detached assets are subtracted from this facility's effective capacity for the
            specified day range. Use this to record assets temporarily reassigned to another unit.
          </Alert>

          {/* Asset type */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('tab3.asset_type')}</InputLabel>
            <Select
              value={detachForm.asset_type}
              label={t('tab3.asset_type')}
              onChange={e => setDetachForm(prev => ({ ...prev, asset_type: e.target.value }))}
            >
              <MenuItem value="HELICOPTER">Helicopter</MenuItem>
              <MenuItem value="AMBULANCE">Ground Ambulance</MenuItem>
            </Select>
          </FormControl>

          {/* Quantity + day range */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('tab3.quantity')}
                inputProps={{ min: 1, max: 20 }}
                value={detachForm.quantity}
                onChange={e => setDetachForm(prev => ({ ...prev, quantity: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('tab3.detach_day')}
                inputProps={{ min: 1, max: scenario?.operation_parameters?.duration_days ?? 30 }}
                value={detachForm.detach_day}
                onChange={e => setDetachForm(prev => ({ ...prev, detach_day: e.target.value }))}
                helperText="Day asset leaves"
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('tab3.return_day')}
                inputProps={{ min: 1, max: scenario?.operation_parameters?.duration_days ?? 30 }}
                value={detachForm.return_day}
                onChange={e => setDetachForm(prev => ({ ...prev, return_day: e.target.value }))}
                helperText="Leave blank = permanent"
              />
            </Grid>
          </Grid>

          {/* Destination */}
          <TextField
            fullWidth
            label={t('tab3.destination_unit')}
            placeholder="e.g. 2nd Brigade Role 2B FST support"
            value={detachForm.destination_unit}
            onChange={e => setDetachForm(prev => ({ ...prev, destination_unit: e.target.value }))}
            helperText="Unit receiving the asset"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetachDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSaveDetachment}
            disabled={!detachForm.destination_unit.trim()}
          >
            Record Detachment
          </Button>
        </DialogActions>
      </Dialog>

      {/* v4.3.2: Blue Light Protocol Dismiss Dialog */}
      <Dialog 
        open={blueLightDismissDialog.open} 
        onClose={handleCancelDismiss}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Dismiss MASCAL Escalation Protocol Recommendation
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Medical Assessment:</strong> Conditions warrant MASCAL Escalation Protocol (AMedP-1.10) activation.
            </Typography>
          </Alert>
          <Typography variant="body2" gutterBottom>
            Please provide the operational reason for <strong>not</strong> activating MASCAL Escalation Protocol (AMedP-1.10):
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('tab3.reason_dismissal')}
            placeholder="e.g., Under artillery fire - facility security insufficient; Insufficient blood products available; Command decision - operational tempo prevents protocol activation..."
            value={blueLightDismissDialog.reason}
            onChange={(e) => setBlueLightDismissDialog(prev => ({ ...prev, reason: e.target.value }))}
            sx={{ mt: 2 }}
            helperText="This reason will be included in planning reports for after-action review"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDismiss}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDismiss} 
            variant="contained"
            color="primary"
          >
            Confirm Dismissal
          </Button>
        </DialogActions>
      </Dialog>
      
    </Box>
  );
});

export default Tab3_Medical;

// ============================================================================
// END OF TAB 3 MEDICAL CONFIGURATION - PHASE 2
// ============================================================================
